import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';

// ─── useDebounce ──────────────────────────────────────────────────────────────
// Returns a debounced copy of `value` that only updates after `delay` ms of
// silence. Used to decouple fast-typing inputs from expensive re-renders.
function useDebounce(value, delay) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}


// ─── Shared constants ─────────────────────────────────────────────────────────
const PRESET_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#FBBF24', '#FDE047', '#84CC16',
  '#10B981', '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
  '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#64748B', '#1F2937'
];

const LAYER_ICONS = [
  // ── Fire / Rescue ──────────────────────────────────────────────────────────
  { value: 'fa-fire',              label: 'fire',              name: 'Fire',          type: 'fa', category: 'Fire' },
  { value: 'fa-fire-extinguisher', label: 'fire-extinguisher', name: 'Extinguisher',  type: 'fa', category: 'Fire' },
  { value: 'fa-fire-flame-curved', label: 'fire-flame-curved', name: 'Flame',         type: 'fa', category: 'Fire' },
  { value: 'fa-fire-flame-simple', label: 'fire-flame-simple', name: 'Simple Flame',  type: 'fa', category: 'Fire' },
  { value: 'fa-truck-fire',        label: 'truck-fire',        name: 'Fire Truck',    type: 'fa', category: 'Fire' },
  { value: 'fa-house-fire',        label: 'house-fire',        name: 'House Fire',    type: 'fa', category: 'Fire' },
  { value: 'fa-water',             label: 'water',             name: 'Water Source',  type: 'fa', category: 'Fire' },
  { value: 'fa-bucket',            label: 'bucket',            name: 'Bucket',        type: 'fa', category: 'Fire' },

  // ── Police / Law Enforcement ───────────────────────────────────────────────
  { value: 'fa-shield-halved',   label: 'shield-halved',   name: 'Police Badge',  type: 'fa', category: 'Police' },
  { value: 'fa-shield',          label: 'shield',          name: 'Shield',        type: 'fa', category: 'Police' },
  { value: 'fa-user-shield',     label: 'user-shield',     name: 'Security',      type: 'fa', category: 'Police' },
  { value: 'fa-handcuffs',       label: 'handcuffs',       name: 'Handcuffs',     type: 'fa', category: 'Police' },
  { value: 'fa-gavel',           label: 'gavel',           name: 'Gavel',         type: 'fa', category: 'Police' },
  { value: 'fa-fingerprint',     label: 'fingerprint',     name: 'Fingerprint',   type: 'fa', category: 'Police' },
  { value: 'fa-scale-balanced',  label: 'scale-balanced',  name: 'Justice',       type: 'fa', category: 'Police' },
  { value: 'fa-gun',             label: 'gun',             name: 'Firearm',       type: 'fa', category: 'Police' },
  { value: 'fa-magnifying-glass',label: 'magnifying-glass',name: 'Investigation', type: 'fa', category: 'Police' },
  { value: 'fa-eye',             label: 'eye',             name: 'Surveillance',  type: 'fa', category: 'Police' },
  { value: 'fa-car-burst',       label: 'car-burst',       name: 'Accident',      type: 'fa', category: 'Police' },

  // ── Medical / EMS ─────────────────────────────────────────────────────────
  { value: 'fa-truck-medical',   label: 'truck-medical',   name: 'Ambulance',     type: 'fa', category: 'Medical' },
  { value: 'fa-kit-medical',     label: 'kit-medical',     name: 'First Aid',     type: 'fa', category: 'Medical' },
  { value: 'fa-hospital',        label: 'hospital',        name: 'Hospital',      type: 'fa', category: 'Medical' },
  { value: 'fa-suitcase-medical',label: 'suitcase-medical',name: 'EMS Kit',       type: 'fa', category: 'Medical' },
  { value: 'fa-heart-pulse',     label: 'heart-pulse',     name: 'Vital Signs',   type: 'fa', category: 'Medical' },
  { value: 'fa-user-doctor',     label: 'user-doctor',     name: 'Doctor',        type: 'fa', category: 'Medical' },
  { value: 'fa-pills',           label: 'pills',           name: 'Medication',    type: 'fa', category: 'Medical' },
  { value: 'fa-syringe',         label: 'syringe',         name: 'Syringe',       type: 'fa', category: 'Medical' },
  { value: 'fa-stethoscope',     label: 'stethoscope',     name: 'Stethoscope',   type: 'fa', category: 'Medical' },
  { value: 'fa-wheelchair',      label: 'wheelchair',      name: 'Wheelchair',    type: 'fa', category: 'Medical' },
  { value: 'fa-bed-pulse',       label: 'bed-pulse',       name: 'ICU Bed',       type: 'fa', category: 'Medical' },
  { value: 'fa-staff-snake',     label: 'staff-snake',     name: 'Medical Symbol',type: 'fa', category: 'Medical' },
  { value: 'fa-brain',           label: 'brain',           name: 'Brain',         type: 'fa', category: 'Medical' },
  { value: 'fa-lungs',           label: 'lungs',           name: 'Lungs',         type: 'fa', category: 'Medical' },
  { value: 'fa-bandage',         label: 'bandage',         name: 'Bandage',       type: 'fa', category: 'Medical' },

  // ── Hazard / Emergency ────────────────────────────────────────────────────
  { value: 'fa-triangle-exclamation', label: 'triangle-exclamation', name: 'Warning',     type: 'fa', category: 'Hazard' },
  { value: 'fa-circle-exclamation',   label: 'circle-exclamation',   name: 'Alert',       type: 'fa', category: 'Hazard' },
  { value: 'fa-radiation',            label: 'radiation',            name: 'Radiation',   type: 'fa', category: 'Hazard' },
  { value: 'fa-biohazard',            label: 'biohazard',            name: 'Biohazard',   type: 'fa', category: 'Hazard' },
  { value: 'fa-skull-crossbones',     label: 'skull-crossbones',     name: 'Hazmat',      type: 'fa', category: 'Hazard' },
  { value: 'fa-explosion',            label: 'explosion',            name: 'Explosion',   type: 'fa', category: 'Hazard' },
  { value: 'fa-skull',                label: 'skull',                name: 'Skull',       type: 'fa', category: 'Hazard' },
  { value: 'fa-house-flood-water',    label: 'house-flood-water',    name: 'Flood',       type: 'fa', category: 'Hazard' },
  { value: 'fa-tornado',              label: 'tornado',              name: 'Tornado',     type: 'fa', category: 'Hazard' },
  { value: 'fa-hill-rockslide',       label: 'hill-rockslide',       name: 'Rockslide',   type: 'fa', category: 'Hazard' },
  { value: 'fa-person-drowning',      label: 'person-drowning',      name: 'Drowning',    type: 'fa', category: 'Hazard' },
  { value: 'fa-virus',                label: 'virus',                name: 'Virus/Pathogen', type: 'fa', category: 'Hazard' },

  // ── Vehicles / Transport ──────────────────────────────────────────────────
  { value: 'fa-car',         label: 'car',         name: 'Car',         type: 'fa', category: 'Vehicle' },
  { value: 'fa-truck',       label: 'truck',       name: 'Truck',       type: 'fa', category: 'Vehicle' },
  { value: 'fa-helicopter',  label: 'helicopter',  name: 'Helicopter',  type: 'fa', category: 'Vehicle' },
  { value: 'fa-motorcycle',  label: 'motorcycle',  name: 'Motorcycle',  type: 'fa', category: 'Vehicle' },
  { value: 'fa-bus',         label: 'bus',         name: 'Bus',         type: 'fa', category: 'Vehicle' },
  { value: 'fa-train',       label: 'train',       name: 'Train',       type: 'fa', category: 'Vehicle' },
  { value: 'fa-plane',       label: 'plane',       name: 'Aircraft',    type: 'fa', category: 'Vehicle' },
  { value: 'fa-ship',        label: 'ship',        name: 'Ship/Boat',   type: 'fa', category: 'Vehicle' },
  { value: 'fa-bicycle',     label: 'bicycle',     name: 'Bicycle',     type: 'fa', category: 'Vehicle' },
  { value: 'fa-truck-pickup',label: 'truck-pickup',name: 'Pickup Truck',type: 'fa', category: 'Vehicle' },
  { value: 'fa-van-shuttle', label: 'van-shuttle', name: 'Van/Shuttle', type: 'fa', category: 'Vehicle' },
  { value: 'fa-tractor',     label: 'tractor',     name: 'Tractor',     type: 'fa', category: 'Vehicle' },
  { value: 'fa-snowplow',    label: 'snowplow',    name: 'Snowplow',    type: 'fa', category: 'Vehicle' },

  // ── Location / Map ────────────────────────────────────────────────────────
  { value: 'fa-location-dot',        label: 'location-dot',        name: 'Location Pin',  type: 'fa', category: 'Location' },
  { value: 'fa-location-crosshairs', label: 'location-crosshairs', name: 'Crosshairs',    type: 'fa', category: 'Location' },
  { value: 'fa-location-pin',        label: 'location-pin',        name: 'Pin',           type: 'fa', category: 'Location' },
  { value: 'fa-map-pin',             label: 'map-pin',             name: 'Map Pin',       type: 'fa', category: 'Location' },
  { value: 'fa-bullseye',            label: 'bullseye',            name: 'Target',        type: 'fa', category: 'Location' },
  { value: 'fa-map',                 label: 'map',                 name: 'Map',           type: 'fa', category: 'Location' },
  { value: 'fa-compass',             label: 'compass',             name: 'Compass',       type: 'fa', category: 'Location' },
  { value: 'fa-signs-post',          label: 'signs-post',          name: 'Signpost',      type: 'fa', category: 'Location' },
  { value: 'fa-road',                label: 'road',                name: 'Road',          type: 'fa', category: 'Location' },
  { value: 'fa-bridge',              label: 'bridge',              name: 'Bridge',        type: 'fa', category: 'Location' },

  // ── Buildings / Infrastructure ─────────────────────────────────────────────
  { value: 'fa-house',            label: 'house',            name: 'House',         type: 'fa', category: 'Building' },
  { value: 'fa-building',         label: 'building',         name: 'Building',      type: 'fa', category: 'Building' },
  { value: 'fa-school',           label: 'school',           name: 'School',        type: 'fa', category: 'Building' },
  { value: 'fa-church',           label: 'church',           name: 'Church',        type: 'fa', category: 'Building' },
  { value: 'fa-store',            label: 'store',            name: 'Store',         type: 'fa', category: 'Building' },
  { value: 'fa-industry',         label: 'industry',         name: 'Industrial',    type: 'fa', category: 'Building' },
  { value: 'fa-warehouse',        label: 'warehouse',        name: 'Warehouse',     type: 'fa', category: 'Building' },
  { value: 'fa-hotel',            label: 'hotel',            name: 'Hotel',         type: 'fa', category: 'Building' },
  { value: 'fa-city',             label: 'city',             name: 'City',          type: 'fa', category: 'Building' },
  { value: 'fa-landmark',         label: 'landmark',         name: 'Landmark',      type: 'fa', category: 'Building' },
  { value: 'fa-gas-pump',         label: 'gas-pump',         name: 'Gas Station',   type: 'fa', category: 'Building' },
  { value: 'fa-charging-station', label: 'charging-station', name: 'EV Charger',    type: 'fa', category: 'Building' },
  { value: 'fa-tower-cell',       label: 'tower-cell',       name: 'Cell Tower',    type: 'fa', category: 'Building' },
  { value: 'fa-traffic-light',    label: 'traffic-light',    name: 'Traffic Light', type: 'fa', category: 'Building' },

  // ── Communication ─────────────────────────────────────────────────────────
  { value: 'fa-tower-broadcast', label: 'tower-broadcast', name: 'Radio Tower', type: 'fa', category: 'Comm' },
  { value: 'fa-walkie-talkie',   label: 'walkie-talkie',   name: 'Radio',       type: 'fa', category: 'Comm' },
  { value: 'fa-satellite-dish',  label: 'satellite-dish',  name: 'Satellite',   type: 'fa', category: 'Comm' },
  { value: 'fa-phone',           label: 'phone',           name: 'Phone',       type: 'fa', category: 'Comm' },
  { value: 'fa-bullhorn',        label: 'bullhorn',        name: 'Bullhorn',    type: 'fa', category: 'Comm' },
  { value: 'fa-bell',            label: 'bell',            name: 'Alert Bell',  type: 'fa', category: 'Comm' },
  { value: 'fa-wifi',            label: 'wifi',            name: 'WiFi',        type: 'fa', category: 'Comm' },
  { value: 'fa-radio',           label: 'radio',           name: 'Radio Unit',  type: 'fa', category: 'Comm' },

  // ── Utility / Infrastructure ──────────────────────────────────────────────
  { value: 'fa-wrench',             label: 'wrench',             name: 'Wrench',        type: 'fa', category: 'Utility' },
  { value: 'fa-screwdriver-wrench', label: 'screwdriver-wrench', name: 'Tools',         type: 'fa', category: 'Utility' },
  { value: 'fa-bolt',               label: 'bolt',               name: 'Electric',      type: 'fa', category: 'Utility' },
  { value: 'fa-droplet',            label: 'droplet',            name: 'Water',         type: 'fa', category: 'Utility' },
  { value: 'fa-plug',               label: 'plug',               name: 'Power',         type: 'fa', category: 'Utility' },
  { value: 'fa-solar-panel',        label: 'solar-panel',        name: 'Solar',         type: 'fa', category: 'Utility' },
  { value: 'fa-temperature-high',   label: 'temperature-high',   name: 'High Temp',     type: 'fa', category: 'Utility' },
  { value: 'fa-temperature-low',    label: 'temperature-low',    name: 'Low Temp',      type: 'fa', category: 'Utility' },
  { value: 'fa-dumpster-fire',      label: 'dumpster-fire',      name: 'Dumpster Fire', type: 'fa', category: 'Utility' },

  // ── People ────────────────────────────────────────────────────────────────
  { value: 'fa-person',           label: 'person',           name: 'Person',        type: 'fa', category: 'People' },
  { value: 'fa-person-walking',   label: 'person-walking',   name: 'Walking',       type: 'fa', category: 'People' },
  { value: 'fa-person-running',   label: 'person-running',   name: 'Running',       type: 'fa', category: 'People' },
  { value: 'fa-people-group',     label: 'people-group',     name: 'Group',         type: 'fa', category: 'People' },
  { value: 'fa-users',            label: 'users',            name: 'Crowd',         type: 'fa', category: 'People' },
  { value: 'fa-child',            label: 'child',            name: 'Child',         type: 'fa', category: 'People' },
  { value: 'fa-dog',              label: 'dog',              name: 'K9 Unit',       type: 'fa', category: 'People' },
  { value: 'fa-person-military-pointing', label: 'person-military-pointing', name: 'Command', type: 'fa', category: 'People' },

  // ── Weather ───────────────────────────────────────────────────────────────
  { value: 'fa-cloud',       label: 'cloud',       name: 'Cloud',     type: 'fa', category: 'Weather' },
  { value: 'fa-cloud-rain',  label: 'cloud-rain',  name: 'Rain',      type: 'fa', category: 'Weather' },
  { value: 'fa-cloud-bolt',  label: 'cloud-bolt',  name: 'Lightning', type: 'fa', category: 'Weather' },
  { value: 'fa-snowflake',   label: 'snowflake',   name: 'Snow',      type: 'fa', category: 'Weather' },
  { value: 'fa-wind',        label: 'wind',        name: 'Wind',      type: 'fa', category: 'Weather' },
  { value: 'fa-sun',         label: 'sun',         name: 'Sun',       type: 'fa', category: 'Weather' },
  { value: 'fa-smog',        label: 'smog',        name: 'Smog/Smoke',type: 'fa', category: 'Weather' },
  { value: 'fa-rainbow',     label: 'rainbow',     name: 'Rainbow',   type: 'fa', category: 'Weather' },

  // ── Shapes ────────────────────────────────────────────────────────────────
  { value: 'circle',   label: '●', name: 'Circle',   type: 'shape', category: 'Shape' },
  { value: 'square',   label: '■', name: 'Square',   type: 'shape', category: 'Shape' },
  { value: 'triangle', label: '▲', name: 'Triangle', type: 'shape', category: 'Shape' },
  { value: 'star',     label: '★', name: 'Star',     type: 'shape', category: 'Shape' },
  { value: 'diamond',  label: '◆', name: 'Diamond',  type: 'shape', category: 'Shape' },

  // ── Emoji ─────────────────────────────────────────────────────────────────
  { value: 'pin',           label: '📍', name: 'Pin',           type: 'emoji', category: 'Emoji' },
  { value: 'fire-emoji',    label: '🚒', name: 'Fire Truck',    type: 'emoji', category: 'Emoji' },
  { value: 'police-emoji',  label: '🚓', name: 'Police Car',    type: 'emoji', category: 'Emoji' },
  { value: 'ambulance-emoji',label:'🚑', name: 'Ambulance',     type: 'emoji', category: 'Emoji' },
  { value: 'warning-emoji', label: '⚠️', name: 'Warning',       type: 'emoji', category: 'Emoji' },
  { value: 'hazard-emoji',  label: '☢️', name: 'Hazard',        type: 'emoji', category: 'Emoji' },
  { value: 'alert-emoji',   label: '🚨', name: 'Siren',         type: 'emoji', category: 'Emoji' },
];

// ─── Pure utility functions (no component state deps) ─────────────────────────
function parseLocalDateTime(dateTimeString) {
  if (!dateTimeString) return null;
  const cleaned = dateTimeString.replace(/Z$|[+-]\d{2}:\d{2}$/, '');
  const parts = cleaned.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (parts) {
    const [, year, month, day, hour, minute, second] = parts;
    return new Date(year, month - 1, day, hour, minute, second);
  }
  return new Date(cleaned);
}

function formatDateTime(dateTime) {
  if (!dateTime) return '-';
  const date = parseLocalDateTime(dateTime);
  if (!date) return '-';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function calculateResponseTime(event) {
  if (!event.ArrivedDateTime || !event.DispatchedDateTime) return null;
  const arrivedTime = parseLocalDateTime(event.ArrivedDateTime);
  const dispatchedTime = parseLocalDateTime(event.DispatchedDateTime);
  if (arrivedTime < dispatchedTime) return null;
  return (arrivedTime - dispatchedTime) / (1000 * 60);
}

function getStatusBadgeStyle(status, isDark) {
  const s = status?.toLowerCase() || '';
  if (s === 'completed' || s === 'closed' || s === 'complete') {
    return isDark ? { background: '#065F46', color: '#D1FAE5' } : { background: '#D4EDDA', color: '#155724' };
  }
  if (s === 'pending') {
    return isDark ? { background: '#92400E', color: '#FEF3C7' } : { background: '#FFF3CD', color: '#856404' };
  }
  return isDark ? { background: '#1E3A8A', color: '#BFDBFE' } : { background: '#D1ECF1', color: '#0C5460' };
}

// ─── SectionResizeHandle ──────────────────────────────────────────────────────
function SectionResizeHandle({ sectionId, resizingSection, theme, onResizeStart, onReset }) {
  return (
    <div
      style={{
        height: '10px', cursor: 'ns-resize', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        background: resizingSection?.id === sectionId ? 'rgba(99,102,241,0.2)' : 'transparent',
        borderTop: `1px solid ${theme.borderColor}`, transition: 'background 0.15s',
        userSelect: 'none', flexShrink: 0,
      }}
      onMouseDown={(e) => onResizeStart(e, sectionId)}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.15)'; }}
      onMouseLeave={(e) => { if (resizingSection?.id !== sectionId) e.currentTarget.style.background = 'transparent'; }}
      title="Drag to resize section · Double-click to reset"
      onDoubleClick={() => onReset(sectionId)}
    >
      <div style={{ width: '40px', height: '3px', borderRadius: '99px', background: theme.textSecondary, opacity: 0.35, pointerEvents: 'none' }} />
    </div>
  );
}

// ─── LayerFormFields ──────────────────────────────────────────────────────────
// Shared form body used by both Add and Edit layer modals.
function LayerFormFields({ theme, form, onChange, layerType, showTypeSelector }) {
  const [iconSearch, setIconSearch] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('All');

  const CATEGORIES = ['All', 'Fire', 'Police', 'Medical', 'Hazard', 'Vehicle', 'Location', 'Building', 'Comm', 'Utility', 'People', 'Weather', 'Shape', 'Emoji'];

  const visibleIcons = React.useMemo(() => {
    const q = iconSearch.trim().toLowerCase();
    return LAYER_ICONS.filter(icon => {
      const matchesSearch = !q || icon.name.toLowerCase().includes(q) || icon.category?.toLowerCase().includes(q);
      const matchesCat = activeCategory === 'All' || icon.category === activeCategory;
      return matchesSearch && matchesCat;
    });
  }, [iconSearch, activeCategory]);

  return (
    <>
      {/* Layer Name */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.85em', color: theme.textPrimary }}>
          Layer Name
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="e.g., Fire Stations, Coverage Zones"
          style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.9em', boxSizing: 'border-box' }}
          autoFocus
        />
      </div>

      {/* Layer Type selector (Add only) */}
      {showTypeSelector && (
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.85em', color: theme.textPrimary }}>Layer Type</label>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[{ value: 'marker', icon: '📍', label: 'Markers' }, { value: 'polygon', icon: '🔷', label: 'Polygon' }, { value: 'line', icon: '📏', label: 'Line' }].map(t => (
              <button key={t.value} onClick={() => onChange('type', t.value)} style={{ flex: 1, padding: '6px 10px', background: form.type === t.value ? theme.accentColor : theme.detailBg, color: form.type === t.value ? 'white' : theme.textPrimary, border: `1.5px solid ${form.type === t.value ? theme.accentColor : theme.borderColor}`, borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.82em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', transition: 'all 0.15s' }}>
                <span>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Layer Type display (Edit only) */}
      {!showTypeSelector && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.textPrimary }}>Layer Type</label>
          <div style={{ padding: '12px', background: theme.detailBg, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.textSecondary, fontSize: '0.9em' }}>
            <span style={{ fontSize: '1.5em' }}>{layerType === 'marker' ? '📍' : layerType === 'polygon' ? '🔷' : '📏'}</span>
            <span>{layerType === 'marker' ? 'Markers' : layerType === 'polygon' ? 'Polygon' : 'Line'}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.85em', fontStyle: 'italic' }}>(cannot be changed)</span>
          </div>
        </div>
      )}

      {/* Color */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.textPrimary }}>Color</label>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input type="color" value={form.color} onChange={(e) => onChange('color', e.target.value)} style={{ width: '60px', height: '40px', border: `2px solid ${theme.borderColor}`, borderRadius: '8px', cursor: 'pointer' }} />
          <div style={{ flex: 1, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(color => (
              <button key={color} onClick={() => onChange('color', color)} style={{ width: '32px', height: '32px', background: color, border: form.color === color ? '3px solid white' : 'none', borderRadius: '50%', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', outline: form.color === color ? `2px solid ${color}` : 'none', outlineOffset: '2px' }} />
            ))}
          </div>
        </div>
      </div>

      {/* Marker Icon */}
      {(form.type === 'marker' || layerType === 'marker') && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.textPrimary }}>Marker Icon</label>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <i className="fas fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: theme.textSecondary, fontSize: '0.85em', pointerEvents: 'none' }} />
            <input
              type="text"
              value={iconSearch}
              onChange={(e) => { setIconSearch(e.target.value); setActiveCategory('All'); }}
              placeholder="Search icons…"
              style={{ width: '100%', padding: '8px 10px 8px 30px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.9em', boxSizing: 'border-box' }}
            />
            {iconSearch && (
              <button onClick={() => setIconSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSecondary, fontSize: '1em', padding: '0', lineHeight: 1 }}>✕</button>
            )}
          </div>

          {/* Category tabs */}
          {!iconSearch && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75em', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${activeCategory === cat ? theme.accentColor : theme.borderColor}`, background: activeCategory === cat ? theme.accentColor : theme.inputBg, color: activeCategory === cat ? 'white' : theme.textSecondary }}>
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Icon grid */}
          <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${theme.borderColor}`, borderRadius: '8px', padding: '10px' }}>
            {visibleIcons.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: theme.textSecondary, fontSize: '0.9em' }}>No icons match "{iconSearch}"</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                {visibleIcons.map(icon => (
                  <button key={icon.value} onClick={() => onChange('markerIcon', icon.value)} title={`${icon.category}: ${icon.name}`}
                    style={{ padding: '8px 4px', background: form.markerIcon === icon.value ? theme.accentColor : theme.detailBg, color: form.markerIcon === icon.value ? 'white' : theme.textPrimary, border: `2px solid ${form.markerIcon === icon.value ? theme.accentColor : theme.borderColor}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.7em', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'all 0.15s', minHeight: '56px' }}>
                    {icon.type === 'fa' ? <i className={`fas fa-${icon.label}`} style={{ fontSize: '1.4em' }} /> : <span style={{ fontSize: '1.4em' }}>{icon.label}</span>}
                    <span style={{ fontSize: '0.8em', textAlign: 'center', lineHeight: '1.1', overflow: 'hidden', width: '100%', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{icon.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginTop: '5px', fontSize: '0.78em', color: theme.textSecondary, textAlign: 'right' }}>
            {visibleIcons.length} icon{visibleIcons.length !== 1 ? 's' : ''}{iconSearch || activeCategory !== 'All' ? ` matching "${iconSearch || activeCategory}"` : ' total'}
          </div>
        </div>
      )}

      {/* Fill Transparency (polygons only) */}
      {(form.type === 'polygon' || layerType === 'polygon') && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontWeight: '600', color: theme.textPrimary }}>
            <span>Fill Transparency</span>
            <span style={{ background: theme.detailBg, padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', fontWeight: '700', color: theme.accentColor }}>{Math.round(form.fillOpacity * 100)}%</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '0.85em', color: theme.textSecondary, minWidth: '60px' }}>Opaque</span>
            <input type="range" min="0" max="1" step="0.05" value={form.fillOpacity} onChange={(e) => onChange('fillOpacity', parseFloat(e.target.value))}
              style={{ flex: 1, cursor: 'pointer', height: '6px', borderRadius: '3px', outline: 'none', background: `linear-gradient(to right, ${theme.accentColor} 0%, ${theme.accentColor} ${form.fillOpacity * 100}%, ${theme.inputBorder} ${form.fillOpacity * 100}%, ${theme.inputBorder} 100%)` }} />
            <span style={{ fontSize: '0.85em', color: theme.textSecondary, minWidth: '80px', textAlign: 'right' }}>Transparent</span>
          </div>
          <div style={{ marginTop: '12px', padding: '16px', borderRadius: '8px', background: theme.detailBg, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '60px', height: '40px', background: form.color, opacity: form.fillOpacity, border: form.showBorder ? `2px solid ${form.color}` : 'none', borderRadius: '6px' }} />
            <div style={{ fontSize: '0.85em', color: theme.textSecondary }}>Preview: This is how your polygon will appear on the map</div>
          </div>
        </div>
      )}

      {/* Show Border (polygons only) */}
      {(form.type === 'polygon' || layerType === 'polygon') && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: theme.detailBg, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: `2px solid ${form.showBorder ? theme.accentColor : theme.borderColor}` }}>
            <input type="checkbox" checked={form.showBorder} onChange={(e) => onChange('showBorder', e.target.checked)} style={{ cursor: 'pointer', width: '20px', height: '20px', accentColor: theme.accentColor }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: theme.textPrimary, marginBottom: '2px' }}>Show Border</div>
              <div style={{ fontSize: '0.85em', color: theme.textSecondary }}>Display a {form.weight}px outline around the polygon</div>
            </div>
          </label>
        </div>
      )}

      {/* Background Pane (polygons + lines) */}
      {(form.type === 'polygon' || form.type === 'line' || layerType === 'polygon' || layerType === 'line') && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: theme.detailBg, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: `2px solid ${form.useBackgroundPane ? theme.accentColor : theme.borderColor}` }}>
            <input type="checkbox" checked={form.useBackgroundPane} onChange={(e) => onChange('useBackgroundPane', e.target.checked)} style={{ cursor: 'pointer', width: '20px', height: '20px', accentColor: theme.accentColor }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: theme.textPrimary, marginBottom: '2px' }}>Render Behind Streets</div>
              <div style={{ fontSize: '0.85em', color: theme.textSecondary }}>Place this layer behind street labels and markers (z-index 210)</div>
            </div>
          </label>
        </div>
      )}
    </>
  );
}

// ─── AddLayerModal ─────────────────────────────────────────────────────────────
// Fully self-contained. All form state lives here. Calls onSave(formData) or onClose().
function AddLayerModal({ theme, onClose, onSave }) {
  const [form, setForm] = React.useState({
    name: '', type: 'marker', color: '#3B82F6',
    fillOpacity: 0.3, weight: 2, showBorder: true,
    markerIcon: 'circle', useBackgroundPane: false,
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const canSave = form.name.trim().length > 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '40px', paddingBottom: '40px', overflowY: 'auto', zIndex: 3000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: theme.cardBg, borderRadius: '14px', padding: '20px', maxWidth: '480px', width: '90%', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: `1px solid ${theme.borderColor}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '14px' }}>
          <span style={{ fontSize: '1em' }}>🗺️</span>
          <span style={{ fontSize: '1em', fontWeight: '700', color: theme.accentColor }}>Create New Layer</span>
        </div>
        <LayerFormFields theme={theme} form={form} onChange={handleChange} layerType={null} showTypeSelector={true} />
        <div style={{ padding: '12px', background: theme.detailBg, borderRadius: '8px', marginBottom: '20px', fontSize: '0.9em', color: theme.textSecondary }}>
          {form.type === 'marker' && '📍 Click on the map to add individual markers'}
          {form.type === 'polygon' && '🔷 Click points on the map to draw an area'}
          {form.type === 'line' && '📏 Click points on the map to draw a route'}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => onSave(form)} disabled={!canSave} style={{ flex: 1, padding: '14px', background: canSave ? theme.accentColor : theme.detailBg, color: canSave ? 'white' : theme.textSecondary, border: 'none', borderRadius: '8px', cursor: canSave ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '1em' }}>
            🎨 Create & Start Drawing
          </button>
          <button onClick={onClose} style={{ padding: '14px 20px', background: theme.detailBg, color: theme.textPrimary, border: `2px solid ${theme.borderColor}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1em' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditLayerModal ────────────────────────────────────────────────────────────
// Receives the layer to edit. All form state lives here. Calls onSave(formData) or onClose().
function EditLayerModal({ theme, layer, onClose, onSave }) {
  const [form, setForm] = React.useState({
    name: layer.name,
    color: layer.style.color,
    fillOpacity: layer.style.fillOpacity ?? 0.3,
    weight: layer.style.weight ?? 2,
    showBorder: layer.style.showBorder !== undefined ? layer.style.showBorder : true,
    markerIcon: layer.style.icon || 'circle',
    useBackgroundPane: layer.useBackgroundPane || false,
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const canSave = form.name.trim().length > 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '40px', paddingBottom: '40px', overflowY: 'auto', zIndex: 3000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: theme.cardBg, borderRadius: '16px', padding: '30px', maxWidth: '500px', width: '90%', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: `1px solid ${theme.borderColor}`, flexShrink: 0 }}>
        <h2 style={{ margin: '0 0 20px 0', color: theme.accentColor, display: 'flex', alignItems: 'center', gap: '10px' }}>✏️ Edit Layer</h2>
        <LayerFormFields theme={theme} form={form} onChange={handleChange} layerType={layer.type} showTypeSelector={false} />
        <div style={{ padding: '12px', background: theme.detailBg, borderRadius: '8px', marginBottom: '20px', fontSize: '0.9em', color: theme.textSecondary }}>
          ℹ️ Changes will apply to all existing {layer.type}s in this layer ({layer.data.length} item{layer.data.length !== 1 ? 's' : ''})
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => onSave(form)} disabled={!canSave} style={{ flex: 1, padding: '14px', background: canSave ? theme.accentColor : theme.detailBg, color: canSave ? 'white' : theme.textSecondary, border: 'none', borderRadius: '8px', cursor: canSave ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '1em' }}>
            ✅ Save Changes
          </button>
          <button onClick={onClose} style={{ padding: '14px 20px', background: theme.detailBg, color: theme.textPrimary, border: `2px solid ${theme.borderColor}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1em' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EventList ────────────────────────────────────────────────────────────────
// Sort + pagination state live here. Receives filteredData as a prop.
const EventList = React.memo(function EventList({
  theme, isDarkTheme, filteredData, onSelectEvent,
  sectionHeight, resizingSection, onResizeStart, onResetHeight,
  allSectionsCollapsed, sectionDragProps, sectionOrderStyle,
  isCollapsed, onToggleCollapse,
}) {
  const [sortConfig, setSortConfig] = React.useState({ column: 'ReceivedDateTime', direction: 'desc' });
  const [currentPage, setCurrentPage] = React.useState(1);
  const ITEMS_PER_PAGE = 50;

  // Reset to page 1 when data or sort changes
  React.useEffect(() => { setCurrentPage(1); }, [filteredData, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => prev.column === column
      ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'desc' });
  };

  const sortedData = React.useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const { column, direction } = sortConfig;
      let aVal, bVal, comparison = 0;
      switch (column) {
        case 'Id': aVal = parseInt(a.Id)||0; bVal = parseInt(b.Id)||0; comparison = aVal-bVal; break;
        case 'EventType': aVal=(a.EventType||'').toLowerCase(); bVal=(b.EventType||'').toLowerCase(); comparison=aVal.localeCompare(bVal); break;
        case 'Address': aVal=(a.Address||'').toLowerCase(); bVal=(b.Address||'').toLowerCase(); comparison=aVal.localeCompare(bVal); break;
        case 'Status': aVal=(a.Status||'').toLowerCase(); bVal=(b.Status||'').toLowerCase(); comparison=aVal.localeCompare(bVal); break;
        case 'Agency': aVal=(a.Agency||'').toLowerCase(); bVal=(b.Agency||'').toLowerCase(); comparison=aVal.localeCompare(bVal); break;
        case 'AgencyType': aVal=(a.AgencyType||'').toLowerCase(); bVal=(b.AgencyType||'').toLowerCase(); comparison=aVal.localeCompare(bVal); break;
        case 'ReceivedDateTime': aVal=parseLocalDateTime(a.ReceivedDateTime)?.getTime()||0; bVal=parseLocalDateTime(b.ReceivedDateTime)?.getTime()||0; comparison=aVal-bVal; break;
        case 'ResponseTime': aVal=calculateResponseTime(a)||0; bVal=calculateResponseTime(b)||0; comparison=aVal-bVal; break;
        default: comparison=0;
      }
      const result = direction === 'asc' ? comparison : -comparison;
      if (result === 0) return (parseInt(a.Id)||0) - (parseInt(b.Id)||0);
      return result;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = sortedData.slice((currentPage-1)*ITEMS_PER_PAGE, currentPage*ITEMS_PER_PAGE);

  const medianResponseTime = React.useMemo(() => {
    const times = filteredData.map(e => calculateResponseTime(e)).filter(t => t != null);
    if (!times.length) return null;
    const sorted = [...times].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid-1]+sorted[mid])/2 : sorted[mid];
  }, [filteredData]);

  const SortTh = ({ col, label }) => (
    <th onClick={() => handleSort(col)}
      style={{ padding: '12px', color: theme.textSecondary, fontWeight: '600', cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }}
      onMouseEnter={(e) => e.currentTarget.style.color = theme.accentColor}
      onMouseLeave={(e) => e.currentTarget.style.color = theme.textSecondary}>
      {label} {sortConfig.column === col && (sortConfig.direction === 'asc' ? '↑' : '↓')}
    </th>
  );

  return (
    <div
      {...sectionDragProps('recentEvents')}
      style={{ background: theme.cardBg, borderRadius: '12px', padding: '0', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: '24px', overflow: 'hidden', ...sectionOrderStyle('recentEvents') }}
    >
      {/* Header */}
      <div style={{ fontSize: '1.05em', fontWeight: '600', padding: '10px 20px', color: theme.sectionLabelColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: allSectionsCollapsed ? 'grab' : 'pointer', userSelect: 'none' }}
        onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
          🗒️ Event List
        </span>
        <span style={{ fontSize: '1em', color: theme.sectionLabelColor, transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
          title={isCollapsed ? 'Expand recent events' : 'Collapse recent events'}>▼</span>
      </div>

      {!isCollapsed && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0 20px 20px', overflowY: 'auto', height: sectionHeight ? `${sectionHeight}px` : 'auto', minHeight: '200px' }}>
            {/* Summary row */}
            <div style={{ fontSize: '0.85em', marginBottom: '12px', color: theme.textSecondary, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <span>
                {filteredData.length === 0 ? 'No events found'
                  : `Showing ${((currentPage-1)*ITEMS_PER_PAGE)+1}–${Math.min(currentPage*ITEMS_PER_PAGE, filteredData.length)} of ${filteredData.length} events`}
              </span>
              {medianResponseTime !== null && (
                <span style={{ color: theme.accentColor, fontWeight: '600' }}>• Median Response: {medianResponseTime.toFixed(1)} min</span>
              )}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', background: theme.cardBg }}>
                <thead style={{ background: theme.detailBg }}>
                  <tr style={{ borderBottom: `2px solid ${theme.borderColor}`, textAlign: 'left' }}>
                    <SortTh col="Id" label="Event ID" />
                    <SortTh col="EventType" label="Event Type" />
                    <SortTh col="Address" label="Address" />
                    <SortTh col="Status" label="Status" />
                    <SortTh col="Agency" label="Agency" />
                    <SortTh col="AgencyType" label="Agency Type" />
                    <SortTh col="ReceivedDateTime" label="Received" />
                    <SortTh col="ResponseTime" label="Response Time" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((event, index) => {
                    const uniqueKey = event.ptsEventID ? `${event.ptsEventID}-${event.ReceivedDateTime}-${index}` : event.Id ? `${event.Id}-${event.ReceivedDateTime}-${index}` : `fallback-${index}`;
                    const responseTime = calculateResponseTime(event);
                    const isAboveMedian = medianResponseTime !== null && responseTime > medianResponseTime;
                    return (
                      <tr key={uniqueKey} onClick={() => onSelectEvent(event, sortedData, sortedData.indexOf(event))}
                        style={{ borderBottom: `1px solid ${theme.borderColor}`, cursor: 'pointer', background: theme.cardBg }}
                        onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBg}
                        onMouseLeave={(e) => e.currentTarget.style.background = theme.cardBg}>
                        <td style={{ padding: '12px', color: theme.textPrimary }}>{event.Id}</td>
                        <td style={{ padding: '12px', color: theme.textPrimary }}>{event.EventType}</td>
                        <td style={{ padding: '12px', color: theme.textPrimary, fontSize: '0.85em' }}>{event.Address || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.85em', fontWeight: '600', ...getStatusBadgeStyle(event.Status, isDarkTheme) }}>{event.Status}</span>
                        </td>
                        <td style={{ padding: '12px', color: theme.textPrimary, fontSize: '0.9em' }}>{event.Agency || '-'}</td>
                        <td style={{ padding: '12px', color: theme.textPrimary, fontSize: '0.9em' }}>{event.AgencyType || '-'}</td>
                        <td style={{ padding: '12px', color: theme.textPrimary }}>{formatDateTime(event.ReceivedDateTime)}</td>
                        <td style={{ padding: '12px' }}>
                          {responseTime == null ? <span style={{ color: theme.textPrimary }}>-</span> : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isAboveMedian ? '#EF4444' : theme.textPrimary, fontWeight: isAboveMedian ? '600' : 'normal' }}>
                              {isAboveMedian && <span style={{ fontSize: '1.1em' }}>⚠️</span>}
                              {responseTime.toFixed(1)} min
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px', paddingTop: '15px', borderTop: `1px solid ${theme.borderColor}` }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1}
                  style={{ padding: '8px 16px', background: currentPage===1 ? theme.inputBg : theme.accentColor, color: currentPage===1 ? theme.textSecondary : 'white', border: 'none', borderRadius: '6px', cursor: currentPage===1 ? 'not-allowed' : 'pointer', fontSize: '0.9em', fontWeight: '600', opacity: currentPage===1 ? 0.5 : 1, transition: 'all 0.2s' }}>
                  ← Prev
                </button>

                {currentPage > 3 && (<>
                  <button onClick={() => setCurrentPage(1)} style={{ padding: '8px 12px', background: theme.inputBg, color: theme.textPrimary, border: `1px solid ${theme.borderColor}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em', minWidth: '40px' }}>1</button>
                  {currentPage > 4 && <span style={{ color: theme.textSecondary }}>...</span>}
                </>)}

                {Array.from({ length: totalPages }, (_, i) => i+1)
                  .filter(p => Math.abs(p - currentPage) <= 2)
                  .map(page => (
                    <button key={page} onClick={() => setCurrentPage(page)}
                      style={{ padding: '8px 12px', background: page===currentPage ? theme.accentColor : theme.inputBg, color: page===currentPage ? 'white' : theme.textPrimary, border: `1px solid ${page===currentPage ? theme.accentColor : theme.borderColor}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em', fontWeight: page===currentPage ? '600' : '400', minWidth: '40px', transition: 'all 0.2s' }}>
                      {page}
                    </button>
                  ))}

                {currentPage < totalPages - 2 && (<>
                  {currentPage < totalPages - 3 && <span style={{ color: theme.textSecondary }}>...</span>}
                  <button onClick={() => setCurrentPage(totalPages)} style={{ padding: '8px 12px', background: theme.inputBg, color: theme.textPrimary, border: `1px solid ${theme.borderColor}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em', minWidth: '40px' }}>{totalPages}</button>
                </>)}

                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage===totalPages}
                  style={{ padding: '8px 16px', background: currentPage===totalPages ? theme.inputBg : theme.accentColor, color: currentPage===totalPages ? theme.textSecondary : 'white', border: 'none', borderRadius: '6px', cursor: currentPage===totalPages ? 'not-allowed' : 'pointer', fontSize: '0.9em', fontWeight: '600', opacity: currentPage===totalPages ? 0.5 : 1, transition: 'all 0.2s' }}>
                  Next →
                </button>
                <span style={{ marginLeft: '10px', fontSize: '0.85em', color: theme.textSecondary }}>Page {currentPage} of {totalPages}</span>
              </div>
            )}
          </div>
          <SectionResizeHandle sectionId="recentEvents" resizingSection={resizingSection} theme={theme} onResizeStart={onResizeStart} onReset={onResetHeight} />
        </div>
      )}
    </div>
  );
});

const CADDashboardFixed = () => {
  // ✅ API Configuration
  const USE_SERVER_SIDE_FILTERING = true; // Set to false to use old client-side filtering
  const API_BASE_URL = '';

  // ── Responsive layout ─────────────────────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isTablet = windowWidth <= 1024;

  // Ensure viewport meta is set correctly for tablet/mobile
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    meta.content = 'width=device-width, initial-scale=1, maximum-scale=5';
  }, []);

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  // When modal is opened from the Event List, stores { list, index } for Prev/Next navigation.
  // null when opened from the map (no navigation available).
  const [eventNavContext, setEventNavContext] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [map, setMap] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [autoRecenterMap, setAutoRecenterMap] = useState(() => {
    const saved = localStorage.getItem('autoRecenterMap');
    return saved !== null ? JSON.parse(saved) : false; // Default to false
  });
  
  // ✅ Default map location (Home button)
  const [defaultMapLocation, setDefaultMapLocation] = useState(() => {
    const saved = localStorage.getItem('defaultMapLocation');
    return saved ? JSON.parse(saved) : {
      lat: 34.7465,  // Little Rock, AR (default)
      lng: -92.2896,
      zoom: 11
    };
  });
  
  // ✅ KPI data from server stats endpoint (with fallback to client-side calculation)
  const [kpiData, setKpiData] = useState(null);
  
  // ✅ Track if we've done initial mount (to prevent double-fetch on mount)
  const isInitialMount = useRef(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    datePreset: '',
    dateFrom: '',
    dateTo: '',
    lastHours: '',  // New: Rolling time window in hours
    callTypes: ['all'],
    priorities: ['all'],
    statuses: ['all'],
    agencies: ['all'],
    agencyTypes: ['all'],
    activeOnly: false
  });
  
  // Polygon filter states
  const [polygonCoords, setPolygonCoords] = useState(null); // Array of [lat, lng] points
  const [polygonFilterMode, setPolygonFilterMode] = useState('none'); // 'none', 'inside', 'outside'
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [isDrawingRadius, setIsDrawingRadius] = useState(false);
  const [radiusValue, setRadiusValue] = useState(1); // Radius in miles
  const [radiusUnit, setRadiusUnit] = useState('miles'); // 'miles' or 'km'
  const [currentPolygonName, setCurrentPolygonName] = useState(''); // Name of currently loaded polygon
  const polygonLayerRef = useRef(null);
  
  // Saved polygons state
  const [savedPolygons, setSavedPolygons] = useState(() => {
    const saved = localStorage.getItem('savedPolygons');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSavePolygonDialog, setShowSavePolygonDialog] = useState(false);
  const [polygonNameInput, setPolygonNameInput] = useState('');
  const [showLoadPolygonMenu, setShowLoadPolygonMenu] = useState(false);
  const [showPolygonsDropdown, setShowPolygonsDropdown] = useState(false); // Drawn Areas dropdown
  const [showStandardLayersDropdown, setShowStandardLayersDropdown] = useState(true); // Standard Layers dropdown (default open)
  const [currentLoadedPolygonId, setCurrentLoadedPolygonId] = useState(null); // Track loaded polygon for filtering
  const [polygonSearchTerm, setPolygonSearchTerm] = useState('');
  const [drawnAreasViewMode, setDrawnAreasViewMode] = useState('list'); // 'list' or 'manage'
  const [customLayersViewMode, setCustomLayersViewMode] = useState('list'); // 'list' or 'manage'
  const [customLayerSearchTerm, setCustomLayerSearchTerm] = useState('');
  const [showPolygonNames, setShowPolygonNames] = useState(() => {
    const saved = localStorage.getItem('showPolygonNames');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Custom Map Layers state
  const [customLayers, setCustomLayers] = useState(() => {
    const saved = localStorage.getItem('customMapLayers');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCustomLayersPanel, setShowCustomLayersPanel] = useState(false);
  const [showZoneLayersPanel, setShowZoneLayersPanel] = useState(false);
  const [showEsnLayersPanel, setShowEsnLayersPanel] = useState(false);
  const [currentDrawingLayer, setCurrentDrawingLayer] = useState(null); // { type: 'marker'|'polygon'|'line', name: '', style: {} }
  const [isDrawingCustomLayer, setIsDrawingCustomLayer] = useState(false);
  const [customLayerPoints, setCustomLayerPoints] = useState([]); // Temp storage while drawing
  
  // Add Layer Dialog state
  const [showAddLayerDialog, setShowAddLayerDialog] = useState(false);
  const [showEditLayerDialog, setShowEditLayerDialog] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState(null); // still needed to look up layer for EditLayerModal
  // newLayerForm + layerNameInput moved to AddLayerModal component
  // editLayerForm moved to EditLayerModal component
  
  // Ref for custom layers group on map
  const customLayersGroupRef = useRef(null);
  
  
  // Response Plan (Playbook) state
  const [playbooks, setPlaybooks] = useState(() => {
    const saved = localStorage.getItem('responsePlans');
    return saved ? JSON.parse(saved) : {};
  });
  const [showPlaybookDialog, setShowPlaybookDialog] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [showViewPlaybookModal, setShowViewPlaybookModal] = useState(false);
  const [viewingPlaybook, setViewingPlaybook] = useState(null);
  const [highlightedLayers, setHighlightedLayers] = useState([]); // Track temporarily highlighted layers
  const [currentPlaybookForm, setCurrentPlaybookForm] = useState({
    areaName: '',
    priority: 'Recommended',
    priorityThreshold: 'all',
    timeWindow: 'always',
    startTime: '00:00',
    endTime: '23:59',
    dispatchGuidance: '',
    stagingInstructions: '',
    safetyNotes: '',
    supervisorNotification: '',
    specialInstructions: '',
    status: 'Draft',
    createdBy: '',
    createdDate: '',
    approvedBy: '',
    approvedDate: '',
    startDate: '',
    expirationDate: ''
  });
  
  // Search states
  const [searchTerms, setSearchTerms] = useState({
    callType: '',
    status: '',
    agency: '',
    agencyType: ''
  });

  // ─── Debounced search values ──────────────────────────────────────────────
  // Inputs update instantly (responsive feel); filtering/rendering uses the
  // debounced value so expensive re-renders only fire after typing pauses.
  const debouncedSearchTerms    = useDebounce(searchTerms, 150);
  const debouncedPolygonSearch  = useDebounce(polygonSearchTerm, 150);
  const debouncedLayerSearch    = useDebounce(customLayerSearchTerm, 150);

  
  // Sort state for Event List table
  // sortConfig, currentPage, itemsPerPage moved to EventList component
  
  // Map Layers state
  const [mapLayers, setMapLayers] = useState(() => {
    const saved = localStorage.getItem('mapLayers');
    return saved ? JSON.parse(saved) : {
      incidents: true,        // Event markers
      heatmap: false,         // Heat map overlay
      clusters: false,        // Marker clustering
      responseZones: false    // Saved polygons as zones
    };
  });
  const [mapViewType, setMapViewType] = useState(() => {
    const saved = localStorage.getItem('mapViewType');
    return saved || 'street'; // 'street' or 'satellite'
  });
  const [showLayerPanel, setShowLayerPanel] = useState(true);
  const [showDrawingTools, setShowDrawingTools] = useState(true); // Drawing tools expanded by default
  const [selectedPolygonIds, setSelectedPolygonIds] = useState(() => {
    const saved = localStorage.getItem('selectedPolygonIds');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Available options
  const [availableCallTypes, setAvailableCallTypes] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [availableAgencyTypes, setAvailableAgencyTypes] = useState([]);
  
  // Chart type states
  const [chartTypes, setChartTypes] = useState({
    callType: 'bar',
    hourly: 'line',
    timeline: 'line',
    responseTime: 'bar',
    dayOfWeek: 'bar',
    month: 'bar'
  });
  
  // Chart layout state (positions and sizes)
  const [chartLayout, setChartLayout] = useState(() => {
    const saved = localStorage.getItem('chartLayout');
    return saved ? JSON.parse(saved) : {
      callType:     { width: '100%', height: '400px', order: 0 },
      timeline:     { width: '100%', height: '400px', order: 1 },
      hourly:       { width: '100%', height: '400px', order: 2 },
      dayOfWeek:    { width: '100%', height: '400px', order: 3 },
      month:        { width: '100%', height: '400px', order: 4 },
      responseTime: { width: '100%', height: '400px', order: 5 }
    };
  });
  const [draggedChart, setDraggedChart] = useState(null);
  const [resizingChart, setResizingChart] = useState(null);
  
  // Map size state
  const [mapSize, setMapSize] = useState(() => {
    const saved = localStorage.getItem('mapSize');
    return saved ? JSON.parse(saved) : { width: '100%', height: '600px' };
  });
  const [resizingMap, setResizingMap] = useState(null);
  const [isMapCollapsed, setIsMapCollapsed] = useState(() => {
    const saved = localStorage.getItem('isMapCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [mapViewChanged, setMapViewChanged] = useState(0); // Increments when map view changes

  const [isChartsCollapsed, setIsChartsCollapsed] = useState(() => {
    const saved = localStorage.getItem('isChartsCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [isKpiCollapsed, setIsKpiCollapsed] = useState(() => {
    const saved = localStorage.getItem('isKpiCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [isRecentEventsCollapsed, setIsRecentEventsCollapsed] = useState(() => {
    const saved = localStorage.getItem('isRecentEventsCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('sectionOrder');
    return saved ? JSON.parse(saved) : ['filters', 'kpi', 'map', 'charts', 'eventTime', 'repeatedEvents', 'recentEvents'];
  });
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' or 'after'
  
  // Threshold settings
  const [thresholds, setThresholds] = useState({
    pending: null,
    dispatched: null,
    enroute: null,
    arrived: null,
    active: null,
    closed: null,
    total: null,
    pendingTime: null,      // minutes
    responseTime: null,     // minutes
    onSceneTime: null,      // minutes
    turnoutTime: null,      // minutes
    travelTime: null,       // minutes
    eventTotalTime: null,   // minutes
    eventCreationTime: null, // minutes
    // Warning percentages for time thresholds (default 90%)
    pendingTimeWarning: 90,
    responseTimeWarning: 90,
    onSceneTimeWarning: 90,
    turnoutTimeWarning: 90,
    travelTimeWarning: 90,
    eventTotalTimeWarning: 90,
    eventCreationTimeWarning: 90
  });
  
  // Snapshot Window state
  const [snapshotWindowMinutes, setSnapshotWindowMinutes] = useState('');
  const [playbackSpeed, setPlaybackSpeed] = useState('');
  const [isSnapshotPlaying, setIsSnapshotPlaying] = useState(false);
  const [isSnapshotPaused, setIsSnapshotPaused] = useState(false);
  const [currentSnapshotStart, setCurrentSnapshotStart] = useState(null);
  const [currentSnapshotEnd, setCurrentSnapshotEnd] = useState(null);
  const [snapshotRefreshCount, setSnapshotRefreshCount] = useState(0); // Track snapshot advances
  const snapshotIntervalRef = useRef(null);
  
  // Cumulative Window state
  const [cumulativeWindowMinutes, setCumulativeWindowMinutes] = useState('');
  const [cumulativePlaybackSpeed, setCumulativePlaybackSpeed] = useState('');
  const [isCumulativePlaying, setIsCumulativePlaying] = useState(false);
  const [isCumulativePaused, setIsCumulativePaused] = useState(false);
  const [currentCumulativeStart, setCurrentCumulativeStart] = useState(null);
  const [currentCumulativeEnd, setCurrentCumulativeEnd] = useState(null);
  const cumulativeIntervalRef = useRef(null);
  
  // Collapsible section states
  const [isSnapshotExpanded, setIsSnapshotExpanded] = useState(false);
  const [isRollingWindowExpanded, setIsRollingWindowExpanded] = useState(false);
  const [isCumulativeExpanded, setIsCumulativeExpanded] = useState(false);
  
  // Auto-refresh settings
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('autoRefreshEnabled');
    return saved !== null ? JSON.parse(saved) : false; // Default: Historical mode (auto-refresh disabled)
  });
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('refreshInterval');
    return saved ? parseInt(saved) : 30; // Default 30 seconds
  });
  const [refreshIntervalWarning, setRefreshIntervalWarning] = useState(false);
  const [nextRefreshIn, setNextRefreshIn] = useState(0);
  // Incrementing this resets the auto-refresh interval when a filter-triggered
  // fetch fires — ensures the 30s clock measures time since last actual fetch.
  const [refreshResetKey, setRefreshResetKey] = useState(0);
  
  // ✅ Incremental updates tracking
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [lastFullRefreshTime, setLastFullRefreshTime] = useState(null);
  const FULL_REFRESH_INTERVAL = 600000; // 10 minutes in milliseconds
  
  // ✅ Section height resize state - persisted to localStorage
  const [sectionHeights, setSectionHeights] = useState(() => {
    const saved = localStorage.getItem('sectionHeights');
    return saved ? JSON.parse(saved) : { kpi: null, charts: null, eventTime: null, repeatedEvents: null, recentEvents: null };
  });
  const [resizingSection, setResizingSection] = useState(null); // { id, startY, startHeight }
  
  // ✅ Track if filter options have been loaded (to prevent re-extraction from filtered data)
  const filterOptionsLoadedRef = useRef(false);
  
  // ✅ Ref to always hold latest fetchData (prevents stale closures in auto-refresh interval)
  const fetchDataRef = useRef(null);
  
  // KPI order state
  const [kpiOrder, setKpiOrder] = useState(() => {
    const saved = localStorage.getItem('kpiOrder');
    return saved ? JSON.parse(saved) : [6, 0, 1, 2, 3, 4, 5, 13, 7, 10, 11, 8, 9, 12];
  });
  const [draggedKpi, setDraggedKpi] = useState(null);
  const [dragOverKpi, setDragOverKpi] = useState(null);
  
  // KPI and Chart visibility state
  const [visibleKpis, setVisibleKpis] = useState(() => {
    const saved = localStorage.getItem('visibleKpis');
    return saved ? JSON.parse(saved) : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  });
  
  // KPI card size state (small, medium, large)
  const [kpiSize, setKpiSize] = useState(() => {
    const saved = localStorage.getItem('kpiSize');
    return saved || 'medium';
  });

  // Marker cluster group ref
  const markerClusterGroupRef = useRef(null);
  const heatmapLayerRef = useRef(null);
  const responseZonesLayerRef = useRef(null);
  const [visibleCharts, setVisibleCharts] = useState(() => {
    const saved = localStorage.getItem('visibleCharts');
    return saved ? JSON.parse(saved) : ['callType', 'hourly', 'timeline', 'responseTime', 'dayOfWeek', 'month'];
  });
  
  // Chart refs
  const callTypeChartRef = useRef(null);
  const hourlyChartRef = useRef(null);
  const timelineChartRef = useRef(null);
  const responseTimeChartRef = useRef(null);
  const dayOfWeekChartRef = useRef(null);
  const monthChartRef = useRef(null);
  
  // Chart instances
  const chartInstancesRef = useRef({});
  
  // Dispatch time threshold analysis state
  const [dispatchTargetSeconds, setDispatchTargetSeconds] = useState(120);
  const [dispatchRangeMin, setDispatchRangeMin] = useState(0);
  const [dispatchRangeMax, setDispatchRangeMax] = useState(300);
  const [isDispatchAnalysisExpanded, setIsDispatchAnalysisExpanded] = useState(false);
  const [isRepeatedEventsExpanded, setIsRepeatedEventsExpanded] = useState(false);
  const [repeatedEventsThreshold, setRepeatedEventsThreshold] = useState(3);
  const [repeatedEventsMin, setRepeatedEventsMin] = useState(2);
  const [repeatedEventsMax, setRepeatedEventsMax] = useState(50);
  const [repeatedEventsSortBy, setRepeatedEventsSortBy] = useState('alphabetical'); // 'alphabetical' or 'count'
  const [repeatedLocationsDirection, setRepeatedLocationsDirection] = useState('desc'); // 'asc' or 'desc' for location list
  const [timeFrom, setTimeFrom] = useState('ReceivedDateTime');
  const [timeTo, setTimeTo] = useState('DispatchedDateTime');
  const [timeUnit, setTimeUnit] = useState('seconds'); // 'seconds' or 'minutes'
  
  const mapContainerRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const tileLayerRef = useRef(null); // Store main map tile layer
  
  // Modal map refs
  const modalMapContainerRef = useRef(null);
  const modalMapInstanceRef = useRef(null);
  const modalTileLayerRef = useRef(null); // Store modal map tile layer

  // Theme colors
  const theme = useMemo(() => {
    if (isDarkTheme) {
      return {
        bgPrimary: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        bgSecondary: '#2a2a2a',
        textPrimary: '#f0f0f0',
        textSecondary: '#c0c0c0',
        borderColor: '#4a4a4a',
        cardBg: '#2a2a2a',
        inputBg: '#353535',
        inputBorder: '#555555',
        hoverBg: '#3a3a3a',
        accentColor: '#a0a0a0',
        detailBg: '#1a1a1a',
        sectionLabelColor: '#6ba3d6',
        chartColors: ['#808080', '#999999', '#b3b3b3', '#cccccc', '#666666', '#737373'],
        chartBorderColor: '#808080',
        chartBg: 'transparent',
        chartGridColor: 'rgba(255, 255, 255, 0.1)'
      };
    } else {
      return {
        bgPrimary: '#f0f0f0',
        bgSecondary: 'white',
        textPrimary: '#333',
        textSecondary: '#666',
        borderColor: '#e0e0e0',
        cardBg: 'white',
        inputBg: 'white',
        inputBorder: '#e0e0e0',
        hoverBg: '#f9f9f9',
        accentColor: '#1e3c72',
        detailBg: '#f5f5f5',
        sectionLabelColor: '#1e3c72',
        chartColors: ['#1e3c72', '#2a5298', '#3567ba', '#4179d4', '#5a8de6', '#73a1f8'],
        chartBorderColor: '#1e3c72',
        chartBg: '#e0e0e0',
        chartGridColor: 'rgba(0, 0, 0, 0.15)'
      };
    }
  }, [isDarkTheme]);

  // Helper function to format text with bullet points
  const formatWithBullets = (text) => {
    if (!text) return null;
    
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;
    if (lines.length === 1) return text;
    
    // If lines already start with bullets, just display them
    return lines.map((line, idx) => {
      const trimmedLine = line.trim();
      // Remove existing bullets/dashes if present
      const cleanLine = trimmedLine.replace(/^[-*•]\s*/, '');
  return (
        <div key={idx} style={{ 
          display: 'flex', 
          gap: '8px',
          marginBottom: idx < lines.length - 1 ? '6px' : '0'
        }}>
          <span style={{ color: theme.accentColor, fontWeight: '700' }}>•</span>
          <span style={{ flex: 1 }}>{cleanLine}</span>
        </div>
      );
    });
  };

  // Save KPI order to localStorage
  useEffect(() => {
    localStorage.setItem('kpiOrder', JSON.stringify(kpiOrder));
  }, [kpiOrder]);

  // Save KPI visibility to localStorage
  useEffect(() => {
    localStorage.setItem('visibleKpis', JSON.stringify(visibleKpis));
  }, [visibleKpis]);
  
  // Save KPI size to localStorage
  useEffect(() => {
    localStorage.setItem('kpiSize', kpiSize);
  }, [kpiSize]);

  // Save chart visibility to localStorage
  useEffect(() => {
    localStorage.setItem('visibleCharts', JSON.stringify(visibleCharts));
  }, [visibleCharts]);

  // Save chart layout to localStorage
  useEffect(() => {
    localStorage.setItem('chartLayout', JSON.stringify(chartLayout));
  }, [chartLayout]);

  // Save map size to localStorage
  useEffect(() => {
    localStorage.setItem('mapSize', JSON.stringify(mapSize));
  }, [mapSize]);

  // Save map collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('isMapCollapsed', JSON.stringify(isMapCollapsed));
  }, [isMapCollapsed]);

  useEffect(() => {
    localStorage.setItem('isChartsCollapsed', JSON.stringify(isChartsCollapsed));
    // Re-render charts when section is expanded (canvases remount)
    if (!isChartsCollapsed) {
      setTimeout(() => renderAllCharts(), 150);
    }
  }, [isChartsCollapsed]);

  useEffect(() => {
    localStorage.setItem('isKpiCollapsed', JSON.stringify(isKpiCollapsed));
  }, [isKpiCollapsed]);

  useEffect(() => {
    localStorage.setItem('isRecentEventsCollapsed', JSON.stringify(isRecentEventsCollapsed));
  }, [isRecentEventsCollapsed]);

  useEffect(() => {
    localStorage.setItem('sectionOrder', JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  // Save auto-recenter map preference to localStorage
  useEffect(() => {
    localStorage.setItem('autoRecenterMap', JSON.stringify(autoRecenterMap));
  }, [autoRecenterMap]);

  // Save savedPolygons to localStorage
  useEffect(() => {
    localStorage.setItem('savedPolygons', JSON.stringify(savedPolygons));
  }, [savedPolygons]);
  
  // Save playbooks to localStorage
  useEffect(() => {
    localStorage.setItem('responsePlans', JSON.stringify(playbooks));
  }, [playbooks]);
  
  // Populate playbook form when dialog opens
  useEffect(() => {
    if (showPlaybookDialog && editingPlaybook) {
      const existing = playbooks[editingPlaybook];
      if (existing) {
        setCurrentPlaybookForm(existing);
      } else {
        // Initialize with default values if creating new
        setCurrentPlaybookForm({
          areaName: editingPlaybook,
          priority: 'Recommended',
          priorityThreshold: 'all',
          timeWindow: 'always',
          startTime: '00:00',
          endTime: '23:59',
          dispatchGuidance: '',
          stagingInstructions: '',
          safetyNotes: '',
          supervisorNotification: '',
          specialInstructions: '',
          status: 'Draft',
          createdBy: '',
          createdDate: new Date().toISOString().slice(0, 16),
          approvedBy: '',
          approvedDate: '',
          startDate: '',
          expirationDate: ''
        });
      }
    }
  }, [showPlaybookDialog, editingPlaybook, playbooks]);
  
  // Save map layers to localStorage
  useEffect(() => {
    localStorage.setItem('mapLayers', JSON.stringify(mapLayers));
  }, [mapLayers]);

  // Save map view type to localStorage
  useEffect(() => {
    localStorage.setItem('mapViewType', mapViewType);
  }, [mapViewType]);

  // Save selected polygon IDs to localStorage
  useEffect(() => {
    localStorage.setItem('selectedPolygonIds', JSON.stringify(selectedPolygonIds));
  }, [selectedPolygonIds]);

  // Save show polygon names preference to localStorage
  useEffect(() => {
    localStorage.setItem('showPolygonNames', JSON.stringify(showPolygonNames));
  }, [showPolygonNames]);

  // Save custom map layers to localStorage
  useEffect(() => {
    localStorage.setItem('customMapLayers', JSON.stringify(customLayers));
  }, [customLayers]);

  // Automatically invalidate map size when container dimensions change
  useEffect(() => {
    if (mapContainerRef.current && mapInstanceRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          // Only invalidate if container has actual visible size (not collapsed)
          const { width, height } = entry.contentRect;
          if (width === 0 || height === 0) return;

          setTimeout(() => {
            if (mapInstanceRef.current && mapContainerRef.current) {
              // Double-check container is still visible before calling
              const rect = mapContainerRef.current.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                mapInstanceRef.current.invalidateSize();
              }
            }
          }, 50);
        }
      });
      
      resizeObserver.observe(mapContainerRef.current);
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [map]); // Re-run when map is initialized


  // Save default map location to localStorage
  useEffect(() => {
    localStorage.setItem('defaultMapLocation', JSON.stringify(defaultMapLocation));
  }, [defaultMapLocation]);

  // KPI drag and drop handlers
  const handleKpiDragStart = (e, index) => {
    e.stopPropagation(); // Prevent section drag from interfering
    setDraggedKpi(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleKpiDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent section drag from interfering
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedKpi !== null && draggedKpi !== index) {
      setDragOverKpi(index);
    }
  };

  const handleKpiDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent section drag from interfering
    
    if (draggedKpi === null || draggedKpi === dropIndex) {
      setDraggedKpi(null);
      setDragOverKpi(null);
      return;
    }

    const newOrder = [...kpiOrder];
    const draggedItem = newOrder[draggedKpi];
    
    // Remove the dragged item
    newOrder.splice(draggedKpi, 1);
    // Insert it at the drop position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setKpiOrder(newOrder);
    setDraggedKpi(null);
    setDragOverKpi(null);
  };

  const handleKpiDragEnd = () => {
    setDraggedKpi(null);
    setDragOverKpi(null);
  };

  // Chart drag and drop handlers
  const handleChartDragStart = (e, chartName) => {
    setDraggedChart(chartName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleChartDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleChartDrop = (e, targetChartName) => {
    e.preventDefault();
    
    if (draggedChart === null || draggedChart === targetChartName) {
      return;
    }

    setChartLayout(prev => {
      const newLayout = { ...prev };
      const draggedOrder = prev[draggedChart].order;
      const targetOrder = prev[targetChartName].order;
      
      // Swap orders
      newLayout[draggedChart] = { ...prev[draggedChart], order: targetOrder };
      newLayout[targetChartName] = { ...prev[targetChartName], order: draggedOrder };
      
      return newLayout;
    });
    
    setDraggedChart(null);
  };

  const handleChartDragEnd = () => {
    setDraggedChart(null);
  };

  // Chart resize handlers
  const handleResizeStart = (e, chartName) => {
    e.stopPropagation();
    setResizingChart({ 
      name: chartName, 
      startX: e.clientX, 
      startY: e.clientY,
      startWidth: parseInt(chartLayout[chartName].width),
      startHeight: parseInt(chartLayout[chartName].height)
    });
  };

  useEffect(() => {
    if (!resizingChart) return;

    const handleMouseMove = (e) => {
      const widthDiff = e.clientX - resizingChart.startX;
      const heightDiff = e.clientY - resizingChart.startY;
      
      const newWidth = Math.max(300, Math.min(1200, resizingChart.startWidth + widthDiff));
      const newHeight = Math.max(250, Math.min(800, resizingChart.startHeight + heightDiff));
      
      setChartLayout(prev => ({
        ...prev,
        [resizingChart.name]: {
          ...prev[resizingChart.name],
          width: `${newWidth}px`,
          height: `${newHeight}px`
        }
      }));
    };

    const handleMouseUp = () => {
      setResizingChart(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingChart, chartLayout]);

  // Handle map resize
  const handleMapResizeStart = (e) => {
    e.stopPropagation();
    const currentWidth = mapWrapperRef.current?.offsetWidth || parseInt(mapSize.width) || 800;
    setResizingMap({ 
      startX: e.clientX, 
      startY: e.clientY,
      startWidth: currentWidth,
      startHeight: parseInt(mapSize.height)
    });
  };

  useEffect(() => {
    if (!resizingMap) return;

    const handleMouseMove = (e) => {
      const widthDiff = e.clientX - resizingMap.startX;
      const heightDiff = e.clientY - resizingMap.startY;
      
      const newWidth = Math.max(400, Math.min(1600, resizingMap.startWidth + widthDiff));
      const newHeight = Math.max(300, Math.min(1000, resizingMap.startHeight + heightDiff));
      
      setMapSize({
        width: `${newWidth}px`,
        height: `${newHeight}px`
      });
    };

    const handleMouseUp = () => {
      setResizingMap(null);
      // Invalidate map size to trigger Leaflet recalculation
      if (mapInstanceRef.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 100);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingMap]);

  // Reset chart layout to default
  const resetChartLayout = () => {
    const defaultLayout = {
      callType:     { width: '100%', height: '400px', order: 0 },
      timeline:     { width: '100%', height: '400px', order: 1 },
      hourly:       { width: '100%', height: '400px', order: 2 },
      dayOfWeek:    { width: '100%', height: '400px', order: 3 },
      month:        { width: '100%', height: '400px', order: 4 },
      responseTime: { width: '100%', height: '400px', order: 5 }
    };
    setChartLayout(defaultLayout);
    localStorage.setItem('chartLayout', JSON.stringify(defaultLayout));
  };

  const resetMapSize = () => {
    const defaultSize = { width: '100%', height: '600px' };
    setMapSize(defaultSize);
    localStorage.setItem('mapSize', JSON.stringify(defaultSize));
    // Invalidate map size to trigger Leaflet recalculation
    if (mapInstanceRef.current) {
      setTimeout(() => {
        mapInstanceRef.current.invalidateSize();
      }, 100);
    }
  };


  // Persist sectionHeights to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('sectionHeights', JSON.stringify(sectionHeights));
  }, [sectionHeights]);

  // layerNameInput debounce removed — state moved to AddLayerModal

  // Section resize mouse handler
  const handleSectionResizeStart = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    const contentEl = e.currentTarget.previousSibling;
    const currentHeight = contentEl ? contentEl.getBoundingClientRect().height : (sectionHeights[sectionId] || 300);
    setResizingSection({ id: sectionId, startY: e.clientY, startHeight: currentHeight });
  };

  useEffect(() => {
    if (!resizingSection) return;
    const handleMouseMove = (e) => {
      const diff = e.clientY - resizingSection.startY;
      const newHeight = Math.max(120, resizingSection.startHeight + diff);
      setSectionHeights(prev => ({ ...prev, [resizingSection.id]: newHeight }));
    };
    const handleMouseUp = () => setResizingSection(null);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingSection]);

  // Reset a single section height back to auto
  const resetSectionHeight = (sectionId) => {
    setSectionHeights(prev => ({ ...prev, [sectionId]: null }));
  };

  // Invalidate map size when layer panel is toggled
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Wait for CSS transition to complete
      setTimeout(() => {
        mapInstanceRef.current.invalidateSize();
      }, 350); // Matches the 0.3s transition
    }
  }, [showLayerPanel]);

  // ✅ Load initial data and filter options on mount
  useEffect(() => {
    const initializeDashboard = async () => {
      
      // Try to fetch filter options from dedicated endpoint
      const filterOptionsLoaded = await fetchFilterOptions();
      
      if (!filterOptionsLoaded) {
        
        // Clear filters temporarily for initial load to get ALL data
        const savedFilters = {...filters};
        
        // Make an unfiltered call to get ALL possible filter options
        try {
          const response = await fetch(`${API_BASE_URL}/api/cad-events`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data && result.data.length > 0) {
              extractFilterOptionsFromData(result.data);
            }
          }
        } catch (error) {
          console.error('❌ Failed to load unfiltered data for filter extraction:', error);
        }
      }
      
      // Then fetch initial data with current filters
      fetchData();
    };
    
    initializeDashboard();
  }, []);

  // Auto-refresh data at configured interval
  useEffect(() => {
    if (!autoRefreshEnabled) {
      setNextRefreshIn(0);
      return;
    }
    
    setNextRefreshIn(refreshInterval); // Initialize countdown
    
    const interval = setInterval(() => {
      fetchDataRef.current(false, true); // Use ref to avoid stale closure; forceFullRefresh=true keeps rolling window current
      setNextRefreshIn(refreshInterval); // Reset countdown after refresh
    }, refreshInterval * 1000); // Convert seconds to milliseconds

    // Cleanup interval on unmount or when settings change
    return () => {
      clearInterval(interval);
    };
  }, [autoRefreshEnabled, refreshInterval, refreshResetKey]);

  // Countdown timer for next refresh
  useEffect(() => {
    if (!autoRefreshEnabled || nextRefreshIn <= 0) {
      return;
    }

    const countdown = setInterval(() => {
      setNextRefreshIn(prev => Math.max(0, prev - 1));
    }, 1000); // Update every second

    return () => clearInterval(countdown);
  }, [autoRefreshEnabled, nextRefreshIn]);

  // ✅ Apply filters when filters or time windows change
  useEffect(() => {
    
    // Skip on initial mount (let the mount useEffect handle initial load)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // ✅ With server-side filtering: fetch directly (filtering happens on server)
    if (USE_SERVER_SIDE_FILTERING) {
      // Force full refresh when filters change (incremental updates can't handle filter changes)
      fetchData(false, true); // forceFullRefresh = true
      // Reset the auto-refresh interval so the timer measures time since this fetch,
      // not time since the last scheduled tick. Only matters when Live mode is on.
      if (autoRefreshEnabled) {
        setRefreshResetKey(k => k + 1);
      }
    } else {
      // Client-side filtering: apply filters to existing data
      if (allData.length > 0) {
        applyFilters();
      } else {
      }
    }
  }, [filters, currentSnapshotStart, currentSnapshotEnd, currentCumulativeStart, currentCumulativeEnd, snapshotRefreshCount]);

  // ✅ Apply filters when data loads (only needed for client-side filtering)
  useEffect(() => {
  }, [filteredData]);
  
  useEffect(() => {
    if (allData.length > 0 && !USE_SERVER_SIDE_FILTERING) {
      applyFilters();
    }
  }, [allData]);

  // Save auto-refresh enabled setting to localStorage
  useEffect(() => {
    localStorage.setItem('autoRefreshEnabled', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  // Save refresh interval to localStorage
  useEffect(() => {
    localStorage.setItem('refreshInterval', refreshInterval.toString());
  }, [refreshInterval]);

  // Automatically manage auto-refresh based on active time window
  useEffect(() => {
    // Enable auto-refresh when Rolling Window is active
    if (filters.lastHours && filters.lastHours > 0) {
      if (!autoRefreshEnabled) {
        setAutoRefreshEnabled(true);
      }
    }
    // Disable auto-refresh when Snapshot or Cumulative is playing
    else if (isSnapshotPlaying || isCumulativePlaying) {
      if (autoRefreshEnabled) {
        setAutoRefreshEnabled(false);
      }
    }
  }, [filters.lastHours, isSnapshotPlaying, isCumulativePlaying]);

  // Scroll to top when filters are expanded
  useEffect(() => {
    if (showFilters) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [showFilters]);

  // ✅ Chart rendering - Split into individual useEffects to prevent unnecessary re-renders
  // This ensures changing one chart type only re-renders that specific chart
  
  useEffect(() => {
    // Load Chart.js dynamically (once)
    const loadChartJS = async () => {
      if (!window.Chart) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
        script.async = true;
        script.onload = () => {
          // Render all charts after Chart.js loads
          setTimeout(() => renderAllCharts(), 100);
        };
        script.onerror = () => {
          console.error('Failed to load Chart.js');
        };
        document.head.appendChild(script);
      }
    };
    loadChartJS();
  }, []); // Only run once on mount
  
  // ✅ Call Type Chart - Only re-render when filteredData, callType chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderCallTypeChart();
  }, [filteredData, chartTypes.callType, isDarkTheme, isChartsCollapsed]);
  
  // ✅ Hourly Chart - Only re-render when filteredData, hourly chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderHourlyChart();
  }, [filteredData, chartTypes.hourly, isDarkTheme, isChartsCollapsed]);
  
  // ✅ Timeline Chart - Only re-render when filteredData, timeline chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderTimelineChart();
  }, [filteredData, chartTypes.timeline, isDarkTheme, isChartsCollapsed]);
  
  // ✅ Response Time Chart - Only re-render when filteredData, responseTime chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderResponseTimeChart();
  }, [filteredData, chartTypes.responseTime, isDarkTheme, isChartsCollapsed]);

  // ✅ Day of Week Chart - Only re-render when filteredData, dayOfWeek chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderDayOfWeekChart();
  }, [filteredData, chartTypes.dayOfWeek, isDarkTheme, isChartsCollapsed]);

  // ✅ Month Chart - Only re-render when filteredData, month chart type, or theme changes
  useEffect(() => {
    if (isChartsCollapsed || !window.Chart) return;
    renderMonthChart();
  }, [filteredData, chartTypes.month, isDarkTheme, isChartsCollapsed]);

  useEffect(() => {
    // Load Leaflet.markercluster dynamically
    const loadMarkerCluster = async () => {
      if (!window.L || !window.L.markerClusterGroup) {
        // Load CSS first
        if (!document.querySelector('link[href*="MarkerCluster"]')) {
          const css1 = document.createElement('link');
          css1.rel = 'stylesheet';
          css1.href = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
          document.head.appendChild(css1);

          const css2 = document.createElement('link');
          css2.rel = 'stylesheet';
          css2.href = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css';
          document.head.appendChild(css2);
        }

        // Load JavaScript
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
        script.async = true;
        script.onload = () => {
          if (mapInstanceRef.current && filteredData.length > 0) {
            setTimeout(() => updateMapMarkers(), 100);
          }
        };
        script.onerror = () => {
          console.error('Failed to load Leaflet.markercluster');
        };
        document.head.appendChild(script);
      }
    };
    
    if (window.L) {
      loadMarkerCluster();
    }
  }, []); // Load once

  // Load Leaflet.heat dynamically
  useEffect(() => {
    const loadLeafletHeat = async () => {
      if (!window.L || window.L.heatLayer) {
        if (window.L && window.L.heatLayer) {
        }
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/leaflet.heat@0.2.0/dist/leaflet-heat.js';
      script.async = true;
      script.onload = () => {
        if (mapLayers.heatmap && filteredData.length > 0) {
          setTimeout(() => updateHeatMapLayer(), 100);
        }
      };
      script.onerror = () => {
        console.error('Failed to load Leaflet.heat');
      };
      document.head.appendChild(script);
    };
    
    if (window.L) {
      loadLeafletHeat();
    }
  }, []); // Load once

  // ✅ Load Leaflet.draw library for polygon drawing
  useEffect(() => {
    // Check if Leaflet.draw is already loaded
    if (window.L && window.L.Control && window.L.Control.Draw) {
      return;
    }

    // Load Leaflet.draw CSS
    const drawCss = document.createElement('link');
    drawCss.rel = 'stylesheet';
    drawCss.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.css';
    document.head.appendChild(drawCss);

    // Load Leaflet.draw JS
    const drawScript = document.createElement('script');
    drawScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.js';
    drawScript.async = true;
    drawScript.onload = () => {
    };
    drawScript.onerror = () => {
      console.error('❌ Failed to load Leaflet.draw library');
    };
    document.head.appendChild(drawScript);

    return () => {
      // Cleanup on unmount (prevent memory leaks)
      if (drawCss.parentNode) document.head.removeChild(drawCss);
      if (drawScript.parentNode) document.head.removeChild(drawScript);
    };
  }, []);

  // Handle custom layer drawing clicks (marker mode only - polygons/lines use Leaflet.draw)
  useEffect(() => {
    if (!mapInstanceRef.current || !isDrawingCustomLayer || !currentDrawingLayer) return;
    if (currentDrawingLayer.type !== 'marker') return; // Only for markers
    
    const handleClick = (e) => {
      handleMapClickForCustomLayer(e);
    };
    
    // For marker mode, finish button is used instead of right-click
    // ESC still works to finish
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        finishDrawingCustomLayer();
      }
    };
    
    mapInstanceRef.current.on('click', handleClick);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('click', handleClick);
      }
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawingCustomLayer, currentDrawingLayer, customLayerPoints]);

  // Initialize map - improved version
  useEffect(() => {
    const initializeMap = () => {
      // Check if Leaflet is loaded
      if (typeof window.L === 'undefined') {
        return false;
      }

      // Check if container exists
      if (!mapContainerRef.current) {
        return false;
      }

      // Prevent double initialization
      if (mapInstanceRef.current) {
        return true;
      }

      try {
        
        // Create map instance - use saved home location if available
        const mapInstance = window.L.map(mapContainerRef.current, {
          center: [defaultMapLocation.lat, defaultMapLocation.lng],
          zoom: defaultMapLocation.zoom,
          zoomControl: true,
          scrollWheelZoom: true
        });

        // Add theme-aware and view-type-aware tile layer
        let tileUrl, attribution;
        
        if (mapViewType === 'satellite') {
          // Satellite view using ESRI World Imagery
          tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
          attribution = '© Esri, DigitalGlobe, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community';
        } else {
          // Street view
          tileUrl = isDarkTheme 
            ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
          
          attribution = isDarkTheme
            ? '© OpenStreetMap contributors © CartoDB'
            : '© OpenStreetMap contributors';
        }

        const tileLayer = window.L.tileLayer(tileUrl, {
          attribution: attribution,
          maxZoom: 18,
          minZoom: 3
        }).addTo(mapInstance);
        
        tileLayerRef.current = tileLayer; // Store tile layer reference

        mapInstanceRef.current = mapInstance;
        
        // Create custom pane for background polygons (z-index 210, between tiles and overlays)
        mapInstance.createPane('backgroundPolygons');
        mapInstance.getPane('backgroundPolygons').style.zIndex = 210;
        
        setMap(mapInstance);
        setMapError(null);
        
        return true;
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError(error.message);
        return false;
      }
    };

    // Try to initialize immediately
    if (initializeMap()) {
      return;
    }

    // If failed, set up polling
    const checkInterval = setInterval(() => {
      if (initializeMap()) {
        clearInterval(checkInterval);
      }
    }, 100);

    // Cleanup
    return () => {
      clearInterval(checkInterval);
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        } catch (error) {
          console.error('Error removing map:', error);
        }
      }
    };
  }, []); // Initialize map once, theme changes handled separately

  // Track map view changes (pan/zoom) to update out-of-view indicators
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const handleMapViewChange = () => {
      setMapViewChanged(prev => prev + 1);
    };

    mapInstanceRef.current.on('moveend', handleMapViewChange);
    mapInstanceRef.current.on('zoomend', handleMapViewChange);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('moveend', handleMapViewChange);
        mapInstanceRef.current.off('zoomend', handleMapViewChange);
      }
    };
  }, [map]); // Trigger when map is initialized

  // Update map markers when data changes
  useEffect(() => {
    if (!mapInstanceRef.current || isMapCollapsed) return;
    // Always call updateMapMarkers, even with empty data, to clear old markers
    updateMapMarkers();
  }, [filteredData, mapLayers.incidents, mapLayers.clusters, isMapCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fix map display when expanding from collapsed state
  useEffect(() => {
    if (!mapInstanceRef.current) {
      return;
    }

    // When map is expanded (not collapsed), invalidate size to fix display
    if (!isMapCollapsed) {
      // Delay to ensure DOM has updated and CSS transition completed
      setTimeout(() => {
        if (mapInstanceRef.current) {
          // Force browser to recalculate styles (triggers reflow)
          void (mapWrapperRef.current?.offsetHeight);
          
          // Invalidate map size
          mapInstanceRef.current.invalidateSize();
        }
      }, 350); // Slightly longer than the CSS transition (300ms)
    }
  }, [isMapCollapsed]);

  // Update heat map when data or layer toggle changes
  useEffect(() => {
    if (!mapInstanceRef.current || isMapCollapsed) return;
    updateHeatMapLayer();
  }, [filteredData, mapLayers.heatmap, isMapCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update response zones when layer toggle or polygons change
  useEffect(() => {
    if (!mapInstanceRef.current) {
      return;
    }

    updateResponseZonesLayer();
  }, [savedPolygons, playbooks, selectedPolygonIds, showPolygonNames, map, highlightedLayers]);

  // Render custom layers on map
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    
    // Create or clear custom layers group
    if (!customLayersGroupRef.current) {
      customLayersGroupRef.current = window.L.layerGroup().addTo(mapInstanceRef.current);
    } else {
      customLayersGroupRef.current.clearLayers();
    }
    
    
    // Render each visible layer
    customLayers.forEach(layer => {
      if (!layer.visible) {
        return;
      }
      
      
      if (layer.type === 'marker') {
        // Render markers
        layer.data.forEach((point, idx) => {
          const iconType = layer.style.icon || 'circle';
          let iconHtml = '';
          
          // Font Awesome icons
          if (iconType.startsWith('fa-')) {
            const faIconClass = iconType.substring(3);
            iconHtml = `<div style="
              width: 32px;
              height: 32px;
              background: ${layer.style.color};
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              position: relative;
            ">
              <i class="fas fa-${faIconClass}" style="font-size: 16px;"></i>
              <div style="
                position: absolute;
                bottom: -4px;
                right: -4px;
                background: white;
                color: ${layer.style.color};
                font-size: 10px;
                font-weight: bold;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 2px solid ${layer.style.color};
                box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              ">${idx + 1}</div>
            </div>`;
          }
          // Emoji icons
          else if (iconType.endsWith('-emoji')) {
            const emojiMap = {
              'fire-emoji': '🚒',
              'police-emoji': '🚓',
              'ambulance-emoji': '🚑',
              'warning-emoji': '⚠️',
              'hazard-emoji': '☢️',
              'alert-emoji': '🚨'
            };
            iconHtml = `<div style="
              font-size: 28px;
              filter: drop-shadow(0 2px 8px rgba(0,0,0,0.4));
              line-height: 1;
              position: relative;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
            ">${emojiMap[iconType] || '📍'}<div style="
              position: absolute;
              bottom: -4px;
              right: -4px;
              background: white;
              color: ${layer.style.color};
              font-size: 10px;
              font-weight: bold;
              width: 16px;
              height: 16px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid ${layer.style.color};
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            ">${idx + 1}</div></div>`;
          }
          // Basic shapes
          else {
            // Create different icon shapes
            switch(iconType) {
            case 'circle':
              iconHtml = `<div style="
                background: ${layer.style.color}; 
                width: 24px; 
                height: 24px; 
                border-radius: 50%; 
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 12px;
                font-weight: bold;
              ">${idx + 1}</div>`;
              break;
            case 'square':
              iconHtml = `<div style="
                background: ${layer.style.color}; 
                width: 24px; 
                height: 24px; 
                border-radius: 3px; 
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 12px;
                font-weight: bold;
              ">${idx + 1}</div>`;
              break;
            case 'triangle':
              iconHtml = `<div style="
                width: 0; 
                height: 0; 
                border-left: 15px solid transparent;
                border-right: 15px solid transparent;
                border-bottom: 26px solid ${layer.style.color};
                filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
                position: relative;
              "><div style="
                position: absolute;
                top: 8px;
                left: -8px;
                color: white;
                font-size: 10px;
                font-weight: bold;
                width: 16px;
                text-align: center;
              ">${idx + 1}</div></div>`;
              break;
            case 'star':
              iconHtml = `<div style="
                font-size: 28px;
                color: ${layer.style.color};
                filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
                line-height: 1;
                text-shadow: 0 0 2px white, 0 0 2px white;
                position: relative;
              ">★<div style="
                position: absolute;
                top: 6px;
                left: 50%;
                transform: translateX(-50%);
                color: white;
                font-size: 10px;
                font-weight: bold;
              ">${idx + 1}</div></div>`;
              break;
            case 'diamond':
              iconHtml = `<div style="
                width: 20px;
                height: 20px;
                background: ${layer.style.color};
                transform: rotate(45deg);
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                position: relative;
              "><div style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                color: white;
                font-size: 10px;
                font-weight: bold;
              ">${idx + 1}</div></div>`;
              break;
            case 'pin':
              iconHtml = `<div style="
                width: 24px;
                height: 32px;
                position: relative;
              ">
                <div style="
                  width: 24px;
                  height: 24px;
                  background: ${layer.style.color};
                  border-radius: 50% 50% 50% 0;
                  transform: rotate(-45deg);
                  border: 3px solid white;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  position: absolute;
                  top: 0;
                  left: 0;
                "></div>
                <div style="
                  position: absolute;
                  top: 4px;
                  left: 50%;
                  transform: translateX(-50%);
                  color: white;
                  font-size: 10px;
                  font-weight: bold;
                  z-index: 1;
                ">${idx + 1}</div>
              </div>`;
              break;
            default:
              iconHtml = `<div style="
                background: ${layer.style.color}; 
                width: 24px; 
                height: 24px; 
                border-radius: 50%; 
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 12px;
                font-weight: bold;
              ">${idx + 1}</div>`;
            }
          }
          
          const marker = window.L.marker([point.lat, point.lng], {
            icon: window.L.divIcon({
              className: 'custom-layer-marker',
              html: iconHtml,
              iconSize: iconType.startsWith('fa-') ? [32, 32] :
                        (iconType.endsWith('-emoji') ? [32, 32] : 
                        (iconType === 'pin' ? [24, 32] : [30, 30])),
              iconAnchor: iconType.startsWith('fa-') ? [16, 16] :
                          (iconType.endsWith('-emoji') ? [16, 16] :
                          (iconType === 'pin' ? [12, 32] : [15, 15]))
            })
          });
          
          marker.bindPopup(`
            <div style="font-family: Arial, sans-serif;">
              <b style="color: ${layer.style.color}">${layer.name}</b><br/>
              ${point.label || 'Marker ' + (idx + 1)}<br/>
              <small>${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}</small>
            </div>
          `);
          
          customLayersGroupRef.current.addLayer(marker);
        });
        
      } else if (layer.type === 'polygon') {
        // Render polygons
        layer.data.forEach((coords, idx) => {
          // Check if this layer is highlighted
          const isHighlighted = highlightedLayers.includes(layer.name);
          if (isHighlighted) {
          }
          
          const polygon = window.L.polygon(coords, {
            color: isHighlighted ? '#FFFF00' : layer.style.color,
            fillColor: isHighlighted ? '#FFFF00' : layer.style.fillColor,
            fillOpacity: isHighlighted ? 0.4 : layer.style.fillOpacity,
            weight: isHighlighted ? 4 : (layer.style.showBorder !== false ? layer.style.weight : 0),
            pane: layer.useBackgroundPane ? 'backgroundPolygons' : 'overlayPane'
          });
          
          // Store metadata on layer for click handler
          polygon._polygonData = {
            name: layer.name,
            color: layer.style.color,
            type: 'Custom Layer',
            hasPlaybook: false
          };
          
          customLayersGroupRef.current.addLayer(polygon);
        });
        
      } else if (layer.type === 'line') {
        // Render polylines
        layer.data.forEach((coords, idx) => {
          const polyline = window.L.polyline(coords, {
            color: layer.style.color,
            weight: layer.style.weight,
            opacity: 0.8,
            pane: layer.useBackgroundPane ? 'backgroundPolygons' : 'overlayPane'
          });
          
          polyline.bindPopup(`
            <div style="font-family: Arial, sans-serif;">
              <b style="color: ${layer.style.color}">${layer.name}</b><br/>
              Line ${idx + 1}<br/>
              <small>${coords.length} points</small>
            </div>
          `);
          
          customLayersGroupRef.current.addLayer(polyline);
        });
      }
    });
    
    // Note: Temp drawing preview for polygons and lines is now handled by Leaflet.draw
    // Markers still use simple click mode and don't need temp preview
    
  }, [customLayers, isDrawingCustomLayer, customLayerPoints, currentDrawingLayer, map, highlightedLayers]);

  // Handle map clicks to show all overlapping layers
  useEffect(() => {
    if (!mapInstanceRef.current || !map) return;

    const handleMapClick = (e) => {
      // Skip if we're in a special drawing mode
      if (isDrawingCustomLayer || isDrawingRadius || isDrawingPolygon) return;

      const clickedPoint = e.latlng;
      const overlappingLayers = [];

      // Ray casting algorithm to check if point is inside polygon
      const isPointInPolygon = (point, polygon) => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].lat, yi = polygon[i].lng;
          const xj = polygon[j].lat, yj = polygon[j].lng;
          
          const intersect = ((yi > point.lng) !== (yj > point.lng)) &&
            (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
          
          if (intersect) inside = !inside;
        }
        return inside;
      };

      // Check drawn polygons (response zones)
      if (responseZonesLayerRef.current) {
        responseZonesLayerRef.current.eachLayer((layer) => {
          if (layer instanceof window.L.Polygon && layer._polygonData) {
            const bounds = layer.getBounds();
            if (bounds.contains(clickedPoint)) {
              const coords = layer.getLatLngs()[0];
              if (isPointInPolygon(clickedPoint, coords)) {
                overlappingLayers.push(layer._polygonData);
              }
            }
          }
        });
      }

      // Check custom layers
      if (customLayersGroupRef.current) {
        customLayersGroupRef.current.eachLayer((layer) => {
          if (layer instanceof window.L.Polygon && layer._polygonData) {
            const bounds = layer.getBounds();
            if (bounds.contains(clickedPoint)) {
              const coords = layer.getLatLngs()[0];
              if (isPointInPolygon(clickedPoint, coords)) {
                overlappingLayers.push(layer._polygonData);
              }
            }
          }
        });
      }

      // If multiple layers overlap, show combined popup
      if (overlappingLayers.length > 0) {
        // Theme-aware colors matching marker popup style
        const bgColor      = isDarkTheme ? '#1a202c' : '#ffffff';
        const headerBorder = isDarkTheme ? '#4a5568' : '#e2e8f0';
        const headerText   = isDarkTheme ? '#f7fafc' : '#1a202c';
        const cardBg       = isDarkTheme ? '#2d3748' : '#f7fafc';
        const cardHover    = isDarkTheme ? '#4a5568' : '#e2e8f0';
        const labelColor   = isDarkTheme ? '#a0aec0' : '#4a5568';
        
        const popupContent = `
          <div style="font-family: Arial, sans-serif; max-width: 320px; background: ${bgColor}; border-radius: 6px; overflow: hidden;">
            <div style="
              font-weight: 700;
              font-size: 14px;
              padding: 12px 14px;
              border-bottom: 2px solid ${headerBorder};
              color: ${headerText};
              background: ${isDarkTheme ? '#2d3748' : '#edf2f7'};
              display: flex;
              align-items: center;
              gap: 6px;
            ">
              📐 ${overlappingLayers.length} Layer${overlappingLayers.length > 1 ? 's' : ''}
              <span style="
                margin-left: auto;
                background: ${isDarkTheme ? '#4a5568' : '#cbd5e0'};
                color: ${headerText};
                border-radius: 12px;
                padding: 2px 10px;
                font-size: 12px;
                font-weight: 600;
                white-space: nowrap;
              ">at this location</span>
            </div>
            <div style="padding: 8px;">
              ${overlappingLayers.map((layer, idx) => `
                <div 
                  data-layer-name="${layer.name}"
                  onclick="window.handleLayerPopupClick('${layer.name.replace(/'/g, "\\'")}', false)"
                  style="
                    padding: 10px 12px;
                    margin-bottom: ${idx < overlappingLayers.length - 1 ? '6px' : '0'};
                    border-left: 4px solid ${layer.color};
                    background: ${cardBg};
                    border-radius: 4px;
                    transition: background 0.15s;
                    cursor: pointer;
                  "
                  onmouseover="this.style.background='${cardHover}'"
                  onmouseout="this.style.background='${cardBg}'"
                >
                  <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 10px;">
                    <div style="flex: 1;">
                      <div style="font-weight: 700; color: ${layer.color}; font-size: 13px; margin-bottom: 5px;">
                        ${layer.name}
                      </div>
                      <div style="font-size: 11px; color: ${labelColor};">
                        📍 ${layer.type}
                      </div>
                      ${layer.hasPlaybook ? `
                      <div style="font-size: 11px; color: #10B981; margin-top: 4px; font-weight: 600;">
                        📋 Response Plan Active
                      </div>` : ''}
                      <div style="font-size: 10px; color: ${labelColor}; margin-top: 4px; font-style: italic;">
                        Click to highlight on map
                      </div>
                    </div>
                    ${layer.hasPlaybook ? `
                    <button
                      onclick="event.stopPropagation(); window.handleLayerPopupClick('${layer.name.replace(/'/g, "\\'")}', true);"
                      style="
                        background: #10B981;
                        color: white;
                        border: none;
                        padding: 8px 12px;
                        border-radius: 6px;
                        font-size: 11px;
                        font-weight: 600;
                        cursor: pointer;
                        white-space: nowrap;
                        transition: background 0.2s;
                      "
                      onmouseover="this.style.background='#059669'"
                      onmouseout="this.style.background='#10B981'"
                      title="View Response Plan"
                    >
                      📋 View Plan
                    </button>` : ''}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `;
        
        const popup = window.L.popup({
          maxWidth: 360,
          className: `layer-popup ${isDarkTheme ? 'dark-popup' : 'light-popup'}`
        })
          .setLatLng(clickedPoint)
          .setContent(popupContent)
          .openOn(mapInstanceRef.current);
        
        // Set up global click handler function for inline onclick
        window.handleLayerPopupClick = (layerName, hasPlaybook) => {
          
          // Highlight the layer temporarily
          setHighlightedLayers(prev => {
            return [...prev, layerName];
          });
          
          // Remove highlight after 2 seconds
          setTimeout(() => {
            setHighlightedLayers(prev => {
              return prev.filter(name => name !== layerName);
            });
          }, 2000);
          
          // If layer has playbook, open the dialog
          if (hasPlaybook) {
            
            // Get the playbook data
            const existing = playbooks[layerName];
            if (existing) {
              setViewingPlaybook(existing);
              setShowViewPlaybookModal(true);
            } else {
            }
            
            // Close the popup
            if (mapInstanceRef.current) {
              mapInstanceRef.current.closePopup();
            }
          }
        };
        
        popup.on('popupopen', () => {
        });
        
        // Clean up global handler when popup closes
        popup.on('popupclose', () => {
          if (window.handleLayerPopupClick) {
            delete window.handleLayerPopupClick;
          }
        });
      }
    };

    mapInstanceRef.current.on('click', handleMapClick);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off('click', handleMapClick);
      }
    };
  }, [map, theme, isDarkTheme, isDrawingCustomLayer, isDrawingRadius, isDrawingPolygon, playbooks]);

  // Update map tile layers when theme changes (without re-initializing entire map)
  useEffect(() => {
    // Update main map tile layer
    if (mapInstanceRef.current && tileLayerRef.current) {
      try {
        // Remove old tile layer
        mapInstanceRef.current.removeLayer(tileLayerRef.current);
        
        // Add new tile layer based on theme
        const tileUrl = isDarkTheme 
          ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
          : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        
        const attribution = isDarkTheme
          ? '© OpenStreetMap contributors © CartoDB'
          : '© OpenStreetMap contributors';
        
        const newTileLayer = window.L.tileLayer(tileUrl, {
          attribution: attribution,
          maxZoom: 18,
          minZoom: 3
        }).addTo(mapInstanceRef.current);
        
        tileLayerRef.current = newTileLayer;
      } catch (error) {
        console.warn('Error updating main map tile layer:', error);
      }
    }
    
    // Update modal map tile layer
    if (modalMapInstanceRef.current && modalTileLayerRef.current) {
      try {
        // Remove old tile layer
        modalMapInstanceRef.current.removeLayer(modalTileLayerRef.current);
        
        // Add new tile layer based on theme
        const tileUrl = isDarkTheme 
          ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
          : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        
        const attribution = isDarkTheme
          ? '© OpenStreetMap contributors © CartoDB'
          : '© OpenStreetMap contributors';
        
        const newTileLayer = window.L.tileLayer(tileUrl, {
          attribution: attribution,
          maxZoom: 18
        }).addTo(modalMapInstanceRef.current);
        
        modalTileLayerRef.current = newTileLayer;
      } catch (error) {
        console.warn('Error updating modal map tile layer:', error);
      }
    }
  }, [isDarkTheme]);

  // Initialize modal map when event is selected
  useEffect(() => {
    // Clean up previous modal map if it exists
    if (modalMapInstanceRef.current) {
      try {
        modalMapInstanceRef.current.remove();
        modalMapInstanceRef.current = null;
      } catch (error) {
        console.warn('Error removing modal map:', error);
      }
    }

    // If no event selected or no Leaflet, return
    if (!selectedEvent || typeof window.L === 'undefined') {
      return;
    }

    // Wait for modal container to be available
    const initModalMap = () => {
      if (!modalMapContainerRef.current) {
        return;
      }

      try {
        // Check if event has coordinates
        if (!selectedEvent.LatitudeDegree || !selectedEvent.LongitudeDegree) {
          return;
        }

        const lat = parseFloat(selectedEvent.LatitudeDegree) * (selectedEvent.LatitudeSign === '-' ? -1 : 1);
        const lng = parseFloat(selectedEvent.LongitudeDegree) * (selectedEvent.LongitudeSign === '-' ? -1 : 1);

        if (isNaN(lat) || isNaN(lng)) {
          console.warn('Invalid coordinates');
          return;
        }


        // Create modal map instance
        const modalMap = window.L.map(modalMapContainerRef.current, {
          center: [lat, lng],
          zoom: 15,
          zoomControl: true,
          scrollWheelZoom: false, // Disable scroll zoom in modal
          dragging: true
        });

        // Add theme-aware tile layer
        const tileUrl = isDarkTheme 
          ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
          : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        
        const attribution = isDarkTheme
          ? '© OpenStreetMap contributors © CartoDB'
          : '© OpenStreetMap contributors';

        const tileLayer = window.L.tileLayer(tileUrl, {
          attribution: attribution,
          maxZoom: 18
        }).addTo(modalMap);
        
        modalTileLayerRef.current = tileLayer; // Store tile layer reference

        // Add marker for the event
        const markerColor = selectedEvent.EventPriority === 1 ? '#EF4444' : 
                           selectedEvent.EventPriority === 2 ? '#F59E0B' : '#10B981';

        const marker = window.L.marker([lat, lng], {
          icon: window.L.divIcon({
            className: 'custom-marker',
            html: `<div style="background-color: ${markerColor}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15]
          })
        }).addTo(modalMap);

        // Add popup to marker
        marker.bindPopup(`
          <div style="font-family: Arial, sans-serif;">
            <strong>${selectedEvent.EventType}</strong><br>
            <small>${selectedEvent.Address}</small><br>
            <small>Priority ${selectedEvent.EventPriority}</small>
          </div>
        `).openPopup();

        modalMapInstanceRef.current = modalMap;

        // Force map to invalidate size after a short delay (ensures proper rendering)
        setTimeout(() => {
          if (modalMap) {
            modalMap.invalidateSize();
          }
        }, 100);

      } catch (error) {
        console.error('Error initializing modal map:', error);
      }
    };

    // Try to initialize after a short delay to ensure modal is rendered
    const timeoutId = setTimeout(initModalMap, 50);

    return () => {
      clearTimeout(timeoutId);
      if (modalMapInstanceRef.current) {
        try {
          modalMapInstanceRef.current.remove();
          modalMapInstanceRef.current = null;
        } catch (error) {
          console.warn('Error cleaning up modal map:', error);
        }
      }
    };
  }, [selectedEvent]); // Initialize modal map when event selected, theme changes handled separately

  /**
   * ✅ Save current map position as default "Home" location
   */
  const saveCurrentMapLocationAsDefault = () => {
    if (!mapInstanceRef.current) {
      alert('Map not ready');
      return;
    }
    
    const center = mapInstanceRef.current.getCenter();
    const zoom = mapInstanceRef.current.getZoom();
    
    // ✅ Show confirmation dialog with current coordinates
    const confirmed = window.confirm(
      `Save current map view as home location?\n\n` +
      `Current Position:\n` +
      `Latitude: ${center.lat.toFixed(4)}\n` +
      `Longitude: ${center.lng.toFixed(4)}\n` +
      `Zoom Level: ${zoom}\n\n` +
      `This will replace your existing home location:\n` +
      `Lat: ${defaultMapLocation.lat.toFixed(4)}, Lng: ${defaultMapLocation.lng.toFixed(4)}, Zoom: ${defaultMapLocation.zoom}`
    );
    
    if (!confirmed) {
      return;
    }
    
    const newDefault = {
      lat: center.lat,
      lng: center.lng,
      zoom: zoom
    };
    
    setDefaultMapLocation(newDefault);
    alert(`Default map location saved!\n\nNew Home Location:\nLat: ${center.lat.toFixed(4)}\nLng: ${center.lng.toFixed(4)}\nZoom: ${zoom}`);
  };

  /**
   * ✅ Reset home location to default (Little Rock, AR)
   */
  const resetToDefaultLocation = () => {
    const defaultLocation = {
      lat: 34.7465,  // Little Rock, AR
      lng: -92.2896,
      zoom: 11
    };
    
    setDefaultMapLocation(defaultLocation);
    alert(`Home location reset to default!\n\nLittle Rock, AR\nLat: ${defaultLocation.lat.toFixed(4)}\nLng: ${defaultLocation.lng.toFixed(4)}\nZoom: ${defaultLocation.zoom}`);
  };

  /**
   * ✅ Return map to default "Home" location
   */
  const returnToHomeLocation = () => {
    if (!mapInstanceRef.current) {
      return;
    }
    
    mapInstanceRef.current.setView(
      [defaultMapLocation.lat, defaultMapLocation.lng],
      defaultMapLocation.zoom,
      { animate: true, duration: 0.5 }
    );
    
  };

  /**
   * ✅ Check if a point is inside a polygon using ray casting algorithm
   */
  const isPointInPolygon = (point, polygon) => {
    const [lat, lng] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [lat1, lng1] = polygon[i];
      const [lat2, lng2] = polygon[j];
      
      const intersect = ((lng1 > lng) !== (lng2 > lng)) &&
        (lat < (lat2 - lat1) * (lng - lng1) / (lng2 - lng1) + lat1);
      
      if (intersect) inside = !inside;
    }
    
    return inside;
  };

  /**
   * ✅ Start drawing polygon on map
   */
  const startDrawingPolygon = () => {
    if (!mapInstanceRef.current || !window.L) return;
    
    setIsDrawingPolygon(true);
    
    // Clear existing polygon if any
    if (polygonLayerRef.current) {
      mapInstanceRef.current.removeLayer(polygonLayerRef.current);
      polygonLayerRef.current = null;
    }
    
    const drawnItems = new window.L.FeatureGroup();
    mapInstanceRef.current.addLayer(drawnItems);
    
    // Use programmatic drawing API (starts immediately, no toolbar)
    const drawHandler = new window.L.Draw.Polygon(mapInstanceRef.current, {
      allowIntersection: false,
      showArea: true,
      shapeOptions: {
        color: '#3B82F6',
        fillOpacity: 0.2
      }
    });
    
    // Enable drawing immediately
    drawHandler.enable();
    
    // Store for cleanup
    window._currentDrawHandler = drawHandler;
    window._currentDrawnItems = drawnItems;
    
    // Handle polygon creation
    const handleCreated = (e) => {
      const layer = e.layer;
      
      // Get polygon coordinates
      const latlngs = layer.getLatLngs()[0];
      const coords = latlngs.map(latlng => [latlng.lat, latlng.lng]);
      
      // Create a permanent polygon with the drawn coordinates
      const permanentPolygon = window.L.polygon(coords, {
        color: '#3B82F6',
        fillColor: '#3B82F6',
        fillOpacity: 0.2,
        weight: 2
      });
      
      // Add permanent polygon to map and store reference
      permanentPolygon.addTo(mapInstanceRef.current);
      polygonLayerRef.current = permanentPolygon;
      
      setPolygonCoords(coords);
      setCurrentPolygonName(''); // Clear name for manually drawn polygon
      setIsDrawingPolygon(false);
      
      // Cleanup drawing tools
      if (drawHandler) {
        drawHandler.disable();
      }
      
      // Remove the temporary drawnItems layer (contains the drawing preview)
      if (drawnItems && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(drawnItems);
      }
      
      // Remove event listener
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off(window.L.Draw.Event.CREATED, handleCreated);
      }
      
      // Remove key handler
      if (window._drawnAreaKeyHandler) {
        window.removeEventListener('keydown', window._drawnAreaKeyHandler);
        delete window._drawnAreaKeyHandler;
      }
      
      delete window._currentDrawHandler;
      delete window._currentDrawnItems;
      delete window._currentCreatedHandler;
      
    };
    
    mapInstanceRef.current.on(window.L.Draw.Event.CREATED, handleCreated);
    window._currentCreatedHandler = handleCreated;
    
    // Handle ESC key to cancel
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancelDrawing();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window._drawnAreaKeyHandler = handleKeyDown;
  };

  /**
   * Cancel polygon drawing
   */
  const cancelDrawing = () => {
    if (!mapInstanceRef.current) return;
    
    setIsDrawingPolygon(false);
    
    // Disable and cleanup draw handler if it exists
    if (window._currentDrawHandler) {
      try {
        window._currentDrawHandler.disable();
      } catch (error) {
      }
      delete window._currentDrawHandler;
    }
    
    // Remove drawn items layer if it exists
    if (window._currentDrawnItems) {
      try {
        mapInstanceRef.current.removeLayer(window._currentDrawnItems);
      } catch (error) {
      }
      delete window._currentDrawnItems;
    }
    
    // Remove created event handler
    if (window._currentCreatedHandler && mapInstanceRef.current) {
      try {
        mapInstanceRef.current.off(window.L.Draw.Event.CREATED, window._currentCreatedHandler);
      } catch (error) {
      }
      delete window._currentCreatedHandler;
    }
    
    // Remove key handler
    if (window._drawnAreaKeyHandler) {
      window.removeEventListener('keydown', window._drawnAreaKeyHandler);
      delete window._drawnAreaKeyHandler;
    }
    
  };

  /**
   * Start drawing radius circle
   */
  const startDrawingRadius = () => {
    if (!mapInstanceRef.current || !window.L) return;
    
    setIsDrawingRadius(true);
    
    // Clear existing polygon if any
    if (polygonLayerRef.current) {
      mapInstanceRef.current.removeLayer(polygonLayerRef.current);
      polygonLayerRef.current = null;
    }
    
    
    // Create click handler for radius drawing - use native DOM capture to intercept before layers
    const handleRadiusClick = (e) => {
      // Get leaflet latlng from DOM event
      const latlng = mapInstanceRef.current.mouseEventToLatLng(e);
      const center = latlng;
      
      // Convert radius to meters based on unit
      const radiusInMeters = radiusUnit === 'miles' 
        ? radiusValue * 1609.34  // miles to meters
        : radiusValue * 1000;     // km to meters
      
      // Create circle
      const circle = window.L.circle(center, {
        radius: radiusInMeters,
        color: '#3B82F6',
        fillColor: '#3B82F6',
        fillOpacity: 0.2,
        weight: 2
      });
      
      // Convert circle to polygon coordinates (approximation with 64 points)
      const numPoints = 64;
      const coords = [];
      
      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        const dx = radiusInMeters * Math.cos(angle);
        const dy = radiusInMeters * Math.sin(angle);
        
        // Calculate new lat/lng using approximation
        // 1 degree latitude ≈ 111,139 meters
        // 1 degree longitude ≈ 111,139 * cos(latitude) meters
        const lat = center.lat + (dy / 111139);
        const lng = center.lng + (dx / (111139 * Math.cos(center.lat * Math.PI / 180)));
        
        coords.push([lat, lng]);
      }
      
      // Create polygon layer from circle coords
      const polygon = window.L.polygon(coords, {
        color: '#3B82F6',
        fillColor: '#3B82F6',
        fillOpacity: 0.2,
        weight: 2
      });
      
      polygon.addTo(mapInstanceRef.current);
      polygonLayerRef.current = polygon;
      
      // Set polygon coordinates
      setPolygonCoords(coords);
      setCurrentPolygonName(''); // Clear name for manually drawn circle
      
      // Cleanup
      setIsDrawingRadius(false);
      mapInstanceRef.current.getContainer().removeEventListener('click', handleRadiusClick, true);
      
      // Remove key handler
      if (window._radiusKeyHandler) {
        window.removeEventListener('keydown', window._radiusKeyHandler);
        delete window._radiusKeyHandler;
      }
      
    };
    
    // Add click handler to map container DOM element with capture=true to intercept before layers
    mapInstanceRef.current.getContainer().addEventListener('click', handleRadiusClick, true);
    
    // Add ESC key handler to cancel
    const keyHandler = (e) => {
      if (e.key === 'Escape') {
        cancelDrawingRadius();
      }
    };
    window.addEventListener('keydown', keyHandler);
    window._radiusKeyHandler = keyHandler;
  };

  /**
   * Cancel radius drawing
   */
  const cancelDrawingRadius = () => {
    if (!mapInstanceRef.current) return;
    
    setIsDrawingRadius(false);
    
    // Remove click handler from DOM container
    if (mapInstanceRef.current.getContainer()) {
      mapInstanceRef.current.getContainer().removeEventListener('click', () => {}, true);
    }
    
    // Remove key handler
    if (window._radiusKeyHandler) {
      window.removeEventListener('keydown', window._radiusKeyHandler);
      delete window._radiusKeyHandler;
    }
    
  };

  /**
   * ✅ Clear polygon and reset filter
   */
  const clearPolygon = () => {
    if (polygonLayerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(polygonLayerRef.current);
      polygonLayerRef.current = null;
    }
    
    setPolygonCoords(null);
    setPolygonFilterMode('none');
    setCurrentPolygonName(''); // Clear polygon name
    setIsDrawingPolygon(false);
    setCurrentLoadedPolygonId(null); // Reset loaded polygon ID
    
  };

  /**
   * Save current polygon with a name
   */
  const saveCurrentPolygon = () => {
    if (!polygonCoords || polygonCoords.length === 0) {
      alert('Please draw a polygon first');
      return;
    }
    
    setShowSavePolygonDialog(true);
  };

  /**
   * Confirm save polygon with name
   */
  const confirmSavePolygon = () => {
    if (!polygonNameInput.trim()) {
      alert('Please enter a name for this polygon');
      return;
    }
    
    const newPolygon = {
      id: Date.now(),
      name: polygonNameInput.trim(),
      coords: polygonCoords,
      filterMode: polygonFilterMode,
      createdAt: new Date().toISOString(),
      filterEligible: true
    };
    
    setSavedPolygons(prev => [...prev, newPolygon]);
    setShowSavePolygonDialog(false);
    setPolygonNameInput('');
    
  };

  /**
   * Load a saved polygon
   */
  const loadSavedPolygon = (polygon) => {
    // Clear existing polygon
    if (polygonLayerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(polygonLayerRef.current);
      polygonLayerRef.current = null;
    }
    
    // Draw the saved polygon on the map
    if (mapInstanceRef.current && window.L) {
      const layer = window.L.polygon(polygon.coords, {
        color: '#3B82F6',
        fillOpacity: 0.2
      }).addTo(mapInstanceRef.current);
      
      polygonLayerRef.current = layer;
      
      // Zoom to fit the polygon
      mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [50, 50] });
    }
    
    setPolygonCoords(polygon.coords);
    setPolygonFilterMode(polygon.filterMode || 'none');
    setCurrentPolygonName(polygon.name); // Store the polygon name
    setShowLoadPolygonMenu(false);
    setPolygonSearchTerm(''); // Clear search after loading
    
  };

  /**
   * Delete a saved polygon
   */
  const deleteSavedPolygon = (polygonId) => {
    if (window.confirm('Are you sure you want to delete this saved polygon?')) {
      // If deleting the currently loaded polygon, clear it from the map
      if (currentLoadedPolygonId === polygonId) {
        clearPolygon();
      }
      
      setSavedPolygons(prev => prev.filter(p => p.id !== polygonId));
      setPolygonSearchTerm(''); // Clear search after deleting
    }
  };

  /**
   * ✅ Convert custom layer polygon to drawn polygon
   */
  const convertCustomLayerToDrawnPolygons = (layerId) => {
    const layer = customLayers.find(l => l.id === layerId);
    
    if (!layer || layer.type !== 'polygon') {
      alert('This layer is not a polygon layer');
      return;
    }
    
    if (layer.data.length === 0) {
      alert('This layer has no polygons to convert');
      return;
    }
    
    // Confirm before converting
    const polygonWord = layer.data.length === 1 ? 'polygon' : 'polygons';
    const confirmed = window.confirm(
      `Convert layer "${layer.name}" to drawn polygons?\n\n` +
      `This will create ${layer.data.length} filterable ${polygonWord} from this custom layer.\n\n` +
      `The original custom layer will remain unchanged.`
    );
    
    if (!confirmed) {
      return;
    }
    
    // Convert each polygon in the layer to a drawn polygon
    const newPolygons = layer.data.map((coords, idx) => ({
      id: Date.now() + idx, // Unique ID for each polygon
      name: layer.data.length > 1 
        ? `${layer.name} - Polygon ${idx + 1}` 
        : layer.name,
      coords: coords,
      filterMode: 'inside', // Default filter mode
      createdAt: new Date().toISOString()
    }));
    
    // Add all new polygons to savedPolygons
    setSavedPolygons(prev => [...prev, ...newPolygons]);
    
    alert(`✅ Converted ${newPolygons.length} polygon${newPolygons.length !== 1 ? 's' : ''} from "${layer.name}" to drawn polygons!\n\nYou can now use ${newPolygons.length === 1 ? 'it' : 'them'} for data filtering.`);
    
  };

  /**
   * ✅ Apply polygon filter (inside or outside)
   */
  const applyPolygonFilter = (mode) => {
    if (!polygonCoords) {
      alert('Please draw a polygon first');
      return;
    }
    
    setPolygonFilterMode(mode);
  };

  /**
   * ✅ Apply polygon filter to data (works with both server-side and client-side filtering)
   */
  const applyPolygonFilterToData = (data) => {
    
    if (polygonFilterMode === 'none' || !polygonCoords || polygonCoords.length === 0) {
      return data; // No polygon filter, return data as-is
    }

    
    const filtered = data.filter(e => {
      // Check if event has valid coordinates
      if (!e.LatitudeDegree || !e.LongitudeDegree) {
        return false;
      }
      
      // Calculate lat/lng with signs
      const lat = parseFloat(e.LatitudeDegree) * (e.LatitudeSign === '-' ? -1 : 1);
      const lng = parseFloat(e.LongitudeDegree) * (e.LongitudeSign === '-' ? -1 : 1);
      
      if (isNaN(lat) || isNaN(lng)) {
        return false;
      }
      
      const point = [lat, lng];
      const inPolygon = isPointInPolygon(point, polygonCoords);
      
      // Include if mode is 'inside' and point is in polygon, or mode is 'outside' and point is not in polygon
      return polygonFilterMode === 'inside' ? inPolygon : !inPolygon;
    });
    
    return filtered;
  };

  // Response Plan Functions
  const openPlaybookDialog = (polygon) => {
    const existing = playbooks[polygon.name];
    if (existing) {
      setCurrentPlaybookForm(existing);
    } else {
      setCurrentPlaybookForm({
        areaName: polygon.name,
        priority: 'Recommended',
        priorityThreshold: 'all',
        timeWindow: 'always',
        startTime: '00:00',
        endTime: '23:59',
        dispatchGuidance: '',
        stagingInstructions: '',
        safetyNotes: '',
        supervisorNotification: '',
        specialInstructions: '',
        status: 'Draft',
        createdBy: '',
        createdDate: new Date().toISOString().slice(0, 16),
        approvedBy: '',
        approvedDate: '',
        startDate: '',
        expirationDate: ''
      });
    }
    setEditingPlaybook(polygon.name);
    setShowPlaybookDialog(true);
  };
  
  const savePlaybook = () => {
    if (!currentPlaybookForm.dispatchGuidance.trim()) {
      alert('Please provide dispatch guidance');
      return;
    }
    setPlaybooks(prev => ({ ...prev, [editingPlaybook]: currentPlaybookForm }));
    setShowPlaybookDialog(false);
  };
  
  const deletePlaybook = (areaName) => {
    if (window.confirm('Delete response plan?')) {
      setPlaybooks(prev => {
        const updated = { ...prev };
        delete updated[areaName];
        return updated;
      });
    }
  };

  // ===== CUSTOM LAYER MANAGEMENT FUNCTIONS =====

  const createCustomLayer = (formData) => {
    const newLayer = {
      id: `layer_${Date.now()}`,
      name: formData.name.trim(),
      type: formData.type,
      visible: true,
      created: new Date().toISOString(),
      useBackgroundPane: formData.useBackgroundPane,
      style: {
        color: formData.color,
        fillColor: formData.color,
        fillOpacity: formData.fillOpacity,
        weight: formData.weight,
        showBorder: formData.showBorder,
        icon: formData.markerIcon
      },
      data: []
    };
    setCustomLayers(prev => [...prev, newLayer]);
    setShowAddLayerDialog(false);
    startDrawingCustomLayer(newLayer);
  };

  const deleteCustomLayer = (layerId) => {
    setCustomLayers(prev => prev.filter(l => l.id !== layerId));
  };

  const openEditLayer = (layerId) => {
    setEditingLayerId(layerId);
    setShowEditLayerDialog(true);
  };

  const updateCustomLayer = (formData) => {
    setCustomLayers(prev => prev.map(layer =>
      layer.id === editingLayerId
        ? {
            ...layer,
            name: formData.name.trim(),
            useBackgroundPane: formData.useBackgroundPane,
            style: {
              ...layer.style,
              color: formData.color,
              fillColor: formData.color,
              fillOpacity: formData.fillOpacity,
              weight: formData.weight,
              showBorder: formData.showBorder,
              icon: formData.markerIcon
            }
          }
        : layer
    ));
    setShowEditLayerDialog(false);
    setEditingLayerId(null);
  };

  const toggleLayerVisibility = (layerId) => {
    setCustomLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? { ...layer, visible: !layer.visible }
        : layer
    ));
  };

  const addPointToLayer = (layerId, point) => {
    setCustomLayers(prev => prev.map(layer =>
      layer.id === layerId
        ? { ...layer, data: [...layer.data, point] }
        : layer
    ));
  };

  const startDrawingCustomLayer = (layer) => {
    if (!mapInstanceRef.current || !window.L) return;
    
    setCurrentDrawingLayer(layer);
    setIsDrawingCustomLayer(true);
    setCustomLayerPoints([]);
    
    
    if (layer.type === 'marker') {
      // For markers, use simple click mode
      alert(`Drawing marker layer "${layer.name}"\n\nClick on map to add markers.\nClick Finish when done.`);
      return;
    }
    
    // For polygons and lines, use programmatic Leaflet.draw (starts immediately)
    const drawnItems = new window.L.FeatureGroup();
    mapInstanceRef.current.addLayer(drawnItems);
    
    let drawHandler;
    
    if (layer.type === 'polygon') {
      // Create polygon draw handler
      drawHandler = new window.L.Draw.Polygon(mapInstanceRef.current, {
        allowIntersection: false,
        showArea: true,
        shapeOptions: {
          color: layer.style.color,
          fillColor: layer.style.fillColor || layer.style.color,
          fillOpacity: layer.style.fillOpacity || 0.2,
          weight: layer.style.weight || 2
        }
      });
    } else {
      // Create polyline draw handler
      drawHandler = new window.L.Draw.Polyline(mapInstanceRef.current, {
        shapeOptions: {
          color: layer.style.color,
          weight: layer.style.weight || 3,
          opacity: layer.style.opacity || 1
        }
      });
    }
    
    // Enable drawing immediately (no need to click toolbar button)
    drawHandler.enable();
    
    // Store for cleanup
    window._currentCustomLayerDrawHandler = drawHandler;
    window._currentCustomLayerDrawnItems = drawnItems;
    window._currentCustomLayerId = layer.id;
    
    // Handle shape creation
    const handleCreated = (e) => {
      const drawnLayer = e.layer;
      drawnItems.addLayer(drawnLayer);
      
      // Get coordinates
      let coords;
      if (layer.type === 'polygon') {
        const latlngs = drawnLayer.getLatLngs()[0];
        coords = latlngs.map(latlng => [latlng.lat, latlng.lng]);
      } else {
        const latlngs = drawnLayer.getLatLngs();
        coords = latlngs.map(latlng => [latlng.lat, latlng.lng]);
      }
      
      // Add to layer
      addPointToLayer(layer.id, coords);
      
      // Cleanup
      setIsDrawingCustomLayer(false);
      setCurrentDrawingLayer(null);
      setCustomLayerPoints([]);
      
      if (drawHandler) {
        drawHandler.disable();
      }
      
      if (drawnItems && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(drawnItems);
      }
      
      // Remove event listener
      if (mapInstanceRef.current) {
        mapInstanceRef.current.off(window.L.Draw.Event.CREATED, handleCreated);
      }
      
      // Remove key handler
      if (window._customLayerKeyHandler) {
        window.removeEventListener('keydown', window._customLayerKeyHandler);
        delete window._customLayerKeyHandler;
      }
      
      delete window._currentCustomLayerDrawHandler;
      delete window._currentCustomLayerDrawnItems;
      delete window._currentCustomLayerId;
      
    };
    
    mapInstanceRef.current.on(window.L.Draw.Event.CREATED, handleCreated);
    window._currentCustomLayerCreatedHandler = handleCreated;
    
    // Handle ESC key to cancel
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancelDrawingCustomLayer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window._customLayerKeyHandler = handleKeyDown;
  };

  const handleMapClickForCustomLayer = (e) => {
    if (!isDrawingCustomLayer || !currentDrawingLayer) return;
    
    const { lat, lng } = e.latlng;
    
    if (currentDrawingLayer.type === 'marker') {
      // For markers, each click adds a new marker immediately
      const label = prompt(`Enter label for marker at ${lat.toFixed(4)}, ${lng.toFixed(4)}:`) || `Marker ${customLayers.find(l => l.id === currentDrawingLayer.id)?.data.length + 1 || 1}`;
      addPointToLayer(currentDrawingLayer.id, { lat, lng, label });
      
    } else {
      // For polygons and lines, accumulate points
      setCustomLayerPoints(prev => [...prev, [lat, lng]]);
    }
  };

  const finishDrawingCustomLayer = () => {
    // For line and polygon layers, Leaflet.Draw manages the drawing session.
    // We must call _finishShape() to trigger the CREATED event (which saves coords
    // and cleans up). Simply resetting state without finishing the handler leaves
    // Leaflet.Draw running in the background.
    if (currentDrawingLayer && (currentDrawingLayer.type === 'line' || currentDrawingLayer.type === 'polygon')) {
      const handler = window._currentCustomLayerDrawHandler;
      if (handler) {
        try {
          // _finishShape() commits the drawn shape and fires the CREATED event,
          // which triggers handleCreated → saves data → cleans up state.
          handler._finishShape();
          return; // handleCreated will handle cleanup
        } catch (e) {
          // If shape has fewer than minimum points, just cancel instead
          cancelDrawingCustomLayer();
          return;
        }
      }
    }

    // For marker mode — just stop drawing
    setIsDrawingCustomLayer(false);
    setCurrentDrawingLayer(null);
    setCustomLayerPoints([]);
  };

  const cancelDrawingCustomLayer = () => {
    if (currentDrawingLayer) {
      // If no data was added, delete the layer
      const layer = customLayers.find(l => l.id === currentDrawingLayer.id);
      if (layer && layer.data.length === 0) {
        deleteCustomLayer(currentDrawingLayer.id);
      }
    }
    
    // Disable and cleanup draw handler if it exists
    if (window._currentCustomLayerDrawHandler) {
      try {
        window._currentCustomLayerDrawHandler.disable();
      } catch (error) {
      }
      delete window._currentCustomLayerDrawHandler;
    }
    
    // Remove drawn items layer if it exists
    if (window._currentCustomLayerDrawnItems && mapInstanceRef.current) {
      try {
        mapInstanceRef.current.removeLayer(window._currentCustomLayerDrawnItems);
      } catch (error) {
      }
      delete window._currentCustomLayerDrawnItems;
    }
    
    // Remove created event handler
    if (window._currentCustomLayerCreatedHandler && mapInstanceRef.current) {
      try {
        mapInstanceRef.current.off(window.L.Draw.Event.CREATED, window._currentCustomLayerCreatedHandler);
      } catch (error) {
      }
      delete window._currentCustomLayerCreatedHandler;
    }
    
    // Remove key handler
    if (window._customLayerKeyHandler) {
      window.removeEventListener('keydown', window._customLayerKeyHandler);
      delete window._customLayerKeyHandler;
    }
    
    delete window._currentCustomLayerId;
    
    setIsDrawingCustomLayer(false);
    setCurrentDrawingLayer(null);
    setCustomLayerPoints([]);
  };

  const updateMapMarkers = useCallback(() => {
    
    if (!mapInstanceRef.current || !window.L) {
      return;
    }

    try {
      // Clear existing marker cluster group
      if (markerClusterGroupRef.current) {
        mapInstanceRef.current.removeLayer(markerClusterGroupRef.current);
        markerClusterGroupRef.current = null;
      }

      // Clear existing individual markers (fallback)
      markersRef.current.forEach(marker => {
        try {
          mapInstanceRef.current.removeLayer(marker);
        } catch (e) {
          console.warn('Error removing marker:', e);
        }
      });
      markersRef.current = [];


      // If no data, we're done after clearing
      if (filteredData.length === 0) {
        return;
      }

      // Skip markers if neither incidents nor clusters layer is enabled
      if (!mapLayers.incidents && !mapLayers.clusters) {
        return;
      }

      // Create marker cluster group if library is loaded AND clusters are enabled
      if (mapLayers.clusters && window.L.markerClusterGroup) {
        markerClusterGroupRef.current = window.L.markerClusterGroup({
          maxClusterRadius: 50,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          iconCreateFunction: function(cluster) {
            const count = cluster.getChildCount();
            let className = 'marker-cluster-';
            
            if (count < 10) {
              className += 'small';
            } else if (count < 25) {
              className += 'medium';
            } else {
              className += 'large';
            }
            
            return window.L.divIcon({
              html: `<div><span>${count}</span></div>`,
              className: 'marker-cluster ' + className,
              iconSize: window.L.point(40, 40)
            });
          }
        });
      }

      // Group events by exact location
      const locationGroups = {};
      const coordinates = [];
      

      filteredData.forEach(event => {
        if (event.LatitudeDegree && event.LongitudeDegree) {
          const lat = parseFloat(event.LatitudeDegree) * (event.LatitudeSign === '-' ? -1 : 1);
          const lng = parseFloat(event.LongitudeDegree) * (event.LongitudeSign === '-' ? -1 : 1);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            coordinates.push([lat, lng]);
            const locationKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
            
            if (!locationGroups[locationKey]) {
              locationGroups[locationKey] = [];
            }
            locationGroups[locationKey].push(event);
          }
        }
      });


      // Create markers for each location
      Object.entries(locationGroups).forEach(([locationKey, events]) => {
        const [lat, lng] = locationKey.split(',').map(Number);
        const event = events[0]; // Use first event for marker color
        
        const markerColor = event.EventPriority === 1 ? '#EF4444' : 
                          event.EventPriority === 2 ? '#F59E0B' : '#10B981';
        
        const marker = window.L.marker([lat, lng], {
          icon: window.L.divIcon({
            className: 'custom-marker',
            html: `<div style="background-color: ${markerColor}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 10px;">${events.length > 1 ? events.length : ''}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          })
        });

        // Create popup content based on number of events
        if (events.length === 1) {
          // Single event - simple popup
          const event = events[0];
          marker.bindTooltip(`
            <div style="font-family: Arial, sans-serif; font-size: 12px;">
              <strong style="color: ${markerColor}; font-size: 13px;">${event.EventType || 'Unknown'}</strong><br>
              <span style="color: #555;">📍 ${event.Address || 'No address'}</span><br>
              <span style="color: #555;">🆔 ${event.Id || 'N/A'}</span><br>
              <span style="color: #555;">📊 ${event.Status || 'Unknown'}</span><br>
              <span style="color: #888; font-size: 11px;">Priority ${event.EventPriority || 'N/A'}</span>
            </div>
          `, {
            permanent: false,
            direction: 'top',
            offset: [0, -12],
            opacity: 0.95,
            className: 'custom-tooltip'
          });
          
          marker.bindPopup(`
            <div style="font-family: Arial, sans-serif; min-width: 200px;">
              <strong style="font-size: 14px;">${event.EventType}</strong><br>
              <small style="color: #666;">${event.EventDescription || 'No description'}</small><br>
              <small style="color: #666;">${event.Address}</small><br>
              <small style="color: #666;">Priority ${event.EventPriority}</small>
            </div>
          `);
          
          marker.on('click', () => { setSelectedEvent(event); setEventNavContext(null); });
        } else {
          // Multiple events - list popup with theme-aware colors
          const bgColor      = isDarkTheme ? '#1a202c' : '#ffffff';
          const headerBorder = isDarkTheme ? '#4a5568' : '#e2e8f0';
          const headerText   = isDarkTheme ? '#f7fafc' : '#1a202c';
          const cardBg       = isDarkTheme ? '#2d3748' : '#f7fafc';
          const cardHover    = isDarkTheme ? '#4a5568' : '#e2e8f0';
          const labelColor   = isDarkTheme ? '#a0aec0' : '#4a5568';
          const valueColor   = isDarkTheme ? '#e2e8f0' : '#2d3748';
          const addrColor    = isDarkTheme ? '#90cdf4' : '#2b6cb0';

          const popupContent = `
            <div style="font-family: Arial, sans-serif; max-width: 320px; background: ${bgColor}; border-radius: 6px; overflow: hidden;">
              <div style="
                font-weight: 700;
                font-size: 14px;
                padding: 12px 14px;
                border-bottom: 2px solid ${headerBorder};
                color: ${headerText};
                background: ${isDarkTheme ? '#2d3748' : '#edf2f7'};
                display: flex;
                align-items: center;
                gap: 6px;
              ">
                📍 ${events[0].Address || 'Unknown location'}
                <span style="
                  margin-left: auto;
                  background: ${isDarkTheme ? '#4a5568' : '#cbd5e0'};
                  color: ${headerText};
                  border-radius: 12px;
                  padding: 2px 10px;
                  font-size: 12px;
                  font-weight: 600;
                  white-space: nowrap;
                ">${events.length} Events</span>
              </div>
              <div style="max-height: 280px; overflow-y: auto; padding: 8px;">
                ${events.map((evt, idx) => {
                  const evtColor = evt.EventPriority === 1 ? '#EF4444' : 
                                   evt.EventPriority === 2 ? '#F59E0B' : '#10B981';
                  return `
                    <div 
                      data-event-id="${evt.ptsEventID}"
                      style="
                        padding: 10px 12px;
                        margin-bottom: ${idx < events.length - 1 ? '6px' : '0'};
                        border-left: 4px solid ${evtColor};
                        background: ${cardBg};
                        border-radius: 4px;
                        cursor: pointer;
                        transition: background 0.15s;
                      "
                      onmouseover="this.style.background='${cardHover}'"
                      onmouseout="this.style.background='${cardBg}'"
                    >
                      <div style="font-weight: 700; color: ${evtColor}; font-size: 13px; margin-bottom: 5px;">
                        ${evt.EventType || 'Unknown'}
                      </div>
                      <div style="font-size: 11px; color: ${labelColor}; margin-bottom: 2px;">
                        🆔 <span style="color: ${valueColor};">${evt.Id || 'N/A'}</span>
                      </div>
                      <div style="font-size: 11px; color: ${labelColor}; margin-bottom: 2px;">
                        📊 <span style="color: ${valueColor};">${evt.Status || 'Unknown'}</span>
                      </div>
                      <div style="font-size: 11px; color: ${labelColor}; margin-bottom: 2px;">
                        ⚡ <span style="color: ${valueColor};">Priority ${evt.EventPriority || 'N/A'}</span>
                      </div>
                      ${evt.EventDescription ? `
                      <div style="font-size: 11px; color: ${labelColor}; margin-top: 4px; border-top: 1px solid ${headerBorder}; padding-top: 4px;">
                        ${evt.EventDescription}
                      </div>` : ''}
                      <div style="font-size: 10px; color: ${addrColor}; margin-top: 4px; font-style: italic;">
                        Click to view full details →
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
          
          marker.bindPopup(popupContent, {
            maxWidth: 360,
            className: `multi-event-popup ${isDarkTheme ? 'dark-popup' : 'light-popup'}`
          });

          // Add click handlers to popup items after popup opens
          marker.on('popupopen', () => {
            events.forEach(evt => {
              const element = document.querySelector(`[data-event-id="${evt.ptsEventID}"]`);
              if (element) {
                element.addEventListener('click', () => {
                  setSelectedEvent(evt);
                  setEventNavContext(null);
                  marker.closePopup();
                });
              }
            });
          });

          marker.bindTooltip(`
            <div style="font-family: Arial, sans-serif; font-size: 12px;">
              <strong>${events.length} Events</strong><br>
              <span style="color: #666;">Click to see list</span>
            </div>
          `, {
            permanent: false,
            direction: 'top',
            offset: [0, -12],
            opacity: 0.95
          });
        }

        // Add marker to cluster group or map based on layer settings
        if (mapLayers.clusters && markerClusterGroupRef.current) {
          markerClusterGroupRef.current.addLayer(marker);
        } else if (mapLayers.incidents) {
          marker.addTo(mapInstanceRef.current);
          markersRef.current.push(marker);
        }
      });

      // Add cluster group to map
      if (markerClusterGroupRef.current) {
        mapInstanceRef.current.addLayer(markerClusterGroupRef.current);
      } else {
      }

      // Fit map bounds to show all markers (only if auto-recenter is enabled)
      if (autoRecenterMap && coordinates.length > 0) {
        try {
          const bounds = window.L.latLngBounds(coordinates);
          mapInstanceRef.current.fitBounds(bounds, {
            padding: [50, 50], // Add padding around markers (pixels)
            maxZoom: 15 // Don't zoom in too close if only one marker
          });
        } catch (error) {
          console.warn('Error fitting map bounds:', error);
        }
      } else if (!autoRecenterMap) {
      }
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, [filteredData, mapLayers, mapInstanceRef, theme, isDarkTheme, autoRecenterMap]);

  const updateHeatMapLayer = useCallback(() => {
    if (!mapInstanceRef.current || !window.L || !window.L.heatLayer) {
      return;
    }

    try {
      // Remove existing heat layer
      if (heatmapLayerRef.current) {
        mapInstanceRef.current.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }

      if (!mapLayers.heatmap || filteredData.length === 0) {
        return;
      }

      // Prepare heat map data
      const heatData = [];
      
      filteredData.forEach(event => {
        if (event.LatitudeDegree && event.LongitudeDegree) {
          const lat = parseFloat(event.LatitudeDegree) * (event.LatitudeSign === '-' ? -1 : 1);
          const lng = parseFloat(event.LongitudeDegree) * (event.LongitudeSign === '-' ? -1 : 1);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            // Calculate intensity based on priority
            let intensity = 1;
            
            if (event.PriorityDescription === 'Priority 1 (High)') {
              intensity = 3;
            } else if (event.PriorityDescription === 'Priority 2 (Medium)') {
              intensity = 2;
            }
            
            // Format: [lat, lng, intensity]
            heatData.push([lat, lng, intensity]);
          }
        }
      });


      // Create heat layer
      heatmapLayerRef.current = window.L.heatLayer(heatData, {
        radius: 25,           // Radius of each heat point
        blur: 15,             // Amount of blur
        maxZoom: 17,          // Zoom level where max intensity is reached
        max: 1.0,             // Maximum point intensity
        gradient: {           // Color gradient (blue → lime → yellow → red)
          0.0: 'blue',
          0.5: 'lime',
          0.7: 'yellow',
          1.0: 'red'
        }
      });

      // Add to map
      heatmapLayerRef.current.addTo(mapInstanceRef.current);

    } catch (error) {
      console.error('Error creating heat map:', error);
    }
  }, [filteredData, mapLayers, mapInstanceRef]);

  const updateResponseZonesLayer = useCallback(() => {
    
    if (!mapInstanceRef.current || !window.L) {
      return;
    }

    try {
      // Remove existing response zones layer
      if (responseZonesLayerRef.current) {
        mapInstanceRef.current.removeLayer(responseZonesLayerRef.current);
        responseZonesLayerRef.current = null;
      }

      if (savedPolygons.length === 0) {
        return;
      }

      // Show selected polygons (if any are selected)
      const polygonsToShow = selectedPolygonIds.length > 0
        ? savedPolygons.filter(p => selectedPolygonIds.includes(p.id))
        : [];  // Show none if nothing is selected

      if (polygonsToShow.length === 0) {
        return;
      }

      
      // Create layer group for selected polygons
      responseZonesLayerRef.current = window.L.layerGroup();

      polygonsToShow.forEach((polygon, index) => {
        
        // Check if this layer is highlighted
        const isHighlighted = highlightedLayers.includes(polygon.name);
        if (isHighlighted) {
        }
        
        const layer = window.L.polygon(polygon.coords, {
          color: isHighlighted ? '#FFFF00' : (polygon.color || '#3B82F6'),
          fillColor: isHighlighted ? '#FFFF00' : (polygon.color || '#3B82F6'),
          fillOpacity: isHighlighted ? 0.4 : 0.15,
          weight: isHighlighted ? 4 : 2,
          dashArray: isHighlighted ? '' : '5, 5'
        });

        // Store metadata on layer for click handler
        layer._polygonData = {
          name: polygon.name,
          color: polygon.color || '#3B82F6',
          type: 'Drawn Area',
          hasPlaybook: !!playbooks[polygon.name]
        };

        // Tooltip for hover (global click handler will show popup with all overlapping layers)
        layer.bindTooltip(polygon.name, { 
          permanent: showPolygonNames, 
          direction: 'center',
          className: 'polygon-label'
        });

        layer.addTo(responseZonesLayerRef.current);
      });

      responseZonesLayerRef.current.addTo(mapInstanceRef.current);

    } catch (error) {
      console.error('   ❌ Error creating drawn polygons layer:', error);
    }
  }, [savedPolygons, playbooks, selectedPolygonIds, showPolygonNames, mapInstanceRef, theme, isDarkTheme, highlightedLayers]);

  /**
   * ✅ Deduplicate events by ptsEventID
   * Combines multiple agency records into a single event with concatenated agencies
   */
  const deduplicateEvents = (events) => {
    if (!events || events.length === 0) {
      return [];
    }
    
    const startTime = Date.now();
    
    try {
      // Group by ptsEventID
      const grouped = events.reduce((acc, event) => {
        const key = event.ptsEventID || event.Id || `unknown-${Math.random()}`;
        
        if (!acc[key]) {
          // First occurrence - create the base record
          acc[key] = { ...event };
        } else {
          // Duplicate found - combine agencies
          const existing = acc[key];
          
          // Combine Agency (comma-separated, unique values)
          if (event.Agency && event.Agency !== existing.Agency) {
            const agencies = existing.Agency ? existing.Agency.split(', ') : [];
            if (!agencies.includes(event.Agency)) {
              agencies.push(event.Agency);
              existing.Agency = agencies.join(', ');
            }
          }
          
          // Combine AgencyType (comma-separated, unique values)
          if (event.AgencyType && event.AgencyType !== existing.AgencyType) {
            const agencyTypes = existing.AgencyType ? existing.AgencyType.split(', ') : [];
            if (!agencyTypes.includes(event.AgencyType)) {
              agencyTypes.push(event.AgencyType);
              existing.AgencyType = agencyTypes.join(', ');
            }
          }
        }
        
        return acc;
      }, {});
      
      const deduplicated = Object.values(grouped);
      const dedupeTime = Date.now() - startTime;
      const duplicatesRemoved = events.length - deduplicated.length;
      const duplicatePercent = ((duplicatesRemoved / events.length) * 100).toFixed(1);
      
      
      return deduplicated;
    } catch (error) {
      console.error('❌ Deduplication error:', error);
      console.error('⚠️ Returning original events without deduplication');
      return events; // Return original events if deduplication fails
    }
  };

  /**
   * ✅ Fetch filter options from dedicated endpoint
   * This loads ALL possible filter values (not limited by current filters)
   * Should be called once on initial load
   */
  const fetchFilterOptions = async () => {
    try {
      
      const response = await fetch(`${API_BASE_URL}/api/cad-events/filters`);
      
      if (!response.ok) {
        console.warn('⚠️ Filter options endpoint returned error:', response.status);
        return false;
      }
      
      const result = await response.json();
      
      if (!result.success) {
        console.warn('⚠️ Filter options fetch failed:', result.error);
        return false;
      }
      
      // Set all filter options
      if (result.eventTypes && result.eventTypes.length > 0) {
        setAvailableCallTypes(result.eventTypes);
      }
      
      if (result.statuses && result.statuses.length > 0) {
        setAvailableStatuses(result.statuses);
      }
      
      if (result.agencies && result.agencies.length > 0) {
        setAvailableAgencies(result.agencies);
      }
      
      if (result.agencyTypes && result.agencyTypes.length > 0) {
        setAvailableAgencyTypes(result.agencyTypes);
      }
      
      
      // Mark as loaded so we don't re-extract from filtered data later
      filterOptionsLoadedRef.current = true;
      
      return true;
      
    } catch (error) {
      console.error('❌ Error fetching filter options:', error);
      console.warn('⚠️ Will extract filter options from data instead');
      return false;
    }
  };

  /**
   * ✅ Extract filter options from data (fallback method)
   * Used when dedicated endpoint is not available
   * Only extracts on initial load to ensure ALL options are available
   */
  const extractFilterOptionsFromData = (data) => {
    if (!data || data.length === 0) {
      console.warn('⚠️ No data available to extract filter options');
      return;
    }
    
    
    const uniqueTypes = [...new Set(data.map(e => e.EventType).filter(Boolean))].sort();
    const uniqueStatuses = [...new Set(data.map(e => e.Status).filter(Boolean))].sort();
    const uniqueAgencies = [...new Set(data.map(e => e.Agency).filter(Boolean))].sort();
    const uniqueAgencyTypes = [...new Set(data.map(e => e.AgencyType).filter(Boolean))].sort();
    
    setAvailableCallTypes(uniqueTypes);
    setAvailableStatuses(uniqueStatuses);
    setAvailableAgencies(uniqueAgencies);
    setAvailableAgencyTypes(uniqueAgencyTypes);
    
    
    // Mark as loaded so we don't re-extract from filtered data later
    filterOptionsLoadedRef.current = true;
  };

  /**
   * ✅ Build query string from current filters for server-side filtering
   * Handles time windows (Cumulative, Snapshot, Rolling) with proper priority
   */
  const buildQueryString = (currentFilters) => {
    const params = new URLSearchParams();
    
    
    // ✅ Calculate effective date range based on time window priority
    // Priority: 1. Cumulative Window, 2. Snapshot Window, 3. Rolling Window, 4. Manual Dates
    let effectiveDateFrom = currentFilters.dateFrom;
    let effectiveDateTo = currentFilters.dateTo;
    
    // Cumulative Window takes highest precedence
    if (currentCumulativeStart && currentCumulativeEnd) {
      effectiveDateFrom = formatDateTimeLocal(currentCumulativeStart);
      effectiveDateTo = formatDateTimeLocal(currentCumulativeEnd);
    }
    // Snapshot Window takes precedence over rolling/manual
    else if (currentSnapshotStart && currentSnapshotEnd) {
      effectiveDateFrom = formatDateTimeLocal(currentSnapshotStart);
      effectiveDateTo = formatDateTimeLocal(currentSnapshotEnd);
    }
    // Rolling time window
    else if (currentFilters.lastHours && !isNaN(currentFilters.lastHours) && currentFilters.lastHours > 0) {
      const now = new Date();
      const hoursAgo = new Date(now.getTime() - (currentFilters.lastHours * 60 * 60 * 1000));
      effectiveDateFrom = formatDateTimeLocal(hoursAgo);
      effectiveDateTo = formatDateTimeLocal(now);
    }
    // Manual dates (default)
    else if (effectiveDateFrom || effectiveDateTo) {
    }
    
    
    // ✅ Use calculated effective dates (includes time windows!)
    if (effectiveDateFrom) {
      params.append('dateFrom', effectiveDateFrom);
    }
    if (effectiveDateTo) {
      params.append('dateTo', effectiveDateTo);
    }
    
    // Event types (send as comma-separated string)
    if (currentFilters.callTypes && currentFilters.callTypes.length > 0 && !currentFilters.callTypes.includes('all')) {
      params.append('eventType', currentFilters.callTypes.join(','));
    }
    
    // Statuses (send as comma-separated string)
    if (currentFilters.statuses && currentFilters.statuses.length > 0 && !currentFilters.statuses.includes('all')) {
      params.append('status', currentFilters.statuses.join(','));
    }
    
    // Agencies (send as comma-separated string)
    if (currentFilters.agencies && currentFilters.agencies.length > 0 && !currentFilters.agencies.includes('all')) {
      params.append('agency', currentFilters.agencies.join(','));
    }
    
    // Agency types (send as comma-separated string)
    if (currentFilters.agencyTypes && currentFilters.agencyTypes.length > 0 && !currentFilters.agencyTypes.includes('all')) {
      params.append('agencyType', currentFilters.agencyTypes.join(','));
    }
    
    // Priorities (send as comma-separated string)
    if (currentFilters.priorities && currentFilters.priorities.length > 0 && !currentFilters.priorities.includes('all')) {
      params.append('priority', currentFilters.priorities.join(','));
    }
    
    // Active only
    if (currentFilters.activeOnly) {
      params.append('activeOnly', 'true');
    }
    
    return params.toString();
  };

  /**
   * ✅ Fetch KPI statistics from server endpoint
   * Much faster than calculating client-side (5KB vs 5MB)
   * Falls back to client-side calculation if endpoint fails
   */
  const fetchKPIStats = async (queryString = '') => {
    try {
      const statsUrl = queryString 
        ? `${API_BASE_URL}/api/cad-events/stats?${queryString}`
        : `${API_BASE_URL}/api/cad-events/stats`;
      
      
      const response = await fetch(statsUrl);
      
      if (!response.ok) {
        console.warn('⚠️ Stats endpoint failed, will use client-side calculation');
        return null;
      }
      
      const result = await response.json();
      
      if (!result.success) {
        console.warn('⚠️ Stats endpoint returned error, will use client-side calculation');
        return null;
      }
      
      
      return result.stats;
      
    } catch (error) {
      console.error('❌ Error fetching KPI stats:', error);
      return null;
    }
  };

const fetchData = async (showLoading = true, forceFullRefresh = false) => {
  try {
    const now = new Date();
    
    // ✅ Decide: Full refresh or incremental update?
    const needsFullRefresh = 
      forceFullRefresh || // User action requires full refresh
      !lastFetchTime || // First load ever
      !lastFullRefreshTime || // Never did full refresh
      (now - lastFullRefreshTime) > FULL_REFRESH_INTERVAL; // Been 10+ minutes since last full refresh
    
    if (showLoading) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    
    // ===== FULL REFRESH =====
    if (needsFullRefresh) {
      
      // ✅ Build API URL with filters if server-side filtering is enabled
      let apiUrl = `${API_BASE_URL}/api/cad-events`;
      
      if (USE_SERVER_SIDE_FILTERING) {
        const queryString = buildQueryString(filters);
        if (queryString) {
          apiUrl += `?${queryString}`;
        }
      }
      
      const response = await fetch(apiUrl);
      
      // Check what we got back
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Got HTML instead of JSON:', text.substring(0, 100));
        throw new Error('Backend returned HTML. Is it running on port 5050?');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API error');
      }
      
      const data = result.data;
      
      // Log performance metrics
      if (result.metadata) {
        if (result.metadata.totalCount) {
        }
      } else {
      }
      
      // ✅ If using server-side filtering, data is already filtered
      if (USE_SERVER_SIDE_FILTERING) {
        // ✅ Directly compute and set filteredData here rather than waiting for the polygon
        // filter useEffect to react to allData changing. This ensures rolling window (and all
        // other refreshes) immediately update the visible data with no intermediate render gap.
        const deduplicated = deduplicateEvents(data);
        const filtered = applyPolygonFilterToData(deduplicated);
        setAllData(data);
        setFilteredData(filtered);
      } else {
        // Old behavior: store all data and filter client-side
        setAllData(data);
        // applyFilters() will be called separately
      }
      
      // Update timestamps
      setLastFetchTime(now);
      setLastFullRefreshTime(now);
      
    } 
    // ===== INCREMENTAL UPDATE =====
    else {
      
      // Add 5-second buffer to handle clock drift and avoid missing events
      const sinceTime = new Date(lastFetchTime.getTime() - 5000);
      const apiUrl = `${API_BASE_URL}/api/cad-events/updates?since=${sinceTime.toISOString()}`;
      
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        console.warn('⚠️ Incremental update failed, falling back to full refresh');
        // Fallback to full refresh on error
        return fetchData(showLoading, true);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        console.warn('⚠️ Incremental update error, falling back to full refresh');
        return fetchData(showLoading, true);
      }
      
      const updates = result.data;
      
      
      if (updates.length > 500) {
        // Too many updates - more efficient to do full refresh
        console.warn('⚠️ Large update detected (>500 events), switching to full refresh');
        return fetchData(showLoading, true);
      }
      
      if (updates.length > 0) {
        // Merge new/updated events with existing data
        // Create a map for quick lookup by ptsEventID
        const existingMap = new Map(allData.map(e => [e.ptsEventID, e]));
        
        // Add or update events
        updates.forEach(event => {
          existingMap.set(event.ptsEventID, event);
        });
        
        // Convert back to array
        const mergedData = Array.from(existingMap.values());
        
        // Sort by ReceivedDateTime (newest first)
        mergedData.sort((a, b) => 
          new Date(b.ReceivedDateTime) - new Date(a.ReceivedDateTime)
        );
        
        
        const deduplicated = deduplicateEvents(mergedData);
        const filtered = applyPolygonFilterToData(deduplicated);
        setAllData(mergedData);
        setFilteredData(filtered);
        
      } else {
      }
      
      // Update last fetch time
      setLastFetchTime(now);
    }
    
    // ✅ Fetch KPI statistics from server (much faster than client-side calculation)
    // Uses same filters as data fetch
    // Skip when a polygon filter is active — server KPIs don't know about polygon geometry,
    // so we rely on client-side clientKpiData (computed from polygon-filtered filteredData).
    const queryString = USE_SERVER_SIDE_FILTERING ? buildQueryString(filters) : '';
    if (polygonFilterMode === 'none') {
      const serverKpiData = await fetchKPIStats(queryString);
      if (serverKpiData) {
        // Use server-calculated KPIs
        setKpiData(serverKpiData);
      } else {
        // Fallback to client-side calculation
        setKpiData(null); // Will trigger client-side calculation in render
      }
    } else {
      // Polygon active — force client-side KPI calculation from filtered data
      setKpiData(null);
    }
    
    if (showLoading) {
      setLoading(false);
    } else {
      setIsRefreshing(false);
    }
    setLastUpdated(new Date());
    
  } catch (error) {
    console.error('❌ Error fetching data:', error);
    if (showLoading) {
      setLoading(false);
      alert('Failed to load data.\n\nCheck:\n1. Backend running? (npm start in cad-backend folder)\n2. Try http://localhost:5050/api/cad-events in browser');
    } else {
      setIsRefreshing(false);
      // Silent fail on auto-refresh - just log to console
      console.warn('Auto-refresh failed - will retry in 30 seconds');
    }
  }
};

  // ✅ Keep fetchDataRef current on every render so auto-refresh never has a stale closure
  useEffect(() => {
    fetchDataRef.current = fetchData;
  });

  const applyDatePreset = (preset) => {
    const now = new Date();
    let dateFrom = new Date();
    let dateTo = new Date();

    switch(preset) {
      case 'today':
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        dateFrom.setDate(now.getDate() - 1);
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setDate(now.getDate() - 1);
        dateTo.setHours(23, 59, 59, 999);
        break;
      case 'this_week':
        const dayOfWeek = now.getDay();
        dateFrom.setDate(now.getDate() - dayOfWeek);
        dateFrom.setHours(0, 0, 0, 0);
        break;
      case 'last_week':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        dateFrom = lastWeekStart;
        dateTo = lastWeekEnd;
        break;
      case 'this_month':
        dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        dateTo = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'last_7_days':
        dateFrom.setDate(now.getDate() - 7);
        break;
      case 'last_30_days':
        dateFrom.setDate(now.getDate() - 30);
        break;
      case 'last_90_days':
        dateFrom.setDate(now.getDate() - 90);
        break;
      default:
        return;
    }

    const formattedFrom = formatDateTimeLocal(dateFrom);
    const formattedTo = formatDateTimeLocal(dateTo);

    setFilters(prev => ({
      ...prev,
      dateFrom: formattedFrom,
      dateTo: formattedTo,
      datePreset: preset
    }));
  };

  // Helper to format date for datetime-local input (local time, not UTC)
  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Parse database datetime strings as local time (not UTC)
  // SQL Server sends datetimes that may have Z suffix, but they're actually local time
  // parseLocalDateTime moved to module level

  // Calculate the display value for Date From field
  const getDateFromDisplay = () => {
    if (filters.lastHours && !isNaN(filters.lastHours) && filters.lastHours > 0) {
      const now = new Date();
      const hoursAgo = new Date(now.getTime() - (filters.lastHours * 60 * 60 * 1000));
      return formatDateTimeLocal(hoursAgo);
    }
    return filters.dateFrom;
  };

  // Calculate the display value for Date To field
  const getDateToDisplay = () => {
    if (filters.lastHours && !isNaN(filters.lastHours) && filters.lastHours > 0) {
      const now = new Date();
      return formatDateTimeLocal(now);
    }
    return filters.dateTo;
  };

  // Format datetime-local value for readable display
  const formatDateTimeReadable = (datetimeLocal) => {
    if (!datetimeLocal) return '';
    try {
      const date = new Date(datetimeLocal);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return datetimeLocal;
    }
  };

  const applyFilters = useCallback(() => {
    // ✅ If using server-side filtering, re-fetch with new filters
    if (USE_SERVER_SIDE_FILTERING) {
      fetchData(false); // false = silent refresh, no loading state
      return;
    }
    
    // ✅ Client-side filtering (original logic)
    let filtered = [...allData];

    // Calculate dynamic dateFrom and dateTo
    // Priority: 1. Cumulative Window, 2. Snapshot Window, 3. Rolling Window, 4. Manual Dates
    let effectiveDateFrom = filters.dateFrom;
    let effectiveDateTo = filters.dateTo;
    
    // Cumulative Window takes highest precedence (when playing)
    if (currentCumulativeStart && currentCumulativeEnd) {
      effectiveDateFrom = formatDateTimeLocal(currentCumulativeStart);
      effectiveDateTo = formatDateTimeLocal(currentCumulativeEnd);
    }
    // Snapshot Window takes precedence over other date filters (when playing)
    else if (currentSnapshotStart && currentSnapshotEnd) {
      effectiveDateFrom = formatDateTimeLocal(currentSnapshotStart);
      effectiveDateTo = formatDateTimeLocal(currentSnapshotEnd);
    }
    // Rolling time window (if snapshot/cumulative not active)
    else if (filters.lastHours && !isNaN(filters.lastHours) && filters.lastHours > 0) {
      const now = new Date();
      const hoursAgo = new Date(now.getTime() - (filters.lastHours * 60 * 60 * 1000));
      effectiveDateFrom = formatDateTimeLocal(hoursAgo);
      effectiveDateTo = formatDateTimeLocal(now);
    }
    // Manual dates (default when no snapshot/cumulative/rolling window active)
    else if (effectiveDateFrom || effectiveDateTo) {
    }

    if (effectiveDateFrom) {
      filtered = filtered.filter(e => 
        parseLocalDateTime(e.ReceivedDateTime) >= new Date(effectiveDateFrom)
      );
    }
    if (effectiveDateTo) {
      filtered = filtered.filter(e => 
        parseLocalDateTime(e.ReceivedDateTime) <= new Date(effectiveDateTo)
      );
    }

    if (!filters.callTypes.includes('all') && filters.callTypes.length > 0) {
      filtered = filtered.filter(e => filters.callTypes.includes(e.EventType));
    }

    // Priority filter
    if (!filters.priorities.includes('all') && filters.priorities.length > 0) {
      filtered = filtered.filter(e => filters.priorities.includes(e.EventPriority));
    }

    if (!filters.statuses.includes('all') && filters.statuses.length > 0) {
      filtered = filtered.filter(e => filters.statuses.includes(e.Status));
    }

    // Agency filter
    if (!filters.agencies.includes('all') && filters.agencies.length > 0) {
      filtered = filtered.filter(e => filters.agencies.includes(e.Agency));
    }

    // AgencyType filter
    if (!filters.agencyTypes.includes('all') && filters.agencyTypes.length > 0) {
      filtered = filtered.filter(e => filters.agencyTypes.includes(e.AgencyType));
    }

    if (filters.activeOnly) {
      filtered = filtered.filter(e => {
        const status = e.Status?.toLowerCase() || '';
        return status !== 'completed' && 
               status !== 'closed' && 
               status !== 'complete';
      });
    }

    // ✅ Polygon filter (inside or outside) - apply to filtered data
    filtered = applyPolygonFilterToData(filtered);

    // ✅ Deduplicate by ptsEventID before displaying
    const deduplicated = deduplicateEvents(filtered);
    
    setFilteredData(deduplicated);
  }, [filters, allData, polygonFilterMode, polygonCoords, currentSnapshotStart, currentSnapshotEnd, currentCumulativeStart, currentCumulativeEnd]);

  // Re-apply dedup + polygon filter when the user draws/changes a polygon.
  // NOTE: Data refreshes (rolling window, filter changes, auto-refresh) now set filteredData
  // DIRECTLY inside fetchData, so this useEffect only needs to handle polygon state changes.
  // It still has allData as a dep so it stays correct if something else sets allData.
  useEffect(() => {
    
    if (allData.length === 0) {
      return;
    }
    
    if (USE_SERVER_SIDE_FILTERING) {
      // For server-side filtering, just re-apply polygon filter to existing data
      // (No need to re-fetch from server)
      let deduplicated = deduplicateEvents(allData);
      deduplicated = applyPolygonFilterToData(deduplicated);
      setFilteredData(deduplicated);
      // ✅ When a polygon filter is active, clear server KPI data so clientKpiData
      // (computed from the polygon-filtered filteredData) is used for KPI cards.
      // Server KPI data is unaware of polygon geometry. When the filter is cleared,
      // the next auto-refresh or filter change will restore fast server KPIs.
      if (polygonFilterMode !== 'none') {
        setKpiData(null);
      }
    } else {
      // For client-side filtering, re-apply all filters
      applyFilters();
    }
  }, [polygonFilterMode, polygonCoords, allData]); // ✅ Added allData dependency for auto-refresh support

  // Snapshot Window Control Functions
  const startSnapshotPlayback = () => {
    if (!filters.dateFrom || !filters.dateTo) {
      alert('Please set both From and To dates first');
      return;
    }
    
    const windowMinutes = parseInt(snapshotWindowMinutes);
    if (!windowMinutes || windowMinutes <= 0) {
      alert('Please enter a valid snapshot window (minutes)');
      return;
    }

    // Cancel cumulative window if active
    if (isCumulativePlaying || isCumulativePaused || currentCumulativeStart) {
      stopCumulativePlayback();
      setCurrentCumulativeStart(null);
      setCurrentCumulativeEnd(null);
    }

    // Clear rolling window if active
    if (filters.lastHours) {
      setFilters(prev => ({ ...prev, lastHours: '' }));
    }

    // Initialize snapshot to start at From date
    const startDate = new Date(filters.dateFrom);
    const endDate = new Date(startDate.getTime() + (windowMinutes * 60 * 1000));
    
    setCurrentSnapshotStart(startDate);
    setCurrentSnapshotEnd(endDate);
    setSnapshotRefreshCount(0); // Reset counter
    setIsSnapshotPlaying(true);
    setIsSnapshotPaused(false);
  };

  const stopSnapshotPlayback = () => {
    setIsSnapshotPlaying(false);
    setIsSnapshotPaused(false);
    if (snapshotIntervalRef.current) {
      clearInterval(snapshotIntervalRef.current);
      snapshotIntervalRef.current = null;
    }
  };

  const resetSnapshotPlayback = () => {
    stopSnapshotPlayback();
    setCurrentSnapshotStart(null);
    setCurrentSnapshotEnd(null);
  };

  const cancelSnapshotPlayback = () => {
    // Stop playback
    stopSnapshotPlayback();
    // Clear current snapshot state
    setCurrentSnapshotStart(null);
    setCurrentSnapshotEnd(null);
    // Clear input values
    setSnapshotWindowMinutes('');
    setPlaybackSpeed('');
  };

  const advanceSnapshot = () => {
    if (!currentSnapshotStart || !filters.dateTo) return;
    
    const windowMinutes = parseInt(snapshotWindowMinutes);
    if (!windowMinutes) return;

    const newStart = new Date(currentSnapshotStart.getTime() + (windowMinutes * 60 * 1000));
    const newEnd = new Date(newStart.getTime() + (windowMinutes * 60 * 1000));
    const toDate = new Date(filters.dateTo);

    // Stop if we've reached the end
    if (newStart >= toDate) {
      stopSnapshotPlayback();
      return;
    }

    setCurrentSnapshotStart(newStart);
    setCurrentSnapshotEnd(newEnd);
    setSnapshotRefreshCount(prev => prev + 1); // Force refresh indicator
  };

  const rewindSnapshot = () => {
    if (!currentSnapshotStart || !filters.dateFrom) return;
    
    const windowMinutes = parseInt(snapshotWindowMinutes);
    if (!windowMinutes) return;

    const newStart = new Date(currentSnapshotStart.getTime() - (windowMinutes * 60 * 1000));
    const newEnd = new Date(newStart.getTime() + (windowMinutes * 60 * 1000));
    const fromDate = new Date(filters.dateFrom);

    // Don't go before the start date
    if (newStart < fromDate) {
      setCurrentSnapshotStart(fromDate);
      setCurrentSnapshotEnd(new Date(fromDate.getTime() + (windowMinutes * 60 * 1000)));
      setSnapshotRefreshCount(prev => prev + 1); // Force refresh
      return;
    }

    setCurrentSnapshotStart(newStart);
    setCurrentSnapshotEnd(newEnd);
    setSnapshotRefreshCount(prev => prev + 1); // Force refresh
  };

  const forwardSnapshot = () => {
    advanceSnapshot();
  };

  const pauseSnapshotPlayback = () => {
    setIsSnapshotPlaying(false);
    setIsSnapshotPaused(true);
    if (snapshotIntervalRef.current) {
      clearInterval(snapshotIntervalRef.current);
      snapshotIntervalRef.current = null;
    }
  };

  const resumeSnapshotPlayback = () => {
    setIsSnapshotPlaying(true);
    setIsSnapshotPaused(false);
  };

  // Auto-advance snapshot playback
  useEffect(() => {
    if (isSnapshotPlaying) {
      const speed = parseInt(playbackSpeed);
      if (speed && speed > 0) {
        snapshotIntervalRef.current = setInterval(() => {
          advanceSnapshot();
        }, speed * 1000);
      }
    } else {
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
        snapshotIntervalRef.current = null;
      }
    }

    return () => {
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
        snapshotIntervalRef.current = null;
      }
    };
  }, [isSnapshotPlaying, playbackSpeed, currentSnapshotStart]);

  // Cumulative Window Control Functions
  const startCumulativePlayback = () => {
    if (!filters.dateFrom || !filters.dateTo) {
      alert('Please set both From and To dates first');
      return;
    }
    
    const windowMinutes = parseInt(cumulativeWindowMinutes);
    if (!windowMinutes || windowMinutes <= 0) {
      alert('Please enter a valid cumulative window (minutes)');
      return;
    }

    // Cancel snapshot window if active
    if (isSnapshotPlaying || isSnapshotPaused || currentSnapshotStart) {
      stopSnapshotPlayback();
      setCurrentSnapshotStart(null);
      setCurrentSnapshotEnd(null);
    }

    // Clear rolling window if active
    if (filters.lastHours) {
      setFilters(prev => ({ ...prev, lastHours: '' }));
    }

    // Initialize cumulative window - start stays at From date, end advances
    const startDate = new Date(filters.dateFrom);
    const endDate = new Date(startDate.getTime() + (windowMinutes * 60 * 1000));
    
    setCurrentCumulativeStart(startDate); // This stays fixed
    setCurrentCumulativeEnd(endDate);
    setIsCumulativePlaying(true);
    setIsCumulativePaused(false);
  };

  const stopCumulativePlayback = () => {
    setIsCumulativePlaying(false);
    setIsCumulativePaused(false);
    if (cumulativeIntervalRef.current) {
      clearInterval(cumulativeIntervalRef.current);
      cumulativeIntervalRef.current = null;
    }
  };

  const resetCumulativePlayback = () => {
    stopCumulativePlayback();
    setCurrentCumulativeStart(null);
    setCurrentCumulativeEnd(null);
  };

  const cancelCumulativePlayback = () => {
    // Stop playback
    stopCumulativePlayback();
    // Clear current cumulative state
    setCurrentCumulativeStart(null);
    setCurrentCumulativeEnd(null);
    // Clear input values
    setCumulativeWindowMinutes('');
    setCumulativePlaybackSpeed('');
  };

  const advanceCumulative = () => {
    if (!currentCumulativeStart || !filters.dateTo) return;
    
    const windowMinutes = parseInt(cumulativeWindowMinutes);
    if (!windowMinutes) return;

    // Start stays the same, only end advances
    const newEnd = new Date(currentCumulativeEnd.getTime() + (windowMinutes * 60 * 1000));
    const toDate = new Date(filters.dateTo);

    // Stop if we've reached the end
    if (newEnd >= toDate) {
      setCurrentCumulativeEnd(toDate);
      stopCumulativePlayback();
      return;
    }

    // Start stays fixed, only end moves forward
    setCurrentCumulativeEnd(newEnd);
  };

  const rewindCumulative = () => {
    if (!currentCumulativeStart || !currentCumulativeEnd) return;
    
    const windowMinutes = parseInt(cumulativeWindowMinutes);
    if (!windowMinutes) return;

    const newEnd = new Date(currentCumulativeEnd.getTime() - (windowMinutes * 60 * 1000));

    // Don't go before start + window
    const minEnd = new Date(currentCumulativeStart.getTime() + (windowMinutes * 60 * 1000));
    if (newEnd < minEnd) {
      setCurrentCumulativeEnd(minEnd);
      return;
    }

    // Start stays fixed, only end moves backward
    setCurrentCumulativeEnd(newEnd);
  };

  const forwardCumulative = () => {
    advanceCumulative();
  };

  const pauseCumulativePlayback = () => {
    setIsCumulativePlaying(false);
    setIsCumulativePaused(true);
    if (cumulativeIntervalRef.current) {
      clearInterval(cumulativeIntervalRef.current);
      cumulativeIntervalRef.current = null;
    }
  };

  const resumeCumulativePlayback = () => {
    setIsCumulativePlaying(true);
    setIsCumulativePaused(false);
  };

  // Auto-advance cumulative playback
  useEffect(() => {
    if (isCumulativePlaying) {
      const speed = parseInt(cumulativePlaybackSpeed);
      if (speed && speed > 0) {
        cumulativeIntervalRef.current = setInterval(() => {
          advanceCumulative();
        }, speed * 1000);
      }
    } else {
      if (cumulativeIntervalRef.current) {
        clearInterval(cumulativeIntervalRef.current);
        cumulativeIntervalRef.current = null;
      }
    }

    return () => {
      if (cumulativeIntervalRef.current) {
        clearInterval(cumulativeIntervalRef.current);
        cumulativeIntervalRef.current = null;
      }
    };
  }, [isCumulativePlaying, cumulativePlaybackSpeed, currentCumulativeEnd]);

  // calculateResponseTime moved to module level

  const calculatePendingTime = (event) => {
    // Exclude if either time is null
    if (!event.DispatchedDateTime || !event.ReceivedDateTime) return null;
    
    const dispatchedTime = parseLocalDateTime(event.DispatchedDateTime);
    const receivedTime = parseLocalDateTime(event.ReceivedDateTime);
    
    // Exclude if dispatched time is before received time
    if (dispatchedTime < receivedTime) return null;
    
    const diff = dispatchedTime - receivedTime;
    return diff / (1000 * 60);
  };

  const calculateOnSceneTime = (event) => {
    // Exclude if either time is null
    if (!event.CompletedDateTime || !event.ArrivedDateTime) return null;
    
    const completedTime = parseLocalDateTime(event.CompletedDateTime);
    const arrivedTime = parseLocalDateTime(event.ArrivedDateTime);
    
    // Exclude if completed time is before arrived time
    if (completedTime < arrivedTime) return null;
    
    const diff = completedTime - arrivedTime;
    return diff / (1000 * 60);
  };

  // Calculate Turnout Time (Dispatched → Enroute): Time for crew to gear up and get in vehicle
  const calculateResponseDelay = (event) => {
    // Exclude if either time is null
    if (!event.EnrouteDateTime || !event.DispatchedDateTime) return null;
    
    const enrouteTime = parseLocalDateTime(event.EnrouteDateTime);
    const dispatchedTime = parseLocalDateTime(event.DispatchedDateTime);
    
    // Exclude if enroute time is before dispatched time
    if (enrouteTime < dispatchedTime) return null;
    
    const diff = enrouteTime - dispatchedTime;
    return diff / (1000 * 60);
  };

  const calculateTravelTime = (event) => {
    // Exclude if either time is null
    if (!event.ArrivedDateTime || !event.EnrouteDateTime) return null;
    
    const arrivedTime = parseLocalDateTime(event.ArrivedDateTime);
    const enrouteTime = parseLocalDateTime(event.EnrouteDateTime);
    
    // Exclude if arrived time is before enroute time
    if (arrivedTime < enrouteTime) return null;
    
    const diff = arrivedTime - enrouteTime;
    return diff / (1000 * 60);
  };

  const calculateEventTotalTime = (event) => {
    // Exclude if either time is null
    if (!event.CompletedDateTime || !event.ReceivedDateTime) return null;
    
    const completedTime = parseLocalDateTime(event.CompletedDateTime);
    const receivedTime = parseLocalDateTime(event.ReceivedDateTime);
    
    // Exclude if completed time is before received time
    if (completedTime < receivedTime) return null;
    
    const diff = completedTime - receivedTime;
    return diff / (1000 * 60);
  };

  const calculateEventCreationTime = (event) => {
    // Exclude if either time is null
    if (!event.ReceivedDateTime || !event.EventStartDateTime) return null;
    
    const receivedTime = parseLocalDateTime(event.ReceivedDateTime);
    const startTime = parseLocalDateTime(event.EventStartDateTime);
    
    // Exclude if received time is before start time
    if (receivedTime < startTime) return null;
    
    const diff = receivedTime - startTime;
    return diff / (1000 * 60);
  };

  const getKPIData = () => {
    // Helper function to check if status is closed/completed
    const isClosed = (status) => {
      const s = status?.toLowerCase() || '';
      return s === 'completed' || s === 'closed' || s === 'complete';
    };

    // Helper function to calculate percentiles
    const calculatePercentile = (arr, percentile) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = (percentile / 100) * (sorted.length - 1);
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      const weight = index % 1;
      
      if (lower === upper) {
        return sorted[lower];
      }
      return sorted[lower] * (1 - weight) + sorted[upper] * weight;
    };

    const pending = filteredData.filter(e => e.Status === 'Pending').length;
    const dispatched = filteredData.filter(e => e.Status === 'Dispatched').length;
    const enroute = filteredData.filter(e => e.Status === 'Enroute').length;
    const arrived = filteredData.filter(e => e.Status === 'On Scene' || e.Status === 'Arrived').length;
    const closed = filteredData.filter(e => isClosed(e.Status)).length;
    const active = filteredData.filter(e => !isClosed(e.Status)).length;
    
    const pendingTimes = filteredData.map(calculatePendingTime).filter(t => t !== null);
    const responseTimes = filteredData.map(calculateResponseTime).filter(t => t !== null);
    const onSceneTimes = filteredData.map(calculateOnSceneTime).filter(t => t !== null);
    const responseDelayTimes = filteredData.map(calculateResponseDelay).filter(t => t !== null);
    const travelTimes = filteredData.map(calculateTravelTime).filter(t => t !== null);
    const eventTotalTimes = filteredData.map(calculateEventTotalTime).filter(t => t !== null);
    const eventCreationTimes = filteredData.map(calculateEventCreationTime).filter(t => t !== null);
    
    const avgPending = pendingTimes.length > 0 
      ? pendingTimes.reduce((a, b) => a + b, 0) / pendingTimes.length 
      : 0;
    const avgResponse = responseTimes.length > 0 
      ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
      : 0;
    const avgOnScene = onSceneTimes.length > 0 
      ? onSceneTimes.reduce((a, b) => a + b, 0) / onSceneTimes.length 
      : 0;
    const avgResponseDelay = responseDelayTimes.length > 0 
      ? responseDelayTimes.reduce((a, b) => a + b, 0) / responseDelayTimes.length 
      : 0;
    const avgTravel = travelTimes.length > 0 
      ? travelTimes.reduce((a, b) => a + b, 0) / travelTimes.length 
      : 0;
    const avgEventTotal = eventTotalTimes.length > 0 
      ? eventTotalTimes.reduce((a, b) => a + b, 0) / eventTotalTimes.length 
      : 0;
    const avgEventCreation = eventCreationTimes.length > 0 
      ? eventCreationTimes.reduce((a, b) => a + b, 0) / eventCreationTimes.length 
      : 0;

    // Calculate response time percentiles
    const responseMin = responseTimes.length > 0 ? Math.min(...responseTimes) : 0;
    const responseMax = responseTimes.length > 0 ? Math.max(...responseTimes) : 0;
    const response25th = calculatePercentile(responseTimes, 25);
    const responseMedian = calculatePercentile(responseTimes, 50);
    const response75th = calculatePercentile(responseTimes, 75);
    const response90th = calculatePercentile(responseTimes, 90);
    const response95th = calculatePercentile(responseTimes, 95);

    // Calculate pending time percentiles
    const pendingMin = pendingTimes.length > 0 ? Math.min(...pendingTimes) : 0;
    const pendingMax = pendingTimes.length > 0 ? Math.max(...pendingTimes) : 0;
    const pending25th = calculatePercentile(pendingTimes, 25);
    const pendingMedian = calculatePercentile(pendingTimes, 50);
    const pending75th = calculatePercentile(pendingTimes, 75);
    const pending90th = calculatePercentile(pendingTimes, 90);
    const pending95th = calculatePercentile(pendingTimes, 95);

    // Calculate on scene time percentiles
    const onSceneMin = onSceneTimes.length > 0 ? Math.min(...onSceneTimes) : 0;
    const onSceneMax = onSceneTimes.length > 0 ? Math.max(...onSceneTimes) : 0;
    const onScene25th = calculatePercentile(onSceneTimes, 25);
    const onSceneMedian = calculatePercentile(onSceneTimes, 50);
    const onScene75th = calculatePercentile(onSceneTimes, 75);
    const onScene90th = calculatePercentile(onSceneTimes, 90);
    const onScene95th = calculatePercentile(onSceneTimes, 95);

    // Calculate turnout time percentiles (Dispatched → Enroute)
    const responseDelayMin = responseDelayTimes.length > 0 ? Math.min(...responseDelayTimes) : 0;
    const responseDelayMax = responseDelayTimes.length > 0 ? Math.max(...responseDelayTimes) : 0;
    const responseDelay25th = calculatePercentile(responseDelayTimes, 25);
    const responseDelayMedian = calculatePercentile(responseDelayTimes, 50);
    const responseDelay75th = calculatePercentile(responseDelayTimes, 75);
    const responseDelay90th = calculatePercentile(responseDelayTimes, 90);
    const responseDelay95th = calculatePercentile(responseDelayTimes, 95);

    // Calculate travel time percentiles
    const travelMin = travelTimes.length > 0 ? Math.min(...travelTimes) : 0;
    const travelMax = travelTimes.length > 0 ? Math.max(...travelTimes) : 0;
    const travel25th = calculatePercentile(travelTimes, 25);
    const travelMedian = calculatePercentile(travelTimes, 50);
    const travel75th = calculatePercentile(travelTimes, 75);
    const travel90th = calculatePercentile(travelTimes, 90);
    const travel95th = calculatePercentile(travelTimes, 95);

    // Calculate event total time percentiles
    const eventTotalMin = eventTotalTimes.length > 0 ? Math.min(...eventTotalTimes) : 0;
    const eventTotalMax = eventTotalTimes.length > 0 ? Math.max(...eventTotalTimes) : 0;
    const eventTotal25th = calculatePercentile(eventTotalTimes, 25);
    const eventTotalMedian = calculatePercentile(eventTotalTimes, 50);
    const eventTotal75th = calculatePercentile(eventTotalTimes, 75);
    const eventTotal90th = calculatePercentile(eventTotalTimes, 90);
    const eventTotal95th = calculatePercentile(eventTotalTimes, 95);

    // Calculate event creation time percentiles
    const eventCreationMin = eventCreationTimes.length > 0 ? Math.min(...eventCreationTimes) : 0;
    const eventCreationMax = eventCreationTimes.length > 0 ? Math.max(...eventCreationTimes) : 0;
    const eventCreation25th = calculatePercentile(eventCreationTimes, 25);
    const eventCreationMedian = calculatePercentile(eventCreationTimes, 50);
    const eventCreation75th = calculatePercentile(eventCreationTimes, 75);
    const eventCreation90th = calculatePercentile(eventCreationTimes, 90);
    const eventCreation95th = calculatePercentile(eventCreationTimes, 95);

    // Debug logging to verify KPI calculation

    return {
      pending, dispatched, enroute, arrived, closed, active,
      total: filteredData.length,
      avgPending, avgResponse, avgOnScene, avgResponseDelay, avgTravel, avgEventTotal, avgEventCreation,
      responseMin, response25th, responseMedian, response75th, response90th, response95th, responseMax,
      pendingMin, pending25th, pendingMedian, pending75th, pending90th, pending95th, pendingMax,
      onSceneMin, onScene25th, onSceneMedian, onScene75th, onScene90th, onScene95th, onSceneMax,
      responseDelayMin, responseDelay25th, responseDelayMedian, responseDelay75th, responseDelay90th, responseDelay95th, responseDelayMax,
      travelMin, travel25th, travelMedian, travel75th, travel90th, travel95th, travelMax,
      eventTotalMin, eventTotal25th, eventTotalMedian, eventTotal75th, eventTotal90th, eventTotal95th, eventTotalMax,
      eventCreationMin, eventCreation25th, eventCreationMedian, eventCreation75th, eventCreation90th, eventCreation95th, eventCreationMax
    };
  };

  // Helper function to get status badge colors
  // getStatusBadgeStyle moved to module level (now takes isDark param)

  // ── Section drag-to-reorder ─────────────────────────────────────────────
  // Dragging is only enabled when ALL sections are collapsed.
  const allSectionsCollapsed =
    !showFilters && isKpiCollapsed && isMapCollapsed &&
    isChartsCollapsed && !isDispatchAnalysisExpanded && !isRepeatedEventsExpanded && isRecentEventsCollapsed;

  const handleSectionDragStart = (e, sectionId) => {
    if (!allSectionsCollapsed) { e.preventDefault(); return; }
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionId);
  };

  const handleSectionDragOver = (e, sectionId) => {
    if (!allSectionsCollapsed || !draggedSection || draggedSection === sectionId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Determine if we should insert before or after based on mouse position
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const isAfter = e.clientY > midpoint;
    
    setDragOverSection(sectionId);
    setDragOverPosition(isAfter ? 'after' : 'before');
  };

  const handleSectionDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent any click events from firing
    if (!draggedSection || draggedSection === targetId) return;
    
    setSectionOrder(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(draggedSection);
      const toIdx = next.indexOf(targetId);
      
      // Remove from current position
      const [removed] = next.splice(fromIdx, 1);
      
      // Calculate new insertion index
      let newIdx = next.indexOf(targetId);
      
      // If dropping after, increment index
      if (dragOverPosition === 'after') {
        newIdx++;
      }
      
      // Insert at new position
      next.splice(newIdx, 0, removed);
      
      return next;
    });
    setDraggedSection(null);
    setDragOverSection(null);
    setDragOverPosition(null);
  };

  const handleSectionDragEnd = () => {
    setDraggedSection(null);
    setDragOverSection(null);
    setDragOverPosition(null);
  };

  // Spread onto each section's outer wrapper div
  const sectionDragProps = (sectionId) => ({
    draggable: allSectionsCollapsed,
    onDragStart: (e) => handleSectionDragStart(e, sectionId),
    onDragOver:  (e) => handleSectionDragOver(e, sectionId),
    onDrop:      (e) => handleSectionDrop(e, sectionId),
    onDragEnd:   handleSectionDragEnd,
  });

  // Extra inline styles: CSS flex `order` + drop-target highlight + drag opacity
  const sectionOrderStyle = (sectionId) => {
    const isDropTarget = dragOverSection === sectionId && draggedSection !== sectionId;
    
    return {
      order: sectionOrder.indexOf(sectionId),
      borderTop: isDropTarget && dragOverPosition === 'before' 
        ? `4px solid ${theme.accentColor}` 
        : 'none',
      borderBottom: isDropTarget && dragOverPosition === 'after' 
        ? `4px solid ${theme.accentColor}` 
        : 'none',
      borderRadius: '12px',
      opacity: draggedSection === sectionId ? 0.5 : 1,
      cursor: allSectionsCollapsed ? 'grab' : 'default',
      transition: 'opacity 0.15s ease, border 0.1s ease',
      marginTop: isDropTarget && dragOverPosition === 'before' ? '8px' : '0',
      marginBottom: isDropTarget && dragOverPosition === 'after' ? '8px' : '0',
    };
  };

  // ──── Layer View and Center Helper Functions ────────────────────────────
  
  // Check if a polygon is within current map bounds
  const isPolygonInView = (polygon) => {
    // Force re-evaluation when map view changes
    const _ = mapViewChanged;
    
    if (!mapInstanceRef.current || !polygon || !polygon.coords) {
      return true;
    }
    
    try {
      const bounds = mapInstanceRef.current.getBounds();
      
      // Polygon coords are stored as arrays: [[lat, lng], [lat, lng], ...]
      const inView = polygon.coords.some(coord => {
        // Handle both array format [lat, lng] and object format {lat, lng}
        const lat = Array.isArray(coord) ? coord[0] : coord.lat;
        const lng = Array.isArray(coord) ? coord[1] : coord.lng;
        return bounds.contains([lat, lng]);
      });
      
      return inView;
    } catch (e) {
      console.error('Error checking polygon bounds:', e);
      return true; // If error, assume it's in view
    }
  };

  // Check if a custom layer is within current map bounds
  const isLayerInView = (layer) => {
    // Force re-evaluation when map view changes
    const _ = mapViewChanged;
    
    if (!mapInstanceRef.current || !layer || !layer.data) {
      return true;
    }
    
    try {
      const bounds = mapInstanceRef.current.getBounds();
      
      // Check if any point in the layer data is within bounds
      const inView = layer.data.some(point => 
        bounds.contains([point.lat, point.lng])
      );
      
      return inView;
    } catch (e) {
      console.error('Error checking layer bounds:', e);
      return true; // If error, assume it's in view
    }
  };

  // Center map on a polygon
  const centerMapOnPolygon = (polygon) => {
    if (!mapInstanceRef.current || !polygon || !polygon.coords || polygon.coords.length === 0) return;
    
    try {
      
      // Handle both array format [lat, lng] and object format {lat, lng}
      const latlngs = polygon.coords.map(coord => {
        if (Array.isArray(coord)) {
          return [coord[0], coord[1]]; // [lat, lng]
        } else {
          return [coord.lat, coord.lng]; // {lat, lng}
        }
      });
      
      // Create bounds that include all points
      const bounds = window.L.latLngBounds(latlngs);
      
      // Fit to bounds with padding
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } catch (e) {
      console.error('Error centering map on polygon:', e);
    }
  };

  // Center map on a custom layer
  const centerMapOnLayer = (layer) => {
    if (!mapInstanceRef.current || !layer || !layer.data || layer.data.length === 0) return;
    
    try {
      // Calculate center of layer
      const lats = layer.data.map(d => d.lat);
      const lngs = layer.data.map(d => d.lng);
      const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
      const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
      
      // Create bounds that include all points
      const bounds = window.L.latLngBounds(layer.data.map(d => [d.lat, d.lng]));
      
      // Fit to bounds with padding
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } catch (e) {
      console.error('Error centering map on layer:', e);
    }
  };
  // ────────────────────────────────────────────────────────────────────────
  // ────────────────────────────────────────────────────────────────────────

  const renderAllCharts = () => {
    if (!window.Chart) return;
    
    
    renderCallTypeChart();
    renderHourlyChart();
    renderTimelineChart();
    renderResponseTimeChart();
    renderDayOfWeekChart();
    renderMonthChart();
  };

  const renderCallTypeChart = () => {
    const canvas = callTypeChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.callType) {
      chartInstancesRef.current.callType.destroy();
    }

    const typeCounts = {};
    filteredData.forEach(e => {
      typeCounts[e.EventType] = (typeCounts[e.EventType] || 0) + 1;
    });

    // Sort labels alphabetically
    const sortedLabels = Object.keys(typeCounts).sort((a, b) => a.localeCompare(b));
    const sortedData = sortedLabels.map(label => typeCounts[label]);

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.callType = new window.Chart(ctx, {
      type: chartTypes.callType === 'pie' || chartTypes.callType === 'doughnut' ? chartTypes.callType : 'bar',
      data: {
        labels: sortedLabels,
        datasets: [{
          label: 'Event Types',
          data: sortedData,
          backgroundColor: theme.chartColors,
          borderColor: theme.chartBorderColor,
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,  // Allow chart to fill container when resized
        plugins: {
          legend: { 
            display: chartTypes.callType !== 'bar',
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
                const pct = total > 0 ? Math.round(ctx.parsed / (ctx.parsed?.y !== undefined ? ctx.parsed.y : ctx.parsed) / total * 10000) / 100 : 0;
                const val = ctx.parsed?.y !== undefined ? ctx.parsed.y : ctx.parsed;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  const renderHourlyChart = () => {
    const canvas = hourlyChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.hourly) {
      chartInstancesRef.current.hourly.destroy();
    }

    const hourlyCounts = new Array(24).fill(0);
    filteredData.forEach(e => {
      const date = parseLocalDateTime(e.ReceivedDateTime);
      if (date) {
        const hour = date.getHours();
        if (!isNaN(hour)) hourlyCounts[hour]++;
      }
    });

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.hourly = new window.Chart(ctx, {
      type: chartTypes.hourly,
      data: {
        labels: Array.from({length: 24}, (_, i) => `${i}:00`),
        datasets: [{
          label: 'Events by Hour',
          data: hourlyCounts,
          borderColor: theme.chartBorderColor,
          backgroundColor: chartTypes.hourly === 'bar' ? theme.chartColors[0] : `${theme.chartColors[0]}33`,
          fill: chartTypes.hourly === 'line',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,  // Allow chart to fill container when resized
        plugins: {
          legend: {
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const data = ctx.dataset.data;
                const total = data.reduce((s, v) => s + (v || 0), 0);
                const val = ctx.parsed.y;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  const renderTimelineChart = () => {
    const canvas = timelineChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.timeline) {
      chartInstancesRef.current.timeline.destroy();
    }

    const dateCounts = {};
    filteredData.forEach(e => {
      const dateObj = parseLocalDateTime(e.ReceivedDateTime);
      if (dateObj) {
        const date = dateObj.toLocaleDateString();
        dateCounts[date] = (dateCounts[date] || 0) + 1;
      }
    });

    const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
    const counts = sortedDates.map(date => dateCounts[date]);

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.timeline = new window.Chart(ctx, {
      type: chartTypes.timeline === 'area' ? 'line' : chartTypes.timeline,
      data: {
        labels: sortedDates,
        datasets: [{
          label: 'Events',
          data: counts,
          borderColor: theme.chartBorderColor,
          backgroundColor: chartTypes.timeline === 'area' ? `${theme.chartColors[0]}33` : 
                         chartTypes.timeline === 'bar' ? theme.chartColors[0] : 'transparent',
          fill: chartTypes.timeline === 'area',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,  // Allow chart to fill container when resized
        plugins: {
          legend: {
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const data = ctx.dataset.data;
                const total = data.reduce((s, v) => s + (v || 0), 0);
                const val = ctx.parsed.y;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  const renderResponseTimeChart = () => {
    const canvas = responseTimeChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.responseTime) {
      chartInstancesRef.current.responseTime.destroy();
    }

    const responseTimes = filteredData
      .map(calculateResponseTime)
      .filter(t => t !== null);

    const bins = [0, 5, 10, 15, 20, 30, 60];
    const binCounts = new Array(bins.length - 1).fill(0);
    
    responseTimes.forEach(time => {
      for (let i = 0; i < bins.length - 1; i++) {
        if (time >= bins[i] && time < bins[i + 1]) {
          binCounts[i]++;
          break;
        }
      }
    });

    const labels = bins.slice(0, -1).map((bin, i) => `${bin}-${bins[i + 1]}min`);

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.responseTime = new window.Chart(ctx, {
      type: chartTypes.responseTime,
      data: {
        labels,
        datasets: [{
          label: 'Response Time Distribution',
          data: binCounts,
          borderColor: theme.chartBorderColor,
          backgroundColor: chartTypes.responseTime === 'bar' ? theme.chartColors[0] : `${theme.chartColors[0]}33`,
          fill: chartTypes.responseTime === 'line',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,  // Allow chart to fill container when resized
        plugins: {
          legend: {
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const data = ctx.dataset.data;
                const total = data.reduce((s, v) => s + (v || 0), 0);
                const val = ctx.parsed.y;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  const renderDayOfWeekChart = () => {
    const canvas = dayOfWeekChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.dayOfWeek) {
      chartInstancesRef.current.dayOfWeek.destroy();
    }

    // Count events by day of week (0 = Sunday, 6 = Saturday)
    const dayCounts = new Array(7).fill(0);
    filteredData.forEach(e => {
      const date = parseLocalDateTime(e.ReceivedDateTime);
      if (date) {
        const dayOfWeek = date.getDay();
        if (!isNaN(dayOfWeek)) dayCounts[dayOfWeek]++;
      }
    });

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.dayOfWeek = new window.Chart(ctx, {
      type: chartTypes.dayOfWeek,
      data: {
        labels: dayNames,
        datasets: [{
          label: 'Events by Day of Week',
          data: dayCounts,
          borderColor: theme.chartBorderColor,
          backgroundColor: chartTypes.dayOfWeek === 'bar' ? theme.chartColors[0] : `${theme.chartColors[0]}33`,
          fill: chartTypes.dayOfWeek === 'line',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const data = ctx.dataset.data;
                const total = data.reduce((s, v) => s + (v || 0), 0);
                const val = ctx.parsed.y;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  const renderMonthChart = () => {
    const canvas = monthChartRef.current;
    if (!canvas || !window.Chart) return;

    if (chartInstancesRef.current.month) {
      chartInstancesRef.current.month.destroy();
    }

    // Count events by month
    const monthCounts = new Array(12).fill(0);
    filteredData.forEach(e => {
      const date = parseLocalDateTime(e.ReceivedDateTime);
      if (date) {
        const month = date.getMonth();
        if (!isNaN(month)) monthCounts[month]++;
      }
    });

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];

    const ctx = canvas.getContext('2d');
    chartInstancesRef.current.month = new window.Chart(ctx, {
      type: chartTypes.month,
      data: {
        labels: monthNames,
        datasets: [{
          label: 'Events by Month',
          data: monthCounts,
          borderColor: theme.chartBorderColor,
          backgroundColor: chartTypes.month === 'bar' ? theme.chartColors[0] : `${theme.chartColors[0]}33`,
          fill: chartTypes.month === 'line',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: theme.textPrimary
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const data = ctx.dataset.data;
                const total = data.reduce((s, v) => s + (v || 0), 0);
                const val = ctx.parsed.y;
                const p = total > 0 ? Math.round(val / total * 100) : 0;
                return `${ctx.dataset.label}: ${val.toLocaleString()} (${p}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          },
          y: {
            beginAtZero: true,
            ticks: { color: theme.textPrimary },
            grid: { color: theme.chartGridColor }
          }
        }
      }
    });
  };

  // Calculate dispatch time performance metrics
  const calculateDispatchPerformance = () => {
    const dispatchTimes = [];
    let metTarget = 0;
    let total = 0;
    
    filteredData.forEach(event => {
      const fromTime = parseLocalDateTime(event[timeFrom]);
      const toTime = parseLocalDateTime(event[timeTo]);
      
      if (fromTime && toTime) {
        const diffSeconds = (toTime - fromTime) / 1000;
        // Only include positive time differences
        if (diffSeconds >= 0) {
          dispatchTimes.push(diffSeconds);
          total++;
          if (diffSeconds <= dispatchTargetSeconds) {
            metTarget++;
          }
        }
      }
    });
    
    // Calculate percentiles
    const sortedTimes = [...dispatchTimes].sort((a, b) => a - b);
    const median = sortedTimes.length > 0 
      ? sortedTimes[Math.floor(sortedTimes.length / 2)] 
      : 0;
    const p75 = sortedTimes.length > 0 
      ? sortedTimes[Math.floor(sortedTimes.length * 0.75)] 
      : 0;
    const p90 = sortedTimes.length > 0 
      ? sortedTimes[Math.floor(sortedTimes.length * 0.90)] 
      : 0;
    const p95 = sortedTimes.length > 0 
      ? sortedTimes[Math.floor(sortedTimes.length * 0.95)] 
      : 0;
    
    const percentage = total > 0 ? (metTarget / total) * 100 : 0;
    
    return {
      percentage: percentage.toFixed(1),
      metTarget,
      total,
      overTarget: total - metTarget,
      median,
      p75,
      p90,
      p95,
      dispatchTimes
    };
  };

  // formatDateTime moved to module level

  const formatTime = (minutes) => {
    if (!minutes || minutes === 0) return '-';
    if (minutes < 60) return `${minutes.toFixed(1)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // handleSort, getSortedData, getPaginatedData, totalPages, reset effect moved to EventList

  // ✅ Use server KPI data if available, otherwise calculate client-side
  // Memoized so it only recalculates when filteredData actually changes.
  const clientKpiData = useMemo(() => isKpiCollapsed ? null : getKPIData(), [filteredData, isKpiCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps
  const currentKpiData = kpiData || clientKpiData;
  // ── Chart header stats — memoized per filteredData change ──────────────────
  const chartHeaderStats = useMemo(() => {
    if (isChartsCollapsed) return {};
    const total = filteredData.length;

    // Top Event Types
    const tc = {};
    filteredData.forEach(e => { if (e.EventType) tc[e.EventType] = (tc[e.EventType] || 0) + 1; });
    const topType = Object.entries(tc).sort((a, b) => b[1] - a[1])[0];

    // Hourly
    const hc = new Array(24).fill(0);
    filteredData.forEach(e => { const d = parseLocalDateTime(e.ReceivedDateTime); if (d) hc[d.getHours()]++; });
    const hourlyTotal = hc.reduce((s, v) => s + v, 0);
    const peakHour = hc.indexOf(Math.max(...hc));

    // Timeline
    const dc = {};
    filteredData.forEach(e => { const d = parseLocalDateTime(e.ReceivedDateTime); if (d) { const k = d.toLocaleDateString(); dc[k] = (dc[k] || 0) + 1; } });
    const days = Object.keys(dc).length;
    const peakDay = Object.entries(dc).sort((a, b) => b[1] - a[1])[0];

    // Response time
    const rt = filteredData.map(calculateResponseTime).filter(t => t != null);
    const rtTotal = rt.length;
    const rtAvg = rtTotal > 0 ? (rt.reduce((s, v) => s + v, 0) / rtTotal).toFixed(1) : null;
    const bins = [0, 5, 10, 15, 20, 30, 60];
    const bc = new Array(bins.length - 1).fill(0);
    rt.forEach(t => { for (let i = 0; i < bins.length - 1; i++) { if (t >= bins[i] && t < bins[i + 1]) { bc[i]++; break; } } });
    const peakBin = bc.indexOf(Math.max(...bc));

    // Day of week
    const wc = new Array(7).fill(0);
    filteredData.forEach(e => { const d = parseLocalDateTime(e.ReceivedDateTime); if (d) wc[d.getDay()]++; });
    const wTotal = wc.reduce((s, v) => s + v, 0);
    const peakDay7 = wc.indexOf(Math.max(...wc));
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Month
    const mc = new Array(12).fill(0);
    filteredData.forEach(e => { const d = parseLocalDateTime(e.ReceivedDateTime); if (d) mc[d.getMonth()]++; });
    const mTotal = mc.reduce((s, v) => s + v, 0);
    const peakMonth = mc.indexOf(Math.max(...mc));
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Mappable events (have valid coordinates)
    const mappable = filteredData.filter(e => {
      if (!e.LatitudeDegree || !e.LongitudeDegree) return false;
      const lat = parseFloat(e.LatitudeDegree);
      const lng = parseFloat(e.LongitudeDegree);
      return !isNaN(lat) && !isNaN(lng);
    }).length;

    return {
      mappableCount: mappable,
      unmappableCount: total - mappable,
      callType: {
        total,
        subtitle: topType && total > 0
          ? `${total.toLocaleString()} events · Top: ${topType[0]} (${Math.round(topType[1] / total * 100)}%)`
          : `${total.toLocaleString()} events`
      },
      hourly: {
        total: hourlyTotal,
        subtitle: hourlyTotal > 0
          ? `${hourlyTotal.toLocaleString()} events · Peak: ${peakHour}:00–${peakHour + 1}:00 (${Math.round(hc[peakHour] / hourlyTotal * 100)}%)`
          : `${hourlyTotal.toLocaleString()} events`
      },
      timeline: {
        total,
        subtitle: total > 0
          ? `${total.toLocaleString()} events across ${days} day${days !== 1 ? 's' : ''}${peakDay ? ` · Busiest: ${peakDay[0]} (${Math.round(peakDay[1] / total * 100)}%)` : ''}`
          : `${total.toLocaleString()} events`
      },
      responseTime: {
        total: rtTotal,
        subtitle: rtTotal > 0
          ? `${rtTotal.toLocaleString()} timed events${rtAvg ? ` · Avg: ${rtAvg} min` : ''} · Peak: ${bins[peakBin]}–${bins[peakBin + 1]} min (${Math.round(bc[peakBin] / rtTotal * 100)}%)`
          : `${rtTotal.toLocaleString()} timed events`
      },
      dayOfWeek: {
        total: wTotal,
        subtitle: wTotal > 0
          ? `${wTotal.toLocaleString()} events · Busiest: ${dayNames[peakDay7]} (${Math.round(wc[peakDay7] / wTotal * 100)}%)`
          : `${wTotal.toLocaleString()} events`
      },
      month: {
        total: mTotal,
        subtitle: mTotal > 0
          ? `${mTotal.toLocaleString()} events · Busiest: ${monthNames[peakMonth]} (${Math.round(mc[peakMonth] / mTotal * 100)}%)`
          : `${mTotal.toLocaleString()} events`
      },
    };
  }, [filteredData, isChartsCollapsed]);

  // ── Repeated Events by Location — memoized ────────────────────────────────
  const repeatedLocationData = useMemo(() => {
    if (!isRepeatedEventsExpanded) return [];
    const locationEvents = {};
    filteredData.forEach(event => {
      const location = event.Address || 'Unknown Location';
      if (!locationEvents[location]) {
        locationEvents[location] = { address: location, count: 0, events: [] };
      }
      locationEvents[location].count++;
      locationEvents[location].events.push(event);
    });
    return Object.values(locationEvents);
  }, [filteredData, isRepeatedEventsExpanded]);


  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.bgPrimary,
        color: 'white',
        fontSize: '1.2em'
      }}>
        Loading CAD Analytics...
      </div>
    );
  }

  // medianResponseTime moved to EventList component

  // SectionResizeHandle is now a module-level component.
  // Use it in JSX as: <SectionResizeHandle sectionId="x" resizingSection={resizingSection} theme={theme} onResizeStart={handleSectionResizeStart} onReset={resetSectionHeight} />

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bgPrimary,
      padding: isTablet ? '8px' : '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      transition: 'background 0.3s ease'
    }}>
      {/* Pulse animation for refresh indicator */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.1); }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes activePolygonPulse {
            0%, 100% { 
              box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            50% { 
              box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.25), 0 4px 16px rgba(20, 184, 166, 0.15);
            }
          }

          /* Marker Cluster Styling */
          .marker-cluster-small {
            background-color: rgba(110, 204, 57, 0.6);
          }
          .marker-cluster-small div {
            background-color: rgba(110, 204, 57, 0.8);
          }

          .marker-cluster-medium {
            background-color: rgba(241, 211, 87, 0.6);
          }
          .marker-cluster-medium div {
            background-color: rgba(241, 211, 87, 0.8);
          }

          .marker-cluster-large {
            background-color: rgba(253, 156, 115, 0.6);
          }
          .marker-cluster-large div {
            background-color: rgba(253, 156, 115, 0.8);
          }

          .marker-cluster {
            background-clip: padding-box;
            border-radius: 20px;
          }
          .marker-cluster div {
            width: 30px;
            height: 30px;
            margin-left: 5px;
            margin-top: 5px;
            text-align: center;
            border-radius: 15px;
            font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
            font-weight: bold;
            line-height: 30px;
            color: #fff;
          }
          .marker-cluster span {
            line-height: 30px;
          }

          /* Multi-event popup styling */
          .multi-event-popup .leaflet-popup-content {
            margin: 0;
            padding: 0;
          }
          .multi-event-popup .leaflet-popup-content-wrapper {
            border-radius: 8px;
            padding: 0;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.35);
          }
          /* Dark theme popup */
          .dark-popup .leaflet-popup-content-wrapper {
            background: #1a202c;
            color: #f7fafc;
            border: 1px solid #4a5568;
          }
          .dark-popup .leaflet-popup-tip {
            background: #1a202c;
          }
          /* Light theme popup */
          .light-popup .leaflet-popup-content-wrapper {
            background: #ffffff;
            color: #1a202c;
            border: 1px solid #e2e8f0;
          }
          .light-popup .leaflet-popup-tip {
            background: #ffffff;
          }

          /* ── Tablet responsive classes (≤1024px) ─────────────────────── */

          /* Touch-friendly table horizontal scroll */
          .cad-table-scroll {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          /* Layer panel overlay on tablet — floats over map */
          .cad-layer-overlay {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            z-index: 500;
            box-shadow: 4px 0 20px rgba(0,0,0,0.25);
          }

          /* Event details modal fills screen on tablet */
          @media (max-width: 1024px) {
            .cad-modal-inner {
              width: 96vw !important;
              max-width: 96vw !important;
              max-height: 92vh !important;
              margin: 0 !important;
            }
            .cad-modal-body {
              flex-direction: column !important;
              overflow-y: auto !important;
            }
            .cad-modal-left, .cad-modal-right {
              width: 100% !important;
              min-width: 0 !important;
              overflow-y: visible !important;
            }
            .cad-modal-right {
              border-top: 1px solid rgba(128,128,128,0.2);
            }
            /* Header actions wrap on tablet */
            .cad-header-actions {
              flex-wrap: wrap;
              gap: 8px !important;
            }
            /* Hide secondary header text on small screens */
            .cad-header-refresh-detail {
              display: none !important;
            }
            /* Filter grid tighter */
            .cad-filter-grid {
              grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)) !important;
              gap: 10px !important;
            }
            /* KPI time metrics wrap */
            .cad-kpi-scroll {
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              padding-bottom: 4px;
            }
            /* Performance 4-card grid stays 2x2 on small tablet */
            .cad-perf-grid-4 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
        `}
      </style>
      
      <div style={{ maxWidth: isTablet ? '100%' : '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{
          background: theme.cardBg,
          borderRadius: isTablet ? '8px' : '12px',
          padding: isTablet ? '10px 14px' : '15px 20px',
          marginBottom: isTablet ? '10px' : '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isTablet ? 'flex-start' : 'center',
          flexWrap: isTablet ? 'wrap' : 'nowrap',
          gap: isTablet ? '8px' : '0',
          order: -1
        }}>
          <div>
            <h1 style={{
              color: theme.accentColor,
              fontSize: isTablet ? '1.3em' : '1.8em',
              margin: '0 0 5px 0'
            }}>
              CAD Metrics
            </h1>
            {lastUpdated && (
              <div style={{
                fontSize: '0.85em',
                color: theme.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <span
                  onClick={() => !isRefreshing && fetchData(false)}
                  title={isRefreshing ? 'Refreshing...' : 'Click to refresh data'}
                  style={{ 
                    opacity: isRefreshing ? 1 : 0.7,
                    transition: 'opacity 0.3s',
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    cursor: isRefreshing ? 'default' : 'pointer',
                  }}
                  onMouseEnter={e => { if (!isRefreshing) e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={e => { if (!isRefreshing) e.currentTarget.style.opacity = '0.7'; }}
                >🔄</span>
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                {isRefreshing && (
                  <span style={{
                    fontSize: '0.8em',
                    color: theme.accentColor,
                    fontWeight: '600'
                  }}>
                    (updating...)
                  </span>
                )}
                {!isRefreshing && autoRefreshEnabled && nextRefreshIn > 0 && (
                  <span style={{
                    fontSize: '0.75em',
                    color: theme.accentColor,
                    fontWeight: '500',
                    opacity: 0.8
                  }}>
                    • Next refresh in {nextRefreshIn}s
                  </span>
                )}
                {!autoRefreshEnabled && (
                  <span style={{
                    fontSize: '0.75em',
                    color: '#F59E0B',
                    fontWeight: '500'
                  }}>
                    • Auto-refresh disabled
                  </span>
                )}
              </div>
            )}
            {/* Rolling Window Status */}
            {filters.lastHours && filters.lastHours > 0 && (
              <div style={{
                fontSize: '0.85em',
                color: '#10B981',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                marginTop: '5px',
                fontWeight: '600'
              }}>
                <span>🕐</span>
                <span>
                  Rolling Window: Last {filters.lastHours} hours (active)
                </span>
              </div>
            )}
            {/* Snapshot Window Status */}
            {currentSnapshotStart && currentSnapshotEnd && (
              <div style={{
                fontSize: '0.85em',
                color: '#3B82F6',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                marginTop: '5px',
                fontWeight: '600'
              }}>
                <span style={{ 
                  animation: isSnapshotPlaying ? 'pulse 1s ease-in-out infinite' : 'none'
                }}>📸</span>
                <span>
                  Snapshot: {currentSnapshotStart.toLocaleString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                  })} - {currentSnapshotEnd.toLocaleString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                  })}
                </span>
                {isSnapshotPlaying && (
                  <span style={{ color: '#10B981' }}>
                    (playing)
                  </span>
                )}
              </div>
            )}
            {/* Cumulative Window Status */}
            {currentCumulativeStart && currentCumulativeEnd && (
              <div style={{
                fontSize: '0.85em',
                color: '#A855F7',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                marginTop: '5px',
                fontWeight: '600'
              }}>
                <span style={{ 
                  animation: isCumulativePlaying ? 'pulse 1s ease-in-out infinite' : 'none'
                }}>📈</span>
                <span>
                  Cumulative: {currentCumulativeStart.toLocaleString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                  })} - {currentCumulativeEnd.toLocaleString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                  })}
                </span>
                {isCumulativePlaying && (
                  <span style={{ color: '#10B981' }}>
                    (playing)
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="cad-header-actions" style={{ display: 'flex', gap: isTablet ? '8px' : '15px', alignItems: 'center', flexWrap: isTablet ? 'wrap' : 'nowrap' }}>
            {/* Live/Historical Toggle Slider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: theme.inputBg,
              border: `2px solid ${theme.inputBorder}`,
              padding: '8px 15px',
              borderRadius: '8px'
            }}>
              <span style={{
                fontSize: '0.9em',
                color: autoRefreshEnabled ? theme.accentColor : theme.textSecondary,
                fontWeight: autoRefreshEnabled ? '600' : '500',
                transition: 'all 0.3s ease'
              }}>
                Live
              </span>
              
              {/* Toggle Switch */}
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '50px',
                height: '24px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={!autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(!e.target.checked)}
                  style={{ display: 'none' }}
                />
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: autoRefreshEnabled ? '#10B981' : '#6B7280',
                  borderRadius: '24px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }} />
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: autoRefreshEnabled ? '3px' : '29px',
                  bottom: '3px',
                  background: 'white',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </label>
              
              <span style={{
                fontSize: '0.9em',
                color: !autoRefreshEnabled ? theme.accentColor : theme.textSecondary,
                fontWeight: !autoRefreshEnabled ? '600' : '500',
                transition: 'all 0.3s ease'
              }}>
                Historical
              </span>
            </div>
            
            <button
              onClick={() => setShowOptions(true)}
              style={{
                background: theme.inputBg,
                border: `2px solid ${theme.inputBorder}`,
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: theme.textPrimary,
                fontSize: '1em',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              ⚙️ Options
            </button>

            {/* Expand/Collapse All — compact split pill */}
            <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: `2px solid ${theme.inputBorder}` }}>
              <button
                onClick={() => {
                  setShowFilters(true);
                  setIsKpiCollapsed(false);
                  setIsMapCollapsed(false);
                  setIsChartsCollapsed(false);
                  setIsDispatchAnalysisExpanded(true);
                  setIsRepeatedEventsExpanded(true);
                  setIsRecentEventsCollapsed(false);
                }}
                title="Expand all sections"
                style={{
                  padding: '8px 12px',
                  background: theme.inputBg,
                  border: 'none',
                  borderRight: `1px solid ${theme.inputBorder}`,
                  cursor: 'pointer',
                  color: theme.textSecondary,
                  fontSize: '0.85em',
                  fontWeight: '700',
                  transition: 'all 0.2s',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.detailBg; e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = theme.inputBg; e.currentTarget.style.color = theme.textSecondary; }}
              >▼ All</button>
              <button
                onClick={() => {
                  setShowFilters(false);
                  setIsKpiCollapsed(true);
                  setIsMapCollapsed(true);
                  setIsChartsCollapsed(true);
                  setIsDispatchAnalysisExpanded(false);
                  setIsRepeatedEventsExpanded(false);
                  setIsRecentEventsCollapsed(true);
                }}
                title="Collapse all sections"
                style={{
                  padding: '8px 12px',
                  background: theme.inputBg,
                  border: 'none',
                  cursor: 'pointer',
                  color: theme.textSecondary,
                  fontSize: '0.85em',
                  fontWeight: '700',
                  transition: 'all 0.2s',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.detailBg; e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = theme.inputBg; e.currentTarget.style.color = theme.textSecondary; }}
              >▲ All</button>
            </div>
          </div>
        </div>

        {/* Drag-to-reorder hint — only shown when all sections are collapsed */}
        {allSectionsCollapsed && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px', order: -2 }}>
            <span style={{ fontSize: '0.72em', color: theme.textSecondary, userSelect: 'none' }}>
              ⠿ Drag sections to reorder
            </span>
          </div>
        )}

        {/* Filters section - keeping only the compact version for space */}
        <div
          {...sectionDragProps('filters')}
          style={{
            background: theme.cardBg,
            borderRadius: '12px',
            padding: '0',
            marginBottom: '20px',
            boxShadow: showFilters ? '0 4px 20px rgba(0,0,0,0.1)' : '0 6px 30px rgba(0,0,0,0.2)',
            position: (showFilters || allSectionsCollapsed) ? 'static' : 'sticky',
            top: (showFilters || allSectionsCollapsed) ? 'auto' : '0',
            zIndex: (showFilters || allSectionsCollapsed) ? 'auto' : '999',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            ...sectionOrderStyle('filters'),
          }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: allSectionsCollapsed ? 'grab' : 'pointer',
              padding: '10px 20px',
              gap: '15px'
            }}
            onClick={(e) => {
              // Allow expanding even in drag mode (will exit drag mode)
              e.stopPropagation(); // Prevent drag handling
              setShowFilters(!showFilters);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
              <span style={{
                margin: 0,
                color: theme.sectionLabelColor,
                fontSize: '1.05em',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                🔍 Filters
              </span>
              <span style={{
                fontSize: '0.78em',
                fontWeight: '600',
                color: theme.textSecondary,
                background: theme.detailBg,
                border: `1px solid ${theme.borderColor}`,
                borderRadius: '10px',
                padding: '2px 8px',
                whiteSpace: 'nowrap'
              }}>
                {filteredData.length.toLocaleString()} events
              </span>
              
              {/* Polygon filter indicator badge */}
              {polygonCoords && polygonFilterMode !== 'none' && (
                <div style={{
                  padding: '4px 10px',
                  background: polygonFilterMode === 'inside' 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : 'rgba(245, 158, 11, 0.2)',
                  border: `2px solid ${polygonFilterMode === 'inside' ? '#10B981' : '#F59E0B'}`,
                  borderRadius: '6px',
                  fontSize: '0.65em',
                  fontWeight: '600',
                  color: polygonFilterMode === 'inside' ? '#10B981' : '#F59E0B',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  maxWidth: '300px',
                  overflow: 'hidden'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    📐 
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {currentPolygonName || 'Polygon'}
                    </span>
                    : {polygonFilterMode === 'inside' ? 'Inside' : 'Outside'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Date Range Display - Only show when collapsed */}
            {!showFilters && (
              <div style={{
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                flex: 1,
                fontSize: '0.85em',
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'hidden'
              }}>
                <div style={{ 
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  📅 {formatDateTimeReadable(getDateFromDisplay()) || 'No start date'}
                </div>
                <div style={{ 
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  →
                </div>
                <div style={{ 
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  📅 {formatDateTimeReadable(getDateToDisplay()) || 'No end date'}
                </div>
                {filters.lastHours && filters.lastHours > 0 && (
                  <div style={{ 
                    padding: '4px 8px',
                    background: 'rgba(100, 200, 100, 0.2)',
                    borderRadius: '4px',
                    color: theme.accentColor,
                    fontSize: '0.9em',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    🕐 {filters.lastHours}h
                  </div>
                )}

                {/* Other filters active badge */}
                {(() => {
                  const hasCallTypes  = filters.callTypes  && !filters.callTypes.includes('all');
                  const hasPriorities = filters.priorities && !filters.priorities.includes('all');
                  const hasStatuses   = filters.statuses   && !filters.statuses.includes('all');
                  const hasAgencies   = filters.agencies   && !filters.agencies.includes('all');
                  const hasAgencyTypes= filters.agencyTypes&& !filters.agencyTypes.includes('all');
                  const hasActiveOnly = filters.activeOnly;
                  const count = [hasCallTypes, hasPriorities, hasStatuses, hasAgencies, hasAgencyTypes, hasActiveOnly].filter(Boolean).length;
                  if (!count) return null;
                  return (
                    <div style={{
                      padding: '4px 8px',
                      background: 'rgba(99, 102, 241, 0.15)',
                      border: '1px solid rgba(99, 102, 241, 0.4)',
                      borderRadius: '4px',
                      color: '#818CF8',
                      fontSize: '0.8em',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>⚡</span>
                      <span>{count} filter{count !== 1 ? 's' : ''} active</span>
                    </div>
                  );
                })()}
                
                {/* Snapshot Playback Controls - Show when collapsed and snapshot has values */}
                {(currentSnapshotStart || (snapshotWindowMinutes && playbackSpeed)) && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginLeft: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  >
                    {/* Snapshot window indicator */}
                    {currentSnapshotStart && currentSnapshotEnd && (
                      <div style={{
                        padding: '4px 8px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        borderRadius: '4px',
                        color: '#3B82F6',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        📸 {currentSnapshotStart.toLocaleString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                        })} - {currentSnapshotEnd.toLocaleString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                        })}
                      </div>
                    )}
                    
                    {/* Start/Pause/Resume/Stop buttons */}
                    {!isSnapshotPlaying && !isSnapshotPaused ? (
                      <button
                        onClick={startSnapshotPlayback}
                        disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                        style={{
                          padding: '6px 12px',
                          background: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? theme.inputBg : '#10B981',
                          color: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? theme.textSecondary : 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? 'not-allowed' : 'pointer',
                          fontSize: '0.85em',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          opacity: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? 0.5 : 1,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) {
                            e.currentTarget.style.background = '#059669';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) {
                            e.currentTarget.style.background = '#10B981';
                          }
                        }}
                      >
                        ▶ Start
                      </button>
                    ) : isSnapshotPaused ? (
                      <>
                        <button
                          onClick={resumeSnapshotPlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#10B981'}
                        >
                          ▶ Resume
                        </button>
                        <button
                          onClick={stopSnapshotPlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                        >
                          ⏹ Stop
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={pauseSnapshotPlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#F59E0B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#D97706'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#F59E0B'}
                        >
                          ⏸ Pause
                        </button>
                        <button
                          onClick={stopSnapshotPlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                        >
                          ⏹ Stop
                        </button>
                      </>
                    )}
                    
                    {/* Rewind button */}
                    <button
                      onClick={rewindSnapshot}
                      disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                      style={{
                        padding: '6px 10px',
                        background: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? theme.accentColor : theme.inputBg,
                        color: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        opacity: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      ⏪
                    </button>
                    
                    {/* Forward button */}
                    <button
                      onClick={forwardSnapshot}
                      disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                      style={{
                        padding: '6px 10px',
                        background: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? theme.accentColor : theme.inputBg,
                        color: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        opacity: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      ⏩
                    </button>
                    {/* Cancel button - clears window and controls entirely */}
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelSnapshotPlayback(); }}
                      style={{
                        padding: '6px 12px',
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                      title="Cancel snapshot — clears window and playback controls"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                )}
                
                {/* Cumulative Playback Controls - Show when collapsed and cumulative has values */}
                {(currentCumulativeStart || (cumulativeWindowMinutes && cumulativePlaybackSpeed)) && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginLeft: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  >
                    {/* Cumulative window indicator */}
                    {currentCumulativeStart && currentCumulativeEnd && (
                      <div style={{
                        padding: '4px 8px',
                        background: 'rgba(168, 85, 247, 0.2)',
                        borderRadius: '4px',
                        color: '#A855F7',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        📈 {currentCumulativeStart.toLocaleString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                        })} - {currentCumulativeEnd.toLocaleString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                        })}
                      </div>
                    )}
                    
                    {/* Start/Pause/Resume/Stop buttons */}
                    {!isCumulativePlaying && !isCumulativePaused ? (
                      <button
                        onClick={startCumulativePlayback}
                        disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                        style={{
                          padding: '6px 12px',
                          background: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? theme.inputBg : '#A855F7',
                          color: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? theme.textSecondary : 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? 'not-allowed' : 'pointer',
                          fontSize: '0.85em',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          opacity: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? 0.5 : 1,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) {
                            e.currentTarget.style.background = '#9333EA';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) {
                            e.currentTarget.style.background = '#A855F7';
                          }
                        }}
                      >
                        ▶ Start
                      </button>
                    ) : isCumulativePaused ? (
                      <>
                        <button
                          onClick={resumeCumulativePlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#A855F7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#9333EA'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#A855F7'}
                        >
                          ▶ Resume
                        </button>
                        <button
                          onClick={stopCumulativePlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                        >
                          ⏹ Stop
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={pauseCumulativePlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#F59E0B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#D97706'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#F59E0B'}
                        >
                          ⏸ Pause
                        </button>
                        <button
                          onClick={stopCumulativePlayback}
                          style={{
                            padding: '6px 12px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                        >
                          ⏹ Stop
                        </button>
                      </>
                    )}
                    
                    {/* Rewind button */}
                    <button
                      onClick={rewindCumulative}
                      disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                      style={{
                        padding: '6px 10px',
                        background: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? theme.accentColor : theme.inputBg,
                        color: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        opacity: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      ⏪
                    </button>
                    
                    {/* Forward button */}
                    <button
                      onClick={forwardCumulative}
                      disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                      style={{
                        padding: '6px 10px',
                        background: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? theme.accentColor : theme.inputBg,
                        color: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'white' : theme.textSecondary,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        opacity: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      ⏩
                    </button>
                    {/* Cancel button - clears window and controls entirely */}
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelCumulativePlayback(); }}
                      style={{
                        padding: '6px 12px',
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                      title="Cancel cumulative — clears window and playback controls"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <span style={{
              fontSize: '1em',
              color: theme.sectionLabelColor,
              transform: showFilters ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.3s ease'
            }}>
              ▼
            </span>
          </div>

          {showFilters && (
            <div style={{ padding: '0 20px 20px' }}>
              <div className="cad-filter-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '15px'
              }}>
                {/* Date preset selector - compact */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                    Quick Date Range {filters.lastHours && filters.lastHours > 0 && <span style={{ color: theme.textSecondary, fontStyle: 'italic' }}>(Disabled)</span>}
                  </label>
                  <select
                    value={filters.datePreset}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, datePreset: e.target.value, lastHours: '' }));
                      applyDatePreset(e.target.value);
                    }}
                    disabled={filters.lastHours && filters.lastHours > 0}
                    style={{
                      width: '100%', padding: '8px 12px', border: `2px solid ${theme.inputBorder}`,
                      borderRadius: '6px', 
                      background: (filters.lastHours && filters.lastHours > 0) ? theme.detailBg : theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '1em', 
                      boxSizing: 'border-box',
                      cursor: (filters.lastHours && filters.lastHours > 0) ? 'not-allowed' : 'pointer',
                      opacity: (filters.lastHours && filters.lastHours > 0) ? 0.6 : 1
                    }}
                  >
                    <option value="">Custom Range</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="last_7_days">Last 7 Days</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="last_90_days">Last 90 Days</option>
                  </select>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', marginBottom: '4px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      From Date/Time {filters.lastHours && filters.lastHours > 0 && <span style={{ color: theme.accentColor, fontStyle: 'italic' }}>(Auto-calculated)</span>}
                    </label>
                    {filters.dateFrom && !(filters.lastHours && filters.lastHours > 0) && (
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, dateFrom: '', datePreset: '' }))}
                        style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: '0.85em', padding: '0 2px' }}
                        title="Clear From Date/Time"
                      >✕ Clear</button>
                    )}
                  </div>
                  <input
                    type="datetime-local"
                    value={getDateFromDisplay()}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value, datePreset: '', lastHours: '' }))}
                    readOnly={filters.lastHours && filters.lastHours > 0}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginBottom: '4px',
                      border: `2px solid ${theme.inputBorder}`,
                      borderRadius: '6px',
                      background: (filters.lastHours && filters.lastHours > 0) ? theme.detailBg : theme.inputBg,
                      color: theme.textPrimary,
                      fontSize: '1em',
                      boxSizing: 'border-box',
                      cursor: (filters.lastHours && filters.lastHours > 0) ? 'not-allowed' : 'text',
                      opacity: (filters.lastHours && filters.lastHours > 0) ? 0.7 : 1
                    }}
                    title={filters.lastHours && filters.lastHours > 0 ? 'Calculated from Last X Hours setting' : 'Manually set start date/time'}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      To Date/Time {filters.lastHours && filters.lastHours > 0 && <span style={{ color: theme.accentColor, fontStyle: 'italic' }}>(Auto-calculated)</span>}
                    </label>
                    {filters.dateTo && !(filters.lastHours && filters.lastHours > 0) && (
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, dateTo: '', datePreset: '' }))}
                        style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: '0.85em', padding: '0 2px' }}
                        title="Clear To Date/Time"
                      >✕ Clear</button>
                    )}
                  </div>
                  <input
                    type="datetime-local"
                    value={getDateToDisplay()}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value, datePreset: '', lastHours: '' }))}
                    readOnly={filters.lastHours && filters.lastHours > 0}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginBottom: '4px',
                      border: `2px solid ${theme.inputBorder}`,
                      borderRadius: '6px',
                      background: (filters.lastHours && filters.lastHours > 0) ? theme.detailBg : theme.inputBg,
                      color: theme.textPrimary,
                      fontSize: '1em',
                      boxSizing: 'border-box',
                      cursor: (filters.lastHours && filters.lastHours > 0) ? 'not-allowed' : 'text',
                      opacity: (filters.lastHours && filters.lastHours > 0) ? 0.7 : 1
                    }}
                    title={filters.lastHours && filters.lastHours > 0 ? 'Calculated as current time (updates every 30s)' : 'Manually set end date/time'}
                  />
                  
                {/* Rolling Window */}
                  <div style={{ 
                    marginTop: '10px',
                    opacity: (currentSnapshotStart || currentCumulativeStart) ? 0.5 : 1,
                    pointerEvents: (currentSnapshotStart || currentCumulativeStart) ? 'none' : 'auto', 
                    padding: '10px', 
                    background: filters.lastHours ? 'rgba(100, 200, 100, 0.1)' : theme.detailBg,
                    border: filters.lastHours ? `2px solid rgba(100, 200, 100, 0.3)` : `2px solid ${theme.borderColor}`,
                    borderRadius: '6px',
                    transition: 'all 0.3s ease'
                  }}>
                    <div 
                      onClick={() => {
                        setIsRollingWindowExpanded(!isRollingWindowExpanded);
                        if (!isRollingWindowExpanded) {
                          setIsSnapshotExpanded(false);
                          setIsCumulativeExpanded(false);
                        }
                      }}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: isRollingWindowExpanded ? '5px' : '0',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <label style={{ 
                        fontSize: '0.85em', 
                        color: theme.textSecondary, 
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ 
                          fontSize: '0.8em',
                          transition: 'transform 0.2s',
                          transform: isRollingWindowExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          display: 'inline-block'
                        }}>
                          ▶
                        </span>
                        Rolling Window
                      </label>
                      {filters.lastHours && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilters(prev => ({ ...prev, lastHours: '' }));
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.textSecondary,
                            fontSize: '1.2em',
                            cursor: 'pointer',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.5,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                          title="Clear rolling window"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    
                    {isRollingWindowExpanded && (
                      <>
                    <input 
                      type="number" 
                      min="0"
                      step="1"
                      placeholder="e.g., 24 for last 24 hours"
                      value={filters.lastHours} 
                      onChange={(e) => {
                        setFilters(prev => ({ 
                          ...prev, 
                          lastHours: e.target.value,
                          datePreset: '' 
                        }));
                        // Clear other time windows when rolling window is used
                        if (e.target.value) {
                          setCurrentSnapshotStart(null);
                          setCurrentSnapshotEnd(null);
                          setCurrentCumulativeStart(null);
                          setCurrentCumulativeEnd(null);
                        }
                      }}
                      disabled={currentSnapshotStart !== null || currentCumulativeStart !== null}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '6px', 
                        background: (currentSnapshotStart || currentCumulativeStart) ? theme.detailBg : theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '1em', 
                        boxSizing: 'border-box',
                        cursor: (currentSnapshotStart || currentCumulativeStart) ? 'not-allowed' : 'text',
                        opacity: (currentSnapshotStart || currentCumulativeStart) ? 0.6 : 1
                      }} 
                    />
                    {currentSnapshotStart && (
                      <div style={{ 
                        marginTop: '5px', 
                        fontSize: '0.75em', 
                        color: '#F59E0B',
                        fontStyle: 'italic'
                      }}>
                        ⚠️ Disabled while Snapshot Window is active
                      </div>
                    )}
                    {currentCumulativeStart && (
                      <div style={{ 
                        marginTop: '5px', 
                        fontSize: '0.75em', 
                        color: '#F59E0B',
                        fontStyle: 'italic'
                      }}>
                        ⚠️ Disabled while Cumulative Window is active
                      </div>
                    )}
                    {filters.lastHours && !isNaN(filters.lastHours) && filters.lastHours > 0 && !currentSnapshotStart && !currentCumulativeStart && (
                      <div style={{ 
                        marginTop: '5px', 
                        fontSize: '0.75em', 
                        color: theme.accentColor,
                        fontStyle: 'italic'
                      }}>
                        ✓ Active: Shows last {filters.lastHours} hours (updates every 30s)
                      </div>
                    )}
                    <div style={{ 
                      marginTop: '5px', 
                      fontSize: '0.7em', 
                      color: theme.textSecondary,
                      fontStyle: 'italic'
                    }}>
                      When set, overrides Date From with rolling time window
                    </div>
                    </>
                    )}
                  </div>

                  
                  {/* Snapshot Window */}
                  <div style={{ 
                    marginTop: '15px',
                    opacity: (!filters.dateFrom || !filters.dateTo || filters.lastHours || currentCumulativeStart) ? 0.5 : 1,
                    pointerEvents: (!filters.dateFrom || !filters.dateTo || filters.lastHours || currentCumulativeStart) ? 'none' : 'auto', 
                    padding: '12px', 
                    background: currentSnapshotStart ? 'rgba(59, 130, 246, 0.1)' : theme.detailBg,
                    border: currentSnapshotStart ? `2px solid rgba(59, 130, 246, 0.3)` : `2px solid ${theme.borderColor}`,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}>
                    <div 
                      onClick={() => {
                        setIsSnapshotExpanded(!isSnapshotExpanded);
                        if (!isSnapshotExpanded) {
                          setIsRollingWindowExpanded(false);
                          setIsCumulativeExpanded(false);
                        }
                      }}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: isSnapshotExpanded ? '8px' : '0',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <label style={{ 
                        fontSize: '0.85em', 
                        color: theme.textSecondary, 
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ 
                          fontSize: '0.8em',
                          transition: 'transform 0.2s',
                          transform: isSnapshotExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          display: 'inline-block'
                        }}>
                          ▶
                        </span>
                        Snapshot Window
                      </label>
                      {currentSnapshotStart && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            resetSnapshotPlayback();
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.textSecondary,
                            fontSize: '1.2em',
                            cursor: 'pointer',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.5,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                          title="Reset snapshot playback"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {isSnapshotExpanded && (
                      <>
                    {/* Current Snapshot Range Display */}
                    {currentSnapshotStart && currentSnapshotEnd && (
                      <div style={{
                        padding: '10px',
                        background: 'rgba(59, 130, 246, 0.15)',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        fontSize: '0.85em',
                        color: theme.textPrimary,
                        fontWeight: '600',
                        textAlign: 'center',
                        border: '1px solid rgba(59, 130, 246, 0.3)'
                      }}>
                        <div style={{ marginBottom: '3px', color: theme.textSecondary, fontSize: '0.9em' }}>
                          Current Window:
                        </div>
                        <div>
                          {currentSnapshotStart.toLocaleString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                          })}
                        </div>
                        <div style={{ fontSize: '0.9em', color: theme.textSecondary }}>to</div>
                        <div>
                          {currentSnapshotEnd.toLocaleString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>
                        Snapshot Window (minutes)
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="e.g., 15 for 15-minute snapshots"
                        value={snapshotWindowMinutes} 
                        onChange={(e) => {
                          setSnapshotWindowMinutes(e.target.value);
                          // Clear other time windows when snapshot is used
                          if (e.target.value) {
                            setFilters(prev => ({ ...prev, lastHours: '' }));
                            setCurrentCumulativeStart(null);
                            setCurrentCumulativeEnd(null);
                          }
                        }}
                        disabled={isSnapshotPlaying || filters.lastHours || currentCumulativeStart}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '6px', 
                          background: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? theme.detailBg : theme.inputBg, 
                          color: theme.textPrimary, 
                          fontSize: '1em',
                          boxSizing: 'border-box',
                          cursor: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? 'not-allowed' : 'text',
                          opacity: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? 0.6 : 1
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>
                        Playback Speed (seconds)
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="e.g., 5 for 5-second intervals"
                        value={playbackSpeed} 
                        onChange={(e) => {
                          setPlaybackSpeed(e.target.value);
                          // Clear other time windows when snapshot is used
                          if (e.target.value) {
                            setFilters(prev => ({ ...prev, lastHours: '' }));
                            setCurrentCumulativeStart(null);
                            setCurrentCumulativeEnd(null);
                          }
                        }}
                        disabled={isSnapshotPlaying || filters.lastHours || currentCumulativeStart}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '6px', 
                          background: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? theme.detailBg : theme.inputBg, 
                          color: theme.textPrimary, 
                          fontSize: '1em',
                          boxSizing: 'border-box',
                          cursor: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? 'not-allowed' : 'text',
                          opacity: (isSnapshotPlaying || filters.lastHours || currentCumulativeStart) ? 0.6 : 1
                        }}
                      />
                      {filters.lastHours && (
                        <div style={{ 
                          marginTop: '5px', 
                          fontSize: '0.75em', 
                          color: '#F59E0B',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ Disabled while Rolling Window is active
                        </div>
                      )}
                      {currentCumulativeStart && (
                        <div style={{ 
                          marginTop: '5px', 
                          fontSize: '0.75em', 
                          color: '#F59E0B',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ Disabled while Cumulative Window is active
                        </div>
                      )}
                    </div>

                    {/* Playback Controls */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {!isSnapshotPlaying && !isSnapshotPaused ? (
                        <button
                          onClick={startSnapshotPlayback}
                          disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                          style={{
                            gridColumn: '1 / -1',
                            padding: '10px',
                            background: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? theme.inputBg : '#10B981',
                            color: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? theme.textSecondary : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? 'not-allowed' : 'pointer',
                            fontSize: '0.95em',
                            fontWeight: '600',
                            opacity: (!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed) ? 0.5 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) {
                              e.currentTarget.style.background = '#059669';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) {
                              e.currentTarget.style.background = '#10B981';
                            }
                          }}
                        >
                          ▶ Start Playback
                        </button>
                      ) : isSnapshotPaused ? (
                        <>
                          <button
                            onClick={resumeSnapshotPlayback}
                            style={{
                              padding: '10px',
                              background: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#10B981'}
                          >
                            ▶ Resume
                          </button>
                          <button
                            onClick={stopSnapshotPlayback}
                            style={{
                              padding: '10px',
                              background: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                          >
                            ⏹ Stop
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={pauseSnapshotPlayback}
                            style={{
                              padding: '10px',
                              background: '#F59E0B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#D97706'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#F59E0B'}
                          >
                            ⏸ Pause
                          </button>
                          <button
                            onClick={stopSnapshotPlayback}
                            style={{
                              padding: '10px',
                              background: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                          >
                            ⏹ Stop
                          </button>
                        </>
                      )}

                      <button
                        onClick={rewindSnapshot}
                        disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                        style={{
                          padding: '8px',
                          background: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? theme.accentColor : theme.inputBg,
                          color: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'white' : theme.textSecondary,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'pointer' : 'not-allowed',
                          fontSize: '0.9em',
                          fontWeight: '600',
                          opacity: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 1 : 0.5
                        }}
                      >
                        ⏪ Rewind
                      </button>

                      <button
                        onClick={forwardSnapshot}
                        disabled={!filters.dateFrom || !filters.dateTo || !snapshotWindowMinutes || !playbackSpeed}
                        style={{
                          padding: '8px',
                          background: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? theme.accentColor : theme.inputBg,
                          color: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'white' : theme.textSecondary,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 'pointer' : 'not-allowed',
                          fontSize: '0.9em',
                          fontWeight: '600',
                          opacity: (filters.dateFrom && filters.dateTo && snapshotWindowMinutes && playbackSpeed) ? 1 : 0.5
                        }}
                      >
                        ⏩ Forward
                      </button>

                      {/* Cancel Playback Button */}
                      {(snapshotWindowMinutes || playbackSpeed || currentSnapshotStart) && (
                        <button
                          onClick={cancelSnapshotPlayback}
                          style={{
                            gridColumn: '1 / -1',
                            padding: '8px',
                            background: '#6B7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            fontWeight: '600',
                            marginTop: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#4B5563'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#6B7280'}
                        >
                          ✕ Cancel Playback
                        </button>
                      )}
                    </div>

                    <div style={{ 
                      marginTop: '10px', 
                      fontSize: '0.75em', 
                      color: theme.textSecondary, 
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {!currentSnapshotStart ? (
                        filters.dateFrom && filters.dateTo ? (
                          snapshotWindowMinutes && playbackSpeed ? (
                            <span>
                              ✓ Showing full date range<br/>
                              <span style={{ fontSize: '0.95em', color: theme.textPrimary }}>
                                {new Date(filters.dateFrom).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })} - {new Date(filters.dateTo).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })}
                              </span><br/>
                              <span style={{ color: '#10B981', fontSize: '0.9em' }}>Ready to start playback</span>
                            </span>
                          ) : (
                            <span>
                              ✓ Showing full date range<br/>
                              <span style={{ fontSize: '0.95em', color: theme.textPrimary }}>
                                {new Date(filters.dateFrom).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })} - {new Date(filters.dateTo).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })}
                              </span><br/>
                              <span style={{ color: '#F59E0B', fontSize: '0.9em' }}>
                                Enter snapshot window & playback speed to enable controls
                              </span>
                            </span>
                          )
                        ) : (
                          'Set date range, snapshot window, and playback speed to enable playback controls'
                        )
                      ) : (
                        <span style={{ color: '#3B82F6' }}>
                          📸 Snapshot playback {isSnapshotPlaying ? 'active' : 'paused'}
                        </span>
                      )}
                    </div>
                    </>
                    )}
                  </div>

                  {/* Cumulative Window */}
                  <div style={{ 
                    marginTop: '15px',
                    opacity: (!filters.dateFrom || !filters.dateTo || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 0.5 : 1,
                    pointerEvents: (!filters.dateFrom || !filters.dateTo || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 'none' : 'auto', 
                    padding: '12px', 
                    background: currentCumulativeStart ? 'rgba(168, 85, 247, 0.1)' : theme.detailBg,
                    border: currentCumulativeStart ? `2px solid rgba(168, 85, 247, 0.3)` : `2px solid ${theme.borderColor}`,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}>
                    <div 
                      onClick={() => {
                        setIsCumulativeExpanded(!isCumulativeExpanded);
                        if (!isCumulativeExpanded) {
                          setIsRollingWindowExpanded(false);
                          setIsSnapshotExpanded(false);
                        }
                      }}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: isCumulativeExpanded ? '8px' : '0',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <label style={{ 
                        fontSize: '0.85em', 
                        color: theme.textSecondary, 
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ 
                          fontSize: '0.8em',
                          transition: 'transform 0.2s',
                          transform: isCumulativeExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          display: 'inline-block'
                        }}>
                          ▶
                        </span>
                        Cumulative Window
                      </label>
                      {currentCumulativeStart && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            resetCumulativePlayback();
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.textSecondary,
                            fontSize: '1.2em',
                            cursor: 'pointer',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.5,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                          title="Reset cumulative playback"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {isCumulativeExpanded && (
                      <>
                    {/* Current Cumulative Range Display */}
                    {currentCumulativeStart && currentCumulativeEnd && (
                      <div style={{
                        padding: '10px',
                        background: 'rgba(168, 85, 247, 0.15)',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        fontSize: '0.85em',
                        color: theme.textPrimary,
                        fontWeight: '600',
                        textAlign: 'center',
                        border: '1px solid rgba(168, 85, 247, 0.3)'
                      }}>
                        <div style={{ marginBottom: '3px', color: theme.textSecondary, fontSize: '0.9em' }}>
                          Growing Window:
                        </div>
                        <div>
                          {currentCumulativeStart.toLocaleString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                          })}
                        </div>
                        <div style={{ fontSize: '0.9em', color: theme.textSecondary }}>to</div>
                        <div>
                          {currentCumulativeEnd.toLocaleString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                          })}
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '0.8em', color: '#A855F7', fontStyle: 'italic' }}>
                          Start fixed • End advancing
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>
                        Advance Interval (minutes)
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="e.g., 15 for 15-minute advances"
                        value={cumulativeWindowMinutes} 
                        onChange={(e) => {
                          setCumulativeWindowMinutes(e.target.value);
                          // Clear other time windows when cumulative is used
                          if (e.target.value) {
                            setFilters(prev => ({ ...prev, lastHours: '' }));
                            setCurrentSnapshotStart(null);
                            setCurrentSnapshotEnd(null);
                          }
                        }}
                        disabled={isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '6px', 
                          background: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? theme.detailBg : theme.inputBg, 
                          color: theme.textPrimary, 
                          fontSize: '1em',
                          boxSizing: 'border-box',
                          cursor: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 'not-allowed' : 'text',
                          opacity: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 0.6 : 1
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>
                        Playback Speed (seconds)
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="e.g., 5 for 5-second intervals"
                        value={cumulativePlaybackSpeed} 
                        onChange={(e) => {
                          setCumulativePlaybackSpeed(e.target.value);
                          // Clear other time windows when cumulative is used
                          if (e.target.value) {
                            setFilters(prev => ({ ...prev, lastHours: '' }));
                            setCurrentSnapshotStart(null);
                            setCurrentSnapshotEnd(null);
                          }
                        }}
                        disabled={isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart}
                        style={{ 
                          width: '100%', 
                          padding: '8px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '6px', 
                          background: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? theme.detailBg : theme.inputBg, 
                          color: theme.textPrimary, 
                          fontSize: '1em',
                          boxSizing: 'border-box',
                          cursor: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 'not-allowed' : 'text',
                          opacity: (isCumulativePlaying || (filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) ? 0.6 : 1
                        }}
                      />
                      {((filters.lastHours && filters.lastHours > 0) || currentSnapshotStart) && (
                        <div style={{ 
                          marginTop: '5px', 
                          fontSize: '0.75em', 
                          color: '#F59E0B',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ Disabled while {(filters.lastHours && filters.lastHours > 0) ? 'Rolling Window' : 'Snapshot Window'} is active
                        </div>
                      )}
                    </div>

                    {/* Playback Controls */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {!isCumulativePlaying && !isCumulativePaused ? (
                        <button
                          onClick={startCumulativePlayback}
                          disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                          style={{
                            gridColumn: '1 / -1',
                            padding: '10px',
                            background: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? theme.inputBg : '#A855F7',
                            color: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? theme.textSecondary : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? 'not-allowed' : 'pointer',
                            fontSize: '0.95em',
                            fontWeight: '600',
                            opacity: (!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed) ? 0.5 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) {
                              e.currentTarget.style.background = '#9333EA';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) {
                              e.currentTarget.style.background = '#A855F7';
                            }
                          }}
                        >
                          ▶ Start Playback
                        </button>
                      ) : isCumulativePaused ? (
                        <>
                          <button
                            onClick={resumeCumulativePlayback}
                            style={{
                              padding: '10px',
                              background: '#A855F7',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#9333EA'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#A855F7'}
                          >
                            ▶ Resume
                          </button>
                          <button
                            onClick={stopCumulativePlayback}
                            style={{
                              padding: '10px',
                              background: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                          >
                            ⏹ Stop
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={pauseCumulativePlayback}
                            style={{
                              padding: '10px',
                              background: '#F59E0B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#D97706'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#F59E0B'}
                          >
                            ⏸ Pause
                          </button>
                          <button
                            onClick={stopCumulativePlayback}
                            style={{
                              padding: '10px',
                              background: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                          >
                            ⏹ Stop
                          </button>
                        </>
                      )}

                      <button
                        onClick={rewindCumulative}
                        disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                        style={{
                          padding: '8px',
                          background: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? theme.accentColor : theme.inputBg,
                          color: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'white' : theme.textSecondary,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'pointer' : 'not-allowed',
                          fontSize: '0.9em',
                          fontWeight: '600',
                          opacity: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 1 : 0.5
                        }}
                      >
                        ⏪ Rewind
                      </button>

                      <button
                        onClick={forwardCumulative}
                        disabled={!filters.dateFrom || !filters.dateTo || !cumulativeWindowMinutes || !cumulativePlaybackSpeed}
                        style={{
                          padding: '8px',
                          background: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? theme.accentColor : theme.inputBg,
                          color: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'white' : theme.textSecondary,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 'pointer' : 'not-allowed',
                          fontSize: '0.9em',
                          fontWeight: '600',
                          opacity: (filters.dateFrom && filters.dateTo && cumulativeWindowMinutes && cumulativePlaybackSpeed) ? 1 : 0.5
                        }}
                      >
                        ⏩ Forward
                      </button>

                      {/* Cancel Playback Button */}
                      {(cumulativeWindowMinutes || cumulativePlaybackSpeed || currentCumulativeStart) && (
                        <button
                          onClick={cancelCumulativePlayback}
                          style={{
                            gridColumn: '1 / -1',
                            padding: '8px',
                            background: '#6B7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            fontWeight: '600',
                            marginTop: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#4B5563'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#6B7280'}
                        >
                          ✕ Cancel Playback
                        </button>
                      )}
                    </div>

                    <div style={{ 
                      marginTop: '10px', 
                      fontSize: '0.75em', 
                      color: theme.textSecondary, 
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {!currentCumulativeStart ? (
                        filters.dateFrom && filters.dateTo ? (
                          cumulativeWindowMinutes && cumulativePlaybackSpeed ? (
                            <span>
                              ✓ Showing full date range<br/>
                              <span style={{ fontSize: '0.95em', color: theme.textPrimary }}>
                                {new Date(filters.dateFrom).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })} - {new Date(filters.dateTo).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })}
                              </span><br/>
                              <span style={{ color: '#A855F7', fontSize: '0.9em' }}>Ready to start playback</span>
                            </span>
                          ) : (
                            <span>
                              ✓ Showing full date range<br/>
                              <span style={{ fontSize: '0.95em', color: theme.textPrimary }}>
                                {new Date(filters.dateFrom).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })} - {new Date(filters.dateTo).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
                                })}
                              </span><br/>
                              <span style={{ color: '#F59E0B', fontSize: '0.9em' }}>
                                Enter advance interval & playback speed to enable controls
                              </span>
                            </span>
                          )
                        ) : (
                          'Set date range, advance interval, and playback speed to enable playback controls'
                        )
                      ) : (
                        <span style={{ color: '#A855F7' }}>
                          📈 Cumulative playback {isCumulativePlaying ? 'active' : 'paused'}
                        </span>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                </div>

                {/* Event Type with search */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      Event Type
                    </label>
                    <span style={{
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: filters.callTypes.includes('all') ? theme.inputBg : theme.accentColor,
                      color: filters.callTypes.includes('all') ? theme.textSecondary : 'white',
                      fontWeight: '600'
                    }}>
                      {filters.callTypes.includes('all') ? 'All' : `${filters.callTypes.length} selected`}
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      value={searchTerms.callType}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, callType: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px',
                        paddingRight: searchTerms.callType ? '35px' : '12px', 
                        marginBottom: '5px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '6px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em', 
                        boxSizing: 'border-box' 
                      }} 
                    />
                    {searchTerms.callType && (
                      <button
                        onClick={() => setSearchTerms(prev => ({ ...prev, callType: '' }))}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '5px'
                        }}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <select multiple value={filters.callTypes}
                    onChange={(e) => { const selected = Array.from(e.target.selectedOptions, option => option.value); setFilters(prev => ({ ...prev, callTypes: selected })); }}
                    style={{ width: '100%', height: '350px', maxHeight: '350px', overflow: 'auto', padding: '8px 12px', border: `2px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.85em', boxSizing: 'border-box' }}>
                    <option value="all">All Types</option>
                    {availableCallTypes.filter(type => type.toLowerCase().includes(debouncedSearchTerms.callType.toLowerCase())).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Status with search */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      Status
                    </label>
                    <span style={{
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: filters.statuses.includes('all') ? theme.inputBg : theme.accentColor,
                      color: filters.statuses.includes('all') ? theme.textSecondary : 'white',
                      fontWeight: '600'
                    }}>
                      {filters.statuses.includes('all') ? 'All' : `${filters.statuses.length} selected`}
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      value={searchTerms.status}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, status: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px',
                        paddingRight: searchTerms.status ? '35px' : '12px', 
                        marginBottom: '5px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '6px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em', 
                        boxSizing: 'border-box' 
                      }} 
                    />
                    {searchTerms.status && (
                      <button
                        onClick={() => setSearchTerms(prev => ({ ...prev, status: '' }))}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '5px'
                        }}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <select multiple value={filters.statuses}
                    onChange={(e) => { const selected = Array.from(e.target.selectedOptions, option => option.value); setFilters(prev => ({ ...prev, statuses: selected })); }}
                    style={{ width: '100%', height: '350px', maxHeight: '350px', overflow: 'auto', padding: '8px 12px', border: `2px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.85em', boxSizing: 'border-box' }}>
                    <option value="all">All Statuses</option>
                    {availableStatuses.filter(status => status.toLowerCase().includes(debouncedSearchTerms.status.toLowerCase())).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Agency with search */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      Agency
                    </label>
                    <span style={{
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: filters.agencies.includes('all') ? theme.inputBg : theme.accentColor,
                      color: filters.agencies.includes('all') ? theme.textSecondary : 'white',
                      fontWeight: '600'
                    }}>
                      {filters.agencies.includes('all') ? 'All' : `${filters.agencies.length} selected`}
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      value={searchTerms.agency}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, agency: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px',
                        paddingRight: searchTerms.agency ? '35px' : '12px', 
                        marginBottom: '5px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '6px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em', 
                        boxSizing: 'border-box' 
                      }} 
                    />
                    {searchTerms.agency && (
                      <button
                        onClick={() => setSearchTerms(prev => ({ ...prev, agency: '' }))}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '5px'
                        }}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <select multiple value={filters.agencies}
                    onChange={(e) => { const selected = Array.from(e.target.selectedOptions, option => option.value); setFilters(prev => ({ ...prev, agencies: selected })); }}
                    style={{ width: '100%', height: '350px', maxHeight: '350px', overflow: 'auto', padding: '8px 12px', border: `2px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.85em', boxSizing: 'border-box' }}>
                    <option value="all">All Agencies</option>
                    {availableAgencies.filter(agency => agency.toLowerCase().includes(debouncedSearchTerms.agency.toLowerCase())).map(agency => (
                      <option key={agency} value={agency}>{agency}</option>
                    ))}
                  </select>
                </div>

                {/* AgencyType with search */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      Agency Type
                    </label>
                    <span style={{
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: filters.agencyTypes.includes('all') ? theme.inputBg : theme.accentColor,
                      color: filters.agencyTypes.includes('all') ? theme.textSecondary : 'white',
                      fontWeight: '600'
                    }}>
                      {filters.agencyTypes.includes('all') ? 'All' : `${filters.agencyTypes.length} selected`}
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="text" 
                      placeholder="Search..." 
                      value={searchTerms.agencyType}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, agencyType: e.target.value }))}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px',
                        paddingRight: searchTerms.agencyType ? '35px' : '12px', 
                        marginBottom: '5px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '6px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em', 
                        boxSizing: 'border-box' 
                      }} 
                    />
                    {searchTerms.agencyType && (
                      <button
                        onClick={() => setSearchTerms(prev => ({ ...prev, agencyType: '' }))}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'transparent',
                          border: 'none',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          fontSize: '1.2em',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '5px'
                        }}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <select multiple value={filters.agencyTypes}
                    onChange={(e) => { const selected = Array.from(e.target.selectedOptions, option => option.value); setFilters(prev => ({ ...prev, agencyTypes: selected })); }}
                    style={{ width: '100%', height: '350px', maxHeight: '350px', overflow: 'auto', padding: '8px 12px', border: `2px solid ${theme.inputBorder}`, borderRadius: '6px', background: theme.inputBg, color: theme.textPrimary, fontSize: '0.85em', boxSizing: 'border-box' }}>
                    <option value="all">All Agency Types</option>
                    {availableAgencyTypes.filter(type => type.toLowerCase().includes(debouncedSearchTerms.agencyType.toLowerCase())).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Priority Filter */}
                <div style={{ background: theme.detailBg, padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ fontSize: '0.85em', color: theme.textSecondary, fontWeight: '600' }}>
                      Priority Level
                    </label>
                    <span style={{
                      fontSize: '0.75em',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: filters.priorities.includes('all') ? theme.inputBg : theme.accentColor,
                      color: filters.priorities.includes('all') ? theme.textSecondary : 'white',
                      fontWeight: '600'
                    }}>
                      {filters.priorities.includes('all') ? 'All' : `${filters.priorities.length} selected`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: theme.textPrimary }}>
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes('all')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, priorities: ['all'] }));
                          } else {
                            setFilters(prev => ({ ...prev, priorities: [] }));
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: '600' }}>All Priorities</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: theme.textPrimary }}>
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(1)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newPriorities = filters.priorities.filter(p => p !== 'all');
                            setFilters(prev => ({ ...prev, priorities: [...newPriorities, 1] }));
                          } else {
                            setFilters(prev => ({ ...prev, priorities: prev.priorities.filter(p => p !== 1) }));
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        background: '#FEE2E2',
                        color: '#991B1B'
                      }}>
                        Priority 1 (High)
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: theme.textPrimary }}>
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(2)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newPriorities = filters.priorities.filter(p => p !== 'all');
                            setFilters(prev => ({ ...prev, priorities: [...newPriorities, 2] }));
                          } else {
                            setFilters(prev => ({ ...prev, priorities: prev.priorities.filter(p => p !== 2) }));
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        background: '#FEF3C7',
                        color: '#92400E'
                      }}>
                        Priority 2 (Medium)
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: theme.textPrimary }}>
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(3)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newPriorities = filters.priorities.filter(p => p !== 'all');
                            setFilters(prev => ({ ...prev, priorities: [...newPriorities, 3] }));
                          } else {
                            setFilters(prev => ({ ...prev, priorities: prev.priorities.filter(p => p !== 3) }));
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        background: '#D1FAE5',
                        color: '#065F46'
                      }}>
                        Priority 3 (Low)
                      </span>
                    </label>
                  </div>
                  {/* ✅ SAVED AREA FILTER */}
                  <div style={{ 
                    marginTop: '15px', 
                    paddingTop: '15px', 
                    borderTop: `1px solid ${theme.borderColor}` 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      marginBottom: '10px' 
                    }}>
                      <label style={{ 
                        fontSize: '0.85em', 
                        color: theme.textSecondary, 
                        fontWeight: '600' 
                      }}>
                        📍 Filter by Drawn Area
                      </label>
                      {polygonCoords && (
                        <span style={{
                          fontSize: '0.75em',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          background: '#10B981',
                          color: 'white',
                          fontWeight: '600'
                        }}>
                          Active
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                      <select
                        value={polygonCoords && currentPolygonName ? currentPolygonName : ''}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          if (!selectedName) {
                            setPolygonCoords(null);
                            setCurrentPolygonName('');
                            setPolygonFilterMode('none');
                          } else {
                            const polygon = savedPolygons.find(p => p.name === selectedName);
                            if (polygon) {
                              setPolygonCoords(polygon.coords);
                              setCurrentPolygonName(polygon.name);
                              setPolygonFilterMode('inside');
                            }
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: `2px solid ${polygonCoords ? '#10B981' : theme.inputBorder}`,
                          borderRadius: '6px',
                          background: theme.inputBg,
                          color: theme.textPrimary,
                          cursor: 'pointer',
                          fontSize: '0.85em'
                        }}
                      >
                        <option value="">All Areas</option>
                        {savedPolygons
                          .filter(p => p.filterEligible !== false)
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((polygon) => (
                            <option key={polygon.name} value={polygon.name}>
                              {polygon.name}
                            </option>
                          ))}
                      </select>
                      
                      {polygonCoords && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => setPolygonFilterMode('inside')}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: polygonFilterMode === 'inside' ? theme.accentColor : theme.detailBg,
                              color: polygonFilterMode === 'inside' ? 'white' : theme.textPrimary,
                              border: `2px solid ${polygonFilterMode === 'inside' ? theme.accentColor : theme.borderColor}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                          >
                            Inside
                          </button>
                          <button
                            onClick={() => setPolygonFilterMode('outside')}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: polygonFilterMode === 'outside' ? theme.accentColor : theme.detailBg,
                              color: polygonFilterMode === 'outside' ? 'white' : theme.textPrimary,
                              border: `2px solid ${polygonFilterMode === 'outside' ? theme.accentColor : theme.borderColor}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                          >
                            Outside
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {polygonCoords && currentPolygonName && (
                      <div style={{
                        padding: '6px 10px',
                        background: 'rgba(16, 185, 129, 0.1)',
                        color: '#10B981',
                        borderRadius: '6px',
                        fontSize: '0.75em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid rgba(16, 185, 129, 0.3)'
                      }}>
                        <span style={{ fontWeight: '600' }}>
                          {polygonFilterMode === 'inside' ? '✓' : '✗'} {currentPolygonName}
                        </span>
                        <button
                          onClick={() => {
                            setPolygonCoords(null);
                            setCurrentPolygonName('');
                            setPolygonFilterMode('none');
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            padding: '0 5px',
                            fontSize: '1.2em',
                            lineHeight: 1,
                            fontWeight: 'bold'
                          }}
                          title="Clear area filter"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    {/* Active Events Only Checkbox */}
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px solid ${theme.borderColor}` }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        cursor: 'pointer', 
                        color: theme.textPrimary,
                        fontSize: '0.85em',
                        padding: '8px',
                        background: filters.activeOnly ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                        borderRadius: '6px',
                        border: `1px solid ${filters.activeOnly ? 'rgba(16, 185, 129, 0.3)' : 'transparent'}`,
                        transition: 'all 0.2s'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={filters.activeOnly}
                          onChange={(e) => setFilters(prev => ({ ...prev, activeOnly: e.target.checked }))}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }} 
                        />
                        <span>Active Events Only</span>
                      </label>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          )}
        </div>

        {/* Summary Cards Panel - Collapsible */}
        <div
          {...sectionDragProps('kpi')}
          style={{
            background: theme.cardBg,
            borderRadius: '12px',
            padding: '0',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            ...sectionOrderStyle('kpi'),
          }}>
          {/* Header */}
          <div
            style={{
              fontSize: '1.05em',
              fontWeight: '600',
              padding: '10px 20px',
              color: theme.sectionLabelColor,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: allSectionsCollapsed ? 'grab' : 'pointer',
              userSelect: 'none'
            }}
            onClick={(e) => {
              // Allow expanding even in drag mode (will exit drag mode)
              e.stopPropagation(); // Prevent drag handling
              setIsKpiCollapsed(!isKpiCollapsed);
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
              📈 Summary Cards
            </span>
            <span style={{
              fontSize: '1em',
              color: theme.sectionLabelColor,
              transform: isKpiCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }}
              title={isKpiCollapsed ? 'Expand event cards' : 'Collapse event cards'}
            >
              ▼
            </span>
          </div>

          {/* Summary Cards - Drag and Drop */}
          {!isKpiCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="cad-kpi-scroll" style={{
                padding: '0 20px 20px',
                overflowY: 'auto',
                height: sectionHeights.kpi ? `${sectionHeights.kpi}px` : 'auto',
                minHeight: '120px',
              }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: kpiSize === 'small' 
                ? 'repeat(auto-fit, minmax(140px, 1fr))' 
                : kpiSize === 'large' 
                ? 'repeat(auto-fit, minmax(240px, 1fr))' 
                : 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '15px'
            }}>
          {(() => {
            // Define all KPIs with unique IDs
            const allKpis = [
              { id: 0, label: 'Pending', value: currentKpiData.pending, threshold: thresholds.pending },
              { id: 1, label: 'Dispatched', value: currentKpiData.dispatched, threshold: thresholds.dispatched },
              { id: 2, label: 'Enroute', value: currentKpiData.enroute, threshold: thresholds.enroute },
              { id: 3, label: 'Arrived', value: currentKpiData.arrived, threshold: thresholds.arrived },
              { id: 4, label: 'Closed', value: currentKpiData.closed, threshold: thresholds.closed },
              { id: 5, label: 'Total Events', value: currentKpiData.total, threshold: thresholds.total },
              { id: 6, label: 'Active Events', value: currentKpiData.active, threshold: thresholds.active },
              { 
                id: 7, 
                label: 'Pending Time', 
                value: formatTime(currentKpiData.pendingMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.pendingMedian,
                threshold: thresholds.pendingTime,
                warningPercentage: thresholds.pendingTimeWarning,
                boxPlotData: {
                  min: currentKpiData.pendingMin,
                  q1: currentKpiData.pending25th,
                  median: currentKpiData.pendingMedian,
                  q3: currentKpiData.pending75th,
                  p90: currentKpiData.pending90th,
                  p95: currentKpiData.pending95th,
                  max: currentKpiData.pendingMax
                },
                subtitle: 'Received → Dispatched' 
              },
              { 
                id: 8, 
                label: 'Response Time', 
                value: formatTime(currentKpiData.responseMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.responseMedian,
                threshold: thresholds.responseTime,
                warningPercentage: thresholds.responseTimeWarning,
                boxPlotData: {
                  min: currentKpiData.responseMin,
                  q1: currentKpiData.response25th,
                  median: currentKpiData.responseMedian,
                  q3: currentKpiData.response75th,
                  p90: currentKpiData.response90th,
                  p95: currentKpiData.response95th,
                  max: currentKpiData.responseMax
                },
                subtitle: 'Dispatched → Arrived' 
              },
              { 
                id: 9, 
                label: 'On Scene Time', 
                value: formatTime(currentKpiData.onSceneMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.onSceneMedian,
                threshold: thresholds.onSceneTime,
                warningPercentage: thresholds.onSceneTimeWarning,
                boxPlotData: {
                  min: currentKpiData.onSceneMin,
                  q1: currentKpiData.onScene25th,
                  median: currentKpiData.onSceneMedian,
                  q3: currentKpiData.onScene75th,
                  p90: currentKpiData.onScene90th,
                  p95: currentKpiData.onScene95th,
                  max: currentKpiData.onSceneMax
                },
                subtitle: 'Arrived → Completed' 
              },
              { 
                id: 10, 
                label: 'Turnout Time', 
                value: formatTime(currentKpiData.responseDelayMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.responseDelayMedian,
                threshold: thresholds.turnoutTime,
                warningPercentage: thresholds.turnoutTimeWarning,
                boxPlotData: {
                  min: currentKpiData.responseDelayMin,
                  q1: currentKpiData.responseDelay25th,
                  median: currentKpiData.responseDelayMedian,
                  q3: currentKpiData.responseDelay75th,
                  p90: currentKpiData.responseDelay90th,
                  p95: currentKpiData.responseDelay95th,
                  max: currentKpiData.responseDelayMax
                },
                subtitle: 'Dispatched → Enroute' 
              },
              { 
                id: 11, 
                label: 'Travel Time', 
                value: formatTime(currentKpiData.travelMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.travelMedian,
                threshold: thresholds.travelTime,
                warningPercentage: thresholds.travelTimeWarning,
                boxPlotData: {
                  min: currentKpiData.travelMin,
                  q1: currentKpiData.travel25th,
                  median: currentKpiData.travelMedian,
                  q3: currentKpiData.travel75th,
                  p90: currentKpiData.travel90th,
                  p95: currentKpiData.travel95th,
                  max: currentKpiData.travelMax
                },
                subtitle: 'Enroute → Arrived' 
              },
              { 
                id: 12, 
                label: 'Event Total Time', 
                value: formatTime(currentKpiData.eventTotalMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.eventTotalMedian,
                threshold: thresholds.eventTotalTime,
                warningPercentage: thresholds.eventTotalTimeWarning,
                boxPlotData: {
                  min: currentKpiData.eventTotalMin,
                  q1: currentKpiData.eventTotal25th,
                  median: currentKpiData.eventTotalMedian,
                  q3: currentKpiData.eventTotal75th,
                  p90: currentKpiData.eventTotal90th,
                  p95: currentKpiData.eventTotal95th,
                  max: currentKpiData.eventTotalMax
                },
                subtitle: 'Received → Completed' 
              },
              { 
                id: 13, 
                label: 'Event Creation Time', 
                value: formatTime(currentKpiData.eventCreationMedian),
                isTimeWithDistribution: true,
                medianValue: currentKpiData.eventCreationMedian,
                threshold: thresholds.eventCreationTime,
                warningPercentage: thresholds.eventCreationTimeWarning,
                boxPlotData: {
                  min: currentKpiData.eventCreationMin,
                  q1: currentKpiData.eventCreation25th,
                  median: currentKpiData.eventCreationMedian,
                  q3: currentKpiData.eventCreation75th,
                  p90: currentKpiData.eventCreation90th,
                  p95: currentKpiData.eventCreation95th,
                  max: currentKpiData.eventCreationMax
                },
                subtitle: 'Event Start → Received' 
              }
            ];

            // Render KPIs in custom order, filtering by visibility
            return kpiOrder
              .filter(kpiId => visibleKpis.includes(kpiId))
              .map((kpiId, displayIndex) => {
              const kpi = allKpis.find(k => k.id === kpiId);
              if (!kpi) return null;

              // Check threshold status
              // For time-based KPIs, use medianValue (seconds), for others use value (count)
              const thresholdValue = kpi.isTimeWithDistribution ? kpi.medianValue : kpi.value;
              const isOverThreshold = kpi.threshold && thresholdValue > kpi.threshold;
              const isAtThreshold = kpi.threshold && thresholdValue === kpi.threshold;
              
              // ✅ Get size configuration based on kpiSize setting
              const sizeConfig = {
                small: {
                  padding: '8px',
                  labelSize: '0.7em',
                  valueSize: '1.2em',
                  subtitleSize: '0.6em',
                  avgLabelSize: '0.6em',
                  barTextSize: '0.6em'
                },
                medium: {
                  padding: '12px',
                  labelSize: '0.8em',
                  valueSize: '1.4em',
                  subtitleSize: '0.65em',
                  avgLabelSize: '0.65em',
                  barTextSize: '0.65em'
                },
                large: {
                  padding: '16px',
                  labelSize: '0.95em',
                  valueSize: '1.8em',
                  subtitleSize: '0.75em',
                  avgLabelSize: '0.75em',
                  barTextSize: '0.75em'
                }
              };
              
              const sizes = sizeConfig[kpiSize];
              const isDragging = draggedKpi === displayIndex;

              // Determine colors based on threshold status
              let cardBg, borderColor, textColor, labelColor, warningText;
              
              // For time-based KPIs, don't use thick borders - just background color
              const useNormalBorder = kpi.isTimeWithDistribution;
              
              if (isOverThreshold) {
                // Red for over threshold
                cardBg = '#FEE2E2';
                borderColor = useNormalBorder ? `1px solid ${theme.borderColor}` : '3px solid #EF4444';
                textColor = '#DC2626';
                labelColor = '#991B1B';
                warningText = '⚠️ Over threshold';
              } else if (isAtThreshold) {
                // Yellow for at threshold
                cardBg = '#FEF3C7';
                borderColor = useNormalBorder ? `1px solid ${theme.borderColor}` : '3px solid #F59E0B';
                textColor = '#D97706';
                labelColor = '#92400E';
                warningText = '⚠️ At threshold';
              } else {
                // Normal colors
                cardBg = theme.cardBg;
                borderColor = `1px solid ${theme.borderColor}`;
                textColor = theme.textPrimary;
                labelColor = theme.textSecondary;
                warningText = null;
              }

              return (
                <div
                  key={kpi.id}
                  draggable="true"
                  onDragStart={(e) => handleKpiDragStart(e, displayIndex)}
                  onDragOver={(e) => handleKpiDragOver(e, displayIndex)}
                  onDrop={(e) => handleKpiDrop(e, displayIndex)}
                  onDragEnd={handleKpiDragEnd}
                  style={{
                    background: cardBg,
                    border: dragOverKpi === displayIndex && draggedKpi !== displayIndex 
                      ? `3px solid ${theme.accentColor}` 
                      : borderColor,
                    borderRadius: '12px',
                    padding: sizes.padding,
                    textAlign: 'center',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    cursor: 'move',
                    opacity: isDragging ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    userSelect: 'none',
                    transform: dragOverKpi === displayIndex && draggedKpi !== displayIndex 
                      ? 'scale(1.02)' 
                      : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDragging) {
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDragging) {
                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* Top bar with drag handle and close button */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px'
                  }}>
                    {/* Drag Handle Indicator */}
                    <div style={{
                      fontSize: '0.8em',
                      color: theme.textSecondary,
                      opacity: 0.5,
                      fontWeight: '700',
                      letterSpacing: '2px',
                      cursor: 'move'
                    }}>
                      ⋮⋮
                    </div>
                    
                    {/* Close button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisibleKpis(prev => prev.filter(id => id !== kpi.id));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this card (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={{
                    fontSize: sizes.labelSize,
                    color: labelColor,
                    fontWeight: '600',
                    marginBottom: '5px',
                    textTransform: 'uppercase'
                  }}>
                    {kpi.label}
                  </div>
                  
                  {/* Render time with distribution, horizontal bars, multi-value, or single value */}
                  {kpi.isTimeWithDistribution ? (() => {
                    const data = kpi.boxPlotData;
                    const maxValue = Math.max(data.median, data.q3, data.p90, data.p95);
                    
                    // Helper to calculate bar width percentage
                    const getWidth = (value) => {
                      return maxValue > 0 ? (value / maxValue) * 100 : 0;
                    };
                    
                    // Calculate threshold color for time text
                    let timeTextColor = textColor;
                    let showWarningIcon = false;
                    if (kpi.threshold && kpi.medianValue) {
                      const percentOfThreshold = (kpi.medianValue / kpi.threshold) * 100;
                      const warningThreshold = kpi.warningPercentage || 90; // Default to 90 if not set
                      
                      if (percentOfThreshold >= 100) {
                        // Red if at or over threshold
                        timeTextColor = '#DC2626';
                      } else if (percentOfThreshold >= warningThreshold) {
                        // Yellow if at or above warning percentage
                        timeTextColor = '#D97706';
                        showWarningIcon = true;
                      }
                    }
                    
                    return (
                      <div>
                        {/* Median value prominently displayed */}
                        <div style={{
                          fontSize: sizes.valueSize,
                          fontWeight: '700',
                          color: timeTextColor,
                          marginBottom: '2px',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}>
                          {kpi.value}
                          {showWarningIcon && (
                            <span style={{ 
                              fontSize: '0.7em',
                              color: '#D97706'
                            }}>
                              ⚠️
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: sizes.avgLabelSize,
                          color: theme.textSecondary,
                          textAlign: 'center',
                          marginBottom: '8px'
                        }}>
                          Median
                        </div>
                        
                        {/* Percentile distribution bars - always shown */}
                        <div style={{ marginTop: '6px', marginBottom: '3px' }}>
                          
                          {/* 75th */}
                          <div style={{ marginBottom: '5px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px',
                              fontSize: sizes.barTextSize,
                              color: labelColor,
                              marginBottom: '2px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.9em' }}>75%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '8px', 
                                backgroundColor: theme.detailBg,
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${getWidth(data.q3)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#9CA3AF' : '#60A5FA',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '600', 
                                minWidth: '38px',
                                textAlign: 'right',
                                fontSize: '0.95em'
                              }}>
                                {formatTime(data.q3)}
                              </span>
                            </div>
                          </div>
                          
                          {/* 90th */}
                          <div style={{ marginBottom: '5px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px',
                              fontSize: sizes.barTextSize,
                              color: labelColor,
                              marginBottom: '2px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.9em' }}>90%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '8px', 
                                backgroundColor: theme.detailBg,
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${getWidth(data.p90)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#6B7280' : '#2563EB',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '600', 
                                minWidth: '38px',
                                textAlign: 'right',
                                fontSize: '0.95em'
                              }}>
                                {formatTime(data.p90)}
                              </span>
                            </div>
                          </div>
                          
                          {/* 95th */}
                          <div style={{ marginBottom: '3px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px',
                              fontSize: sizes.barTextSize,
                              color: labelColor,
                              marginBottom: '2px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.9em' }}>95%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '8px', 
                                backgroundColor: theme.detailBg,
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${getWidth(data.p95)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#374151' : '#1E40AF',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '600', 
                                minWidth: '38px',
                                textAlign: 'right',
                                fontSize: '0.95em'
                              }}>
                                {formatTime(data.p95)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })() : kpi.isBoxPlot ? (() => {
                    const data = kpi.boxPlotData;
                    const maxValue = Math.max(data.median, data.q3, data.p90);
                    
                    // Helper to calculate bar width percentage
                    const getWidth = (value) => {
                      return maxValue > 0 ? (value / maxValue) * 100 : 0;
                    };
                    
                    return (
                      <div style={{ marginTop: '8px', marginBottom: '5px' }}>
                        
                        {/* 75th Percentile */}
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontSize: '0.7em',
                            color: labelColor,
                            marginBottom: '3px'
                          }}>
                            <span style={{ fontWeight: '600', minWidth: '45px' }}>75th</span>
                            <div style={{ 
                              flex: 1, 
                              height: '10px', 
                              backgroundColor: theme.detailBg,
                              borderRadius: '5px',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${getWidth(data.q3)}%`,
                                height: '100%',
                                backgroundColor: isDarkTheme ? '#9CA3AF' : '#60A5FA',
                                borderRadius: '5px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{ 
                              fontWeight: '700', 
                              minWidth: '50px',
                              textAlign: 'right',
                              color: textColor,
                              fontSize: '1.1em'
                            }}>
                              {formatTime(data.q3)}
                            </span>
                          </div>
                        </div>
                        
                        {/* 90th Percentile */}
                        <div style={{ marginBottom: '5px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontSize: '0.7em',
                            color: labelColor,
                            marginBottom: '3px'
                          }}>
                            <span style={{ fontWeight: '600', minWidth: '45px' }}>90th</span>
                            <div style={{ 
                              flex: 1, 
                              height: '10px', 
                              backgroundColor: theme.detailBg,
                              borderRadius: '5px',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${getWidth(data.p90)}%`,
                                height: '100%',
                                backgroundColor: isDarkTheme ? '#6B7280' : '#2563EB',
                                borderRadius: '5px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{ 
                              fontWeight: '700', 
                              minWidth: '50px',
                              textAlign: 'right',
                              color: textColor,
                              fontSize: '1.1em'
                            }}>
                              {formatTime(data.p90)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Range info */}
                        <div style={{
                          fontSize: '0.6em',
                          color: theme.textSecondary,
                          marginTop: '6px',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          Range: {formatTime(data.min)} – {formatTime(data.max)}
                        </div>
                      </div>
                    );
                  })() : kpi.isMultiValue ? (
                    <div style={{ display: 'flex', justifyContent: 'space-around', gap: '8px' }}>
                      {kpi.values.map((v, idx) => (
                        <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                          <div style={{
                            fontSize: '0.7em',
                            color: labelColor,
                            fontWeight: '600',
                            marginBottom: '2px'
                          }}>
                            {v.label}
                          </div>
                          <div style={{
                            fontSize: '1.2em',
                            fontWeight: '700',
                            color: textColor
                          }}>
                            {v.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: sizes.valueSize,
                      fontWeight: '700',
                      color: textColor
                    }}>
                      {kpi.value}
                    </div>
                  )}
                  
                  {/* Progress Bar - Only show if threshold is set AND not a time KPI */}
                  {kpi.threshold && !kpi.isTimeWithDistribution && (() => {
                    // Calculate percentage of threshold
                    const percentage = (kpi.value / kpi.threshold) * 100;
                    
                    // Determine bar color based on percentage
                    let barColor;
                    if (isOverThreshold) {
                      barColor = '#EF4444'; // Red - over threshold
                    } else if (percentage >= 90) {
                      barColor = '#F97316'; // Orange - 90%+ of threshold
                    } else if (percentage >= 75) {
                      barColor = '#FBBF24'; // Yellow - 75%+ of threshold
                    } else {
                      barColor = '#10B981'; // Green - below 75%
                    }
                    
                    return (
                      <div style={{ marginTop: '6px', marginBottom: '3px' }}>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: theme.detailBg,
                          borderRadius: '3px',
                          overflow: 'hidden',
                          border: `1px solid ${theme.borderColor}`
                        }}>
                          <div style={{
                            width: `${Math.min(percentage, 100)}%`,
                            height: '100%',
                            backgroundColor: barColor,
                            transition: 'width 0.3s ease, background-color 0.3s ease',
                            borderRadius: '2px'
                          }} />
                        </div>
                        <div style={{
                          fontSize: '0.65em',
                          color: theme.textSecondary,
                          marginTop: '2px',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span>0</span>
                          <span style={{ fontWeight: '600' }}>
                            {kpi.value} / {kpi.threshold}
                          </span>
                          <span>{kpi.threshold}</span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {kpi.subtitle && (
                    <div style={{
                      fontSize: sizes.subtitleSize,
                      color: theme.textSecondary,
                      marginTop: '3px'
                    }}>
                      {kpi.subtitle}
                    </div>
                  )}
                  {warningText && !kpi.isTimeWithDistribution && (
                    <div style={{
                      fontSize: '0.7em',
                      color: labelColor,
                      marginTop: '3px',
                      fontWeight: '600'
                    }}>
                      {warningText}
                    </div>
                  )}
                </div>
              );
            });
          })()}
            </div>
            </div>
              <SectionResizeHandle sectionId="kpi" resizingSection={resizingSection} theme={theme} onResizeStart={handleSectionResizeStart} onReset={resetSectionHeight} />
            </div>
          )}
        </div>

        {/* Map Section with Layers Drawer */}
        <div
          {...sectionDragProps('map')}
          style={{
            background: theme.cardBg,
            borderRadius: '12px',
            padding: '0',
            marginBottom: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...sectionOrderStyle('map'),
          }}>
          <div style={{
            fontSize: '1.05em',
            fontWeight: '600',
            padding: '10px 20px',
            color: theme.sectionLabelColor,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* Left side - Map title and auto-recenter (only when expanded) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
                📍 Event Map
              </span>
              
              {/* Map control buttons - only show when expanded */}
              {!isMapCollapsed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Layers Panel button - always visible */}
                  <button
                    onClick={() => setShowLayerPanel(!showLayerPanel)}
                    style={{
                      padding: '6px 12px',
                      background: theme.inputBg,
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75em',
                      color: theme.textPrimary,
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.accentColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = theme.inputBg;
                      e.currentTarget.style.color = theme.textPrimary;
                    }}
                    title={showLayerPanel ? "Collapse layers panel" : "Expand layers panel"}
                  >
                    🗺️ Layers
                    <span style={{ fontSize: '1em' }}>
                      {showLayerPanel ? '◀' : '▶'}
                    </span>
                  </button>
                  
                  {/* Return to Home button */}
                  <button
                    onClick={returnToHomeLocation}
                    style={{
                      padding: '6px 12px',
                      background: theme.inputBg,
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75em',
                      color: theme.textPrimary,
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.accentColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = theme.inputBg;
                      e.currentTarget.style.color = theme.textPrimary;
                    }}
                    title={`Return to home location (Lat: ${defaultMapLocation.lat.toFixed(4)}, Lng: ${defaultMapLocation.lng.toFixed(4)}, Zoom: ${defaultMapLocation.zoom})`}
                  >
                    🏠 Home
                  </button>
                  
                  {/* Save as Home button */}
                  <button
                    onClick={saveCurrentMapLocationAsDefault}
                    style={{
                      padding: '6px 12px',
                      background: theme.inputBg,
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75em',
                      color: theme.textPrimary,
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.accentColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = theme.inputBg;
                      e.currentTarget.style.color = theme.textPrimary;
                    }}
                    title="Save current map view as default home location"
                  >
                    💾 Set Home
                  </button>
                </div>
              )}
              
              {/* Auto-recenter checkbox - only show when expanded */}
              {!isMapCollapsed && (
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '5px',
                  fontSize: '0.75em',
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  userSelect: 'none'
                }}>
                  <input
                    type="checkbox"
                    checked={autoRecenterMap}
                    onChange={(e) => setAutoRecenterMap(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Auto-recenter
                </label>
              )}
            </div>
            
            {/* Right side - Controls and collapse button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              
              {/* Error/marker count - only show when expanded */}
              {!isMapCollapsed && (
                <>
                  {mapError && (
                    <span style={{ fontSize: '0.7em', color: '#EF4444' }}>
                      Map Error: {mapError}
                    </span>
                  )}
                  {!mapError && filteredData.length > 0 && chartHeaderStats.mappableCount > 0 && (
                      <span style={{ fontSize: '0.7em', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: theme.accentColor, fontWeight: '600' }}>
                          {chartHeaderStats.mappableCount} on map
                        </span>
                        {chartHeaderStats.unmappableCount > 0 && (
                          <span style={{ color: theme.textSecondary }}>
                            • {chartHeaderStats.unmappableCount} not mapped
                          </span>
                        )}
                      </span>
                  )}
                </>
              )}
              
              {/* Collapse/Expand button - always visible, on right side */}
              <span 
                onClick={(e) => {
                  // Allow expanding even in drag mode (will exit drag mode)
                  e.stopPropagation(); // Prevent drag handling
                  setIsMapCollapsed(!isMapCollapsed);
                }}
                style={{
                  fontSize: '1em',
                  color: theme.sectionLabelColor,
                  transform: isMapCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  cursor: allSectionsCollapsed ? 'grab' : 'pointer',
                  userSelect: 'none'
                }}
                title={isMapCollapsed ? 'Expand map' : 'Collapse map'}
              >
                ▼
              </span>
            </div>
          </div>
          
          {/* Flex container for drawer and map */}
          <div style={{ 
            display: 'flex',
            gap: '0',
            transition: 'all 0.3s ease',
            position: 'relative'
          }}>
            {/* Layers Drawer - overlays map on tablet, pushes on desktop */}
            {!isMapCollapsed && (
              <div className={isTablet && showLayerPanel ? 'cad-layer-overlay' : ''}
                style={{
                width: showLayerPanel ? '280px' : '0',
                minWidth: showLayerPanel ? (isTablet ? '0' : '280px') : '0',
                maxWidth: isTablet ? '280px' : 'none',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                background: theme.cardBg,
                borderRight: showLayerPanel ? `2px solid ${theme.borderColor}` : 'none',
                borderRadius: showLayerPanel ? '8px 0 0 8px' : '0'
              }}>
                {showLayerPanel && (
                  <>
                    {/* Drawer Header */}
                    <div style={{
                      padding: '16px 20px',
                      borderBottom: `2px solid ${theme.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: theme.detailBg
                    }}>
                      <div style={{
                        fontSize: '1em',
                        fontWeight: '600',
                        color: theme.textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        🗺️ Map Layers
                      </div>
                      <button
                        onClick={() => setShowLayerPanel(false)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: theme.textSecondary,
                          cursor: 'pointer',
                          fontSize: '1.4em',
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme.detailBg;
                          e.currentTarget.style.color = theme.textPrimary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = theme.textSecondary;
                        }}
                        title="Collapse layers panel"
                      >
                        ◀
                      </button>
                    </div>
                    
                    {/* Drawer Content */}
                    <div style={{
                      padding: '20px',
                      background: theme.detailBg
                    }}>
                      {/* Standard Layers Dropdown */}
                      <div style={{ 
                        marginBottom: '16px',
                        padding: '10px', 
                        background: theme.detailBg,
                        border: `2px solid ${theme.borderColor}`,
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }}>
                        {/* Dropdown Header */}
                        <div
                          onClick={() => setShowStandardLayersDropdown(!showStandardLayersDropdown)}
                          style={{
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: showStandardLayersDropdown ? '10px' : '0',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <label style={{ 
                            fontSize: '0.85em', 
                            color: theme.textSecondary, 
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ 
                              fontSize: '0.8em',
                              transition: 'transform 0.2s',
                              transform: showStandardLayersDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block'
                            }}>
                              ▶
                            </span>
                            📍 Event Layers
                          </label>
                        </div>
                        
                        {/* Dropdown Content */}
                        {showStandardLayersDropdown && (
                          <div style={{ padding: '4px 0 0 0' }}>

                            {/* Event Markers */}
                            <div style={{
                              marginBottom: '4px',
                              padding: '6px 8px',
                              background: theme.cardBg,
                              borderRadius: '4px',
                              border: `1px solid ${theme.borderColor}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <input
                                type="checkbox"
                                checked={mapLayers.incidents}
                                onChange={(e) => setMapLayers(prev => ({
                                  ...prev,
                                  incidents: e.target.checked,
                                  clusters: e.target.checked ? prev.clusters : false
                                }))}
                                style={{ cursor: 'pointer', width: '14px', height: '14px', flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: '600', fontSize: '0.85em', color: theme.textPrimary, flex: 1 }}>📍 Event Markers</span>
                            </div>

                            {/* Cluster Markers — indented, dependent on incidents */}
                            <div style={{
                              marginBottom: '4px',
                              marginLeft: '16px',
                              padding: '6px 8px',
                              background: theme.cardBg,
                              borderRadius: '4px',
                              border: `1px solid ${theme.borderColor}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              opacity: mapLayers.incidents ? 1 : 0.4,
                              transition: 'opacity 0.2s'
                            }}>
                              <input
                                type="checkbox"
                                checked={mapLayers.clusters}
                                disabled={!mapLayers.incidents}
                                onChange={(e) => setMapLayers(prev => ({ ...prev, clusters: e.target.checked }))}
                                style={{ cursor: mapLayers.incidents ? 'pointer' : 'not-allowed', width: '14px', height: '14px', flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: '600', fontSize: '0.85em', color: mapLayers.incidents ? theme.textPrimary : theme.textSecondary, flex: 1 }}>🎯 Cluster Markers</span>
                            </div>

                            {/* Heat Map */}
                            <div style={{
                              marginBottom: '4px',
                              padding: '6px 8px',
                              background: theme.cardBg,
                              borderRadius: '4px',
                              border: `1px solid ${theme.borderColor}`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <input
                                type="checkbox"
                                checked={mapLayers.heatmap}
                                onChange={(e) => setMapLayers(prev => ({ ...prev, heatmap: e.target.checked }))}
                                style={{ cursor: 'pointer', width: '14px', height: '14px', flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: '600', fontSize: '0.85em', color: theme.textPrimary, flex: 1 }}>🔥 Heat Map</span>
                            </div>

                          </div>
                        )}
                      </div>
                      
                      {/* Drawn Areas Dropdown */}
                      <div style={{ 
                        marginBottom: '16px',
                        padding: '10px', 
                        background: theme.detailBg,
                        border: `2px solid ${theme.borderColor}`,
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }}>
                        {/* Dropdown Header */}
                        <div
                          style={{
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: showPolygonsDropdown ? '10px' : '0',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <div 
                            onClick={() => setShowPolygonsDropdown(!showPolygonsDropdown)}
                            style={{ 
                              fontSize: '0.85em', 
                              color: theme.textSecondary, 
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              flex: 1
                            }}>
                            <span style={{ 
                              fontSize: '0.8em',
                              transition: 'transform 0.2s',
                              transform: showPolygonsDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block'
                            }}>
                              ▶
                            </span>
                            📐 Drawn Areas
                            {savedPolygons.length > 0 && (() => {
                              const visibleCount = selectedPolygonIds.length;
                              return (
                                <>
                                  <span style={{
                                    background: theme.accentColor,
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.85em',
                                    marginLeft: '4px'
                                  }}>
                                    {visibleCount}/{savedPolygons.length}
                                  </span>
                                  {visibleCount > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setSelectedPolygonIds([]);
                                      }}
                                      style={{
                                        padding: '2px 8px',
                                        background: '#EF4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        fontSize: '0.75em',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                                      onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                                      title="Hide all drawn areas"
                                    >
                                      Hide All
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Dropdown Content */}
                        {showPolygonsDropdown && (
                          <div style={{
                            padding: '12px',
                            background: theme.detailBg
                          }}>
                            {/* View Mode Toggle */}
                            <div style={{
                              display: 'flex',
                              gap: '4px',
                              marginBottom: '8px',
                              background: theme.cardBg,
                              padding: '3px',
                              borderRadius: '4px',
                              border: `1px solid ${theme.borderColor}`
                            }}>
                              <button
                                onClick={() => setDrawnAreasViewMode('list')}
                                style={{
                                  flex: 1,
                                  padding: '4px 6px',
                                  background: drawnAreasViewMode === 'list' ? theme.accentColor : 'transparent',
                                  color: drawnAreasViewMode === 'list' ? 'white' : theme.textPrimary,
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                              >
                                📋 List
                              </button>
                              <button
                                onClick={() => setDrawnAreasViewMode('manage')}
                                style={{
                                  flex: 1,
                                  padding: '4px 6px',
                                  background: drawnAreasViewMode === 'manage' ? theme.accentColor : 'transparent',
                                  color: drawnAreasViewMode === 'manage' ? 'white' : theme.textPrimary,
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                              >
                                ⚙️ Manage
                              </button>
                            </div>

                            {/* Draw New Area Button - only in manage mode */}
                            {drawnAreasViewMode === 'manage' && (
                              <button
                              onClick={startDrawingPolygon}
                              disabled={isDrawingPolygon}
                              style={{
                                width: '100%',
                                padding: '10px',
                                marginBottom: '12px',
                                background: isDrawingPolygon ? theme.detailBg : theme.accentColor,
                                color: isDrawingPolygon ? theme.textSecondary : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: isDrawingPolygon ? 'not-allowed' : 'pointer',
                                fontSize: '0.9em',
                                fontWeight: '600',
                                opacity: isDrawingPolygon ? 0.5 : 1,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isDrawingPolygon) e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                if (!isDrawingPolygon) e.currentTarget.style.opacity = '1';
                              }}
                              title="Draw a new polygon area"
                            >
                              {isDrawingPolygon ? '📐 Drawing... (click map to add points)' : '📐 Draw New Area'}
                            </button>
                            )}
                            
                            {/* Draw Radius Button - only in manage mode */}
                            {drawnAreasViewMode === 'manage' && (
                            <button
                              onClick={startDrawingRadius}
                              disabled={isDrawingRadius || isDrawingPolygon}
                              style={{
                                width: '100%',
                                padding: '10px',
                                marginBottom: '12px',
                                background: isDrawingRadius ? theme.detailBg : '#10B981',
                                color: isDrawingRadius ? theme.textSecondary : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (isDrawingRadius || isDrawingPolygon) ? 'not-allowed' : 'pointer',
                                fontSize: '0.9em',
                                fontWeight: '600',
                                opacity: (isDrawingRadius || isDrawingPolygon) ? 0.5 : 1,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isDrawingRadius && !isDrawingPolygon) e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                if (!isDrawingRadius && !isDrawingPolygon) e.currentTarget.style.opacity = '1';
                              }}
                              title="Click a point to create a radius area"
                            >
                              {isDrawingRadius ? '🎯 Click map to place radius...' : '🎯 Draw Radius'}
                            </button>
                            )}
                            
                            {/* Radius Controls - Show only when radius mode is active */}
                            {drawnAreasViewMode === 'manage' && isDrawingRadius && (
                              <div style={{
                                marginBottom: '12px',
                                padding: '12px',
                                background: theme.cardBg,
                                borderRadius: '6px',
                                border: `2px solid ${theme.accentColor}`,
                                overflow: 'hidden'
                              }}>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.85em',
                                  fontWeight: '600',
                                  color: theme.textPrimary,
                                  marginBottom: '8px'
                                }}>
                                  Radius Size
                                </label>
                                
                                {/* Radius Value Slider */}
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '10px', 
                                  marginBottom: '8px',
                                  width: '100%'
                                }}>
                                  <input
                                    type="range"
                                    min="0.1"
                                    max="10"
                                    step="0.1"
                                    value={radiusValue}
                                    onChange={(e) => setRadiusValue(parseFloat(e.target.value))}
                                    style={{
                                      flex: 1,
                                      cursor: 'pointer',
                                      minWidth: '0'
                                    }}
                                  />
                                  <input
                                    type="number"
                                    min="0.1"
                                    max="100"
                                    step="0.1"
                                    value={radiusValue}
                                    onChange={(e) => setRadiusValue(parseFloat(e.target.value) || 0.1)}
                                    style={{
                                      width: '70px',
                                      padding: '6px',
                                      border: `1px solid ${theme.borderColor}`,
                                      borderRadius: '4px',
                                      background: theme.inputBg,
                                      color: theme.textPrimary,
                                      fontSize: '0.9em',
                                      textAlign: 'center',
                                      boxSizing: 'border-box',
                                      flexShrink: 0
                                    }}
                                  />
                                </div>
                                
                                {/* Unit Toggle */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <button
                                    onClick={() => setRadiusUnit('miles')}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: radiusUnit === 'miles' ? theme.accentColor : theme.detailBg,
                                      color: radiusUnit === 'miles' ? 'white' : theme.textPrimary,
                                      border: `2px solid ${radiusUnit === 'miles' ? theme.accentColor : theme.borderColor}`,
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    Miles
                                  </button>
                                  <button
                                    onClick={() => setRadiusUnit('km')}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: radiusUnit === 'km' ? theme.accentColor : theme.detailBg,
                                      color: radiusUnit === 'km' ? 'white' : theme.textPrimary,
                                      border: `2px solid ${radiusUnit === 'km' ? theme.accentColor : theme.borderColor}`,
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    Kilometers
                                  </button>
                                </div>
                                
                                <div style={{
                                  marginTop: '8px',
                                  fontSize: '0.75em',
                                  color: theme.textSecondary,
                                  textAlign: 'center'
                                }}>
                                  {radiusValue} {radiusUnit}
                                </div>
                              </div>
                            )}
                            
                            {drawnAreasViewMode === 'manage' && isDrawingPolygon && (
                              <button
                                onClick={cancelDrawing}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  marginBottom: '12px',
                                  background: '#EF4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                title="Cancel drawing"
                              >
                                ✕ Cancel Drawing
                              </button>
                            )}
                            
                            {drawnAreasViewMode === 'manage' && isDrawingRadius && (
                              <button
                                onClick={cancelDrawingRadius}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  marginBottom: '12px',
                                  background: '#EF4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                title="Cancel radius drawing"
                              >
                                ✕ Cancel Radius
                              </button>
                            )}
                            
                            {/* Show Polygon Names Toggle - only in manage mode */}
                            {drawnAreasViewMode === 'manage' && selectedPolygonIds.length > 0 && (
                              <button
                                onClick={() => setShowPolygonNames(!showPolygonNames)}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  marginBottom: '12px',
                                  background: showPolygonNames ? theme.accentColor : theme.cardBg,
                                  color: showPolygonNames ? 'white' : theme.textPrimary,
                                  border: `2px solid ${showPolygonNames ? theme.accentColor : theme.borderColor}`,
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                  if (!showPolygonNames) {
                                    e.currentTarget.style.background = theme.hoverBg;
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!showPolygonNames) {
                                    e.currentTarget.style.background = theme.cardBg;
                                  }
                                }}
                                title={showPolygonNames ? 'Hide polygon names on map' : 'Show polygon names on map'}
                              >
                                {showPolygonNames ? '👁️' : '👁️‍🗨️'} {showPolygonNames ? 'Hide' : 'Show'} Polygon Names
                              </button>
                            )}
                            
                            {/* Active polygon filter/save options - only in manage mode */}
                            {drawnAreasViewMode === 'manage' && polygonCoords && (
                              <div style={{
                                marginBottom: '12px',
                                padding: '10px',
                                background: `linear-gradient(135deg, ${theme.cardBg} 0%, ${theme.isDark ? 'rgba(20, 184, 166, 0.05)' : 'rgba(20, 184, 166, 0.08)'} 100%)`,
                                borderRadius: '6px',
                                border: `2px solid ${theme.accentColor}`,
                                boxShadow: `0 0 0 4px ${theme.isDark ? 'rgba(20, 184, 166, 0.1)' : 'rgba(20, 184, 166, 0.15)'}, 0 4px 12px rgba(0, 0, 0, 0.1)`,
                                position: 'relative',
                                animation: 'activePolygonPulse 2s ease-in-out infinite'
                              }}>
                                {/* Active indicator badge */}
                                <div style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '10px',
                                  background: theme.accentColor,
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.7em',
                                  fontWeight: '700',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Active
                                </div>
                                <div style={{
                                  fontSize: '0.8em',
                                  fontWeight: '600',
                                  marginBottom: '8px',
                                  color: theme.textPrimary
                                }}>
                                  Active Polygon: {currentPolygonName || 'Unnamed'}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <button
                                    onClick={() => applyPolygonFilter('inside')}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: polygonFilterMode === 'inside' ? '#10B981' : theme.inputBg,
                                      color: polygonFilterMode === 'inside' ? 'white' : theme.textPrimary,
                                      border: `2px solid ${polygonFilterMode === 'inside' ? '#10B981' : theme.inputBorder}`,
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    title="Show only events inside"
                                  >
                                    ⬇️ Inside
                                  </button>
                                  <button
                                    onClick={() => applyPolygonFilter('outside')}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: polygonFilterMode === 'outside' ? '#F59E0B' : theme.inputBg,
                                      color: polygonFilterMode === 'outside' ? 'white' : theme.textPrimary,
                                      border: `2px solid ${polygonFilterMode === 'outside' ? '#F59E0B' : theme.inputBorder}`,
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    title="Show only events outside"
                                  >
                                    ⬆️ Outside
                                  </button>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={saveCurrentPolygon}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: '#14B8A6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    title="Save this polygon"
                                  >
                                    💾 Save
                                  </button>
                                  <button
                                    onClick={clearPolygon}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: '#EF4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.8em',
                                      fontWeight: '600',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    title="Clear polygon"
                                  >
                                    🗑️ Clear
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {/* List of saved polygons */}
                            {savedPolygons.length === 0 ? (
                              <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: theme.textSecondary,
                                fontSize: '0.85em',
                                fontStyle: 'italic'
                              }}>
                                No saved polygons yet.<br/>
                                Click "Draw New Area" to create one.
                              </div>
                            ) : (
                              <>
                                <div style={{
                                  fontSize: '0.8em',
                                  fontWeight: '600',
                                  marginBottom: '8px',
                                  color: theme.textSecondary
                                }}>
                                  Saved Areas:
                                </div>
                                
                                {/* Search box */}
                                <div style={{ position: 'relative', marginBottom: drawnAreasViewMode === 'list' ? '8px' : '12px' }}>
                                  <input
                                    type="text"
                                    placeholder="🔍 Search areas..."
                                    value={polygonSearchTerm}
                                    onChange={(e) => setPolygonSearchTerm(e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: drawnAreasViewMode === 'list' ? '6px 8px' : '10px',
                                      paddingRight: '35px',
                                      background: theme.inputBg,
                                      border: `1px solid ${theme.inputBorder}`,
                                      borderRadius: drawnAreasViewMode === 'list' ? '4px' : '6px',
                                      color: theme.textPrimary,
                                      fontSize: drawnAreasViewMode === 'list' ? '0.8em' : '0.85em',
                                      outline: 'none',
                                      boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                                  />
                                  {polygonSearchTerm && (
                                    <button
                                      onClick={() => setPolygonSearchTerm('')}
                                      style={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: theme.textSecondary,
                                        cursor: 'pointer',
                                        fontSize: '1.2em',
                                        padding: '0',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.6,
                                        transition: 'opacity 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                                      title="Clear search"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                
                                <div>
                                  {(() => {
                                    // Filter polygons by search term
                                    const filteredPolygons = savedPolygons.filter(p =>
                                      p.name.toLowerCase().includes(debouncedPolygonSearch.toLowerCase())
                                    );
                                    
                                    // Sort alphabetically by name
                                    const sortedPolygons = [...filteredPolygons].sort((a, b) =>
                                      a.name.localeCompare(b.name)
                                    );
                                    
                                    // Show message if no results
                                    if (sortedPolygons.length === 0) {
                                      return (
                                        <div style={{
                                          padding: '20px',
                                          textAlign: 'center',
                                          color: theme.textSecondary,
                                          fontSize: '0.85em',
                                          fontStyle: 'italic'
                                        }}>
                                          No polygons match "{debouncedPolygonSearch}"
                                        </div>
                                      );
                                    }
                                    
                                    return sortedPolygons.map(polygon => (
                                    <div
                                      key={polygon.id}
                                      style={{
                                        marginBottom: drawnAreasViewMode === 'list' ? '4px' : '8px',
                                        padding: drawnAreasViewMode === 'list' ? '6px 8px' : '10px',
                                        background: theme.cardBg,
                                        borderRadius: drawnAreasViewMode === 'list' ? '4px' : '6px',
                                        border: `1px solid ${theme.borderColor}`,
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      {/* Polygon header with checkbox and name */}
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: drawnAreasViewMode === 'list' ? '6px' : '8px'
                                      }}>
                                        <input
                                          type="checkbox"
                                          checked={selectedPolygonIds.includes(polygon.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedPolygonIds(prev => [...prev, polygon.id]);
                                            } else {
                                              setSelectedPolygonIds(prev => prev.filter(id => id !== polygon.id));
                                            }
                                          }}
                                          style={{ 
                                            cursor: 'pointer', 
                                            width: drawnAreasViewMode === 'list' ? '14px' : '16px', 
                                            height: drawnAreasViewMode === 'list' ? '14px' : '16px' 
                                          }}
                                          title="Show/hide on map"
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{
                                            fontWeight: '600',
                                            fontSize: drawnAreasViewMode === 'list' ? '0.85em' : '0.9em',
                                            color: theme.textPrimary,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                          }}>
                                            <span>{polygon.name}</span>
                                            {playbooks[polygon.name] && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setViewingPlaybook(playbooks[polygon.name]);
                                                  setShowViewPlaybookModal(true);
                                                }}
                                                title="View Response Plan"
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  padding: '0',
                                                  cursor: 'pointer',
                                                  fontSize: '0.9em',
                                                  color: '#10B981',
                                                  lineHeight: 1,
                                                  display: 'flex',
                                                  alignItems: 'center'
                                                }}
                                              >
                                                📋
                                              </button>
                                            )}
                                          </div>
                                          {drawnAreasViewMode === 'manage' && (
                                          <div style={{
                                            fontSize: '0.75em',
                                            color: theme.textSecondary,
                                            marginTop: '2px'
                                          }}>
                                            {polygon.coords.length} points
                                          </div>
                                          )}
                                        </div>
                                        {/* Filter toggle button - visible in list mode */}
                                        {drawnAreasViewMode === 'list' && (() => {
                                          const isActiveFilter = polygonCoords === polygon.coords || currentPolygonName === polygon.name;
                                          const isFiltering = isActiveFilter && polygonFilterMode !== 'none';
                                          return (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (isFiltering) {
                                                  // Toggle off
                                                  setPolygonCoords(null);
                                                  setCurrentPolygonName('');
                                                  setPolygonFilterMode('none');
                                                } else {
                                                  // Activate filter
                                                  setPolygonCoords(polygon.coords);
                                                  setCurrentPolygonName(polygon.name);
                                                  setPolygonFilterMode('inside');
                                                }
                                              }}
                                              title={isFiltering ? 'Click to remove area filter' : 'Filter events to this area'}
                                              style={{
                                                flexShrink: 0,
                                                padding: '3px 7px',
                                                background: isFiltering ? '#10B981' : 'transparent',
                                                color: isFiltering ? 'white' : theme.textSecondary,
                                                border: `1px solid ${isFiltering ? '#10B981' : theme.borderColor}`,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.72em',
                                                fontWeight: '600',
                                                transition: 'all 0.15s',
                                                whiteSpace: 'nowrap',
                                              }}
                                              onMouseEnter={(e) => {
                                                if (!isFiltering) {
                                                  e.currentTarget.style.borderColor = '#10B981';
                                                  e.currentTarget.style.color = '#10B981';
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isFiltering) {
                                                  e.currentTarget.style.borderColor = theme.borderColor;
                                                  e.currentTarget.style.color = theme.textSecondary;
                                                }
                                              }}
                                            >
                                              {isFiltering ? '✓ Filtering' : '⬡ Filter'}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                      
                                      {/* Out of view indicator - completely separate, full width */}
                                      {selectedPolygonIds.includes(polygon.id) && !isPolygonInView(polygon) && (
                                        <div style={{ marginTop: '8px' }}>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              centerMapOnPolygon(polygon);
                                            }}
                                            style={{
                                              background: 'rgba(239, 68, 68, 0.1)',
                                              border: `1px solid #EF4444`,
                                              borderRadius: '4px',
                                              padding: '4px 10px',
                                              fontSize: '0.7em',
                                              color: '#EF4444',
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              fontWeight: '600',
                                              transition: 'all 0.2s',
                                              whiteSpace: 'nowrap'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.background = '#EF4444';
                                              e.currentTarget.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                              e.currentTarget.style.color = '#EF4444';
                                            }}
                                            title="This area is outside the current map view. Click to center map."
                                          >
                                            📍 Out of View
                                          </button>
                                        </div>
                                      )}
                                      
                                      {/* Action buttons - only in manage mode */}
                                      {drawnAreasViewMode === 'manage' && (
                                      <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '6px',
                                        marginTop: (selectedPolygonIds.includes(polygon.id) && !isPolygonInView(polygon)) ? '0' : '8px'
                                      }}>
                                        <button
                                          onClick={() => {
                                            loadSavedPolygon(polygon);
                                            setCurrentLoadedPolygonId(polygon.id);
                                          }}
                                          style={{
                                            padding: '6px',
                                            background: currentLoadedPolygonId === polygon.id ? '#10B981' : theme.accentColor,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.75em',
                                            fontWeight: '600',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                          title="Load for editing/filtering"
                                        >
                                          📍 {currentLoadedPolygonId === polygon.id ? 'Loaded' : 'Load'}
                                        </button>
                                        
                                        <button
                                          onClick={() => {
                                            setEditingPlaybook(polygon.name);
                                            setShowPlaybookDialog(true);
                                          }}
                                          style={{
                                            padding: '6px',
                                            background: playbooks[polygon.name] ? '#10B981' : theme.accentColor,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.75em',
                                            fontWeight: '600',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                          title={playbooks[polygon.name] ? 'Edit Response Plan' : 'Add Response Plan'}
                                        >
                                          📋 Plan
                                        </button>
                                        
                                        {/* Filter buttons - show when this polygon is loaded */}
                                        {currentLoadedPolygonId === polygon.id && (
                                          <>
                                            <button
                                              onClick={() => applyPolygonFilter('inside')}
                                              style={{
                                                padding: '6px',
                                                background: polygonFilterMode === 'inside' ? '#10B981' : theme.inputBg,
                                                color: polygonFilterMode === 'inside' ? 'white' : theme.textPrimary,
                                                border: `1px solid ${polygonFilterMode === 'inside' ? '#10B981' : theme.inputBorder}`,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.75em',
                                                fontWeight: '600',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                              title="Filter inside"
                                            >
                                              ⬇️ In
                                            </button>
                                            
                                            <button
                                              onClick={() => applyPolygonFilter('outside')}
                                              style={{
                                                padding: '6px',
                                                background: polygonFilterMode === 'outside' ? '#F59E0B' : theme.inputBg,
                                                color: polygonFilterMode === 'outside' ? 'white' : theme.textPrimary,
                                                border: `1px solid ${polygonFilterMode === 'outside' ? '#F59E0B' : theme.inputBorder}`,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.75em',
                                                fontWeight: '600',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                              title="Filter outside"
                                            >
                                              ⬆️ Out
                                            </button>
                                          </>
                                        )}
                                        
                                        <button
                                          onClick={() => deleteSavedPolygon(polygon.id)}
                                          style={{
                                            padding: '6px',
                                            background: '#EF4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.75em',
                                            fontWeight: '600',
                                            transition: 'all 0.2s',
                                            gridColumn: currentLoadedPolygonId === polygon.id ? 'span 1' : 'span 2'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                          title="Delete polygon"
                                        >
                                          🗑️ Delete
                                        </button>
                                      </div>
                                      )}

                                      {/* Filter Eligibility Checkbox - manage mode only */}
                                      {drawnAreasViewMode === 'manage' && (
                                        <label style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          marginTop: '8px',
                                          padding: '5px 6px',
                                          borderRadius: '4px',
                                          background: polygon.filterEligible !== false ? 'rgba(16, 185, 129, 0.08)' : theme.detailBg,
                                          border: `1px solid ${polygon.filterEligible !== false ? 'rgba(16, 185, 129, 0.25)' : theme.borderColor}`,
                                          cursor: 'pointer',
                                          fontSize: '0.75em',
                                          color: polygon.filterEligible !== false ? '#10B981' : theme.textSecondary,
                                          fontWeight: '600',
                                          userSelect: 'none',
                                          transition: 'all 0.2s'
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={polygon.filterEligible !== false}
                                            onChange={(e) => {
                                              setSavedPolygons(prev => prev.map(p =>
                                                p.id === polygon.id ? { ...p, filterEligible: e.target.checked } : p
                                              ));
                                            }}
                                            style={{ cursor: 'pointer', width: '13px', height: '13px', flexShrink: 0 }}
                                          />
                                          Available in Filter by Drawn Area
                                        </label>
                                      )}
                                    </div>
                                  ));  // Close map
                                  })()}  {/* Close IIFE */}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Custom Layers Section */}
                      <div style={{
                        marginBottom: '15px'
                      }}>
                        <div style={{ 
                          padding: '10px', 
                          background: theme.detailBg,
                          border: `2px solid ${theme.borderColor}`,
                          borderRadius: '6px',
                          transition: 'all 0.3s ease'
                        }}>
                          <div
                            style={{
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginBottom: showCustomLayersPanel ? '10px' : '0',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <div
                              onClick={() => setShowCustomLayersPanel(!showCustomLayersPanel)}
                              style={{ 
                                fontSize: '0.85em', 
                                color: theme.textSecondary, 
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                flex: 1
                              }}>
                              <span style={{ 
                                fontSize: '0.8em',
                                transition: 'transform 0.2s',
                                transform: showCustomLayersPanel ? 'rotate(90deg)' : 'rotate(0deg)',
                                display: 'inline-block'
                              }}>
                                ▶
                              </span>
                              🗺️ Custom Layers 
                              {customLayers.length > 0 && (() => {
                                const visibleCount = customLayers.filter(l => l.visible).length;
                                return (
                                  <>
                                    <span style={{
                                      background: theme.accentColor,
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '0.85em',
                                      marginLeft: '4px'
                                    }}>
                                      {visibleCount}/{customLayers.length}
                                    </span>
                                    {visibleCount > 0 && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          customLayers.forEach(layer => {
                                            if (layer.visible) {
                                              toggleLayerVisibility(layer.id);
                                            }
                                          });
                                        }}
                                        style={{
                                          padding: '2px 8px',
                                          background: '#EF4444',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '12px',
                                          fontSize: '0.75em',
                                          cursor: 'pointer',
                                          fontWeight: '600',
                                          transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                                        title="Hide all custom layers"
                                      >
                                        Hide All
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        
                        {showCustomLayersPanel && (
                          <div style={{
                            padding: '12px',
                            background: theme.detailBg
                          }}>
                            {/* View Mode Toggle */}
                            <div style={{
                              display: 'flex',
                              gap: '4px',
                              marginBottom: '8px',
                              background: theme.cardBg,
                              padding: '3px',
                              borderRadius: '4px',
                              border: `1px solid ${theme.borderColor}`
                            }}>
                              <button
                                onClick={() => setCustomLayersViewMode('list')}
                                style={{
                                  flex: 1,
                                  padding: '4px 6px',
                                  background: customLayersViewMode === 'list' ? theme.accentColor : 'transparent',
                                  color: customLayersViewMode === 'list' ? 'white' : theme.textPrimary,
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                              >
                                📋 List
                              </button>
                              <button
                                onClick={() => setCustomLayersViewMode('manage')}
                                style={{
                                  flex: 1,
                                  padding: '4px 6px',
                                  background: customLayersViewMode === 'manage' ? theme.accentColor : 'transparent',
                                  color: customLayersViewMode === 'manage' ? 'white' : theme.textPrimary,
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  transition: 'all 0.2s'
                                }}
                              >
                                ⚙️ Manage
                              </button>
                            </div>

                            {/* Drawing Status - only in manage mode */}
                            {customLayersViewMode === 'manage' && isDrawingCustomLayer && currentDrawingLayer && (
                              <div style={{
                                padding: '12px',
                                background: theme.accentColor,
                                color: 'white',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                animation: 'pulse 2s ease-in-out infinite'
                              }}>
                                <div style={{ fontWeight: '700', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  🎨 Drawing: {currentDrawingLayer.name}
                                </div>
                                <div style={{ fontSize: '0.85em', marginBottom: '8px', opacity: 0.9 }}>
                                  {currentDrawingLayer.type === 'marker' && `Click map to add markers (${customLayers.find(l => l.id === currentDrawingLayer.id)?.data.length || 0} added)`}
                                  {currentDrawingLayer.type === 'polygon' && `Click points to draw (${customLayerPoints.length} points)`}
                                  {currentDrawingLayer.type === 'line' && `Click points for route (${customLayerPoints.length} points)`}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={finishDrawingCustomLayer}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      background: 'rgba(255,255,255,0.9)',
                                      color: theme.accentColor,
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓ Finish
                                  </button>
                                  <button
                                    onClick={cancelDrawingCustomLayer}
                                    style={{
                                      padding: '8px 12px',
                                      background: 'rgba(255,255,255,0.2)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✕ Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {/* Add Layer Button - only in manage mode */}
                            {customLayersViewMode === 'manage' && (
                            <button
                              onClick={() => setShowAddLayerDialog(true)}
                              disabled={isDrawingCustomLayer}
                              style={{
                                width: '100%',
                                padding: '10px',
                                marginBottom: '12px',
                                background: isDrawingCustomLayer ? theme.detailBg : theme.accentColor,
                                color: isDrawingCustomLayer ? theme.textSecondary : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: isDrawingCustomLayer ? 'not-allowed' : 'pointer',
                                opacity: isDrawingCustomLayer ? 0.5 : 1,
                                transition: 'all 0.2s'
                              }}
                            >
                              ➕ Add New Layer
                            </button>
                            )}
                            
                            {/* Layer List */}
                            {customLayers.length === 0 ? (
                              <div style={{
                                padding: '30px 20px',
                                textAlign: 'center',
                                color: theme.textSecondary,
                                fontSize: '0.9em',
                                background: theme.cardBg,
                                borderRadius: '8px',
                                border: `1px dashed ${theme.borderColor}`
                              }}>
                                <div style={{ fontSize: '2em', marginBottom: '10px' }}>🗺️</div>
                                <div style={{ marginBottom: '4px', fontWeight: '600' }}>
                                  No custom layers yet
                                </div>
                                <div>
                                  Click "Add New Layer" to create markers, polygons, or lines
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* Search box */}
                                <div style={{ position: 'relative', marginBottom: customLayersViewMode === 'list' ? '8px' : '12px' }}>
                                  <input
                                    type="text"
                                    placeholder="🔍 Search layers..."
                                    value={customLayerSearchTerm}
                                    onChange={(e) => setCustomLayerSearchTerm(e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: customLayersViewMode === 'list' ? '6px 8px' : '10px',
                                      paddingRight: '35px',
                                      background: theme.inputBg,
                                      border: `1px solid ${theme.inputBorder}`,
                                      borderRadius: customLayersViewMode === 'list' ? '4px' : '6px',
                                      color: theme.textPrimary,
                                      fontSize: customLayersViewMode === 'list' ? '0.8em' : '0.85em',
                                      outline: 'none',
                                      boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                                  />
                                  {customLayerSearchTerm && (
                                    <button
                                      onClick={() => setCustomLayerSearchTerm('')}
                                      style={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: theme.textSecondary,
                                        cursor: 'pointer',
                                        fontSize: '1.2em',
                                        padding: '0',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.6,
                                        transition: 'opacity 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                                      title="Clear search"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                
                                {(() => {
                                  // Filter layers by search term
                                  const filteredLayers = customLayers.filter(layer =>
                                    layer.name.toLowerCase().includes(debouncedLayerSearch.toLowerCase())
                                  );
                                  
                                  // Sort alphabetically by name
                                  const sortedLayers = [...filteredLayers].sort((a, b) =>
                                    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                                  );
                                  
                                  if (sortedLayers.length === 0) {
                                    return (
                                      <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: theme.textSecondary,
                                        fontSize: '0.85em',
                                        background: theme.cardBg,
                                        borderRadius: '6px',
                                        border: `1px dashed ${theme.borderColor}`
                                      }}>
                                        No layers match "{debouncedLayerSearch}"
                                      </div>
                                    );
                                  }
                                  
                                  return sortedLayers.map((layer, index) => (
                                <div
                                  key={layer.id}
                                  style={{
                                    marginBottom: customLayersViewMode === 'list' ? '4px' : '8px',
                                    padding: customLayersViewMode === 'list' ? '6px 8px' : '12px',
                                    background: theme.cardBg,
                                    borderRadius: customLayersViewMode === 'list' ? '4px' : '8px',
                                    border: `1px solid ${layer.visible ? layer.style.color : theme.borderColor}`,
                                    opacity: layer.visible ? 1 : 0.6,
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {/* Header with checkbox and name */}
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: customLayersViewMode === 'list' ? '6px' : '8px'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={layer.visible}
                                      onChange={() => toggleLayerVisibility(layer.id)}
                                      style={{ 
                                        cursor: 'pointer', 
                                        width: customLayersViewMode === 'list' ? '14px' : '16px', 
                                        height: customLayersViewMode === 'list' ? '14px' : '16px' 
                                      }}
                                      title="Show/hide on map"
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{
                                        fontWeight: '600',
                                        fontSize: customLayersViewMode === 'list' ? '0.85em' : '0.9em',
                                        marginBottom: customLayersViewMode === 'manage' ? '4px' : '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        color: theme.textPrimary
                                      }}>
                                        {layer.type === 'marker' && (() => {
                                            const iconType = layer.style.icon || 'circle';
                                            const color = layer.style.color;
                                            const isListMode = customLayersViewMode === 'list';
                                            
                                            // Font Awesome icons
                                            if (iconType.startsWith('fa-')) {
                                              const faIconClass = iconType.substring(3);
                                              return (
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  width: isListMode ? '16px' : '20px',
                                                  height: isListMode ? '16px' : '20px',
                                                  borderRadius: '50%',
                                                  background: color,
                                                  color: 'white',
                                                  fontSize: isListMode ? '9px' : '11px',
                                                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                  flexShrink: 0
                                                }}>
                                                  <i className={`fas fa-${faIconClass}`}></i>
                                                </span>
                                              );
                                            }
                                            
                                            // Emoji icons
                                            if (iconType.endsWith('-emoji')) {
                                              const emojiMap = {
                                                'fire-emoji': '🚒',
                                                'police-emoji': '🚓',
                                                'ambulance-emoji': '🚑',
                                                'warning-emoji': '⚠️',
                                                'hazard-emoji': '☢️',
                                                'alert-emoji': '🚨'
                                              };
                                              return (
                                                <span style={{
                                                  display: 'inline-block',
                                                  fontSize: isListMode ? '13px' : '16px',
                                                  verticalAlign: 'middle',
                                                  lineHeight: 1,
                                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                                  flexShrink: 0
                                                }}>{emojiMap[iconType] || '📍'}</span>
                                              );
                                            }
                                            
                                            // Render actual marker shape with color for basic shapes
                                            const sz = isListMode ? '13px' : '16px';
                                            switch(iconType) {
                                              case 'circle':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: sz,
                                                    height: sz,
                                                    borderRadius: '50%',
                                                    background: color,
                                                    border: '2px solid white',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    verticalAlign: 'middle',
                                                    flexShrink: 0
                                                  }}></span>
                                                );
                                              case 'square':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: sz,
                                                    height: sz,
                                                    borderRadius: '2px',
                                                    background: color,
                                                    border: '2px solid white',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    verticalAlign: 'middle',
                                                    flexShrink: 0
                                                  }}></span>
                                                );
                                              case 'triangle':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: '0',
                                                    height: '0',
                                                    borderLeft: isListMode ? '6px solid transparent' : '8px solid transparent',
                                                    borderRight: isListMode ? '6px solid transparent' : '8px solid transparent',
                                                    borderBottom: `${isListMode ? '11px' : '14px'} solid ${color}`,
                                                    verticalAlign: 'middle',
                                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                                    flexShrink: 0
                                                  }}></span>
                                                );
                                              case 'star':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    color: color,
                                                    fontSize: isListMode ? '14px' : '18px',
                                                    verticalAlign: 'middle',
                                                    lineHeight: 1,
                                                    textShadow: '0 0 1px white, 0 0 1px white',
                                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                                    flexShrink: 0
                                                  }}>★</span>
                                                );
                                              case 'diamond':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: isListMode ? '10px' : '12px',
                                                    height: isListMode ? '10px' : '12px',
                                                    background: color,
                                                    transform: 'rotate(45deg)',
                                                    border: '2px solid white',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    verticalAlign: 'middle',
                                                    margin: '0 2px',
                                                    flexShrink: 0
                                                  }}></span>
                                                );
                                              case 'pin':
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    color: color,
                                                    fontSize: isListMode ? '13px' : '16px',
                                                    verticalAlign: 'middle',
                                                    lineHeight: 1,
                                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                                    flexShrink: 0
                                                  }}>📍</span>
                                                );
                                              default:
                                                return (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: sz,
                                                    height: sz,
                                                    borderRadius: '50%',
                                                    background: color,
                                                    border: '2px solid white',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    verticalAlign: 'middle',
                                                    flexShrink: 0
                                                  }}></span>
                                                );
                                            }
                                          })()}
                                          {layer.type === 'polygon' && customLayersViewMode === 'manage' && '🔷'}
                                          {layer.type === 'line' && customLayersViewMode === 'manage' && '📏'}
                                        {layer.name}
                                      </div>
                                      {customLayersViewMode === 'manage' && (
                                      <div style={{
                                        fontSize: '0.75em',
                                        color: theme.textSecondary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                      }}>
                                        <span style={{
                                          display: 'inline-block',
                                          width: '12px',
                                          height: '12px',
                                          borderRadius: '50%',
                                          background: layer.style.color
                                        }}></span>
                                        {layer.data.length} {layer.type}
                                        {layer.data.length !== 1 ? 's' : ''}
                                      </div>
                                      )}
                                    </div>
                                  </div>
                                    
                                    {/* Out of view indicator - completely separate, full width */}
                                    {layer.visible && !isLayerInView(layer) && (
                                      <div style={{ marginTop: '8px' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            centerMapOnLayer(layer);
                                          }}
                                          style={{
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            border: `1px solid #EF4444`,
                                            borderRadius: '4px',
                                            padding: '4px 10px',
                                            fontSize: '0.7em',
                                            color: '#EF4444',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontWeight: '600',
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#EF4444';
                                            e.currentTarget.style.color = 'white';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                            e.currentTarget.style.color = '#EF4444';
                                          }}
                                          title="This layer is outside the current map view. Click to center map."
                                        >
                                          📍 Out of View
                                        </button>
                                      </div>
                                    )}
                                    
                                    {/* Action buttons - only in manage mode */}
                                    {customLayersViewMode === 'manage' && (
                                    <div style={{
                                      display: 'flex',
                                      gap: '4px',
                                      flexWrap: 'wrap',
                                      justifyContent: 'flex-end',
                                      alignItems: 'center',
                                      marginTop: (layer.visible && !isLayerInView(layer)) ? '0' : '8px'
                                    }}>
                                      {/* Add to Filters button - only for polygon layers */}
                                      {layer.type === 'polygon' && (
                                        <button
                                          onClick={() => convertCustomLayerToDrawnPolygons(layer.id)}
                                          style={{
                                            padding: '6px 10px',
                                            background: '#10B981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.95em',
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap'
                                          }}
                                          title="Convert to drawn polygons for data filtering"
                                          onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                                          onMouseLeave={(e) => e.currentTarget.style.background = '#10B981'}
                                        >
                                          📐
                                        </button>
                                      )}
                                      
                                      {/* Edit */}
                                      <button
                                        onClick={() => openEditLayer(layer.id)}
                                        style={{
                                          padding: '6px 10px',
                                          background: '#3B82F6',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.95em',
                                          transition: 'all 0.2s'
                                        }}
                                        title="Edit layer settings"
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#2563EB'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#3B82F6'}
                                      >
                                        ✏️
                                      </button>
                                      
                                      {/* Delete */}
                                      <button
                                        onClick={() => {
                                          if (window.confirm(`Delete layer "${layer.name}"?\n\nThis will remove ${layer.data.length} ${layer.type}${layer.data.length !== 1 ? 's' : ''}.`)) {
                                            deleteCustomLayer(layer.id);
                                          }
                                        }}
                                        style={{
                                          padding: '6px 10px',
                                          background: '#EF4444',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.95em',
                                          transition: 'all 0.2s'
                                        }}
                                        title="Delete layer"
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                    )}
                                </div>
                              ));  // Close map
                                })()}  {/* Close IIFE */}
                              </>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                      
                      {/* Zone Layers Section */}
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ 
                          padding: '10px', 
                          background: theme.detailBg,
                          border: `2px solid ${theme.borderColor}`,
                          borderRadius: '6px',
                          transition: 'all 0.3s ease'
                        }}>
                          <div
                            onClick={() => setShowZoneLayersPanel(!showZoneLayersPanel)}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: showZoneLayersPanel ? '10px' : '0',
                              cursor: 'pointer',
                              userSelect: 'none',
                              gap: '6px'
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.7em',
                              transition: 'transform 0.2s',
                              transform: showZoneLayersPanel ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block',
                              color: theme.textSecondary
                            }}>
                              ▶
                            </span>
                            <span style={{ 
                              fontSize: '0.85em', 
                              color: theme.textSecondary, 
                              fontWeight: '600'
                            }}>
                              🗾 Zone Layers
                            </span>
                          </div>

                          {showZoneLayersPanel && (
                            <div style={{
                              padding: '20px',
                              textAlign: 'center',
                              color: theme.textSecondary,
                              fontSize: '0.85em',
                              background: theme.cardBg,
                              borderRadius: '6px',
                              border: `1px dashed ${theme.borderColor}`
                            }}>
                              <div style={{ fontSize: '1.8em', marginBottom: '8px' }}>🗾</div>
                              <div style={{ fontWeight: '600', marginBottom: '4px', color: theme.textPrimary }}>
                                Zone Layers
                              </div>
                              <div>Zone layer functionality coming soon</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ESN Layers Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          padding: '10px', 
                          background: theme.detailBg,
                          border: `2px solid ${theme.borderColor}`,
                          borderRadius: '6px',
                          transition: 'all 0.3s ease'
                        }}>
                          <div
                            onClick={() => setShowEsnLayersPanel(!showEsnLayersPanel)}
                            style={{
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: showEsnLayersPanel ? '10px' : '0',
                              cursor: 'pointer',
                              userSelect: 'none',
                              gap: '6px'
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.7em',
                              transition: 'transform 0.2s',
                              transform: showEsnLayersPanel ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block',
                              color: theme.textSecondary
                            }}>
                              ▶
                            </span>
                            <span style={{ 
                              fontSize: '0.85em', 
                              color: theme.textSecondary, 
                              fontWeight: '600'
                            }}>
                              🔢 ESN Layers
                            </span>
                          </div>

                          {showEsnLayersPanel && (
                            <div style={{
                              padding: '20px',
                              textAlign: 'center',
                              color: theme.textSecondary,
                              fontSize: '0.85em',
                              background: theme.cardBg,
                              borderRadius: '6px',
                              border: `1px dashed ${theme.borderColor}`
                            }}>
                              <div style={{ fontSize: '1.8em', marginBottom: '8px' }}>🔢</div>
                              <div style={{ fontWeight: '600', marginBottom: '4px', color: theme.textPrimary }}>
                                ESN Layers
                              </div>
                              <div>ESN layer functionality coming soon</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Layer Info Section */}
                      <div style={{
                        marginTop: '24px',
                        padding: '12px',
                        background: theme.detailBg,
                        borderRadius: '8px',
                        fontSize: '0.8em',
                        color: theme.textSecondary
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '6px', color: theme.textPrimary }}>
                          💡 Tips
                        </div>
                        <div style={{ lineHeight: '1.5' }}>
                          • Enable clusters for large datasets<br/>
                          • Use heat map for pattern analysis<br/>
                          • Response zones show saved areas
                        </div>
                      </div>
                    </div>
                    
                    {/* Drawer Footer */}
                    <div style={{
                      padding: '12px 20px',
                      borderTop: `1px solid ${theme.borderColor}`,
                      fontSize: '0.75em',
                      color: theme.textSecondary,
                      textAlign: 'center',
                      background: theme.detailBg
                    }}>
                      Settings saved automatically
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Map container - shrinks when drawer is open */}
          <div 
            ref={mapWrapperRef}
            style={{ 
              position: 'relative',
              width: mapSize.width,
              maxWidth: '100%',
              margin: '0 auto',
              display: isMapCollapsed ? 'none' : 'flex',
              flex: 1,
              transition: 'all 0.3s ease',
              alignSelf: 'stretch',
              minHeight: isTablet ? '380px' : '500px'
            }}>
            {/* Tablet: tap anywhere on map to close layer panel */}
            {isTablet && showLayerPanel && (
              <div
                onClick={() => setShowLayerPanel(false)}
                style={{ position: 'absolute', inset: 0, zIndex: 499, cursor: 'pointer' }}
                title="Tap to close layers panel"
              />
            )}
            <div
              ref={mapContainerRef}
              style={{
                height: '100%',
                width: '100%',
                borderRadius: '8px',
                border: `2px solid ${theme.borderColor}`,
                position: 'relative',
                zIndex: 1
              }}
            >
              {!map && !mapError && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: theme.textSecondary,
                  fontSize: '1.1em'
                }}>
                  Loading map...
                </div>
              )}
            </div>
            
            {/* ✅ Floating Home button on map */}
            {map && !mapError && (
              <>
                {/* Map control buttons moved to header */}
              </>
            )}
            
            
            {/* Resize handle for map */}
            <div
              onMouseDown={handleMapResizeStart}
              style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                background: theme.accentColor,
                borderRadius: '0 0 8px 0',
                opacity: 0.3,
                transition: 'opacity 0.2s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.3'}
              title="Drag to resize map"
            >
              <div style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                width: '0',
                height: '0',
                borderStyle: 'solid',
                borderWidth: '0 0 12px 12px',
                borderColor: `transparent transparent ${theme.cardBg} transparent`
              }} />
            </div>
            
            {/* Heat Map Legend */}
            {mapLayers.heatmap && (
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                background: isDarkTheme ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                zIndex: 1000,
                border: `1px solid ${theme.borderColor}`
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '8px', 
                  fontSize: '0.9em',
                  color: theme.textPrimary
                }}>
                  Event Density
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '12px', background: 'blue', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.8em', color: theme.textSecondary }}>Low</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '12px', background: 'lime', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.8em', color: theme.textSecondary }}>Medium</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '12px', background: 'yellow', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.8em', color: theme.textSecondary }}>High</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '12px', background: 'red', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.8em', color: theme.textSecondary }}>Very High</span>
                  </div>
                </div>
              </div>
            )}
          </div> {/* Close map wrapper */}
          </div> {/* Close flex container (drawer + map) */}
        </div> {/* Close map section */}

        {/* Charts Panel - Collapsible */}
        <div
          {...sectionDragProps('charts')}
          style={{
            background: theme.cardBg,
            borderRadius: '12px',
            padding: '0',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            ...sectionOrderStyle('charts'),
          }}>
          {/* Header */}
          <div style={{
            fontSize: '1.05em',
            fontWeight: '600',
            padding: '10px 20px',
            color: theme.sectionLabelColor,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: allSectionsCollapsed ? 'grab' : 'pointer',
            userSelect: 'none'
          }}
            onClick={(e) => {
              // Allow expanding even in drag mode (will exit drag mode)
              e.stopPropagation(); // Prevent drag handling
              setIsChartsCollapsed(!isChartsCollapsed);
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
              📊 Charts
            </span>
            <span style={{
              fontSize: '1em',
              color: theme.sectionLabelColor,
              transform: isChartsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }}
              title={isChartsCollapsed ? 'Expand charts' : 'Collapse charts'}
            >
              ▼
            </span>
          </div>

          {/* Collapsible Charts Content */}
          {!isChartsCollapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '0 20px 20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px',
                overflowY: 'auto',
                height: sectionHeights.charts ? `${sectionHeights.charts}px` : 'auto',
                minHeight: '200px',
              }}>
              {/* Sort charts by order and render */}
              {Object.entries(chartLayout)
                .filter(([chartName]) => visibleCharts.includes(chartName))
                .sort((a, b) => a[1].order - b[1].order)
                .map(([chartName, layout]) => {
              // Render each chart based on its name
              if (chartName === 'callType') {
                return (
                  <div
                    key="callType"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'callType')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'callType')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'callType' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {/* Drag handle */}
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    {/* Close button */}
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'callType'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Top Event Types</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.callType.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['bar', 'pie', 'doughnut'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, callType: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.callType === type ? theme.accentColor : theme.inputBg, color: chartTypes.callType === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={callTypeChartRef} />
                    </div>
                    {/* Resize handle */}
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'callType')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              if (chartName === 'hourly') {
                return (
                  <div
                    key="hourly"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'hourly')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'hourly')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'hourly' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'hourly'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Hourly Distribution</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.hourly.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['line', 'bar'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, hourly: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.hourly === type ? theme.accentColor : theme.inputBg, color: chartTypes.hourly === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={hourlyChartRef} />
                    </div>
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'hourly')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              if (chartName === 'timeline') {
                return (
                  <div
                    key="timeline"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'timeline')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'timeline')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'timeline' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'timeline'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Events Over Time</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.timeline.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['line', 'bar', 'area'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, timeline: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.timeline === type ? theme.accentColor : theme.inputBg, color: chartTypes.timeline === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={timelineChartRef} />
                    </div>
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'timeline')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              if (chartName === 'responseTime') {
                return (
                  <div
                    key="responseTime"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'responseTime')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'responseTime')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'responseTime' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'responseTime'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Response Time Distribution</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.responseTime.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['bar', 'line'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, responseTime: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.responseTime === type ? theme.accentColor : theme.inputBg, color: chartTypes.responseTime === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={responseTimeChartRef} />
                    </div>
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'responseTime')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              if (chartName === 'dayOfWeek') {
                return (
                  <div
                    key="dayOfWeek"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'dayOfWeek')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'dayOfWeek')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'dayOfWeek' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'dayOfWeek'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Events by Day of Week</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.dayOfWeek.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['bar', 'line'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, dayOfWeek: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.dayOfWeek === type ? theme.accentColor : theme.inputBg, color: chartTypes.dayOfWeek === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={dayOfWeekChartRef} />
                    </div>
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'dayOfWeek')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              if (chartName === 'month') {
                return (
                  <div
                    key="month"
                    draggable
                    onDragStart={(e) => handleChartDragStart(e, 'month')}
                    onDragOver={handleChartDragOver}
                    onDrop={(e) => handleChartDrop(e, 'month')}
                    onDragEnd={handleChartDragEnd}
                    style={{ 
                      background: theme.cardBg, 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      width: layout.width,
                      height: layout.height,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: draggedChart ? 'move' : 'grab',
                      opacity: draggedChart === 'month' ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      color: theme.textSecondary,
                      fontSize: '1.2em',
                      cursor: 'grab',
                      opacity: 0.5,
                      zIndex: 10
                    }}
                    title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                    <button
                      onClick={() => setVisibleCharts(prev => prev.filter(c => c !== 'month'))}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        color: theme.textSecondary,
                        fontSize: '1.5em',
                        cursor: 'pointer',
                        padding: '0',
                        width: '25px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                      title="Remove this chart (restore with Reset Cards button)"
                    >
                      ×
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', paddingRight: '30px', paddingLeft: '30px' }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: '600', color: theme.textPrimary }}>Events by Month</div>
                        <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>{chartHeaderStats.month.subtitle}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {['bar', 'line'].map(type => (
                          <button key={type} onClick={() => setChartTypes(prev => ({ ...prev, month: type }))}
                            style={{ padding: '5px 10px', background: chartTypes.month === type ? theme.accentColor : theme.inputBg, color: chartTypes.month === type ? 'white' : theme.textPrimary, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ 
                      background: theme.chartBg, 
                      borderRadius: '8px', 
                      padding: '15px',
                      flex: 1,
                      minHeight: 0
                    }}>
                      <canvas ref={monthChartRef} />
                    </div>
                    <div
                      onMouseDown={(e) => handleResizeStart(e, 'month')}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        width: '20px',
                        height: '20px',
                        cursor: 'nwse-resize',
                        color: theme.textSecondary,
                        fontSize: '1.2em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                        userSelect: 'none'
                      }}
                      title="Drag to resize"
                    >
                      ⤡
                    </div>
                  </div>
                );
              }
              
              return null;
            })}
            </div>
              <SectionResizeHandle sectionId="charts" resizingSection={resizingSection} theme={theme} onResizeStart={handleSectionResizeStart} onReset={resetSectionHeight} />
            </div>
          )}
        </div>

        {/* Event Time Performance Analysis - Collapsible */}
        <div
          {...sectionDragProps('eventTime')}
          style={{ background: theme.cardBg, borderRadius: '12px', padding: '0', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: '24px', overflow: 'hidden', ...sectionOrderStyle('eventTime') }}>
          
          {/* Header with collapse button */}
          <div 
            style={{ 
              fontSize: '1.05em',
              fontWeight: '600',
              padding: '10px 20px',
              color: theme.sectionLabelColor,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: allSectionsCollapsed ? 'grab' : 'pointer',
              userSelect: 'none'
            }}
            onClick={(e) => {
              // Allow expanding even in drag mode (will exit drag mode)
              e.stopPropagation(); // Prevent drag handling
              setIsDispatchAnalysisExpanded(!isDispatchAnalysisExpanded);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
              ⚡ Event Time Performance Analysis
            </div>
            
            {/* Collapse/Expand button - matches map style */}
            <span 
              style={{
                fontSize: '1em',
                color: theme.sectionLabelColor,
                transform: isDispatchAnalysisExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s ease',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              title={isDispatchAnalysisExpanded ? 'Collapse section' : 'Expand section'}
            >
              ▼
            </span>
          </div>

          {/* Collapsible Content */}
          {isDispatchAnalysisExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '0 20px 20px',
                overflowY: 'auto',
                height: sectionHeights.eventTime ? `${sectionHeights.eventTime}px` : 'auto',
                minHeight: '150px',
              }}>
          {(() => {
            const performance = calculateDispatchPerformance();
            
            // Helper to get display name for time field
            const getTimeFieldLabel = (fieldName) => {
              const labels = {
                'EventStartDateTime': 'Event Start',
                'ReceivedDateTime': 'Received',
                'DispatchedDateTime': 'Dispatched',
                'EnrouteDateTime': 'Enroute',
                'ArrivedDateTime': 'Arrived',
                'CompetedDateTime': 'Completed'
              };
              return labels[fieldName] || fieldName;
            };
            
            // Define time field progression order
            const timeFieldOrder = [
              'EventStartDateTime',
              'ReceivedDateTime',
              'DispatchedDateTime',
              'EnrouteDateTime',
              'ArrivedDateTime',
              'CompetedDateTime'
            ];
            
            // Get index of field in progression
            const getFieldIndex = (fieldName) => timeFieldOrder.indexOf(fieldName);
            
            // Handle Time From change - adjust Time To if needed
            const handleTimeFromChange = (newTimeFrom) => {
              setTimeFrom(newTimeFrom);
              const fromIndex = getFieldIndex(newTimeFrom);
              const toIndex = getFieldIndex(timeTo);
              
              // If Time To is now before Time From, adjust it to match Time From
              if (toIndex < fromIndex) {
                setTimeTo(newTimeFrom);
              }
            };
            
            // Unit conversion helpers
            const convertToDisplayUnit = (seconds) => {
              if (timeUnit === 'minutes') {
                return (seconds / 60).toFixed(1);
              }
              return seconds.toFixed(0);
            };
            
            const getUnitLabel = () => timeUnit === 'minutes' ? 'm' : 's';
            
            const getUnitLabelFull = () => timeUnit === 'minutes' ? 'minutes' : 'seconds';
            
            // Get slider values in current unit
            const getSliderValues = () => {
              if (timeUnit === 'minutes') {
                return {
                  target: parseFloat((dispatchTargetSeconds / 60).toFixed(1)),
                  min: parseFloat((dispatchRangeMin / 60).toFixed(1)),
                  max: parseFloat((dispatchRangeMax / 60).toFixed(1)),
                  step: 0.1
                };
              }
              return {
                target: dispatchTargetSeconds,
                min: dispatchRangeMin,
                max: dispatchRangeMax,
                step: 1
              };
            };
            
            const sliderValues = getSliderValues();
            const midValue = ((sliderValues.max + sliderValues.min) / 2).toFixed(timeUnit === 'minutes' ? 1 : 0);
            
            // Handle slider change
            const handleSliderChange = (e) => {
              const value = parseFloat(e.target.value);
              if (timeUnit === 'minutes') {
                // Convert minutes to seconds
                setDispatchTargetSeconds(Math.round(value * 60));
              } else {
                setDispatchTargetSeconds(value);
              }
            };
            
            // Handle min/max input changes
            const handleMinChange = (value) => {
              if (timeUnit === 'minutes') {
                setDispatchRangeMin(Math.round(value * 60));
              } else {
                setDispatchRangeMin(value);
              }
            };
            
            const handleMaxChange = (value) => {
              if (timeUnit === 'minutes') {
                setDispatchRangeMax(Math.round(value * 60));
              } else {
                setDispatchRangeMax(value);
              }
            };
            
            return (
              <div style={{ marginTop: '20px' }}>
                
                {/* Time Field Selectors and Unit Toggle */}
                <div style={{ 
                  display: 'flex', 
                  gap: '15px', 
                  marginBottom: '25px',
                  alignItems: 'flex-end'
                }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ 
                      fontSize: '0.85em', 
                      color: theme.textSecondary,
                      display: 'block',
                      marginBottom: '6px',
                      fontWeight: '600'
                    }}>
                      Time From:
                    </label>
                    <select
                      value={timeFrom}
                      onChange={(e) => handleTimeFromChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: theme.inputBg,
                        border: `2px solid ${theme.inputBorder}`,
                        borderRadius: '6px',
                        color: theme.textPrimary,
                        fontSize: '0.95em',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      <option value="EventStartDateTime">Event Start</option>
                      <option value="ReceivedDateTime">Received</option>
                      <option value="DispatchedDateTime">Dispatched</option>
                      <option value="EnrouteDateTime">Enroute</option>
                      <option value="ArrivedDateTime">Arrived</option>
                      <option value="CompetedDateTime">Completed</option>
                    </select>
                  </div>
                  
                  <div style={{ 
                    fontSize: '1.5em', 
                    color: theme.textSecondary,
                    paddingBottom: '8px',
                    fontWeight: '300'
                  }}>
                    →
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <label style={{ 
                      fontSize: '0.85em', 
                      color: theme.textSecondary,
                      display: 'block',
                      marginBottom: '6px',
                      fontWeight: '600'
                    }}>
                      Time To:
                    </label>
                    <select
                      value={timeTo}
                      onChange={(e) => setTimeTo(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: theme.inputBg,
                        border: `2px solid ${theme.inputBorder}`,
                        borderRadius: '6px',
                        color: theme.textPrimary,
                        fontSize: '0.95em',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {timeFieldOrder.map((field) => {
                        const fromIndex = getFieldIndex(timeFrom);
                        const fieldIndex = getFieldIndex(field);
                        const isDisabled = fieldIndex < fromIndex;
                        
                        return (
                          <option 
                            key={field} 
                            value={field}
                            disabled={isDisabled}
                            style={{
                              color: isDisabled ? theme.textSecondary : theme.textPrimary
                            }}
                          >
                            {getTimeFieldLabel(field)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  {/* Unit Toggle */}
                  <div style={{ paddingBottom: '0px' }}>
                    <label style={{ 
                      fontSize: '0.85em', 
                      color: theme.textSecondary,
                      display: 'block',
                      marginBottom: '6px',
                      fontWeight: '600'
                    }}>
                      Display Unit:
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      gap: '4px',
                      background: theme.detailBg,
                      padding: '4px',
                      borderRadius: '6px',
                      border: `1px solid ${theme.borderColor}`
                    }}>
                      <button
                        onClick={() => setTimeUnit('seconds')}
                        style={{
                          padding: '8px 16px',
                          background: timeUnit === 'seconds' ? theme.accentColor : 'transparent',
                          color: timeUnit === 'seconds' ? 'white' : theme.textPrimary,
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9em',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Seconds
                      </button>
                      <button
                        onClick={() => setTimeUnit('minutes')}
                        style={{
                          padding: '8px 16px',
                          background: timeUnit === 'minutes' ? theme.accentColor : 'transparent',
                          color: timeUnit === 'minutes' ? 'white' : theme.textPrimary,
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9em',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Minutes
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Slider Control */}
                <div style={{ marginBottom: '25px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    alignItems: 'baseline'
                  }}>
                    <label style={{ color: theme.textPrimary, fontWeight: '600', fontSize: '1em' }}>
                      Target Time:
                    </label>
                    <span style={{ 
                      color: theme.accentColor, 
                      fontWeight: '700',
                      fontSize: '1.4em'
                    }}>
                      {sliderValues.target}{getUnitLabel()}
                    </span>
                  </div>
                  
                  <input
                    type="range"
                    min={sliderValues.min}
                    max={sliderValues.max}
                    step={sliderValues.step}
                    value={sliderValues.target}
                    onChange={handleSliderChange}
                    style={{
                      width: '100%',
                      cursor: 'pointer',
                      accentColor: theme.accentColor,
                      height: '8px'
                    }}
                  />
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    fontSize: '0.85em',
                    color: theme.textSecondary,
                    marginTop: '8px'
                  }}>
                    <span>{sliderValues.min}{getUnitLabel()}</span>
                    <span>{midValue}{getUnitLabel()}</span>
                    <span>{sliderValues.max}{getUnitLabel()}</span>
                  </div>

                  {/* Custom Range Inputs */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    marginTop: '15px',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ 
                        fontSize: '0.8em', 
                        color: theme.textSecondary,
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Min ({getUnitLabelFull()})
                      </label>
                      <input
                        type="number"
                        value={sliderValues.min}
                        step={timeUnit === 'minutes' ? '0.1' : '1'}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value)) {
                            handleMinChange(value);
                          } else if (e.target.value === '') {
                            handleMinChange(0);
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseFloat(e.target.value);
                          if (isNaN(value) || value < 0) {
                            handleMinChange(0);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: theme.inputBg,
                          border: `1px solid ${theme.inputBorder}`,
                          borderRadius: '4px',
                          color: theme.textPrimary,
                          fontSize: '0.9em'
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ 
                        fontSize: '0.8em', 
                        color: theme.textSecondary,
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Max ({getUnitLabelFull()})
                      </label>
                      <input
                        type="number"
                        value={sliderValues.max}
                        step={timeUnit === 'minutes' ? '0.1' : '1'}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value)) {
                            handleMaxChange(value);
                          } else if (e.target.value === '') {
                            handleMaxChange(0);
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseFloat(e.target.value);
                          const minInCurrentUnit = sliderValues.min;
                          const minAllowed = minInCurrentUnit + (timeUnit === 'minutes' ? 0.2 : 10);
                          if (isNaN(value) || value < minAllowed) {
                            handleMaxChange(Math.max(minAllowed, timeUnit === 'minutes' ? 5 : 300));
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: theme.inputBg,
                          border: `1px solid ${theme.inputBorder}`,
                          borderRadius: '4px',
                          color: theme.textPrimary,
                          fontSize: '0.9em'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Main Metrics Display */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  
                  {/* Left: Percentage Display */}
                  <div style={{
                    textAlign: 'center',
                    padding: '30px 20px',
                    background: theme.detailBg,
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      fontSize: '4em',
                      fontWeight: '700',
                      color: performance.percentage >= 90 ? '#10B981' : 
                             performance.percentage >= 75 ? '#FBBF24' : '#EF4444',
                      lineHeight: 1
                    }}>
                      {performance.percentage}%
                    </div>
                    <div style={{
                      fontSize: '1.1em',
                      color: theme.textSecondary,
                      marginTop: '12px',
                      fontWeight: '500'
                    }}>
                      met target time
                    </div>
                    <div style={{
                      fontSize: '1em',
                      color: theme.textPrimary,
                      marginTop: '15px',
                      paddingTop: '15px',
                      borderTop: `1px solid ${theme.borderColor}`
                    }}>
                      <strong>{performance.metTarget.toLocaleString()}</strong> of <strong>{performance.total.toLocaleString()}</strong> events
                    </div>
                    {performance.overTarget > 0 && (
                      <div style={{
                        fontSize: '0.9em',
                        color: '#EF4444',
                        marginTop: '10px',
                        fontWeight: '500'
                      }}>
                        ⚠️ {performance.overTarget.toLocaleString()} events over target
                      </div>
                    )}
                  </div>

                  {/* Right: Percentile Distribution (KPI card style) */}
                  <div style={{
                    background: theme.detailBg,
                    padding: '20px',
                    borderRadius: '8px'
                  }}>
                    {/* Median - prominently displayed */}
                    <div style={{
                      fontSize: '2.5em',
                      fontWeight: '700',
                      color: theme.textPrimary,
                      textAlign: 'center',
                      marginBottom: '4px'
                    }}>
                      {convertToDisplayUnit(performance.median)}{getUnitLabel()}
                    </div>
                    <div style={{
                      fontSize: '0.9em',
                      color: theme.textSecondary,
                      textAlign: 'center',
                      marginBottom: '20px',
                      fontWeight: '500'
                    }}>
                      Median Time ({getTimeFieldLabel(timeFrom)} → {getTimeFieldLabel(timeTo)})
                    </div>

                    {/* Percentile bars */}
                    {(() => {
                      // Calculate max for bar width
                      const maxValue = Math.max(performance.p75, performance.p90, performance.p95);
                      const getWidth = (value) => maxValue > 0 ? (value / maxValue) * 100 : 0;

                      return (
                        <div style={{ marginTop: '10px' }}>
                          
                          {/* 75th */}
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              fontSize: '0.85em',
                              color: theme.textSecondary,
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.95em' }}>75%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '10px', 
                                backgroundColor: theme.cardBg,
                                borderRadius: '5px',
                                overflow: 'hidden',
                                border: `1px solid ${theme.borderColor}`
                              }}>
                                <div style={{
                                  width: `${getWidth(performance.p75)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#9CA3AF' : '#60A5FA',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '700', 
                                minWidth: '45px',
                                textAlign: 'right',
                                fontSize: '1em',
                                color: theme.textPrimary
                              }}>
                                {convertToDisplayUnit(performance.p75)}{getUnitLabel()}
                              </span>
                            </div>
                          </div>
                          
                          {/* 90th */}
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              fontSize: '0.85em',
                              color: theme.textSecondary,
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.95em' }}>90%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '10px', 
                                backgroundColor: theme.cardBg,
                                borderRadius: '5px',
                                overflow: 'hidden',
                                border: `1px solid ${theme.borderColor}`
                              }}>
                                <div style={{
                                  width: `${getWidth(performance.p90)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#6B7280' : '#2563EB',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '700', 
                                minWidth: '45px',
                                textAlign: 'right',
                                fontSize: '1em',
                                color: theme.textPrimary
                              }}>
                                {convertToDisplayUnit(performance.p90)}{getUnitLabel()}
                              </span>
                            </div>
                          </div>
                          
                          {/* 95th */}
                          <div style={{ marginBottom: '0px' }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              fontSize: '0.85em',
                              color: theme.textSecondary,
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontWeight: '600', minWidth: '35px', fontSize: '0.95em' }}>95%</span>
                              <div style={{ 
                                flex: 1, 
                                height: '10px', 
                                backgroundColor: theme.cardBg,
                                borderRadius: '5px',
                                overflow: 'hidden',
                                border: `1px solid ${theme.borderColor}`
                              }}>
                                <div style={{
                                  width: `${getWidth(performance.p95)}%`,
                                  height: '100%',
                                  backgroundColor: isDarkTheme ? '#374151' : '#1E40AF',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ 
                                fontWeight: '700', 
                                minWidth: '45px',
                                textAlign: 'right',
                                fontSize: '1em',
                                color: theme.textPrimary
                              }}>
                                {convertToDisplayUnit(performance.p95)}{getUnitLabel()}
                              </span>
                            </div>
                          </div>

                        </div>
                      );
                    })()}
                  </div>

                </div>

                {/* Progress Bar Visualization */}
                <div style={{ 
                  marginTop: '20px',
                  padding: '15px',
                  background: theme.detailBg,
                  borderRadius: '6px'
                }}>
                  <div style={{ 
                    fontSize: '0.9em', 
                    color: theme.textSecondary, 
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>Progress to Target</span>
                    <span>{performance.percentage}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '20px',
                    backgroundColor: theme.cardBg,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{
                      width: `${Math.min(100, performance.percentage)}%`,
                      height: '100%',
                      background: performance.percentage >= 90 
                        ? 'linear-gradient(90deg, #10B981, #059669)' 
                        : performance.percentage >= 75 
                        ? 'linear-gradient(90deg, #FBBF24, #F59E0B)'
                        : 'linear-gradient(90deg, #EF4444, #DC2626)',
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '8px'
                    }}>
                      {performance.percentage >= 10 && (
                        <span style={{ 
                          color: 'white', 
                          fontSize: '0.75em', 
                          fontWeight: '700',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {performance.metTarget.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.75em', 
                    color: theme.textSecondary, 
                    marginTop: '6px',
                    textAlign: 'center'
                  }}>
                    {performance.metTarget.toLocaleString()} events ≤ {sliderValues.target}{getUnitLabel()}  |  {performance.overTarget.toLocaleString()} events > {sliderValues.target}{getUnitLabel()}
                  </div>
                </div>

              </div>
            );
          })()}
            </div>
              <SectionResizeHandle sectionId="eventTime" resizingSection={resizingSection} theme={theme} onResizeStart={handleSectionResizeStart} onReset={resetSectionHeight} />
            </div>
          )}
        </div>

        {/* Locations With Multiple Events - Collapsible */}
        <div
          {...sectionDragProps('repeatedEvents')}
          style={{ background: theme.cardBg, borderRadius: '12px', padding: '0', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: '24px', overflow: 'hidden', ...sectionOrderStyle('repeatedEvents') }}>
          
          {/* Header with collapse button */}
          <div 
            style={{ 
              fontSize: '1.05em',
              fontWeight: '600',
              padding: '10px 20px',
              color: theme.sectionLabelColor,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: allSectionsCollapsed ? 'grab' : 'pointer',
              userSelect: 'none'
            }}
            onClick={(e) => {
              // Allow expanding even in drag mode (will exit drag mode)
              e.stopPropagation(); // Prevent drag handling
              setIsRepeatedEventsExpanded(!isRepeatedEventsExpanded);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {allSectionsCollapsed && <span style={{ color: theme.textSecondary, fontSize: '1em', lineHeight: 1, userSelect: 'none' }}>⠿</span>}
              📍 Locations With Multiple Events
            </div>
            
            {/* Collapse/Expand button */}
            <span 
              style={{
                fontSize: '1em',
                color: theme.sectionLabelColor,
                transform: isRepeatedEventsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s ease',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              title={isRepeatedEventsExpanded ? 'Collapse section' : 'Expand section'}
            >
              ▼
            </span>
          </div>

          {/* Collapsible Content */}
          {isRepeatedEventsExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '0 20px 20px',
                overflowY: 'auto',
                height: sectionHeights.repeatedEvents ? `${sectionHeights.repeatedEvents}px` : 'auto',
                minHeight: '150px',
              }}>
              {(() => {
                const repeatedLocations = repeatedLocationData
                  .filter(loc => loc.count >= repeatedEventsThreshold)
                  .sort((a, b) => repeatedLocationsDirection === 'desc' ? b.count - a.count : a.count - b.count);
                return (
                  <>
                    {/* Threshold Slider */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        alignItems: 'baseline'
                      }}>
                        <label style={{ color: theme.textPrimary, fontWeight: '600', fontSize: '1em' }}>
                          Minimum Events per Location:
                        </label>
                        <span style={{ 
                          color: theme.accentColor, 
                          fontWeight: '700',
                          fontSize: '1.4em'
                        }}>
                          {repeatedEventsThreshold}
                        </span>
                      </div>
                      
                      <input
                        type="range"
                        min={repeatedEventsMin}
                        max={repeatedEventsMax}
                        step="1"
                        value={repeatedEventsThreshold}
                        onChange={(e) => setRepeatedEventsThreshold(parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          cursor: 'pointer',
                          accentColor: theme.accentColor,
                          height: '8px'
                        }}
                      />
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        fontSize: '0.85em',
                        color: theme.textSecondary,
                        marginTop: '8px'
                      }}>
                        <span>{repeatedEventsMin}</span>
                        <span>{Math.round((repeatedEventsMin + repeatedEventsMax) / 2)}</span>
                        <span>{repeatedEventsMax}</span>
                      </div>

                      {/* Custom Range Inputs */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '20px', 
                        marginTop: '15px',
                        alignItems: 'center'
                      }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ 
                            fontSize: '0.8em', 
                            color: theme.textSecondary,
                            display: 'block',
                            marginBottom: '4px'
                          }}>
                            Min (events)
                          </label>
                          <input
                            type="number"
                            value={repeatedEventsMin}
                            step="1"
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value)) {
                                setRepeatedEventsMin(value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);
                              if (isNaN(value) || value < 1) {
                                setRepeatedEventsMin(1);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: `1px solid ${theme.inputBorder}`,
                              borderRadius: '6px',
                              background: theme.inputBg,
                              color: theme.textPrimary,
                              fontSize: '0.9em'
                            }}
                          />
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <label style={{ 
                            fontSize: '0.8em', 
                            color: theme.textSecondary,
                            display: 'block',
                            marginBottom: '4px'
                          }}>
                            Max (events)
                          </label>
                          <input
                            type="number"
                            value={repeatedEventsMax}
                            step="1"
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value)) {
                                setRepeatedEventsMax(value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);
                              if (isNaN(value) || value < repeatedEventsMin) {
                                setRepeatedEventsMax(repeatedEventsMin + 10);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: `1px solid ${theme.inputBorder}`,
                              borderRadius: '6px',
                              background: theme.inputBg,
                              color: theme.textPrimary,
                              fontSize: '0.9em'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Summary with Percentages */}
                    <div style={{
                      padding: '15px 20px',
                      background: theme.detailBg,
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: `1px solid ${theme.borderColor}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: '0.95em', 
                        color: theme.textPrimary,
                        fontWeight: '600',
                        lineHeight: '1.5'
                      }}>
                        <span style={{ color: theme.accentColor, fontWeight: '700', fontSize: '1.1em' }}>
                          {repeatedLocations.length}
                        </span>
                        {' '}Locations{' '}
                        <span style={{ color: theme.textSecondary, fontSize: '0.9em' }}>
                          ({((repeatedLocations.length / repeatedLocationData.length) * 100).toFixed(1)}%)
                        </span>
                        {' '}with a total of{' '}
                        <span style={{ color: theme.accentColor, fontWeight: '700', fontSize: '1.1em' }}>
                          {repeatedLocations.reduce((sum, loc) => sum + loc.count, 0).toLocaleString()}
                        </span>
                        {' '}
                        <span style={{ color: theme.textSecondary, fontSize: '0.9em' }}>
                          ({((repeatedLocations.reduce((sum, loc) => sum + loc.count, 0) / filteredData.length) * 100).toFixed(1)}%)
                        </span>
                        {' '}of events
                      </div>
                    </div>

                    {/* Sort Controls */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '14px',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      {/* Chart sort: A-Z / by count */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>Chart sort:</span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {[
                            { value: 'alphabetical', label: 'A-Z', title: 'Sort event types alphabetically' },
                            { value: 'count',        label: '#',   title: 'Sort event types by count' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setRepeatedEventsSortBy(opt.value)}
                              title={opt.title}
                              style={{
                                padding: '4px 10px',
                                background: repeatedEventsSortBy === opt.value ? theme.accentColor : theme.inputBg,
                                color: repeatedEventsSortBy === opt.value ? 'white' : theme.textPrimary,
                                border: `1px solid ${repeatedEventsSortBy === opt.value ? theme.accentColor : theme.inputBorder}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8em',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* List sort: ascending / descending by count */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.8em', color: theme.textSecondary, fontWeight: '600' }}>List order:</span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {[
                            { value: 'desc', label: '↓ Most', title: 'Most events first' },
                            { value: 'asc',  label: '↑ Least', title: 'Fewest events first' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setRepeatedLocationsDirection(opt.value)}
                              title={opt.title}
                              style={{
                                padding: '4px 10px',
                                background: repeatedLocationsDirection === opt.value ? theme.accentColor : theme.inputBg,
                                color: repeatedLocationsDirection === opt.value ? 'white' : theme.textPrimary,
                                border: `1px solid ${repeatedLocationsDirection === opt.value ? theme.accentColor : theme.inputBorder}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8em',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Location List */}
                    {repeatedLocations.length === 0 ? (
                      <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: theme.textSecondary,
                        fontSize: '0.95em'
                      }}>
                        No locations with {repeatedEventsThreshold} or more events found in the current date range.
                      </div>
                    ) : (
                      <div style={{ 
                        maxHeight: '1600px',
                        overflowY: repeatedLocations.length > 8 ? 'auto' : 'visible',
                        paddingRight: repeatedLocations.length > 8 ? '8px' : '0'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {repeatedLocations.map((location, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '12px',
                              background: theme.cardBg,
                              borderRadius: '8px',
                              border: `2px solid ${theme.borderColor}`,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.accentColor}
                            onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.borderColor}
                          >
                            {/* Location Header */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '8px'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontSize: '1em',
                                  fontWeight: '600',
                                  color: theme.textPrimary,
                                  marginBottom: '4px'
                                }}>
                                  {location.address}
                                </div>
                                <div style={{
                                  fontSize: '0.85em',
                                  color: theme.textSecondary
                                }}>
                                  {location.events[0].City || ''} {location.events[0].State || ''}
                                </div>
                              </div>
                              <div style={{
                                background: theme.accentColor,
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontSize: '1.1em',
                                fontWeight: '700',
                                minWidth: '60px',
                                textAlign: 'center'
                              }}>
                                {location.count}
                              </div>
                            </div>

                            {/* Event Type Bar Chart - Vertical */}
                            <div style={{ marginTop: '8px', overflowX: 'auto', overflowY: 'hidden' }}>
                              {(() => {
                                const eventTypes = {};
                                location.events.forEach(event => {
                                  const type = event.EventType || 'Unknown';
                                  eventTypes[type] = (eventTypes[type] || 0) + 1;
                                });
                                
                                // Sort based on user selection
                                const sortedTypes = Object.entries(eventTypes)
                                  .sort((a, b) => {
                                    if (repeatedEventsSortBy === 'alphabetical') {
                                      return a[0].localeCompare(b[0]); // Sort by name A-Z
                                    } else {
                                      return b[1] - a[1]; // Sort by count (most to least)
                                    }
                                  });
                                
                                const maxCount = Math.max(...sortedTypes.map(([, count]) => count));
                                
                                return (
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '6px',
                                    alignItems: 'flex-end',
                                    height: '150px',
                                    paddingTop: '10px',
                                    minWidth: 'fit-content'
                                  }}>
                                    {sortedTypes.map(([type, count], idx) => {
                                      const percentage = ((count / location.count) * 100).toFixed(1);
                                      const barColor = theme.chartColors[idx % theme.chartColors.length];
                                      
                                      return (
                                        <div 
                                          key={type} 
                                          style={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '4px',
                                            minWidth: '45px',
                                            maxWidth: '80px',
                                            flex: sortedTypes.length <= 6 ? '1' : '0 0 auto'
                                          }}
                                        >
                                          {/* Count and Percentage */}
                                          <div style={{
                                            fontSize: '0.75em',
                                            fontWeight: '700',
                                            color: theme.textPrimary,
                                            textAlign: 'center',
                                            lineHeight: '1.3'
                                          }}>
                                            {count}
                                          </div>
                                          <div style={{
                                            fontSize: '0.65em',
                                            color: theme.textSecondary,
                                            textAlign: 'center',
                                            lineHeight: '1.3'
                                          }}>
                                            {percentage}%
                                          </div>
                                          
                                          {/* Vertical Bar */}
                                          <div style={{ 
                                            width: '100%',
                                            height: '95px',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'flex-end'
                                          }}>
                                            <div style={{
                                              width: '100%',
                                              height: `${(count / maxCount) * 100}%`,
                                              background: barColor,
                                              borderRadius: '4px 4px 0 0',
                                              transition: 'height 0.3s ease',
                                              minHeight: '4px'
                                            }}></div>
                                          </div>
                                          
                                          {/* Type Label */}
                                          <div style={{
                                            fontSize: '0.7em',
                                            color: theme.textSecondary,
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                            maxWidth: '80px'
                                          }}>
                                            {type}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        ))}
                      </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
              <SectionResizeHandle sectionId="repeatedEvents" resizingSection={resizingSection} theme={theme} onResizeStart={handleSectionResizeStart} onReset={resetSectionHeight} />
            </div>
          )}
        </div>


        {/* Event List Panel */}
        <EventList
          theme={theme}
          isDarkTheme={isDarkTheme}
          filteredData={filteredData}
          onSelectEvent={(event, list, index) => {
            setSelectedEvent(event);
            setEventNavContext(list != null ? { list, index } : null);
          }}
          sectionHeight={sectionHeights.recentEvents}
          resizingSection={resizingSection}
          onResizeStart={handleSectionResizeStart}
          onResetHeight={resetSectionHeight}
          allSectionsCollapsed={allSectionsCollapsed}
          sectionDragProps={sectionDragProps}
          sectionOrderStyle={sectionOrderStyle}
          isCollapsed={isRecentEventsCollapsed}
          onToggleCollapse={() => setIsRecentEventsCollapsed(prev => !prev)}
        />
      </div>{/* End maxWidth container */}

      {/* Response Plan Dialog */}
      {showPlaybookDialog && (
        <div onClick={() => setShowPlaybookDialog(false)} style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.7)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 3000, 
          padding: '20px', 
          overflowY: 'auto',
          backdropFilter: 'blur(4px)'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ 
            background: theme.cardBg, 
            borderRadius: '16px', 
            padding: '0', 
            maxWidth: '700px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflowY: 'auto', 
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            border: `1px solid ${theme.borderColor}`,
            boxSizing: 'border-box'
          }}>
            {/* Header */}
            <div style={{
              padding: '25px 30px',
              background: `linear-gradient(135deg, ${theme.accentColor} 0%, ${theme.isDark ? '#0F7C6F' : '#17A396'} 100%)`,
              borderRadius: '16px 16px 0 0',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div>
                  <h2 style={{ 
                    margin: '0 0 8px 0', 
                    color: 'white',
                    fontSize: '1.4em',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    📋 {playbooks[editingPlaybook] ? 'Edit' : 'Create'} Response Plan
                  </h2>
                  <div style={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1.05em',
                    fontWeight: '600',
                    background: 'rgba(255,255,255,0.15)',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    📍 {currentPlaybookForm.areaName}
                  </div>
                </div>
                <button 
                  onClick={() => setShowPlaybookDialog(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '1.3em',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                        boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '30px' }}>
              {/* Plan Status Section */}
              <div style={{ 
                marginBottom: '25px'
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '1.05em', 
                  color: theme.textPrimary,
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⚡ Plan Status
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Status
                    </label>
                    <select 
                      value={currentPlaybookForm.status} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, status: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary,
                        fontSize: '0.95em',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    >
                      <option value="Draft">📝 Draft</option>
                      <option value="Approved">✅ Approved</option>
                      <option value="Active">🟢 Active</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Priority
                    </label>
                    <select 
                      value={currentPlaybookForm.priority} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, priority: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary,
                        fontSize: '0.95em',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    >
                      <option value="Advisory">💡 Advisory</option>
                      <option value="Recommended">⭐ Recommended</option>
                      <option value="Mandatory">🚨 Mandatory</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* When Does This Apply Section */}
              <div style={{ 
                background: theme.detailBg, 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '25px',
                border: `1px solid ${theme.borderColor}`
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '1.05em', 
                  color: theme.textPrimary,
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📅 When Does This Apply?
                </h3>
                
                {/* Start and Expiration Date/Time */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Start Date/Time
                    </label>
                    <input 
                      type="datetime-local" 
                      value={currentPlaybookForm.startDate} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, startDate: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Expiration Date/Time
                    </label>
                    <input 
                      type="datetime-local" 
                      value={currentPlaybookForm.expirationDate} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, expirationDate: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                </div>
                
                {/* Priority Threshold and Time Window */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Priority Threshold
                    </label>
                    <select 
                      value={currentPlaybookForm.priorityThreshold} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, priorityThreshold: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        cursor: 'pointer',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    >
                      <option value="all">All Priorities</option>
                      <option value="1">P1 Only</option>
                      <option value="2">P1-P2</option>
                      <option value="3">P1-P3</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Time Window
                    </label>
                    <select 
                      value={currentPlaybookForm.timeWindow} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, timeWindow: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        cursor: 'pointer',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    >
                      <option value="always">⏰ 24/7</option>
                      <option value="scheduled">📆 Scheduled</option>
                    </select>
                  </div>
                </div>
                
                {/* Daily Start/End Time (conditional) */}
                {currentPlaybookForm.timeWindow === 'scheduled' && (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '15px', 
                    marginTop: '15px',
                    padding: '15px',
                    background: theme.cardBg,
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '600', 
                        fontSize: '0.9em', 
                        color: theme.textSecondary
                      }}>
                        Daily Start Time
                      </label>
                      <input 
                        type="time" 
                        value={currentPlaybookForm.startTime} 
                        onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, startTime: e.target.value }))} 
                        style={{ 
                          width: '100%', 
                          padding: '10px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '8px', 
                          background: theme.inputBg, 
                          color: theme.textPrimary,
                          fontSize: '0.9em',
                          transition: 'border 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                        onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                      />
                    </div>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '600', 
                        fontSize: '0.9em', 
                        color: theme.textSecondary
                      }}>
                        Daily End Time
                      </label>
                      <input 
                        type="time" 
                        value={currentPlaybookForm.endTime} 
                        onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, endTime: e.target.value }))} 
                        style={{ 
                          width: '100%', 
                          padding: '10px 12px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '8px', 
                          background: theme.inputBg, 
                          color: theme.textPrimary,
                          fontSize: '0.9em',
                          transition: 'border 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                        onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Plan of Operation Section */}
              <div style={{ 
                background: theme.detailBg, 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '25px',
                border: `1px solid ${theme.borderColor}`
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '1.05em', 
                  color: theme.textPrimary,
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🎯 Plan of Operation
                </h3>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px', 
                    fontWeight: '600', 
                    fontSize: '0.9em', 
                    color: theme.textSecondary
                  }}>
                    🚨 Dispatch Guidance
                    <span style={{ 
                      color: '#EF4444', 
                      fontSize: '1.1em',
                      fontWeight: '700'
                    }}>*</span>
                  </label>
                  <textarea 
                    value={currentPlaybookForm.dispatchGuidance} 
                    onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, dispatchGuidance: e.target.value }))} 
                    placeholder="Enter each instruction on a new line for bullet formatting&#10;e.g.&#10;Two-unit minimum response&#10;Stage one block out&#10;Contact command on arrival"
                    rows={3} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: `2px solid ${theme.inputBorder}`, 
                      borderRadius: '8px', 
                      background: theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '0.9em', 
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px', 
                    fontWeight: '600', 
                    fontSize: '0.9em', 
                    color: theme.textSecondary
                  }}>
                    📍 Staging Instructions
                  </label>
                  <textarea 
                    value={currentPlaybookForm.stagingInstructions} 
                    onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, stagingInstructions: e.target.value }))} 
                    placeholder="Each line becomes a bullet point&#10;e.g.&#10;Stage at Main St and 1st Ave&#10;Avoid blocking hydrant access&#10;Maintain 100ft clearance"
                    rows={3} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: `2px solid ${theme.inputBorder}`, 
                      borderRadius: '8px', 
                      background: theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '0.9em', 
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px', 
                    fontWeight: '600', 
                    fontSize: '0.9em', 
                    color: theme.textSecondary
                  }}>
                    ⚠️ Safety Notes
                  </label>
                  <textarea 
                    value={currentPlaybookForm.safetyNotes} 
                    onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, safetyNotes: e.target.value }))} 
                    placeholder="Each line becomes a bullet point&#10;e.g.&#10;Hazmat storage on site&#10;Aggressive dog present&#10;Unstable floor structure"
                    rows={3} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: `2px solid ${theme.inputBorder}`, 
                      borderRadius: '8px', 
                      background: theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '0.9em', 
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px', 
                    fontWeight: '600', 
                    fontSize: '0.9em', 
                    color: theme.textSecondary
                  }}>
                    👮 Supervisor Notification
                  </label>
                  <textarea 
                    value={currentPlaybookForm.supervisorNotification} 
                    onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, supervisorNotification: e.target.value }))} 
                    placeholder="Each line becomes a bullet point&#10;e.g.&#10;Notify Watch Commander immediately&#10;Contact Battalion Chief&#10;Request additional resources"
                    rows={3} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: `2px solid ${theme.inputBorder}`, 
                      borderRadius: '8px', 
                      background: theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '0.9em', 
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                  />
                </div>
                
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px', 
                    fontWeight: '600', 
                    fontSize: '0.9em', 
                    color: theme.textSecondary
                  }}>
                    📝 Special Instructions
                  </label>
                  <textarea 
                    value={currentPlaybookForm.specialInstructions} 
                    onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, specialInstructions: e.target.value }))} 
                    placeholder="Each line becomes a bullet point&#10;e.g.&#10;Contact facility manager on arrival&#10;Use west entrance only&#10;Elevator key in Knox box"
                    rows={3} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: `2px solid ${theme.inputBorder}`, 
                      borderRadius: '8px', 
                      background: theme.inputBg, 
                      color: theme.textPrimary, 
                      fontSize: '0.9em', 
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                    onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                  />
                </div>
              </div>
              
              {/* Metadata Section */}
              <div style={{ 
                background: theme.detailBg, 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px',
                border: `1px solid ${theme.borderColor}`
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '1.05em', 
                  color: theme.textPrimary,
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📂 Approval & Tracking
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Created By
                    </label>
                    <input 
                      type="text" 
                      value={currentPlaybookForm.createdBy} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, createdBy: e.target.value }))} 
                      placeholder="Your name"
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Created Date/Time
                    </label>
                    <input 
                      type="datetime-local" 
                      value={currentPlaybookForm.createdDate} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, createdDate: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Approved By
                    </label>
                    <input 
                      type="text" 
                      value={currentPlaybookForm.approvedBy} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, approvedBy: e.target.value }))} 
                      placeholder="Approver name"
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      fontSize: '0.9em', 
                      color: theme.textSecondary
                    }}>
                      Approved Date/Time
                    </label>
                    <input 
                      type="datetime-local" 
                      value={currentPlaybookForm.approvedDate} 
                      onChange={(e) => setCurrentPlaybookForm(prev => ({ ...prev, approvedDate: e.target.value }))} 
                      style={{ 
                        width: '100%', 
                        padding: '10px 12px', 
                        border: `2px solid ${theme.inputBorder}`, 
                        borderRadius: '8px', 
                        background: theme.inputBg, 
                        color: theme.textPrimary, 
                        fontSize: '0.9em',
                        transition: 'border 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = theme.accentColor}
                      onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                paddingTop: '20px', 
                borderTop: `2px solid ${theme.borderColor}`,
                position: 'sticky',
                bottom: 0,
                background: theme.cardBg,
                marginLeft: '-30px',
                marginRight: '-30px',
                marginBottom: '-30px',
                padding: '20px 30px',
                borderRadius: '0 0 16px 16px'
              }}>
                <button 
                  onClick={savePlaybook} 
                  style={{ 
                    flex: 1, 
                    padding: '14px', 
                    background: theme.accentColor, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontWeight: '700',
                    fontSize: '1em',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(20, 184, 166, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(20, 184, 166, 0.3)';
                  }}
                >
                  💾 Save Plan
                </button>
                {playbooks[editingPlaybook] && (
                  <button 
                    onClick={() => { deletePlaybook(editingPlaybook); setShowPlaybookDialog(false); }} 
                    style={{ 
                      padding: '14px 20px', 
                      background: '#EF4444', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      fontWeight: '700',
                      fontSize: '1em',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#EF4444'}
                  >
                    🗑️ Delete
                  </button>
                )}
                <button 
                  onClick={() => setShowPlaybookDialog(false)} 
                  style={{ 
                    padding: '14px 20px', 
                    background: theme.detailBg, 
                    color: theme.textPrimary, 
                    border: `2px solid ${theme.borderColor}`, 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontWeight: '600',
                    fontSize: '1em',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.background = theme.detailBg}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Layer Dialog */}
      {showAddLayerDialog && (
        <AddLayerModal
          theme={theme}
          onClose={() => setShowAddLayerDialog(false)}
          onSave={(formData) => createCustomLayer(formData)}
        />
      )}

      {/* Edit Layer Dialog */}
      {/* Edit Layer Dialog */}
      {showEditLayerDialog && (() => {
        const editingLayer = customLayers.find(l => l.id === editingLayerId);
        if (!editingLayer) return null;
        return (
          <EditLayerModal
            theme={theme}
            layer={editingLayer}
            onClose={() => { setShowEditLayerDialog(false); setEditingLayerId(null); }}
            onSave={(formData) => updateCustomLayer(formData)}
          />
        );
      })()}

      {/* Drawing Instructions Overlay */}
      {isDrawingCustomLayer && currentDrawingLayer && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: theme.cardBg,
          padding: '15px 25px',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: `2px solid ${theme.accentColor}`,
          zIndex: 2000,
          minWidth: '300px'
        }}>
          <div style={{
            fontSize: '0.9em',
            color: theme.textPrimary,
            textAlign: 'center'
          }}>
            <div style={{ fontWeight: '700', marginBottom: '8px' }}>
              🎨 Drawing: {currentDrawingLayer.name}
            </div>
            <div style={{ fontSize: '0.85em', color: theme.textSecondary }}>
              {currentDrawingLayer.type === 'marker' && 'Click map to add markers • Right-click or ESC when done'}
              {currentDrawingLayer.type === 'polygon' && `${customLayerPoints.length} points • Right-click or ESC to finish`}
              {currentDrawingLayer.type === 'line' && `${customLayerPoints.length} points • Right-click or ESC to finish`}
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {selectedEvent && (() => {
        const pendingMin   = calculatePendingTime(selectedEvent);
        const responseMin  = calculateResponseTime(selectedEvent);
        const onSceneMin   = calculateOnSceneTime(selectedEvent);
        const travelMin    = calculateTravelTime(selectedEvent);
        const totalMin     = calculateEventTotalTime(selectedEvent);

        // Nav helpers — only available when opened from Event List
        const navList  = eventNavContext?.list;
        const navIndex = eventNavContext?.index ?? -1;
        const hasPrev  = navList != null && navIndex > 0;
        const hasNext  = navList != null && navIndex < navList.length - 1;
        const goTo = (newIndex) => {
          setSelectedEvent(navList[newIndex]);
          setEventNavContext({ list: navList, index: newIndex });
        };
        const labelStyle   = { fontSize: '0.7em', color: theme.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' };
        const cellStyle    = { padding: '8px 10px', background: theme.detailBg, borderRadius: '6px' };
        const valStyle     = { fontSize: '0.95em', color: theme.textPrimary, fontWeight: '500' };
        const sectionHdr   = { fontSize: '0.72em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: theme.textSecondary, marginBottom: '7px', paddingBottom: '4px', borderBottom: `1px solid ${theme.borderColor}` };
        const timelineItems = [
          { label: 'Event Start', time: selectedEvent.EventStartDateTime, icon: '🕐', delta: null },
          { label: 'Received',   time: selectedEvent.ReceivedDateTime,   icon: '📞', delta: calculateEventCreationTime(selectedEvent) != null ? `+${calculateEventCreationTime(selectedEvent).toFixed(1)}m` : null },
          { label: 'Dispatched', time: selectedEvent.DispatchedDateTime, icon: '🚨', delta: pendingMin  != null ? `+${pendingMin.toFixed(1)}m`  : null },
          { label: 'Enroute',    time: selectedEvent.EnrouteDateTime,    icon: '🚔', delta: calculateResponseDelay(selectedEvent) != null ? `+${calculateResponseDelay(selectedEvent).toFixed(1)}m` : null },
          { label: 'Arrived',    time: selectedEvent.ArrivedDateTime,    icon: '📍', delta: responseMin != null ? `+${responseMin.toFixed(1)}m` : null },
          { label: 'Completed',  time: selectedEvent.CompletedDateTime,  icon: '✅', delta: onSceneMin  != null ? `+${onSceneMin.toFixed(1)}m`  : null },
        ];

        // Dispatch Time: Event Start → Dispatched
        const dispatchMin = (() => {
          if (!selectedEvent.EventStartDateTime || !selectedEvent.DispatchedDateTime) return null;
          const start = parseLocalDateTime(selectedEvent.EventStartDateTime);
          const dispatched = parseLocalDateTime(selectedEvent.DispatchedDateTime);
          if (!start || !dispatched || dispatched < start) return null;
          return (dispatched - start) / (1000 * 60);
        })();
        return (
          <div
            id="event-details-modal"
            onClick={() => setSelectedEvent(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: isTablet ? '4px' : '16px' }}>
            <div
              id="event-details-content"
              className="cad-modal-inner"
              onClick={(e) => e.stopPropagation()}
              style={{ background: theme.cardBg, borderRadius: '12px', maxWidth: '960px', width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>

              {/* ── Compact header ── */}
              <div style={{ padding: '12px 18px', borderBottom: `1px solid ${theme.borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '0.7em', color: theme.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '1px' }}>Event ID</div>
                    <div style={{ fontSize: '1.4em', fontWeight: '800', color: theme.accentColor, lineHeight: 1 }}>
                      {selectedEvent.Id ?? '—'}
                    </div>
                  </div>
                  <div style={{ width: '1px', height: '36px', background: theme.borderColor }} />
                  <div>
                    <div style={{ fontSize: '1em', fontWeight: '700', color: theme.textPrimary, lineHeight: 1.1 }}>
                      {selectedEvent.EventType || 'Event Details'}
                    </div>
                    <div style={{ fontSize: '0.78em', color: theme.textSecondary, marginTop: '2px' }}>
                      Code: {selectedEvent.EventCode || '—'} &nbsp;·&nbsp; PTS {selectedEvent.ptsEventID}
                    </div>
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.8em', fontWeight: '600',
                    background: selectedEvent.EventPriority === 1 ? '#FEE2E2' : selectedEvent.EventPriority === 2 ? '#FEF3C7' : '#D1FAE5',
                    color:      selectedEvent.EventPriority === 1 ? '#991B1B' : selectedEvent.EventPriority === 2 ? '#92400E' : '#065F46' }}>
                    Priority {selectedEvent.EventPriority ?? '-'}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.8em', fontWeight: '600', ...getStatusBadgeStyle(selectedEvent.Status, isDarkTheme) }}>
                    {selectedEvent.Status}
                  </span>
                  {selectedEvent.Agency && (
                    <span style={{ fontSize: '0.82em', color: theme.textSecondary }}>
                      {selectedEvent.AgencyType} · {selectedEvent.Agency}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {navList != null && (
                    <>
                      <button
                        onClick={() => hasPrev && goTo(navIndex - 1)}
                        disabled={!hasPrev}
                        title="Previous event"
                        style={{ background: theme.detailBg, border: `1px solid ${theme.borderColor}`, borderRadius: '6px', padding: '4px 10px', fontSize: '0.85em', cursor: hasPrev ? 'pointer' : 'not-allowed', color: hasPrev ? theme.textPrimary : theme.textSecondary, opacity: hasPrev ? 1 : 0.4, fontWeight: '600' }}
                        onMouseEnter={(e) => { if (hasPrev) e.currentTarget.style.background = theme.hoverBg; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = theme.detailBg}>
                        ← Prev
                      </button>
                      <span style={{ fontSize: '0.75em', color: theme.textSecondary, minWidth: '60px', textAlign: 'center' }}>
                        {navIndex + 1} / {navList.length}
                      </span>
                      <button
                        onClick={() => hasNext && goTo(navIndex + 1)}
                        disabled={!hasNext}
                        title="Next event"
                        style={{ background: theme.detailBg, border: `1px solid ${theme.borderColor}`, borderRadius: '6px', padding: '4px 10px', fontSize: '0.85em', cursor: hasNext ? 'pointer' : 'not-allowed', color: hasNext ? theme.textPrimary : theme.textSecondary, opacity: hasNext ? 1 : 0.4, fontWeight: '600' }}
                        onMouseEnter={(e) => { if (hasNext) e.currentTarget.style.background = theme.hoverBg; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = theme.detailBg}>
                        Next →
                      </button>
                    </>
                  )}
                  <button onClick={() => setSelectedEvent(null)} className="no-print"
                    style={{ background: theme.detailBg, border: 'none', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.3em', cursor: 'pointer', color: theme.textPrimary, flexShrink: 0 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBg}
                    onMouseLeave={(e) => e.currentTarget.style.background = theme.detailBg}>×</button>
                </div>
              </div>

              {/* ── Two-column body ── */}
              <div className="cad-modal-body" style={{ display: 'flex', gap: 0, overflow: 'hidden', flex: 1, minHeight: 0 }}>

                {/* LEFT: Description + Timeline + Metrics */}
                <div className="cad-modal-left" style={{ flex: '1 1 55%', padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                  {/* Description */}
                  {selectedEvent.EventDescription && (
                    <div style={cellStyle}>
                      <div style={labelStyle}>Description</div>
                      <div style={{ ...valStyle, fontSize: '0.9em' }}>{selectedEvent.EventDescription}</div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div>
                    <div style={sectionHdr}>⏱ Timeline</div>
                    <div style={{ position: 'relative', paddingLeft: '26px' }}>
                      <div style={{ position: 'absolute', left: '9px', top: '8px', bottom: '8px', width: '2px', background: theme.borderColor }} />
                      {timelineItems.map((item, idx) => (
                        <div key={idx} style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: idx < timelineItems.length - 1 ? '6px' : 0 }}>
                          <div style={{
                            position: 'absolute', left: '-20px',
                            width: '16px', height: '16px', borderRadius: '50%',
                            background: item.time ? theme.accentColor : theme.borderColor,
                            border: `2px solid ${theme.cardBg}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55em'
                          }}>{item.time ? item.icon : ''}</div>
                          <div style={{ ...cellStyle, flex: 1, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={labelStyle}>{item.label}</div>
                              <div style={{ fontSize: '0.88em', color: item.time ? theme.textPrimary : theme.textSecondary }}>
                                {item.time ? formatDateTime(item.time) : 'Not recorded'}
                              </div>
                            </div>
                            {item.delta && (
                              <div style={{ fontSize: '0.78em', color: theme.textSecondary, fontStyle: 'italic', flexShrink: 0, marginLeft: '8px' }}>
                                {item.delta}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Performance Metrics — 4 cards, single row */}
                  <div>
                    <div style={sectionHdr}>📊 Performance</div>
                    <div className="cad-perf-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                      {[
                        { label: 'Dispatch Time',  val: dispatchMin,                            sub: 'Start → Dispatched' },
                        { label: 'Turnout Time',   val: calculateResponseDelay(selectedEvent),  sub: 'Dispatched → Enroute' },
                        { label: 'Response Time',  val: responseMin,                            sub: 'Dispatched → Arrived' },
                        { label: 'Total Time',     val: totalMin,                               sub: 'Received → Completed' },
                      ].map(m => (
                        <div key={m.label} style={{ ...cellStyle, textAlign: 'center', padding: '7px 4px' }}>
                          <div style={{ fontSize: '0.65em', color: theme.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>{m.label}</div>
                          <div style={{ fontSize: '1.2em', fontWeight: '700', color: theme.textPrimary, lineHeight: 1.1 }}>
                            {m.val != null ? m.val.toFixed(1) : '—'}
                          </div>
                          <div style={{ fontSize: '0.62em', color: theme.textSecondary, marginTop: '1px' }}>min</div>
                          <div style={{ fontSize: '0.6em', color: theme.textSecondary, marginTop: '2px', lineHeight: 1.2 }}>{m.sub}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div style={{ width: '1px', background: theme.borderColor, flexShrink: 0 }} />

                {/* RIGHT: Map + Location + Caller */}
                <div className="cad-modal-right" style={{ flex: '1 1 45%', padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                  {/* Map */}
                  {selectedEvent.LatitudeDegree && selectedEvent.LongitudeDegree && (
                    <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.borderColor}`, flexShrink: 0 }}>
                      <div ref={modalMapContainerRef} style={{ width: '100%', height: '100%' }} />
                    </div>
                  )}

                  {/* Location */}
                  <div>
                    <div style={sectionHdr}>📍 Location</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={cellStyle}>
                        <div style={labelStyle}>Address</div>
                        <div style={{ ...valStyle, fontSize: '0.9em' }}>{selectedEvent.Address || '-'}</div>
                      </div>
                      {selectedEvent.LatitudeDegree && selectedEvent.LongitudeDegree && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <div style={cellStyle}>
                            <div style={labelStyle}>Latitude</div>
                            <div style={{ fontSize: '0.82em', color: theme.textPrimary, fontFamily: 'monospace' }}>
                              {selectedEvent.LatitudeSign}{selectedEvent.LatitudeDegree?.toFixed(5)}°
                            </div>
                          </div>
                          <div style={cellStyle}>
                            <div style={labelStyle}>Longitude</div>
                            <div style={{ fontSize: '0.82em', color: theme.textPrimary, fontFamily: 'monospace' }}>
                              {selectedEvent.LongitudeSign}{selectedEvent.LongitudeDegree?.toFixed(5)}°
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Caller */}
                  <div>
                    <div style={sectionHdr}>👤 Caller</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                      <div style={cellStyle}>
                        <div style={labelStyle}>Name</div>
                        <div style={{ ...valStyle, fontSize: '0.9em' }}>{selectedEvent.CallerName || '-'}</div>
                      </div>
                      <div style={cellStyle}>
                        <div style={labelStyle}>Phone</div>
                        <div style={{ fontSize: '0.88em', color: theme.textPrimary, fontFamily: 'monospace' }}>
                          {selectedEvent.CallerPhoneAreaCode || selectedEvent.CallerPhone
                            ? `(${selectedEvent.CallerPhoneAreaCode || '---'}) ${selectedEvent.CallerPhone || '---'}`
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CAD Record Link */}
                  <button
                    onClick={() => { /* URL to CAD event record will be wired here */ }}
                    style={{
                      width: '100%',
                      padding: '9px 14px',
                      background: 'transparent',
                      border: `1px solid ${theme.accentColor}`,
                      borderRadius: '6px',
                      color: theme.accentColor,
                      fontSize: '0.85em',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.accentColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = theme.accentColor;
                    }}>
                    🗂 View CAD Event Record
                  </button>

                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* View Response Plan Modal (Read-Only) */}
      {showViewPlaybookModal && viewingPlaybook && (
        <div onClick={() => setShowViewPlaybookModal(false)}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.75)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 3000,
            backdropFilter: 'blur(4px)'
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ 
              background: theme.cardBg, 
              borderRadius: '16px', 
              maxWidth: '900px', 
              width: '95%', 
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              border: `1px solid ${theme.borderColor}`,
              display: 'flex',
              flexDirection: 'column'
            }}>
            
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: `linear-gradient(135deg, #10B981 0%, #059669 100%)`,
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '1.3em' }}>📋</span>
                <div>
                  <h2 style={{ 
                    margin: 0, 
                    color: 'white',
                    fontSize: '1.1em',
                    fontWeight: '700'
                  }}>
                    Response Plan
                  </h2>
                  <div style={{
                    color: 'rgba(255,255,255,0.95)',
                    fontSize: '0.9em',
                    fontWeight: '600',
                    marginTop: '2px'
                  }}>
                    {viewingPlaybook.areaName}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowViewPlaybookModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5em',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                ×
              </button>
            </div>

            {/* Content - 2 Column Grid */}
            <div style={{ 
              padding: '20px 24px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              overflowY: 'auto'
            }}>
              
              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Status & Priority */}
                <div style={{
                  background: theme.detailBg,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.borderColor}`
                }}>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75em', color: theme.textSecondary, marginBottom: '4px' }}>📊 STATUS</div>
                      <div style={{ 
                        fontSize: '1.1em', 
                        fontWeight: '700',
                        color: viewingPlaybook.status === 'Active' ? '#10B981' : 
                               viewingPlaybook.status === 'Draft' ? '#F59E0B' : theme.textPrimary
                      }}>
                        {viewingPlaybook.status || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75em', color: theme.textSecondary, marginBottom: '4px' }}>⚡ PRIORITY</div>
                      <div style={{ 
                        fontSize: '1.1em', 
                        fontWeight: '700',
                        color: viewingPlaybook.priority === 'Required' ? '#EF4444' : 
                               viewingPlaybook.priority === 'Recommended' ? '#F59E0B' : '#10B981'
                      }}>
                        {viewingPlaybook.priority || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activation Criteria */}
                <div style={{
                  background: theme.detailBg,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.borderColor}`
                }}>
                  <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '12px' }}>
                    🎯 ACTIVATION CRITERIA
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Priority Threshold: </span>
                      <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                        {viewingPlaybook.priorityThreshold === 'all' ? 'All Priorities' : `Priority ${viewingPlaybook.priorityThreshold}`}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Time Window: </span>
                      <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                        {viewingPlaybook.timeWindow === 'always' ? 'Always Active' : 
                         viewingPlaybook.timeWindow === 'business_hours' ? 'Business Hours' :
                         viewingPlaybook.timeWindow === 'after_hours' ? 'After Hours' : 'Custom'}
                      </span>
                    </div>
                    {viewingPlaybook.timeWindow === 'custom' && (
                      <div>
                        <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Custom Hours: </span>
                        <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                          {viewingPlaybook.startTime} - {viewingPlaybook.endTime}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates */}
                {(viewingPlaybook.startDate || viewingPlaybook.expirationDate) && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '12px' }}>
                      📅 EFFECTIVE DATES
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {viewingPlaybook.startDate && (
                        <div>
                          <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Start: </span>
                          <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                            {new Date(viewingPlaybook.startDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {viewingPlaybook.expirationDate && (
                        <div>
                          <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Expires: </span>
                          <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                            {new Date(viewingPlaybook.expirationDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Approvals */}
                {(viewingPlaybook.createdBy || viewingPlaybook.approvedBy) && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '12px' }}>
                      ✍️ APPROVALS
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {viewingPlaybook.createdBy && (
                        <div>
                          <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Created by: </span>
                          <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                            {viewingPlaybook.createdBy}
                          </span>
                        </div>
                      )}
                      {viewingPlaybook.approvedBy && (
                        <div>
                          <span style={{ fontSize: '0.75em', color: theme.textSecondary }}>Approved by: </span>
                          <span style={{ fontSize: '0.9em', color: theme.textPrimary, fontWeight: '600' }}>
                            {viewingPlaybook.approvedBy}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Dispatch Guidance */}
                <div style={{
                  background: theme.detailBg,
                  padding: '16px',
                  borderRadius: '8px',
                  border: `2px solid #10B981`
                }}>
                  <div style={{ fontSize: '0.85em', fontWeight: '700', color: '#10B981', marginBottom: '10px' }}>
                    📞 DISPATCH GUIDANCE
                  </div>
                  <div style={{ 
                    fontSize: '0.95em', 
                    color: theme.textPrimary, 
                    lineHeight: '1.6'
                  }}>
                    {formatWithBullets(viewingPlaybook.dispatchGuidance) || 'None provided'}
                  </div>
                </div>

                {/* Staging Instructions */}
                {viewingPlaybook.stagingInstructions && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '10px' }}>
                      🚒 STAGING INSTRUCTIONS
                    </div>
                    <div style={{ 
                      fontSize: '0.95em', 
                      color: theme.textPrimary, 
                      lineHeight: '1.6'
                    }}>
                      {formatWithBullets(viewingPlaybook.stagingInstructions)}
                    </div>
                  </div>
                )}

                {/* Safety Notes */}
                {viewingPlaybook.safetyNotes && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `2px solid #F59E0B`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: '#F59E0B', marginBottom: '10px' }}>
                      ⚠️ SAFETY NOTES
                    </div>
                    <div style={{ 
                      fontSize: '0.95em', 
                      color: theme.textPrimary, 
                      lineHeight: '1.6'
                    }}>
                      {formatWithBullets(viewingPlaybook.safetyNotes)}
                    </div>
                  </div>
                )}

                {/* Supervisor Notification */}
                {viewingPlaybook.supervisorNotification && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '10px' }}>
                      👔 SUPERVISOR NOTIFICATION
                    </div>
                    <div style={{ 
                      fontSize: '0.95em', 
                      color: theme.textPrimary, 
                      lineHeight: '1.6'
                    }}>
                      {formatWithBullets(viewingPlaybook.supervisorNotification)}
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {viewingPlaybook.specialInstructions && (
                  <div style={{
                    background: theme.detailBg,
                    padding: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.borderColor}`
                  }}>
                    <div style={{ fontSize: '0.85em', fontWeight: '700', color: theme.accentColor, marginBottom: '10px' }}>
                      ℹ️ SPECIAL INSTRUCTIONS
                    </div>
                    <div style={{ 
                      fontSize: '0.95em', 
                      color: theme.textPrimary, 
                      lineHeight: '1.6'
                    }}>
                      {formatWithBullets(viewingPlaybook.specialInstructions)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Options Modal */}
      {showOptions && (
        <div onClick={() => setShowOptions(false)}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.6)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1000 
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ 
              background: theme.cardBg, 
              borderRadius: '12px', 
              padding: '30px', 
              maxWidth: '450px', 
              width: '90%', 
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)' 
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, color: theme.textPrimary }}>⚙️ Options</h2>
              <button onClick={() => setShowOptions(false)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  fontSize: '1.5em', 
                  cursor: 'pointer', 
                  color: theme.textSecondary 
                }}>
                ×
              </button>
            </div>

            {/* Theme Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Theme</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: theme.inputBg,
                border: `2px solid ${theme.inputBorder}`,
                padding: '12px 20px',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.2em' }}>☀️</span>
                  <span style={{ 
                    color: theme.textPrimary, 
                    fontSize: '0.95em',
                    fontWeight: isDarkTheme ? 'normal' : '600'
                  }}>Light</span>
                </div>
                
                {/* Slider Toggle */}
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '52px',
                  height: '28px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isDarkTheme}
                    onChange={(e) => setIsDarkTheme(e.target.checked)}
                    style={{
                      opacity: 0,
                      width: 0,
                      height: 0
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: isDarkTheme ? theme.accentColor : '#cbd5e0',
                    borderRadius: '28px',
                    transition: 'background-color 0.3s ease',
                    cursor: 'pointer'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '20px',
                      width: '20px',
                      left: isDarkTheme ? '28px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></span>
                  </span>
                </label>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ 
                    color: theme.textPrimary, 
                    fontSize: '0.95em',
                    fontWeight: isDarkTheme ? '600' : 'normal'
                  }}>Dark</span>
                  <span style={{ fontSize: '1.2em' }}>🌙</span>
                </div>
              </div>
            </div>

            {/* KPI Card Size */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>📏 KPI Card Size</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['small', 'medium', 'large'].map(size => (
                  <button
                    key={size}
                    onClick={() => setKpiSize(size)}
                    style={{
                      flex: 1,
                      background: kpiSize === size ? theme.accentColor : theme.inputBg,
                      border: `2px solid ${kpiSize === size ? theme.accentColor : theme.inputBorder}`,
                      padding: '10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: kpiSize === size ? 'white' : theme.textPrimary,
                      fontSize: '0.9em',
                      fontWeight: kpiSize === size ? '600' : 'normal',
                      transition: 'all 0.2s ease',
                      textTransform: 'capitalize'
                    }}
                    onMouseEnter={(e) => {
                      if (kpiSize !== size) {
                        e.currentTarget.style.borderColor = theme.accentColor;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (kpiSize !== size) {
                        e.currentTarget.style.borderColor = theme.inputBorder;
                      }
                    }}
                  >
                    {size === 'small' ? '📱 Small' : size === 'medium' ? '💻 Medium' : '🖥️ Large'}
                  </button>
                ))}
              </div>
              <div style={{ 
                fontSize: '0.75em', 
                color: theme.textSecondary, 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                {kpiSize === 'small' && 'Compact cards, more per row'}
                {kpiSize === 'medium' && 'Balanced size, default view'}
                {kpiSize === 'large' && 'Spacious cards, easier to read'}
              </div>
            </div>

            {/* Thresholds */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>KPI Thresholds</h3>
              <button
                onClick={() => {
                  setShowOptions(false);
                  setShowSettings(true);
                }}
                style={{
                  background: theme.inputBg,
                  border: `2px solid ${theme.inputBorder}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  fontSize: '1em',
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
              >
                ⚙️ Configure Thresholds
              </button>
            </div>

            {/* Auto-refresh controls */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Auto-Refresh</h3>
              <div style={{
                background: theme.detailBg,
                border: `2px solid ${theme.inputBorder}`,
                padding: '15px',
                borderRadius: '8px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '1em',
                  color: theme.textPrimary,
                  marginBottom: autoRefreshEnabled ? '15px' : '0'
                }}>
                  <input
                    type="checkbox"
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    style={{
                      cursor: 'pointer',
                      width: '20px',
                      height: '20px'
                    }}
                  />
                  <span>Enable Auto-Refresh</span>
                </label>
                
                {autoRefreshEnabled && (
                  <>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    paddingTop: '10px',
                    borderTop: `1px solid ${theme.borderColor}`
                  }}>
                    <label style={{ color: theme.textSecondary, fontSize: '0.95em' }}>
                      Refresh every
                    </label>
                    <input
                      type="number"
                      min="10"
                      step="5"
                      value={refreshInterval}
                      onChange={(e) => {
                        // Allow free typing — just update the value as-is
                        const value = parseInt(e.target.value);
                        if (!isNaN(value)) {
                          setRefreshInterval(value);
                          setRefreshIntervalWarning(false);
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 10) {
                          setRefreshInterval(10);
                          setRefreshIntervalWarning(true);
                        } else {
                          setRefreshIntervalWarning(false);
                        }
                      }}
                      style={{
                        width: '80px',
                        padding: '8px 12px',
                        border: `2px solid ${refreshIntervalWarning ? '#F59E0B' : theme.inputBorder}`,
                        borderRadius: '6px',
                        background: theme.cardBg,
                        color: theme.textPrimary,
                        fontSize: '1em',
                        textAlign: 'center'
                      }}
                    />
                    <label style={{ color: theme.textSecondary, fontSize: '0.95em' }}>
                      seconds
                    </label>
                  </div>
                  {refreshIntervalWarning && (
                    <div style={{
                      marginTop: '6px',
                      fontSize: '0.82em',
                      color: '#F59E0B',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ⚠️ Minimum refresh rate is 10 seconds. Value set to 10.
                    </div>
                  )}
                  </>
                )}
                <div style={{ 
                  marginTop: '10px', 
                  fontSize: '0.85em', 
                  color: theme.textSecondary,
                  fontStyle: 'italic'
                }}>
                  {autoRefreshEnabled 
                    ? `Data will refresh automatically every ${refreshInterval} seconds` 
                    : 'Data will only load once at page load'}
                </div>
              </div>
            </div>

            {/* Reset Cards */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Reset Cards</h3>
              <button
                onClick={() => {
                  setKpiOrder([6, 0, 1, 2, 3, 4, 5, 13, 7, 10, 11, 8, 9, 12]);
                  setVisibleKpis([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]);
                  setVisibleCharts(['callType', 'hourly', 'timeline', 'responseTime', 'dayOfWeek', 'month']);
                }}
                style={{
                  background: theme.inputBg,
                  border: `2px solid ${theme.inputBorder}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  fontSize: '1em',
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
                title="Reset KPI cards and charts - restore all hidden items"
              >
                🔄 Reset All Cards and Charts
              </button>
            </div>

            {/* Reset Chart Layout */}
            <div style={{ marginBottom: '0' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Reset Chart Layout</h3>
              <button
                onClick={resetChartLayout}
                style={{
                  background: theme.inputBg,
                  border: `2px solid ${theme.inputBorder}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  fontSize: '1em',
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
                title="Reset chart sizes and positions to default"
              >
                📐 Reset Chart Sizes and Positions
              </button>
            </div>

            {/* Reset Map Size */}
            <div style={{ marginBottom: '0' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Reset Map Size</h3>
              <button
                onClick={resetMapSize}
                style={{
                  background: theme.inputBg,
                  border: `2px solid ${theme.inputBorder}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  fontSize: '1em',
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
                title="Reset map size to default (100% width, 600px height)"
              >
                🗺️ Reset Map Size
              </button>
            </div>

            {/* Reset Section Heights */}
            <div style={{ marginBottom: '0' }}>
              <h3 style={{ color: theme.textPrimary, fontSize: '1.1em', marginBottom: '10px' }}>Reset Section Heights</h3>
              <button
                onClick={() => {
                  setSectionHeights({ kpi: null, charts: null, eventTime: null, repeatedEvents: null, recentEvents: null });
                }}
                style={{
                  background: theme.inputBg,
                  border: `2px solid ${theme.inputBorder}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  fontSize: '1em',
                  transition: 'all 0.3s ease',
                  width: '100%'
                }}
                title="Reset all section heights to auto (fit content)"
              >
                📏 Reset Section Heights
              </button>
            </div>

            {/* ✅ Map Settings Section */}
            <div style={{ 
              marginBottom: '25px', 
              padding: '15px', 
              background: theme.detailBg, 
              borderRadius: '8px',
              border: `1px solid ${theme.borderColor}`
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                color: theme.accentColor, 
                fontSize: '1.1em',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🗺️ Map Settings
              </h3>
              
              <div style={{ marginBottom: '12px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <div>
                    <div style={{ 
                      fontWeight: '600', 
                      color: theme.textPrimary,
                      marginBottom: '4px'
                    }}>
                      Default Map Location (Home)
                    </div>
                    <div style={{ 
                      fontSize: '0.85em', 
                      color: theme.textSecondary 
                    }}>
                      Lat: {defaultMapLocation.lat.toFixed(4)}, Lng: {defaultMapLocation.lng.toFixed(4)}, Zoom: {defaultMapLocation.zoom}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={saveCurrentMapLocationAsDefault}
                      style={{
                        padding: '8px 16px',
                        background: theme.accentColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      📍 Save Current
                    </button>
                    <button
                      onClick={resetToDefaultLocation}
                      style={{
                        padding: '8px 16px',
                        background: theme.detailBg,
                        color: theme.textPrimary,
                        border: `1px solid ${theme.borderColor}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      title="Reset to Little Rock, AR"
                    >
                      🔄 Reset
                    </button>
                  </div>
                </div>
                <div style={{ 
                  fontSize: '0.8em', 
                  color: theme.textSecondary,
                  fontStyle: 'italic'
                }}>
                  Use the 🏠 and 📍 buttons on the map to return or save your location
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Polygon Dialog */}
      {showSavePolygonDialog && (
        <div 
          onClick={() => {
            setShowSavePolygonDialog(false);
            setPolygonNameInput('');
          }}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.6)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1002 
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              background: theme.cardBg, 
              borderRadius: '12px', 
              padding: '30px', 
              maxWidth: '450px', 
              width: '90%', 
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)' 
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '20px', 
              paddingBottom: '15px', 
              borderBottom: `2px solid ${theme.borderColor}` 
            }}>
              <h2 style={{ margin: 0, color: theme.accentColor }}>💾 Save Polygon</h2>
              <button 
                onClick={() => {
                  setShowSavePolygonDialog(false);
                  setPolygonNameInput('');
                }}
                style={{ 
                  background: theme.detailBg, 
                  border: 'none', 
                  width: '30px', 
                  height: '30px', 
                  borderRadius: '50%', 
                  fontSize: '1.5em', 
                  cursor: 'pointer', 
                  color: theme.textPrimary 
                }}
              >
                ×
              </button>
            </div>
            
            <p style={{ color: theme.textSecondary, marginBottom: '20px' }}>
              Give this polygon a name so you can load it later:
            </p>
            
            <input
              type="text"
              value={polygonNameInput}
              onChange={(e) => setPolygonNameInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  confirmSavePolygon();
                }
              }}
              placeholder="e.g., Downtown Area, Service Zone 1..."
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: `2px solid ${theme.inputBorder}`,
                borderRadius: '6px',
                background: theme.inputBg,
                color: theme.textPrimary,
                fontSize: '1em',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            
            <div style={{ 
              fontSize: '0.85em', 
              color: theme.textSecondary, 
              marginBottom: '20px',
              padding: '12px',
              background: theme.detailBg,
              borderRadius: '6px'
            }}>
              <strong>Polygon Info:</strong><br/>
              • {polygonCoords?.length || 0} points<br/>
              • Filter mode: {polygonFilterMode === 'none' ? 'Not applied' : polygonFilterMode}
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={confirmSavePolygon}
                disabled={!polygonNameInput.trim()}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: polygonNameInput.trim() ? '#14B8A6' : theme.detailBg, 
                  color: polygonNameInput.trim() ? 'white' : theme.textSecondary, 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: polygonNameInput.trim() ? 'pointer' : 'not-allowed', 
                  fontWeight: '600',
                  fontSize: '1em',
                  opacity: polygonNameInput.trim() ? 1 : 0.5
                }}
              >
                💾 Save Polygon
              </button>
              <button 
                onClick={() => {
                  setShowSavePolygonDialog(false);
                  setPolygonNameInput('');
                }}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: theme.detailBg, 
                  color: theme.textPrimary, 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: 'pointer', 
                  fontWeight: '600',
                  fontSize: '1em'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal - keeping original */}
      {showSettings && (
        <div onClick={() => setShowSettings(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ 
              background: theme.cardBg, 
              borderRadius: '12px', 
              padding: '30px', 
              maxWidth: '520px', 
              width: '90%', 
              maxHeight: '85vh',
              overflowY: 'auto',
              overflowX: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)' 
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '15px', borderBottom: `2px solid ${theme.borderColor}` }}>
              <h2 style={{ margin: 0, color: theme.accentColor }}>⚙️ Dashboard Settings</h2>
              <button onClick={() => setShowSettings(false)}
                style={{ background: theme.detailBg, border: 'none', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.5em', cursor: 'pointer', color: theme.textPrimary }}>
                ×
              </button>
            </div>
            <p style={{ color: theme.textSecondary, marginBottom: '20px' }}>
              Set thresholds for status counts. KPI cards will turn yellow when at threshold and red when exceeded.
            </p>
            
            {/* Thresholds Section - Two Column Layout */}
            <h3 style={{ 
              margin: '20px 0 15px 0', 
              color: theme.accentColor, 
              fontSize: '1.2em'
            }}>
              ⚙️ Threshold Settings
            </h3>
            
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {/* Left Column - Count-based Thresholds */}
              <div style={{ flex: '0 0 auto', width: '220px' }}>
                <h4 style={{ 
                  margin: '0 0 15px 0', 
                  color: theme.textPrimary, 
                  fontSize: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📊 Count Thresholds
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { key: 'active', label: 'Active Events' },
                    { key: 'pending', label: 'Pending' },
                    { key: 'dispatched', label: 'Dispatched' },
                    { key: 'enroute', label: 'Enroute' },
                    { key: 'arrived', label: 'Arrived' },
                    { key: 'closed', label: 'Closed' },
                    { key: 'total', label: 'Total Events' }
                  ].map((threshold) => (
                    <div key={threshold.key}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.9em', color: theme.textPrimary }}>
                        {threshold.label}
                      </label>
                      <input 
                        type="number" 
                        min="0" 
                        value={thresholds[threshold.key] || ''}
                        onChange={(e) => setThresholds(prev => ({ ...prev, [threshold.key]: e.target.value ? parseInt(e.target.value) : null }))}
                        placeholder="Enter count"
                        style={{ 
                          width: '150px', 
                          padding: '8px', 
                          border: `2px solid ${theme.inputBorder}`, 
                          borderRadius: '6px', 
                          background: theme.inputBg, 
                          color: theme.textPrimary,
                          fontSize: '0.9em'
                        }} 
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right Column - Time-based Thresholds */}
              <div style={{ flex: '0 0 auto', width: '220px' }}>
                <h4 style={{ 
                  margin: '0 0 10px 0', 
                  color: theme.textPrimary, 
                  fontSize: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⏱️ Time Thresholds (minutes)
                </h4>
                <p style={{ 
                  color: theme.textSecondary, 
                  fontSize: '0.8em',
                  marginBottom: '12px',
                  fontStyle: 'italic'
                }}>
                  Set threshold time and warning % (yellow alert when median reaches % of threshold, red at 100%+)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { key: 'pendingTime', label: 'Pending Time', description: 'Received → Dispatched' },
                    { key: 'responseTime', label: 'Response Time', description: 'Dispatched → Arrived' },
                    { key: 'turnoutTime', label: 'Turnout Time', description: 'Dispatched → Enroute' },
                    { key: 'travelTime', label: 'Travel Time', description: 'Enroute → Arrived' },
                    { key: 'onSceneTime', label: 'On Scene Time', description: 'Arrived → Completed' },
                    { key: 'eventTotalTime', label: 'Event Total Time', description: 'Received → Completed' },
                    { key: 'eventCreationTime', label: 'Event Creation Time', description: 'Event Start → Received' }
                  ].map((threshold) => (
                    <div key={threshold.key}>
                      <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', fontSize: '0.9em', color: theme.textPrimary }}>
                        {threshold.label}
                      </label>
                      <div style={{ 
                        fontSize: '0.75em', 
                        color: theme.textSecondary,
                        marginBottom: '4px'
                      }}>
                        {threshold.description}
                      </div>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
                        <div style={{ flex: '0 0 120px' }}>
                          <div style={{ 
                            fontSize: '0.75em', 
                            color: theme.textSecondary,
                            marginBottom: '2px'
                          }}>
                            Threshold (min)
                          </div>
                          <input 
                            type="number" 
                            min="0" 
                            step="0.1"
                            value={thresholds[threshold.key] || ''}
                            onChange={(e) => setThresholds(prev => ({ 
                              ...prev, 
                              [threshold.key]: e.target.value ? parseFloat(e.target.value) : null 
                            }))}
                            placeholder="e.g., 5"
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: `2px solid ${theme.inputBorder}`, 
                              borderRadius: '6px', 
                              background: theme.inputBg, 
                              color: theme.textPrimary,
                              fontSize: '0.9em'
                            }} 
                          />
                        </div>
                        <div style={{ flex: '0 0 70px' }}>
                          <div style={{ 
                            fontSize: '0.75em', 
                            color: theme.textSecondary,
                            marginBottom: '2px'
                          }}>
                            Warning %
                          </div>
                          <input 
                            type="number" 
                            min="1" 
                            max="99"
                            step="1"
                            value={thresholds[threshold.key + 'Warning'] || 90}
                            onChange={(e) => setThresholds(prev => ({ 
                              ...prev, 
                              [threshold.key + 'Warning']: e.target.value ? parseInt(e.target.value) : 90 
                            }))}
                            placeholder="90"
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: `2px solid ${theme.inputBorder}`, 
                              borderRadius: '6px', 
                              background: theme.inputBg, 
                              color: theme.textPrimary,
                              fontSize: '0.9em'
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowSettings(false)}
                style={{ flex: 1, padding: '12px', background: theme.accentColor, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                Save Settings
              </button>
              <button onClick={() => setShowSettings(false)}
                style={{ flex: 1, padding: '12px', background: theme.detailBg, color: theme.textPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CADDashboardFixed;
